/**
 * main.ts — Application bootstrap for Project_X
 *
 * Responsibilities:
 *  1. Generate a unique player ID (or read from URL params)
 *  2. Create and initialize the AppShell with WebSocket connection
 *  3. Instantiate all frontend screen components
 *  4. Wire render callbacks to DOM updates
 *  5. Handle screen transitions (landing → queue → match → game over)
 *  6. Wire player interactions (play, sell, reposition, upgrade) to DOM events
 *  7. Map server messages to UI state updates through GameStateService
 *  8. Handle combat phase transitions and combat event animation
 *  9. Handle reconnection and game-over states
 *
 * This file is the single entry point loaded by index.html.
 * It orchestrates the complete match lifecycle in the browser.
 */

import { AppShell, AppShellEvent } from './frontend/AppShell';
import { QueueScreen, QueueScreenRenderData } from './frontend/screens/QueueScreen';
import {
  QUEUE_MODES,
  QUEUE_MODE_LABELS,
  QUEUE_MODE_PLAYER_COUNTS,
  QUEUE_MODE_SHORT,
} from './shared/models/QueueModes';
import { MatchHUD, MatchHUDRenderData, PlayerSummary } from './frontend/screens/MatchHUD';
import { HandUI, HandUIRenderData, HandCardRenderData } from './frontend/screens/HandUI';
import { BattlefieldUI, BattlefieldUIRenderData, BattlefieldMinionRenderData } from './frontend/screens/BattlefieldUI';
import { CombatBoardView, CombatBoardViewRenderData, CombatMinionRenderData } from './frontend/screens/CombatBoardView';
import {
  updateCombatMinionElements,
  clearCombatMinionElements as clearCombatMinionElementSet,
} from './frontend/services/CombatMinionDom';
import { PreparationTimerUI, PreparationTimerRenderData } from './frontend/screens/PreparationTimerUI';
import { UpgradeChoiceUI, UpgradeChoiceUIRenderData } from './frontend/screens/UpgradeChoiceUI';
import { CombatAnimationView, CombatAnimationViewRenderData, AnimatedCombatEvent } from './frontend/screens/CombatAnimationView';
import { SimultaneousDamageAnimator, SimultaneousDamageAnimationGroup } from './frontend/screens/SimultaneousDamageAnimation';
import { CombatGoldDisplay, CombatGoldDisplayRenderData } from './frontend/screens/CombatGoldDisplay';
import { CombatResultUI } from './frontend/screens/CombatResultUI';
import { DragAndDropService } from './frontend/services/DragAndDropService';
import { CardInspectService } from './frontend/services/CardInspectService';
import {
  computeHandCardScale,
  maxScaleForBaseWidth,
  HAND_CARD_SCALE_PROPERTY,
  HAND_STRIP_SCALE_PROPERTY,
  HAND_CARD_BASE_WIDTH,
  HAND_CARD_REF_COUNT,
  HAND_CARD_FIT_FACTOR,
  CARD_MAX_SCALE,
  MOBILE_HAND_MAX_FIT_COUNT,
  BOARD_CARD_SCALE_PROPERTY,
  BOARD_CARD_BASE_WIDTH,
  BOARD_CARD_REF_COUNT,
  COMBAT_CARD_SCALE_PROPERTY,
  COMBAT_CARD_BASE_WIDTH,
  COMBAT_CARD_REF_COUNT,
} from './frontend/services/HandScaleService';
import { SpectatorModeUI, SpectatorModeRenderData } from './frontend/screens/SpectatorModeUI';
import { EliminatedPlayerScreen, EliminatedPlayerRenderData } from './frontend/screens/EliminatedPlayerScreen';
import { WinnerScreen, WinnerScreenRenderData, PlayerStanding } from './frontend/screens/WinnerScreen';
import { ReconnectUI, ReconnectRenderData } from './frontend/screens/ReconnectUI';
import { ConnectionState } from './frontend/models/ConnectionState';
import { FullscreenService } from './frontend/services/FullscreenService';
import { AuthService, clearSession } from './frontend/services/AuthService';
import { CrazyGamesService, isCrazyGamesContext, getGlobalCrazyGames } from './frontend/services/CrazyGamesService';
import { getPlatform, setPlatform, createPlatform, getGlobalCrazyGamesGame, type PlatformAdapter } from './frontend/services/PlatformAdapter';
import {
  crazyGamesSyncRoom,
  crazyGamesLeaveRoom,
  crazyGamesRoomActionForState,
} from './frontend/services/CrazyGamesRoom';
import { createMinionCard, type MinionCardData } from './frontend/components/MinionCard';
import { GAME_ABILITY_DEFINITIONS } from './services/GameAbilities';
import {
  MINION_TYPE_NAMES,
  MINION_TYPE_ICONS,
  MINION_TYPE_COLORS,
  MinionType,
} from './shared/models/MinionType';
import { resolveSkinImage, orderSkinClasses } from './shared/skins/SkinDefinitions';
import { GameConfig } from './config/gameConfig';
import { GRANDMASTER_ABILITY_ID } from './shared/models/AbilityConstants';
import { RUNE_DEFINITIONS, getRuneById } from './shared/models/RuneDefinitions';
import { HIGH_STAKES_BRANCH_ID, resolveAbilityNames } from './shared/models/UpgradePath';
import {
  testMinions,
  UNDEAD_MINION_DEFINITION,
  SWARMER_MINION_DEFINITION,
} from './shared/models/testMinions';
import type { MinionDefinition } from './shared/models/MinionDefinition';
import { TRANSFORM_DEFINITIONS, getTransformTargetDefinition } from './shared/models/TransformDefinitions';
import {
  KEYWORD_DEPENDENCIES,
  KEYWORD_CLASS_UNLOCKS,
  RUNE_UNIT_EFFECTS,
  GRANDMASTER_ROW,
  DOUBLE_GRANDMASTER_ROW,
} from './shared/models/UpgradePath';
import {
  getEffectiveRuneCap,
  getRuneGoldCost,
  isRuneFeastActive,
} from './shared/models/RunePurchaseRules';
import type { AbilityDefinition } from './shared/models/AbilityRegistry';
import {
  keywordClasses,
  sortKeywordsByLevel,
  filterKeywordsByClass,
  formatLevelRequirement,
  keywordClassLabel,
} from './frontend/services/KeywordsOverlay';
import { buildHowToPlayContent } from './frontend/services/HowToPlay';
import { TutorialShell, TUTORIAL_TIMER_SECONDS } from './frontend/services/TutorialShell';
import { TutorialService } from './frontend/services/TutorialService';
import { FxCanvas, fxForAbility, FX_GOLD_COLORS, FX_RED_COLORS, type FxKind } from './frontend/services/FxCanvas';
import { floatText, screenFlash, retriggerClass, boomText, isReducedMotion } from './frontend/services/FxTriggers';
import { soundService } from './frontend/services/SoundService';
import { consumeCardFxEvents } from './frontend/services/CardFxEvents';
import { shouldShowKillGoldFloat } from './frontend/services/KillGoldFloatRules';
import { getSpellDefinition, SPELL_DEFINITIONS, SPELL_MINION_TYPE, POWER_TOUCH_STAT_BONUS, BAN_HAMMER_SPELL_ID, UPGRADED_SPELL_ID, ENRICHMENT_SPELL_ID } from './shared/models/SpellDefinitions';
import {
  SHARE_TARGETS,
  buildShareCaption,
  copyToClipboard,
  getShareTarget,
  openShareWindow,
} from './frontend/services/ShareService';
import type { ShareTargetId } from './frontend/services/ShareService';
import { COMBAT_RESULT_DISPLAY_MS } from './shared/CombatAnimationTiming';
import {
  AchievementInfo,
  AchievementUnlocked,
  PlayerStats,
  SkinInfo,
  ToplistEntry,
  type CardFxEvent,
  type OverdrawCardData,
  type CoopMatchInfo,
  type MinionInHandData,
  type MinionOnBoardData,
  type PlayerData,
} from './shared/messages/MessageContracts';
import {
  getCoopMissionById,
  COOP_MISSIONS,
  COOP_GIVE_COST,
  COOP_HUMAN_BOARD_SIZE,
} from './shared/models/CoopMissions';
import type { CoopLobbyClientState } from './frontend/services/GameStateService';

// ─── Configuration ────────────────────────────────────────────────────────

/** WebSocket server URL — derived from page origin so it works in dev (Vite proxy) and production (same server). Can be overridden via URL parameter `server`. */
const DEFAULT_SERVER_URL = (() => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
})();

/**
 * CrazyGames subpage (<origin>/crazygames) — the platform's user IS the
 * identity: no login screen, and the player signs in as a FULL real user
 * (verified CrazyGames token → server-side account). The same index.html
 * serves both pages; the URL path decides the mode.
 */
const IS_CRAZYGAMES_MODE = isCrazyGamesContext(window.location.pathname);

/**
 * CrazyGames-subpage guest: the visitor chose "Play as Guest" (a normal
 * guest seat — no account, no progress). Then "Switch Player" on the
 * identity line is meaningful (it re-shows the guest/sign-in choice).
 */
let crazyGamesIdentityIsGuest = false;

/** The one CrazyGames service instance for the page (shared by the auto
 *  sign-in flow and the "Sign in to save progress" button). */
let crazyGamesService: CrazyGamesService | null = null;

// ─── DOM Element References ───────────────────────────────────────────────

// Connection banner
const connectionBanner = document.getElementById('connection-banner')!;

// Reconnect overlay
const reconnectOverlay = document.getElementById('reconnect-overlay')! as HTMLDivElement;
const reconnectMessage = document.getElementById('reconnect-message')!;
const reconnectStatus  = document.getElementById('reconnect-status')!;

// Landing / Queue screen
const landingScreen    = document.getElementById('landing-screen')!;
// Ambient particle background canvas (behind BOTH the identity-choices
// "main landing page" and the logged-in queue view) — drawn by
// startLandingParticles(). Decorative only (pointer-events:none in CSS).
const landingParticlesCanvas = document.getElementById('landing-particles') as HTMLCanvasElement | null;
// Landing fire FX canvas (between the castle and stones layers of
// #landing-bg) — drawn by startLandingFires(). Decorative only
// (pointer-events:none in CSS). Plays on desktop + landscape mobile,
// hidden on portrait (vertical) layouts.
const landingFiresCanvas = document.getElementById('landing-fires') as HTMLCanvasElement | null;
const queueStatusEl    = document.getElementById('queue-status')!;
const queueStatsEl     = document.getElementById('queue-stats')!;
// The queue message box (status + stats + the quick-host button). Hidden
// when NOT queued (no "choose a queue" box on the landing page); visible
// while queued ("Looking for games…" + the Quick host lobby button).
const queueInfoEl      = document.getElementById('queue-info')!;
const btnQuickHost     = document.getElementById('btn-quick-host')! as HTMLButtonElement;
// Bot-fill countdown hint ("Starting with Bots in: X seconds") — the server
// pushes the remaining seconds in QueueStatusUpdate only in the final 20 s
// of the 30 s bot-fill window; hidden while the field is absent.
const queueBotFillCountdownEl = document.getElementById('queue-bot-fill-countdown')!;
const btnLeaveQueue    = document.getElementById('btn-leave-queue')! as HTMLButtonElement;
// Queue-mode picker (fast 4-player vs long 8-player game — shared spec):
// revealed by the Play menu's "Join Ranked Queue" option; a player can
// only be in ONE queue at a time, so while queued the picker is hidden
// and Leave Queue is shown.
const queueModePickerEl = document.getElementById('queue-mode-picker')!;
const btnJoinQueueFast  = document.getElementById('btn-join-queue-fast')! as HTMLButtonElement;
const btnJoinQueueLong  = document.getElementById('btn-join-queue-long')! as HTMLButtonElement;
const btnQueueModeCancel = document.getElementById('btn-queue-mode-cancel')! as HTMLButtonElement;

// ─── COOP (task 163 lobby + task 164 match UI) ─────────────────────────
// The COOP lobby screen (its own screen).
const coopLobbyScreen      = document.getElementById('coop-lobby-screen')!;
const coopLobbyIdentityEl  = document.getElementById('coop-lobby-identity-name')!;
const coopLobbyCodeEl      = document.getElementById('coop-lobby-code')!;
const coopLobbyCodeHintEl  = document.getElementById('coop-lobby-code-hint')!;
const coopLobbyHostNameEl  = document.getElementById('coop-lobby-host-name')!;
const coopLobbyMemberNameEl = document.getElementById('coop-lobby-member-name')!;
const coopLobbyMissionNameEl = document.getElementById('coop-lobby-mission-name')!;
const coopLobbyMissionDescEl = document.getElementById('coop-lobby-mission-desc')!;
const coopLobbyMissionSelectRowEl = document.getElementById('coop-lobby-mission-select-row')!;
const coopLobbyMissionSelectEl = document.getElementById('coop-lobby-mission-select')! as HTMLSelectElement;
const coopLobbySideRowEl = document.getElementById('coop-lobby-side-row')!;
const coopSideLeftEl = document.getElementById('coop-side-left')! as HTMLButtonElement;
const coopSideRightEl = document.getElementById('coop-side-right')! as HTMLButtonElement;
const coopLobbySideNoteEl = document.getElementById('coop-lobby-side-note')!;
const btnCoopLobbyLeave    = document.getElementById('btn-coop-lobby-leave')! as HTMLButtonElement;
const btnCoopLobbyStart    = document.getElementById('btn-coop-lobby-start')! as HTMLButtonElement;

// ─── Unified lobby (Play menu + Create Game + lobby screen) ───────────
// The Play button (the single primary entry, task 191 — enlarged + slow
// pulsing glow) + its pickers: the GAME-MODE picker (Singleplayer /
// Multiplayer) first, then the MULTIPLAYER picker (Join Ranked Queue /
// Join Lobby / Create Game). One sub-menu at a time; the Play button is
// hidden while any of them is open.
const btnPlay          = document.getElementById('btn-play')! as HTMLButtonElement;
const gameModePickerEl = document.getElementById('game-mode-picker')!;
const btnSolo          = document.getElementById('btn-solo')! as HTMLButtonElement;
const btnMultiplayer   = document.getElementById('btn-multiplayer')! as HTMLButtonElement;
const btnMechanics     = document.getElementById('btn-mechanics')! as HTMLButtonElement;
const btnGameModeCancel = document.getElementById('btn-game-mode-cancel')! as HTMLButtonElement;
// Game Mechanics (task 202): the nested Runes / Spells / Keywords
// sub-menu (Play → Game Mechanics → catalogue).
const mechanicsPickerEl   = document.getElementById('mechanics-picker')!;
const btnMechanicsCancel  = document.getElementById('btn-mechanics-cancel')! as HTMLButtonElement;
// Progress (task 203): the achievements / stats / Top 100 sub-menu
// (Play → Progress). Top 100 has TWO entries: the corner icon
// (#btn-toplist) and this sub-menu's (#btn-toplist-progress).
const progressPickerEl    = document.getElementById('progress-picker')!;
const btnProgress         = document.getElementById('btn-progress')! as HTMLButtonElement;
const btnProgressCancel   = document.getElementById('btn-progress-cancel')! as HTMLButtonElement;
const btnToplistProgress  = document.getElementById('btn-toplist-progress')! as HTMLButtonElement;
const playPickerEl     = document.getElementById('play-picker')!;
const btnPlayRanked    = document.getElementById('btn-play-ranked')! as HTMLButtonElement;
const btnPlayCreate    = document.getElementById('btn-play-create')! as HTMLButtonElement;
const btnPlayCancel    = document.getElementById('btn-play-cancel')! as HTMLButtonElement;
// Join Lobby — the unified code input (resolves Original + Co-op).
const joinLobbyRowEl   = document.getElementById('join-lobby-row')!;
const lobbyCodeInputEl = document.getElementById('lobby-code-input')! as HTMLInputElement;
const btnLobbyJoin     = document.getElementById('btn-lobby-join')! as HTMLButtonElement;
// The Create Game screen.
const createGameScreen = document.getElementById('create-game-screen')!;
const cgNameInput      = document.getElementById('cg-name')! as HTMLInputElement;
const cgVisibilityEl   = document.getElementById('cg-visibility')!;
const cgGameTypeEl     = document.getElementById('cg-gametype')!;
const cgSizeFieldEl    = document.getElementById('cg-size-field')!;
const cgSizeEl         = document.getElementById('cg-size')!;
const cgLifeStartFieldEl = document.getElementById('cg-lifestart-field')!;
const cgLifeDefaultEl  = document.getElementById('cg-life-default')! as HTMLInputElement;
const cgLifeCustomEl   = document.getElementById('cg-life-custom')!;
const cgLifeValueEl    = document.getElementById('cg-life-value')! as HTMLInputElement;
const cgRankedFieldEl  = document.getElementById('cg-ranked-field')!;
const cgRankedCheckbox = document.getElementById('cg-ranked')! as HTMLInputElement;
const btnCgCancel      = document.getElementById('btn-cg-cancel')! as HTMLButtonElement;
const btnCgStart       = document.getElementById('btn-cg-start')! as HTMLButtonElement;
// The unified Original lobby screen.
const lobbyScreen        = document.getElementById('lobby-screen')!;
const lobbyIdentityEl    = document.getElementById('lobby-identity-name')!;
const lobbyNameEl        = document.getElementById('lobby-name')!;
const lobbyChipTypeEl    = document.getElementById('lobby-chip-type')!;
const lobbyChipSizeEl    = document.getElementById('lobby-chip-size')!;
const lobbyChipVisEl     = document.getElementById('lobby-chip-visibility')!;
const lobbyChipRankedEl  = document.getElementById('lobby-chip-ranked')!;
const lobbyChipLifeEl    = document.getElementById('lobby-chip-lifestart')!;
const lobbyCodeEl        = document.getElementById('lobby-code')!;
const lobbyCodeHintEl    = document.getElementById('lobby-code-hint')!;
const lobbyPlayersListEl = document.getElementById('lobby-players-list')!;
const lobbyCountdownEl   = document.getElementById('lobby-countdown')!;
const lobbyInviteRowEl   = document.getElementById('lobby-invite-row')!;
const btnLobbyInvite     = document.getElementById('btn-lobby-invite')! as HTMLButtonElement;
const btnLobbyLeave      = document.getElementById('btn-lobby-leave')! as HTMLButtonElement;
const btnLobbyStart      = document.getElementById('btn-lobby-start')! as HTMLButtonElement;

// The COOP prep board (enemy row on top + the teammate's half beside the
// own board — task 164). Hidden outside coop team matches.

// Shared-life HUD: the teammate's name next to the (shared) team life.
const hudCoopTeamName      = document.getElementById('hud-coop-team-name') as HTMLElement | null;
// Daily login streak panel (top-left of the landing page; logged-in only).
const streakPanelEl    = document.getElementById('streak-panel')!;
const streakStatusEl   = document.getElementById('streak-status')!;
const streakDaysEl     = document.getElementById('streak-days')!;
const streakProgressEl = document.getElementById('streak-progress')!;
const btnClaimStreak   = document.getElementById('btn-claim-streak')! as HTMLButtonElement;

// Landing: auth (login / create account / guest) + queue view
const authViewEl            = document.getElementById('auth-view')!;
const authChoicesEl         = document.getElementById('auth-choices')!;
const queueViewEl           = document.getElementById('queue-view')!;
const identityNameEl        = document.getElementById('identity-name')!;
const btnLogout             = document.getElementById('btn-logout')! as HTMLButtonElement;
const btnLogin              = document.getElementById('btn-login')! as HTMLButtonElement;
const btnCreateAccount      = document.getElementById('btn-create-account')! as HTMLButtonElement;
const btnPlayGuest          = document.getElementById('btn-play-guest')! as HTMLButtonElement;
const loginFormEl           = document.getElementById('login-form')!;
const loginUsernameEl       = document.getElementById('login-username')! as HTMLInputElement;
const loginPasswordEl       = document.getElementById('login-password')! as HTMLInputElement;
const loginErrorEl          = document.getElementById('login-error')!;
const btnBackFromLogin      = document.getElementById('btn-back-from-login')! as HTMLButtonElement;
const registerFormEl        = document.getElementById('register-form')!;
const registerUsernameEl    = document.getElementById('register-username')! as HTMLInputElement;
const registerPasswordEl    = document.getElementById('register-password')! as HTMLInputElement;
const registerPasswordConfirmEl = document.getElementById('register-password-confirm')! as HTMLInputElement;
const registerErrorEl       = document.getElementById('register-error')!;
const btnBackFromRegister   = document.getElementById('btn-back-from-register')! as HTMLButtonElement;
// CrazyGames subpage sign-in choices (hidden outside /crazygames)
const cgAuthViewEl          = document.getElementById('cg-auth-view')!;
const btnCgGuest            = document.getElementById('btn-cg-guest')! as HTMLButtonElement;
const btnCgSignin           = document.getElementById('btn-cg-signin')! as HTMLButtonElement;
// TEMPORARY (QA builds): explicit "Login with CrazyGames" button (identity
// line) — the platform's official sign-in popup + token login. CG subpage
// only.
const btnCgQaLogin          = document.getElementById('btn-cg-qa-login')! as HTMLButtonElement;
const cgAuthNoteEl          = document.getElementById('cg-auth-note')!;

// Match screen
const matchScreen      = document.getElementById('match-screen')!;
const matchHud         = document.getElementById('match-hud')!;
const hudLife          = document.getElementById('hud-life')!;
const hudGold          = document.getElementById('hud-gold')!;
const hudRound         = document.getElementById('hud-round')!;
// #hud-phase (the "preparation/Combat/locked" text) was REMOVED (playtest:
// "can be removed") — the floating timer box is the only phase indicator
// left (hidden in combat, red-pulsing in the last 5 s).
const timerBox         = document.getElementById('hud-timer')!;
const timerValue       = document.getElementById('timer-value')!;
const btnFullscreen    = document.getElementById('btn-fullscreen')! as HTMLButtonElement;
const hudPlayers       = document.getElementById('hud-players')!;
// Rotate-device hint (mobile, in-game only — see begin/releaseGameOrientationEnforcement)
const rotateHintOverlay = document.getElementById('rotate-hint-overlay')! as HTMLElement;

// battlefield is a let so it can be replaced with a fresh clone to clear accumulated event listeners
let battlefield        = document.getElementById('battlefield')!;

// Spectator banner
const spectatorBanner  = document.getElementById('spectator-banner')! as HTMLDivElement;
const spectatorMessageEl = document.getElementById('spectator-message')!;

// Preparation area
const preparationArea  = document.getElementById('preparation-area')! as HTMLDivElement;
const handArea         = document.getElementById('hand-area')!;
const handLabel        = document.getElementById('hand-label')!;
const handCount        = document.getElementById('hand-count')!;
const handContainer    = document.getElementById('hand')!;
// COOP teammate viewer: the eye button (upper-right corner of the hand,
// preparation only) + the overlay showing the teammate's hand and board.
const teammateEyeBtn   = document.getElementById('teammate-eye-btn')! as HTMLButtonElement;
const teammateOverlay  = document.getElementById('teammate-overlay')! as HTMLDivElement;
const teammateCloseBtn = document.getElementById('teammate-close')! as HTMLButtonElement;
const teammateNameEl   = document.getElementById('teammate-name')!;
const teammateHandEl   = document.getElementById('teammate-hand')!;
const teammateBoardEl  = document.getElementById('teammate-board')!;
const teammateHandCountEl  = document.getElementById('teammate-hand-count')!;
const teammateBoardCountEl = document.getElementById('teammate-board-count')!;
const buyCardBtn       = document.getElementById('buy-card-btn')! as HTMLButtonElement;
const buyCardCostEl    = document.getElementById('buy-card-cost')!;
const buyRuneBtn       = document.getElementById('buy-rune-btn')! as HTMLButtonElement;
const keywordsBtn      = document.getElementById('keywords-btn')! as HTMLButtonElement;
const keywordsOverlay  = document.getElementById('keywords-overlay')! as HTMLDivElement;
const purchasedRunesRow    = document.getElementById('purchased-runes-row')!;
const spellChargesRow      = document.getElementById('spell-charges-row')!;
const purchasedRuneOverlay = document.getElementById('purchased-rune-overlay')! as HTMLDivElement;
const purchasedRuneName    = document.getElementById('purchased-rune-name')!;
const purchasedRuneImage   = document.getElementById('purchased-rune-image')! as HTMLImageElement;
const purchasedRuneEffect  = document.getElementById('purchased-rune-effect')!;
const purchasedRuneClose   = document.getElementById('purchased-rune-close')!;
const keywordsFilters  = document.getElementById('keywords-filters')! as HTMLDivElement;
const keywordsList     = document.getElementById('keywords-list')!;
const keywordsClose    = document.getElementById('keywords-close')! as HTMLButtonElement;
// Ready button (hand row, lower-right corner — the UnRdy.png/Rdy.png plates):
// toggles the player's preparation-ready flag. The count (x/n) and own state
// are rendered from the authoritative ReadyStateUpdate — the client never
// computes them; updateReadyButton swaps the plate image (UnRdy = not ready,
// Rdy = ready) and keeps the counter in the plate's lower-right frame.
const readyBtn         = document.getElementById('ready-btn')! as HTMLButtonElement;
const readyImgEl       = document.getElementById('ready-img')! as HTMLImageElement;
const readyCountEl     = document.getElementById('ready-count')!;

// Achievements (logged-in users only, outside of a game)
const btnAchievements      = document.getElementById('btn-achievements')! as HTMLButtonElement;
const achievementsOverlay  = document.getElementById('achievements-overlay')! as HTMLDivElement;
const achievementsClose    = document.getElementById('achievements-close')! as HTMLButtonElement;
const achievementsList     = document.getElementById('achievements-list')!;
const achievementToast      = document.getElementById('achievement-toast')! as HTMLDivElement;
const achievementToastTitle = document.getElementById('achievement-toast-title')!;
const achievementToastIcon  = document.getElementById('achievement-toast-icon')!;
const achievementToastName  = document.getElementById('achievement-toast-name')!;

// Stats (logged-in users only, outside of a game)
const btnStats         = document.getElementById('btn-stats')! as HTMLButtonElement;
const statsOverlay     = document.getElementById('stats-overlay')! as HTMLDivElement;
const statsClose       = document.getElementById('stats-close')! as HTMLButtonElement;
const statsList        = document.getElementById('stats-list')!;

// Keywords reference (logged-in users only, on the landing page): the
// in-game 📖 button's sibling — same overlay, opened from the queue view.
const btnKeywordsQueue   = document.getElementById('btn-keywords-queue')! as HTMLButtonElement;

// Skins (logged-in users only, outside of a game)
const btnSkins         = document.getElementById('btn-skins')! as HTMLButtonElement;
const skinsOverlay     = document.getElementById('skins-overlay')! as HTMLDivElement;
const skinsClose       = document.getElementById('skins-close')! as HTMLButtonElement;
const skinsList        = document.getElementById('skins-list')!;

// Toplist (available to EVERYONE — logged-in users AND guests; guests are
// just not placed on it). Outside of a game (button on the queue view).
const btnToplist     = document.getElementById('btn-toplist')! as HTMLButtonElement;
const toplistOverlay = document.getElementById('toplist-overlay')! as HTMLDivElement;
const toplistClose   = document.getElementById('toplist-close')! as HTMLButtonElement;
const toplistList    = document.getElementById('toplist-list')!;
const toplistMine    = document.getElementById('toplist-mine')!;

// How to Play (wiki — available to EVERYONE, guests included). The
// button was REMOVED from the landing page (outdated content, to be
// updated later) — the overlay + open/close functions remain so a future
// entry point can re-wire to openHowToPlayOverlay(). Purely client-side:
// the overlay body is built ONCE from the shared game data
// (HowToPlay.buildHowToPlayContent) — no server round-trip, like the
// keyword overlay.
// Tutorial (scripted in-game guide — no server): the button lives in
// #landing-screen like How to Play used to, so it is visible to
// EVERYONE (the logged-out identity-choices page AND the logged-in queue
// view).
const btnTutorial    = document.getElementById('btn-tutorial')! as HTMLButtonElement;
const howToPlayOverlay = document.getElementById('how-to-play-overlay')! as HTMLDivElement;
const howToPlayClose = document.getElementById('how-to-play-close')! as HTMLButtonElement;
const howToPlayBody  = document.getElementById('how-to-play-body')! as HTMLDivElement;
const howToPlayToc   = document.getElementById('how-to-play-toc')! as HTMLElement;
const howToPlayContent = document.getElementById('how-to-play-content')! as HTMLDivElement;

// In-game Settings (top-right ⚙️ in the match HUD): opens the settings
// overlay with the RESIGN action. The button is visible in the
// PREPARATION phase only (hidden during combat — you can not resign
// during combat) and the overlay auto-closes when combat starts.
const btnSettings    = document.getElementById('btn-settings')! as HTMLButtonElement;

// ─── Sound settings (Settings overlay → Sound row) ────────────────────────
// EVERY game sound goes through soundService (mute + volume enforced in ONE
// place). The button + slider persist via the service (localStorage).
const btnMute        = document.getElementById('btn-mute')! as HTMLButtonElement;
const volumeSlider   = document.getElementById('volume-slider')! as HTMLInputElement;
const volumeValue    = document.getElementById('volume-value')!;

/** Syncs the mute button label/aria + the slider's enabled state. */
function updateSoundSettingsUI(): void {
  const muted = soundService.muted;
  btnMute.textContent = muted ? '🔇 Muted' : '🔊 Sound on';
  btnMute.setAttribute('aria-pressed', String(muted));
  volumeSlider.disabled = muted;
  volumeSlider.value = String(Math.round(soundService.volume * 100));
  volumeValue.textContent = `${Math.round(soundService.volume * 100)}%`;
}

btnMute.addEventListener('click', () => {
  soundService.setMuted(!soundService.muted);
  updateSoundSettingsUI();
  if (!soundService.muted) soundService.play('btnClick'); // audible confirmation
});

volumeSlider.addEventListener('input', () => {
  const v = Number(volumeSlider.value) / 100;
  soundService.setVolume(v);
  volumeValue.textContent = `${Math.round(v * 100)}%`;
});

// Init from the persisted settings (service loaded them in its ctor).
updateSoundSettingsUI();

// Menu button clicks (BtnClick.wav): the document listener is registered in
// main() — AFTER the upgrade panel's outside-click listener (a white-box
// test pins the FIRST document click listener as the upgrade one).
const settingsOverlay = document.getElementById('settings-overlay')! as HTMLDivElement;
const settingsClose  = document.getElementById('settings-close')! as HTMLButtonElement;
const btnResign      = document.getElementById('btn-resign')! as HTMLButtonElement;

// Share overlay (opened by the 📣 Share buttons on the login/identity
// page and the logged-in landing page)
const btnShareAuth   = document.getElementById('btn-share-auth')! as HTMLButtonElement;
const btnShareQueue  = document.getElementById('btn-share-queue')! as HTMLButtonElement;
const shareOverlay   = document.getElementById('share-overlay')! as HTMLDivElement;
const shareClose     = document.getElementById('share-close')! as HTMLButtonElement;
const shareTargetsEl = document.getElementById('share-targets')!;
const shareCopyBtn   = document.getElementById('share-copy')! as HTMLButtonElement;
const shareNote      = document.getElementById('share-note')!;

// Runes: READ-ONLY catalogue reference on the main menu (everyone, guests
// included — there is no pre-match rune selection anymore). The 🪬 Runes
// button (in flow, directly under the queue message box) opens the viewer
// overlay: a vertically scrollable list of ALL runes from the shared
// RUNE_DEFINITIONS catalogue (browse/scroll only — nothing to pick).
const btnRunes          = document.getElementById('btn-runes')! as HTMLButtonElement;
const runeViewerOverlay = document.getElementById('rune-viewer-overlay')! as HTMLDivElement;
const runeViewerClose   = document.getElementById('rune-viewer-close')! as HTMLButtonElement;
const runeViewerContext = document.getElementById('rune-viewer-context')!;
const runeViewerList    = document.getElementById('rune-viewer-list')!;
// Rune shop (in-game, preparation phase): the 🪬 Buy Rune button opens
// the shop overlay — the SAME catalogue list, but clickable: the player
// can buy ANY rune (once per game) at its gold price. The server is
// authoritative (it re-validates the rune, the ownership and the gold);
// the open shop re-renders on every state change so the Owned/affordability
// badges always mirror the authoritative state.
const runeShopOverlay   = document.getElementById('rune-shop-overlay')! as HTMLDivElement;
const runeShopClose     = document.getElementById('rune-shop-close')! as HTMLButtonElement;
const runeShopContext   = document.getElementById('rune-shop-context')!;
const runeShopList      = document.getElementById('rune-shop-list')!;
// Spells: READ-ONLY catalogue reference on the main menu (everyone, guests
// included — the same convention as Runes). The Spells button (in flow,
// directly under the Runes button) opens the viewer overlay: the shared
// SPELL_DEFINITIONS catalogue grouped by spell LEVEL, each group sorted by
// gold value (browse/scroll only — nothing to pick).
const btnSpells          = document.getElementById('btn-spells')! as HTMLButtonElement;
const spellViewerOverlay = document.getElementById('spell-viewer-overlay')! as HTMLDivElement;
const spellViewerClose   = document.getElementById('spell-viewer-close')! as HTMLButtonElement;
const spellViewerContext = document.getElementById('spell-viewer-context')!;
const spellViewerList    = document.getElementById('spell-viewer-list')!;

// Combat area
const combatArea       = document.getElementById('combat-area')! as HTMLDivElement;
const combatBoard      = document.getElementById('combat-board')!;
const combatLabelA     = document.getElementById('combat-label-a')!;
const combatLabelB     = document.getElementById('combat-label-b')!;
const combatLabelAName = document.getElementById('combat-label-a-name')!;
const combatLabelBName = document.getElementById('combat-label-b-name')!;
const combatRunesA     = document.getElementById('combat-runes-a')!;
const combatRunesB     = document.getElementById('combat-runes-b')!;
const combatMinionsA   = document.getElementById('combat-minions-a')!;
const combatMinionsB   = document.getElementById('combat-minions-b')!;
const combatEventLog   = document.getElementById('combat-event-log')!; // debug console — disabled via CSS
const combatGoldDisplayEl = document.getElementById('combat-gold-display')! as HTMLDivElement;
const combatResultEl   = document.getElementById('combat-result')! as HTMLDivElement;
const combatResultLabel = document.getElementById('combat-result-label')!;
const combatResultDetails = document.getElementById('combat-result-details')!;
const suddenDeathIndicator = document.getElementById('sudden-death-indicator')! as HTMLDivElement;
const suddenDeathDamageEl = document.getElementById('sudden-death-damage-value')!;

// Upgrade panel
const upgradePanel     = document.getElementById('upgrade-panel')! as HTMLDivElement;
const upgradeMinionInfo = document.getElementById('upgrade-minion-info')!;
const upgradeBranches  = document.getElementById('upgrade-branches')!;

// Eliminated screen
const eliminatedScreen = document.getElementById('eliminated-screen')! as HTMLDivElement;
const eliminatedMessage = document.getElementById('eliminated-message')!;
const btnLeaveMatch = document.getElementById('btn-leave-match')! as HTMLButtonElement;

// Round transition overlay
const roundTransitionOverlay = document.getElementById('round-transition-overlay')! as HTMLDivElement;
const roundTransitionMessage = document.getElementById('round-transition-message')!;

// Game over screen
const gameoverScreen   = document.getElementById('gameover-screen')!;
const gameoverTitle    = document.getElementById('gameover-title')!;
const gameoverMessage  = document.getElementById('gameover-message')!;
const gameoverStandings = document.getElementById('gameover-standings')!;
const btnBackToQueue   = document.getElementById('btn-back-to-queue')! as HTMLButtonElement;

// ─── Player ID ────────────────────────────────────────────────────────────

/**
 * Generate a unique player ID from URL parameter `playerId` or a random value.
 */
function getPlayerId(): string {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('playerId');
  if (fromUrl) return fromUrl;
  return `player-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Determine server URL from URL parameter `server` or use default.
 */
function getServerUrl(): string {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('server');
  return fromUrl ?? DEFAULT_SERVER_URL;
}

// ─── UI Helpers ───────────────────────────────────────────────────────────

/**
 * Show exactly one screen by ID and hide the others.
 */
function showScreen(screenId: 'landing' | 'match' | 'gameOver' | 'coopLobby' | 'lobby' | 'createGame'): void {
  landingScreen.classList.toggle('hidden', screenId !== 'landing');
  matchScreen.classList.toggle('active', screenId === 'match');
  matchScreen.classList.toggle('hidden', screenId !== 'match');
  gameoverScreen.classList.toggle('active', screenId === 'gameOver');
  gameoverScreen.classList.toggle('hidden', screenId !== 'gameOver');
  // The COOP lobby is its own screen (hidden unless it's the current one).
  coopLobbyScreen.classList.toggle('hidden', screenId !== 'coopLobby');
  // The unified Original lobby + Create Game screens (hidden unless current).
  lobbyScreen.classList.toggle('hidden', screenId !== 'lobby');
  createGameScreen.classList.toggle('hidden', screenId !== 'createGame');
  if (screenId === 'coopLobby') {
    renderCoopLobby();
  } else if (screenId === 'lobby') {
    renderLobby();
  }
}

/**
 * Hide ALL the landing sub-menus (the game-mode picker, the multiplayer
 * play picker, the fast/long queue picker) — at most ONE is ever
 * visible. Called before revealing any of them, whenever the queue state
 * changes, and on every screen change away from the landing screen.
 * The Play button is hidden while ANY sub-menu is open (it is restored
 * by the explicit close paths: showLandingView / the cancels / queueing).
 */
function hideAllPickers(): void {
  gameModePickerEl.classList.add('hidden');
  mechanicsPickerEl.classList.add('hidden');
  progressPickerEl.classList.add('hidden');
  playPickerEl.classList.add('hidden');
  joinLobbyRowEl.classList.add('hidden');
  queueModePickerEl.classList.add('hidden');
  if (shell?.gameState.queue.isInQueue) {
    return; // queued — Leave Queue is the active control (Play stays hidden)
  }
  btnPlay.classList.remove('hidden');
}

/**
 * Show the fast/long queue-mode picker (one sub-menu at a time).
 * Module scope: the queued menu's BACK button (Leave Queue) re-opens it
 * after a leave (see _returnToQueueModePicker + wireQueueScreen).
 */
function showQueueModePicker(): void {
  hideAllPickers();
  btnPlay.classList.add('hidden');
  queueModePickerEl.classList.remove('hidden');
}

/** Hide the picker and restore the Play button (server state decides). */
function hideQueueModePicker(): void {
  queueModePickerEl.classList.add('hidden');
  if (shell?.gameState.queue.isInQueue) {
    // Already queued — the Leave button (not Play) is the active control.
    return;
  }
  btnPlay.classList.remove('hidden');
}

// BACK semantics (playtest): the queued menu's Leave Queue button is a
// BACK button — it leaves the queue AND returns to the PREVIOUS sub-menu
// (the fast/long ranked-queue picker), not the root Play menu. Set on the
// click, consumed once by the first NOT-queued status render.
let _returnToQueueModePicker = false;

/**
 * Update the connection indicator state. The dot itself is now ALWAYS
 * HIDDEN (playtest: "the green status button hovering in the left corner
 * can be hidden, it's not needed any more" — #connection-banner is
 * display:none in index.html); the class + title are kept in sync so the
 * state is still observable (tests / future re-introduction).
 */
function updateConnectionBanner(state: ConnectionState): void {
  connectionBanner.className = state;
  const tooltips: Record<ConnectionState, string> = {
    disconnected: 'Disconnected',
    connecting:   'Connecting…',
    connected:    'Connected',
    reconnecting: 'Reconnecting…',
    error:        'Connection Error',
  };
  connectionBanner.title = tooltips[state];
  connectionBanner.textContent = '';
}

/**
 * Render the player pills in the HUD.
 *
 * @param players The player summaries to render.
 * @param currentPlayerId The local player's ID (gold border).
 * @param nextOpponentId The local player's NEXT combat opponent (authoritative,
 *        from the server — fixed at prep start). That pill gets a red border
 *        so we know who we fight in the preparation round. null when we sit
 *        out or there is no upcoming combat (no red border).
 */
function renderPlayerPills(players: PlayerSummary[], currentPlayerId: string, nextOpponentId: string | null): void {
  hudPlayers.innerHTML = '';
  // MANY-PLAYER matches (the 8-player long game: 9 pills) compact the
  // pills: two stacked rows (name over life/gold), smaller type, tighter
  // padding and a shorter name width with ellipsis ("Danny the overlord"
  // → "Danny t.."). The HUD bar height is fixed — the pills shrink, the
  // header never grows. 8+ players = compact (7 pills still fit roomy).
  hudPlayers.classList.toggle('compact', players.length >= 8);
  for (const p of players) {
    const pill = document.createElement('div');
    pill.className = 'player-pill';
    if (p.id === currentPlayerId) pill.classList.add('current');
    if (!p.isAlive) pill.classList.add('dead');
    if (nextOpponentId && p.id === nextOpponentId) pill.classList.add('opponent');
    pill.title = nextOpponentId && p.id === nextOpponentId
      ? 'Next opponent — you fight this player in the next combat round'
      : '';
    // COOP mission bots show their campaign character name (authoritative
    // PlayerData.displayName — "The Blight", "Siri the Siren", ...); every
    // other player shows their id (regular matches + the two humans).
    // NO GOLD ON ANY PILL (playtest: opponents' gold hidden in task 174,
    // the player's OWN badge too in task 181 — the player's gold lives in
    // the HUD stats, the pills show life only).
    pill.dataset.playerId = p.id; // the hover/long-press history tooltip
    pill.innerHTML = `
      <span class="player-id">${displayUsername(p.displayName ?? p.id)}</span>
      <span class="player-vitals">
        <span class="player-life">❤️ ${p.life}</span>
      </span>
    `;
    hudPlayers.appendChild(pill);
    hidePillHistoryTooltip(); // pills are rebuilt — a stale tooltip would point at a removed node
  }
}

// ─── Pill history tooltip (task 181) ───────────────────────────────────────
// Hover (desktop) / long-press (mobile) a player badge → a floating box
// BELOW the badge: the player's FULL name + the viewer's head-to-head
// fights vs that player (red = the player LOST, green = the player WON,
// gray = tie). The history is a session record: showCombatResult appends
// one entry per combat the viewer takes part in; showMatchComponents
// clears it for the new match.
const _h2hHistory = new Map<string, Array<'win' | 'loss' | 'tie'>>();
const pillHistoryTooltip = document.getElementById('pill-history-tooltip')!;
const pillHistoryName = document.getElementById('pill-history-name')!;
const pillHistoryFights = document.getElementById('pill-history-fights')!;

function hidePillHistoryTooltip(): void {
  pillHistoryTooltip.classList.remove('visible');
}

function showPillHistoryTooltip(pill: HTMLElement): void {
  const id = pill.dataset.playerId;
  if (!id) return;
  const player = shell?.gameState.match.players.find((p) => p.id === id);
  pillHistoryName.textContent = displayUsername(player?.displayName ?? id);
  const fights = _h2hHistory.get(id) ?? [];
  pillHistoryFights.innerHTML = fights.length === 0
    ? '<span id="pill-history-empty">No fights yet</span>'
    : fights.map((f) =>
        `<span class="fight ${f}" title="${f === 'loss' ? 'You beat them' : f === 'win' ? 'They beat you' : 'Tie'}"></span>`
      ).join('');
  // BELOW the badge, centered on it (clamped to the viewport):
  pillHistoryTooltip.classList.add('visible');
  const r = pill.getBoundingClientRect();
  const tw = pillHistoryTooltip.offsetWidth;
  const left = Math.min(Math.max(8, r.left + r.width / 2 - tw / 2), Math.max(8, window.innerWidth - tw - 8));
  pillHistoryTooltip.style.left = `${left}px`;
  pillHistoryTooltip.style.top = `${r.bottom + 8}px`;
}

/**
 * Records one finished combat in the viewer's head-to-head history — the
 * OPPONENT'S result (the tooltip colors it from the badge player's side):
 * viewer win → 'loss' (red), viewer loss → 'win' (green), tie/timeout →
 * 'tie' (gray). COOP: the whole enemy team fought the round, so every
 * mission bot gets the entry.
 */
function recordHeadToHeadResult(resultType: string): void {
  const state = shell?.gameState;
  if (!state?.match) return;
  let opponents: string[] = [];
  const coop = _coopTeamInfo;
  if (coop && coop.mode !== 'normal') {
    opponents = [...coop.enemyIds];
  } else {
    const cs = state.match.combatStarted as { playerAId?: string; playerBId?: string } | null;
    const me = shell?.playerId;
    if (cs?.playerAId && cs?.playerBId && me) {
      opponents = [me === cs.playerAId ? cs.playerBId : cs.playerAId];
    }
  }
  const opponentResult: 'win' | 'loss' | 'tie' =
    resultType === 'win' ? 'loss' : resultType === 'loss' ? 'win' : 'tie';
  for (const opp of opponents) {
    const list = _h2hHistory.get(opp) ?? [];
    list.push(opponentResult);
    _h2hHistory.set(opp, list);
  }
}

/** The in-flight pill long-press (touch) state. */
let _pillPress: { pill: HTMLElement; x: number; y: number; timer: number } | null = null;

function wirePillHistoryTooltip(): void {
  // Desktop: hover in/out (delegated — the pills are rebuilt every render).
  hudPlayers.addEventListener('mouseover', (e) => {
    const pill = (e.target as Element).closest?.('.player-pill') as HTMLElement | null;
    if (pill) showPillHistoryTooltip(pill);
    else hidePillHistoryTooltip();
  });
  hudPlayers.addEventListener('mouseout', (e) => {
    const to = (e.relatedTarget as Element | null)?.closest?.('.player-pill');
    if (!to) hidePillHistoryTooltip();
  });
  // Mobile: LONG-PRESS (550 ms) a badge. A drift > 10 px cancels the press;
  // the release (touchend/cancel) always dismisses the tooltip.
  hudPlayers.addEventListener('touchstart', (e) => {
    const pill = (e.target as Element).closest?.('.player-pill') as HTMLElement | null;
    if (!pill) return;
    const t = e.touches[0];
    if (_pillPress) window.clearTimeout(_pillPress.timer);
    _pillPress = {
      pill,
      x: t.clientX,
      y: t.clientY,
      timer: window.setTimeout(() => {
        const p = _pillPress;
        _pillPress = null;
        if (p) showPillHistoryTooltip(p.pill);
      }, 550),
    };
  }, { passive: true });
  hudPlayers.addEventListener('touchmove', (e) => {
    if (!_pillPress) return;
    const t = e.touches[0];
    if (Math.abs(t.clientX - _pillPress.x) > 10 || Math.abs(t.clientY - _pillPress.y) > 10) {
      window.clearTimeout(_pillPress.timer);
      _pillPress = null;
    }
  }, { passive: true });
  const endPress = () => {
    if (_pillPress) {
      window.clearTimeout(_pillPress.timer);
      _pillPress = null;
    }
    hidePillHistoryTooltip();
  };
  hudPlayers.addEventListener('touchend', endPress);
  hudPlayers.addEventListener('touchcancel', endPress);
}

/**
 * The current player's authoritative skin selections (class → skin ID)
 * from the server state. Guests/bots (and older servers) get {} — the
 * resolver then falls back to each class's default art.
 */
function mySkinSelections(): Record<string, string> {
  const s = shell; // capture — module-level `shell` narrowing does not persist into closures
  if (!s) return {};
  const me = s.gameState.match.players.find((p) => p.id === s.playerId);
  return me?.skins ?? {};
}

/**
 * The OWNING player's skin selections for a combat side (class → skin ID),
 * read from the authoritative CombatStarted message. This is what makes
 * "I see my own skins on my cards and my opponent's skins on theirs" true:
 * each side's art resolves against the side owner's selection, never the
 * local player's.
 */
function combatSideSkins(playerId: string): Record<string, string> {
  const cs = shell?.gameState.match.combatStarted;
  if (!cs) return {};
  if (playerId === cs.playerAId) return cs.playerASkins ?? {};
  if (playerId === cs.playerBId) return cs.playerBSkins ?? {};
  return {};
}

/**
 * The OWNING player id of a combat minion on the COOP team side (the
 * combined row mixes both humans' minions). Built from the authoritative
 * per-member battlefields in the match state + the mission's fixed
 * layout (hostId/teammateId from the coop info). null outside a team
 * mission — regular combats resolve the whole row to the side owner.
 * The battlefields never change mid-combat (combat copies are temporary),
 * so the map is valid for the whole combat.
 */
function coopTeamOwnerMap(): Map<string, string> | null {
  const coop = _coopTeamInfo;
  if (!coop || coop.mode === 'normal') return null;
  const players = shell?.gameState.match.players ?? [];
  const map = new Map<string, string>();
  for (const memberId of [coop.hostId, coop.teammateId]) {
    const member = players.find((p) => p.id === memberId);
    for (const m of member?.battlefield ?? []) map.set(m.instanceId, memberId);
  }
  return map;
}

/**
 * The skin selections for ONE combat minion: the COOP team side resolves
 * the minion's OWNER (host vs teammate — two warriors on the same row can
 * wear different skins) against the authoritative per-member selections
 * (playerASkinsByPlayer, sent by the server); every other case falls back
 * to the side owner's selection (regular combats + legacy servers).
 */
function combatMinionSkins(sideOwnerId: string, minionInstanceId: string): Record<string, string> {
  const cs = shell?.gameState.match.combatStarted;
  const byPlayer = cs?.playerASkinsByPlayer;
  const ownerMap = coopTeamOwnerMap();
  if (byPlayer && ownerMap) {
    const owner = ownerMap.get(minionInstanceId) ?? sideOwnerId;
    return byPlayer[owner] ?? combatSideSkins(owner);
  }
  return combatSideSkins(sideOwnerId);
}

/**
 * Render a hand card element (delegates to the shared MinionCard component).
 * Hand cards are always the local player's → the local player's skins.
 */
function createHandCardElement(card: HandCardRenderData): HTMLElement {
  const skins = mySkinSelections();
  return createMinionCard(card, {
    variant: 'hand',
    // NOT natively draggable: the desktop mouse drag is pure JS (DragAndDropService
    // mousedown/mousemove/mouseup) — the native HTML5 drag initiation was flaky
    // (~50 % of grabs lost, the browser's built-in drag listeners on the
    // `draggable` element). Phase gating happens via isDragEnabled, not the
    // attribute; the attribute being off also kills Chromium mobile's native
    // long-press drag (the "buzz" — CardInspectService's disarm stays as a
    // harmless backstop).
    draggable: false,
    artSrc: resolveSkinImage(card.minionType, skins[card.minionType]),
    onPlay: (instanceId) => {
      // SPELLS are cast, not played on the board. The (hidden) Play button
      // / double-click casts a FIELD spell directly (no target needed); a
      // TARGETED spell needs a drag onto a minion, so the button is a
      // no-op for it (drag is the only cast path). The release position
      // (the card's own center) feeds the star-explosion cast FX.
      if (card.minionType === SPELL_MINION_TYPE) {
        const spec = getSpellDefinition(card.baseMinionId);
        if (spec && spec.target === 'battlefield') {
          const el = (document.querySelector(`.hand-card[data-instance-id="${instanceId}"]`) ?? null) as HTMLElement | null;
          const r = el?.getBoundingClientRect();
          _pendingSpellCastPos = r && r.width > 0
            ? { x: r.left + r.width / 2, y: r.top + r.height / 2 }
            : null;
          handUI?.playSpellCard(instanceId);
        }
        return;
      }
      handUI?.playCard(instanceId);
    },
  });
}

/**
 * Render a battlefield minion element (delegates to the shared MinionCard component).
 * The preparation battlefield is always the local player's → its skins.
 /**
 * OVERDRAW GHOST (task 201): the LOST card rendered as a REAL hand card —
 * the exact face the user would have gotten (art, name, stats, keywords —
 * they know what card got overdrawn) — but NON-INTERACTIVE: no drag, no
 * double-click, no buttons (pointer-events: none + no handlers). It is
 * NOT part of the hand state (the server discarded the card) — it exists
 * only for the draw → destroy animation, and it is excluded from the hand
 * fit measurement (data-overdraw-ghost) so the 8 cards never rescale for
 * it.
 */
function createOverdrawGhostCard(card: OverdrawCardData): HTMLElement {
  const skins = mySkinSelections();
  const abilityNames = card.abilities.map((id) =>
    GAME_ABILITY_DEFINITIONS.find((d) => d.id === id)?.name ?? id,
  );
  const artSrc = resolveSkinImage(card.minionType, skins[card.minionType]);
  const el = createMinionCard({
    // A synthetic instance id (never collides with a real card — the
    // 'overdraw_ghost_' prefix is never produced server-side).
    instanceId: `overdraw_ghost_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`,
    baseMinionId: card.baseMinionId,
    minionType: card.minionType,
    currentHp: card.currentHp,
    attack: card.attack,
    upgradeCount: card.upgradeCount,
    level: card.upgradeCount + 1,
    abilityNames,
    abilityDescriptions: [],
    hasAbilities: card.abilities.length > 0,
    variant: 'hand',
    artSrc,
  } as MinionCardData, {
    variant: 'hand',
    // Non-interactive: no drag, and NO onPlay — the card cannot be
    // played/cast/sold (pointer-events: none below is the backstop).
    draggable: false,
    artSrc,
  });
  el.dataset.overdrawGhost = '1';
  el.classList.add('overdraw-ghost');
  el.style.pointerEvents = 'none';
  return el;
}

/**
 * Queues the overdraw ghosts from a cardEvents batch (task 201) and kicks
 * the SEQUENTIAL drain. Reference-guarded like the FX consumption: each
 * update carries a fresh array, so the same batch never spawns ghosts
 * twice (timer ticks / re-renders keep the same reference).
 */
function queueOverdrawGhosts(batch: CardFxEvent[] | null | undefined, myId: string | null): void {
  if (!batch || batch.length === 0 || batch === _lastGhostBatchRef) return;
  _lastGhostBatchRef = batch;
  for (const ev of batch) {
    if (ev.type === 'overdraw' && ev.card && ev.playerId === myId) {
      _overdrawGhostQueue.push(ev.card);
    }
  }
  processOverdrawGhostQueue();
}

/**
 * The SEQUENTIAL ghost drain (task 201): ONE card at a time — it slides
 * into the hand (the task-200 fx-card-draw), sits for a beat so the user
 * can read it, then bursts with the overdraw effect at its own position
 * (explode sound) and fades out. Only when it is gone does the NEXT
 * overdrawn card start (draw → destroy → next → destroy → …).
 */
const OVERDRAW_GHOST_DESTROY_DELAY_MS = 850; // slide-in (0.5s) + a beat to read
const OVERDRAW_GHOST_FADE_MS = 220; // the burst covers the fade
const OVERDRAW_GHOST_NEXT_DELAY_MS = 260; // the gap between two cards

function processOverdrawGhostQueue(): void {
  if (_overdrawGhostBusy || _overdrawGhostQueue.length === 0) return;
  _overdrawGhostBusy = true;
  const card = _overdrawGhostQueue.shift()!;
  const ghost = createOverdrawGhostCard(card);
  // Append after the (already rebuilt) hand cards — it visually joins the
  // row's end and slides in from the bottom of the hand.
  handContainer.appendChild(ghost);
  retriggerClass(ghost, 'fx-card-draw'); // the slide-in (reduced-motion no-ops)
  soundService.play('apply'); // the card-draw sound
  setTimeout(() => {
    const r = ghost.getBoundingClientRect();
    if (r.width > 0) {
      // The overdraw effect plays AT THE CARD (the big slow red blast —
      // task 196), magnified with the hand zoom.
      getFx().burstAt(r.left + r.width / 2, r.top + r.height / 2, 'overdraw', { scale: _handCardScale });
    }
    soundService.play('explode');
    ghost.classList.add('overdraw-ghost-dying');
    setTimeout(() => {
      ghost.remove();
      _overdrawGhostBusy = false;
      // THE NEXT card starts only after this one is gone (sequential,
      // never parallel).
      if (_overdrawGhostQueue.length > 0) {
        setTimeout(processOverdrawGhostQueue, OVERDRAW_GHOST_NEXT_DELAY_MS);
      }
    }, OVERDRAW_GHOST_FADE_MS);
  }, OVERDRAW_GHOST_DESTROY_DELAY_MS);
}

function createBattlefieldMinionElement(
  minion: BattlefieldMinionRenderData,
  playerGold: number,
  maxUpgrades: number,
): HTMLElement {
  const skins = mySkinSelections();
  return createMinionCard(minion, {
    variant: 'battlefield',
    // NOT natively draggable — the desktop drag is pure JS (see the hand
    // card comment above); phase gating is isDragEnabled, not the attribute.
    draggable: false,
    playerGold,
    maxUpgrades,
    artSrc: resolveSkinImage(minion.minionType, skins[minion.minionType]),
    onSell: (instanceId) => {
      battlefieldUI?.sellMinion(instanceId);
    },
    onUpgrade: (instanceId) => {
      onBattlefieldMinionSelected(instanceId);
    },
  });
}

/**
 * Render a combat minion element (delegates to the shared MinionCard component).
 * `ownerSkins` are the OWNING side's selections (see combatSideSkins) —
 * the local player's side renders with the local player's skins, the
 * opponent's side with the opponent's.
 */
function createCombatMinionElement(minion: CombatMinionRenderData, ownerSkins: Record<string, string> = {}): HTMLElement {
  return createMinionCard(minion, {
    variant: 'combat',
    startingHp: minion.startingHp,
    isAlive: minion.isAlive,
    artSrc: resolveSkinImage(minion.minionType, ownerSkins[minion.minionType]),
  });
}

/**
 * Add a log entry to the combat event log.
 * Disabled — debug console is no longer shown.
 */
function addCombatLogEntry(_message: string, _type: 'attack' | 'damage' | 'death' | 'gold' | 'ability' = 'attack'): void {
  // Debug console disabled
  void _message;
  void _type;
}

/**
 * Show a damage flash on a combat minion element.
 */
function showDamageFlash(instanceId: string, damage: number): void {
  const minionEl = document.querySelector(`.combat-minion[data-instance-id="${instanceId}"]`) as HTMLElement;
  if (!minionEl) return;
  const flash = document.createElement('div');
  flash.className = 'damage-flash';
  flash.textContent = `-${damage}`;
  minionEl.appendChild(flash);
  setTimeout(() => flash.remove(), 800);
}

// ─── Hold State Reset ─────────────────────────────────────────────────────

/**
 * Reset all hold/drag state and clear DOM artifacts.
 * Call this when a match starts or when the UI is rebuilt to ensure
 * no stale drag references or CSS classes persist.
 */
function resetHoldState(): void {
  // Clear any orphaned .selected elements (from stale DOM)
  const selectedEls = document.querySelectorAll('.selected');
  for (const el of selectedEls) {
    el.classList.remove('selected');
  }

  // Reset drag-and-drop service state
  dragAndDropService?.reset();

  // Clear pending upgrade panel
  _pendingUpgradeMinionId = null;
}

/**
 * Called when a battlefield minion is clicked.
 * Toggles between showing the upgrade panel and selling.
 */
function onBattlefieldMinionSelected(instanceId: string): void {
  if (!battlefieldUI || !upgradeChoiceUI) return;
  // Tutorial gating: while the tour runs, only the current tip's action
  // is available — keep the upgrade panel itself from opening on tips
  // that are not about upgrading (the TutorialShell additionally rejects
  // the upgrade request server-side — defense in depth).
  if (tutorialShell && !tutorialShell.allows('upgrade')) return;
  // While the upgrade panel is OPEN, upgrade gestures on the battlefield
  // are ignored — the panel must be dismissed first (a click outside it,
  // or Esc). Without this guard, double-clicking another minion while the
  // panel is open would swap it to that minion's options, letting the
  // player ping-pong between minions while the menu is up. (The server
  // additionally LOCKS each minion's pre-selected ability until the minion
  // is upgraded, so the offered ability can never be cycled regardless —
  // see DevServer._handleGetUpgradeOptions.)
  if (upgradePanel.classList.contains('visible')) return;
  const renderData = battlefieldUI.renderData;
  const minion = renderData.minions.find(m => m.instanceId === instanceId);
  if (!minion) return;

  // Check if we can upgrade. The cap is the base max EXTENDED by one per
  // Grandmaster minion on this player's battlefield (the Grandmaster
  // rule, PlayerState.getMaxUpgrades) — the same mirror the upgrade panel
  // uses. The old hardcoded "< 5" blocked selecting max-level minions
  // while a Grandmaster was on the board. The server re-validates the cap
  // authoritatively on the upgrade request.
  const canUpgrade = minion.upgradeCount < upgradeChoiceUI.currentMaxUpgrades();
  if (canUpgrade) {
    // Set the pending flag BEFORE requesting the options: the render
    // callback that shows the panel keys off it, and a shell that
    // answers synchronously (the TutorialShell — the real backend
    // answers over the wire) emits its state update INSIDE
    // selectMinion's getUpgradeOptions call. On the async real app the
    // first callback render is still the client-side fallback ("Gain an
    // ability…") and the callback's hasServerOptions gate keeps the panel
    // hidden until the server's options arrive — so the earlier flag is
    // safe in both worlds. (If selectMinion rejects — minion vanished —
    // the callback's deselect branch clears the stale flag.)
    _pendingUpgradeMinionId = instanceId;
    // Don't show the panel yet — wait for the server to respond with
    // upgrade options (which include the pre-selected ability).
    // The render callback will show the panel when options arrive.
    upgradeChoiceUI.selectMinion(instanceId);
  }
}

/**
 * Show the upgrade choice panel.
 */
function showUpgradePanel(data: UpgradeChoiceUIRenderData): void {
  upgradePanel.classList.add('visible');
  if (data.selectedMinion) {
    const m = data.selectedMinion;
    upgradeMinionInfo.textContent = `${m.baseMinionId} (Lv.${m.level}) — ⚔${m.attack} ❤${m.currentHp}`;
  }
  upgradeBranches.innerHTML = '';
  for (const branch of data.availableBranches) {
    const el = document.createElement('div');
    el.className = 'upgrade-branch';
    // TRANSFORM options render like a normal option (the "Transform into
    // <Class>" name + cost) plus a RESET WARNING line: the transform
    // replaces the minion with a fresh level-1 minion of the target class,
    // so ALL of the original's levels and skills are lost (the pulsing
    // portal circle used to render here was removed — the text carries the
    // information now). The container keeps its gold glow (CSS class
    // upgrade-branch-transform) so the option still stands out.
    let inner: string;
    if (branch.branchId === 'transform' && branch.transformTargetClass) {
      el.classList.add('upgrade-branch-transform');
      inner = `
        <div class="branch-name">${branch.name}</div>
        <div class="branch-transform-warning">All levels and skills are lost when transforming</div>
        <div class="branch-cost">${data.upgradeCost} gold</div>
      `;
    } else if (branch.branchId === HIGH_STAKES_BRANCH_ID) {
      // RUNE OF HIGH STAKES option: renders like a stat option (+10/+10
      // result lines) plus a RED WARNING line carrying the level-scaled
      // destroy chance (the server's number — the authoritative roll uses
      // the same shared formula). The whole option container gets the red
      // High Stakes styling so it stands out from the normal options.
      el.classList.add('upgrade-branch-high-stakes');
      const chancePct = Math.round((branch.destroyChance ?? 0) * 100);
      inner = `
        <div class="branch-name">${branch.name}</div>
        <div class="branch-stats hp">HP: ${branch.resultingHp}</div>
        <div class="branch-stats atk">ATK: ${branch.resultingAttack}</div>
        <div class="branch-high-stakes-warning">${chancePct}% chance to destroy this minion</div>
        <div class="branch-cost">${data.upgradeCost} gold</div>
      `;
    } else {
      // Ability options show the keyword (branch name) + the ability's
      // description — NO HP/ATK stat lines (ability upgrades grant no
      // stats). Stat options keep the resulting HP/ATK lines.
      const body = branch.hasAbilitiesAdded
        ? (branch.abilityDescription
            ? `<div class="branch-ability-desc">${branch.abilityDescription}</div>`
            : '')
        : `<div class="branch-stats hp">HP: ${branch.resultingHp}</div>
           <div class="branch-stats atk">ATK: ${branch.resultingAttack}</div>`;
      inner = `
        <div class="branch-name">${branch.name}</div>
        ${body}
        <div class="branch-cost">${data.upgradeCost} gold</div>
      `;
    }
    el.innerHTML = inner;
    el.addEventListener('click', () => {
      // SOUND: a level-up branch is selected (PowerUp.wav) — a TRANSFORM
      // branch plays Uppgrade.wav instead (the transformation sound).
      soundService.play(branch.branchId === 'transform' ? 'upgrade' : 'powerUp');
      if (upgradeChoiceUI) {
        upgradeChoiceUI.chooseUpgrade(branch.branchId);
      }
      upgradePanel.classList.remove('visible');
    });
    upgradeBranches.appendChild(el);
  }
}

/**
 * Close the upgrade choice panel and clear the pending selection.
 * Called on clicks OUTSIDE the panel and on Escape (the panel has no
 * dedicated close button — anything outside it dismisses it).
 */
function closeUpgradePanel(): void {
  if (!upgradePanel.classList.contains('visible')) return;
  upgradePanel.classList.remove('visible');
  _pendingUpgradeMinionId = null;
  upgradeChoiceUI?.deselectMinion();
}

// ─── Application State ────────────────────────────────────────────────────

let shell: AppShell | null = null;
// The foreground-reconnect listener (removed/re-added on each shell restart
// so it never accumulates).
let _pokeOnForeground: (() => void) | null = null;
let queueScreen: QueueScreen | null = null;
let matchHudComponent: MatchHUD | null = null;
let handUI: HandUI | null = null;
let battlefieldUI: BattlefieldUI | null = null;
let combatBoardView: CombatBoardView | null = null;
let preparationTimerUI: PreparationTimerUI | null = null;
let upgradeChoiceUI: UpgradeChoiceUI | null = null;
let combatAnimationView: CombatAnimationView | null = null;
let simultaneousDamageAnimator: SimultaneousDamageAnimator | null = null;
let combatGoldDisplay: CombatGoldDisplay | null = null;
let combatResultUI: CombatResultUI | null = null;
let spectatorModeUI: SpectatorModeUI | null = null;
let eliminatedPlayerScreen: EliminatedPlayerScreen | null = null;
let winnerScreen: WinnerScreen | null = null;
let reconnectUI: ReconnectUI | null = null;
let dragAndDropService: DragAndDropService | null = null;
let cardInspectService: CardInspectService | null = null;
let fullscreenService: FullscreenService | null = null;

// Tutorial (scripted in-game guide — no server): while a tour runs, the
// module-level `shell` (cast) is a TutorialShell — the scripted "server"
// the real UI components are bound to — and `tutorial` drives the 11-tip
// state machine (dialog + spotlight). Both are null outside a tour.
let tutorial: TutorialService | null = null;
let tutorialShell: TutorialShell | null = null;

// Track if we've already shown a combat result (to avoid duplicate overlays)
let _combatResultShown = false;

// Track the COOP "Treasure fight!" banner: shown once per treasure combat,
// reset when the combat area hides (so the NEXT treasure turn re-shows it).
let _treasureBannerShownForCombat = false;

// Track current game phase for drag enable/disable
let _isInPreparation = false;

// Ongoing attack animations — a PROMISE CHAIN, not a skip guard (bug fix:
// "when a unit gets buffed right before it attacks, the attack animation is
// sometimes omitted"). The old `if (!_attackAnimationInProgress)` guard
// SILENTLY SKIPPED a lunge when its stream event arrived while the previous
// lunge was still in flight — exactly the window the buff/aura FX (bursts,
// floats, stat-gain pulse) stress the main thread in, so the timers slip and
// the second attack's callback fired inside the first lunge's ~224 ms window.
// Now every attack APPENDS its lunge to the chain: a lunge that starts while
// the previous one is in flight simply waits for it to finish — no attack
// animation is ever dropped (the chain resets on combat transitions).
let _attackAnimationChain: Promise<void> = Promise.resolve();

// Track whether combat stream animations are still playing.
// The server sends GameStateUpdate (phase='preparation') after a short delay
// that may be shorter than the total combat animation time. This flag keeps
// the combat area visible until the frontend animations actually complete,
// preventing the combat screen from disappearing prematurely.
let _combatAnimationsRunning = false;

// Number of sudden-death ticks so far in the current combat. Drives the red
// background fade on #combat-area (more red on each tick; capped).
let _suddenDeathRedLevel = 0;

// Track previously rendered IDs to avoid unnecessary DOM rebuilds on timer ticks
let _lastHandCardIds: string[] = [];
/**
 * The CARD FX event batch (task 186) last CONSUMED by the hand renderer —
 * tracked by ARRAY REFERENCE: each GameStateUpdate carries a fresh
 * `cardEvents` array (the server drains it), so a changed reference means
 * a new batch to play; timer ticks and other broadcasts keep the same
 * reference and never re-play the FX.
 */
let _lastCardEventsRef: ReadonlyArray<CardFxEvent> | null = null;
// OVERDRAW GHOSTS (task 201): the LOST card's face (from the 'overdraw'
// cardEvents) is drawn into the hand as a REAL, non-interactive card and
// destroyed with the overdraw effect after a short delay. SEVERAL
// overdraws in one batch play SEQUENTIALLY (draw → destroy → next → …) —
// _overdrawGhostQueue is a FIFO drained one card at a time by
// _overdrawGhostBusy.
let _overdrawGhostQueue: OverdrawCardData[] = [];
let _overdrawGhostBusy = false;
/** The cardEvents batch the ghosts were already queued from (array
 *  reference guard — the same batch never spawns ghosts twice). */
let _lastGhostBatchRef: ReadonlyArray<CardFxEvent> | null = null;
let _lastBattlefieldMinionIds: string[] = [];
// Minion TYPE per battlefield position from the previous render (transform
// detection: a swap in place = same length, different instance id AND
// different class at a position). Blanked wherever _lastBattlefieldMinionIds
// is (combat→prep reset) so a restored board never reads as a transform.
let _lastBattlefieldMinionTypes: string[] = [];
let _lastBattlefieldMinionStatsHash: number = 0;
// Player gold is tracked too: the upgrade-affordability indicator depends on
// it, and gold can change without any minion changing (e.g. buying a card).
let _lastBattlefieldPlayerGold: number = -1;

// ─── FX (client-side effects) ────────────────────────────────────────────────
// One shared particle engine (single canvas above the DOM) + DOM trigger
// state. Effects are purely cosmetic, fire-and-forget, and suppressed for
// users who prefer reduced motion (JS + CSS layers).
let fx: FxCanvas | null = null;
function getFx(): FxCanvas {
  if (!fx) fx = new FxCanvas();
  return fx;
}

// Diff state for economy/level FX feedback (reset between matches)
let _lastHudGold: number | null = null;
let _lastHudLife: number | null = null;
let _lastBattlefieldLevels: Map<string, number> = new Map();
// Last-seen permanent stats per battlefield minion (instanceId → stats).
// A level-UNCHANGED stat increase between state updates means the gain came
// from a Heirloom transfer or a Teacher redirect (upgrades bump the level,
// which gets the level-up ring instead).
let _lastBattlefieldStats: Map<string, { attack: number; hp: number; level: number }> = new Map();
// SPELL CAST FX (state-diff confirmed): a spell card that VANISHED from the
// hand was either SOLD (gold rose by its sell value) or CAST (the effect
// went into play and the card despawned). The hand render callback detects
// the cast and queues the burst here; the battlefield render callback (the
// same state change — it runs after the hand's) consumes it: the STAR
// explosion plays at _pendingSpellCastPos — the point where the card was
// RELEASED (the drag drop point for targeted spells, the cast card's center
// for button/double-click casts; null = the board center fallback).
let _pendingSpellFx: { baseMinionId: string; targetInstanceId?: string } | null = null;
let _pendingSpellCastPos: { x: number; y: number } | null = null;
let _lastSpellDiffHandSpells: Map<string, string> = new Map();
let _lastSpellDiffGold: number | null = null;

/**
 * BATTLEFIELD CAST FLASH: when a BATTLEFIELD-target spell is CAST (not sold
 * — this is only ever called from the cast-FX sites), the entire playfield
 * flashes baby blue/yellow. A lazily-appended #battlefield-flash overlay
 * (CSS in index.html): the .active class snaps the opacity up in ~50ms and
 * the CSS transition fades it back down over ~450ms. Called on every cast
 * of a field spell; the reflow restart makes rapid consecutive casts flash
 * again. (Reduced motion: the flash is a simple opacity fade — kept, since
 * it is the cast's primary feedback; the particle FX beside it are the
 * reduced-motion-suppressed ones.)
 */
let _battlefieldFlashEl: HTMLDivElement | null = null;
function flashBattlefield(): void {
  if (!battlefield) return;
  if (!_battlefieldFlashEl || !_battlefieldFlashEl.isConnected) {
    _battlefieldFlashEl = document.createElement('div');
    _battlefieldFlashEl.id = 'battlefield-flash';
    battlefield.appendChild(_battlefieldFlashEl);
  }
  const el = _battlefieldFlashEl;
  el.classList.remove('active');
  void el.offsetWidth; // reflow — restart the fade-in transition
  el.classList.add('active');
  window.setTimeout(() => el.classList.remove('active'), 120);
}
// Minion instance IDs that have ever been on the rendered battlefield
// (persistent across the combat→prep forced rebuild — deliberately NOT
// reset there). The card-slam FX (dust puff) must only fire for minions
// that were genuinely PLAYED from the hand this phase; if it used
// _lastBattlefieldMinionIds instead, the post-combat tracking reset
// (_lastBattlefieldMinionIds = []) would make every restored board card
// look brand new: all of them would slam + puff at the start of each round,
// and when that rebuild lands while the prep area is still hidden (combat
// animation window) the puffs spawn at the viewport's top-left corner —
// the "particle effect misses the card" bug.
let _fxSeenBattlefieldMinionIds = new Set<string>();

// One-shot celebration flags (the game-over data can arrive through two
// paths — screenChange 'gameOver' and the WinnerScreen render — so fire
// each celebration at most once per match).
let _victoryFxPlayed = false;
let _defeatFxPlayed = false;
let _eliminationFxPlayed = false;

// Track whether the battlefield drop zone has been set up (to avoid accumulating listeners)
let _battlefieldDropZoneSetup = false;

/**
 * Generate a numeric FNV-1a hash from minion data that includes stats.
 * Used to detect when minion stats change (e.g., after upgrade) even if IDs stay the same.
 * Avoids string allocation on every render check.
 */
function getBattlefieldStatsHash(minions: { instanceId: string; currentHp: number; attack: number; upgradeCount: number; level: number; minionType: string; abilities?: string[] }[]): number {
  let hash = 0x811c9dc5; // FNV offset basis
  for (const m of minions) {
    // Hash the instance ID characters
    for (let i = 0; i < m.instanceId.length; i++) {
      hash ^= m.instanceId.charCodeAt(i);
      hash = (hash * 0x01000193) >>> 0; // FNV prime, unsigned
    }
    // Hash numeric stats
    hash ^= m.currentHp;
    hash = (hash * 0x01000193) >>> 0;
    hash ^= m.attack;
    hash = (hash * 0x01000193) >>> 0;
    hash ^= m.upgradeCount;
    hash = (hash * 0x01000193) >>> 0;
    hash ^= m.level;
    hash = (hash * 0x01000193) >>> 0;
    // Hash the CLASS + keyword list: a TRANSFORM cast changes the class and
    // clears keywords WITHOUT touching the stats — without these the hash
    // is identical and the board never rebuilds (the stale-card bug).
    for (let i = 0; i < m.minionType.length; i++) {
      hash ^= m.minionType.charCodeAt(i);
      hash = (hash * 0x01000193) >>> 0;
    }
    for (const abilityId of m.abilities ?? []) {
      for (let i = 0; i < abilityId.length; i++) {
        hash ^= abilityId.charCodeAt(i);
        hash = (hash * 0x01000193) >>> 0;
      }
    }
  }
  return hash;
}

// Auto-disconnect timer for eliminated players (20 seconds)
let _eliminationAutoDisconnectTimer: ReturnType<typeof setTimeout> | null = null;
const AUTO_DISCONNECT_DELAY_MS = 20_000;

// Track pending upgrade panel: set when minion is selected for upgrade,
// cleared when the panel is shown (after server responds with options).
let _pendingUpgradeMinionId: string | null = null;

/**
 * The minion id last submitted for a HIGH STAKES upgrade (the branch that
 * can DESTROY the minion) — set from the upgrade-panel branch selection and
 * resolved by the confirming board update in wireBattlefieldUI:
 *  - minion still on the board → the upgrade landed (level-up FX covers it);
 *  - minion GONE → the server rolled the destruction → the black smoke
 *    burst plays over its old slot.
 * A minion that disappears WITHOUT this set is a plain sell (no FX).
 * Blanked with the other battlefield trackers (combat→prep reset, match end).
 */
let _pendingHighStakesMinionId: string | null = null;

// ─── Application Bootstrap ────────────────────────────────────────────────

// ─── Player Identity / Session ──────────────────────────────────────────

/** Current player ID (guest ID or username). Set by startApp(). */
let currentPlayerId: string | null = null;
/** Session token for logged-in users (null for guests). */
let currentToken: string | null = null;
/** Username for logged-in users (null for guests). */
let currentUsername: string | null = null;
/**
 * The user's daily login streak (consecutive login days) as computed by the
 * SERVER on this login/session resume — the frontend never derives it. Null
 * for guests (and when the server did not report one) → panel stays hidden.
 */
let currentStreak: number | null = null;
/** True while a claim request is in flight (guards against double-click spam). */
let _streakClaimInFlight = false;
/** Consecutive days required to enable the streak reward claim. */
const STREAK_REWARD_DAYS = 7;
/** Frontend auth client (HTTP + sessionStorage). */
let authService: AuthService | null = null;

function main(): void {
  authService = new AuthService();

  // ── 1. Wire auth/landing controls (before any shell exists) ─────────
  wireAuthView();
  wireCrazyGamesAuth(); // the /crazygames subpage's guest/sign-in choices

  // The COOP lobby screen's controls (safe pre-startApp: shell is
  // optional — the actions use shell? and the state drives the UI).
  wireCoopLobbyUI();

  // The unified lobby UI (Create Game screen + Original lobby screen) —
  // same pre-startApp convention (the actions use shell?; state drives UI).
  wireCreateGameUI();
  wireLobbyUI();

  // ── 2. Wire queue/match controls (safe pre-startApp: shell is optional)

  // The mode picker's labels come from the SHARED QueueModes spec (single
  // source of truth — changing the sizes/labels there updates the picker).
  for (const mode of QUEUE_MODES) {
    const btn = mode === 'fast' ? btnJoinQueueFast : btnJoinQueueLong;
    btn.querySelector<HTMLElement>('.queue-mode-name')!.textContent =
      `${mode === 'fast' ? '⚡' : '⏳'} ${QUEUE_MODE_LABELS[mode]}`;
    btn.querySelector<HTMLElement>('.queue-mode-sub')!.textContent =
      `${QUEUE_MODE_PLAYER_COUNTS[mode]} players`;
  }

  btnJoinQueueFast.addEventListener('click', () => {
    shell?.joinQueue('fast');
    hideQueueModePicker(); // the server's QueueStatusUpdate flips the UI
  });

  btnJoinQueueLong.addEventListener('click', () => {
    shell?.joinQueue('long');
    hideQueueModePicker();
  });

  btnQueueModeCancel.addEventListener('click', () => {
    hideQueueModePicker();
  });

  btnLeaveQueue.addEventListener('click', () => {
    // BACK: leaving the queue returns to the fast/long picker (the
    // previous sub-menu), not the root Play menu — the server-confirmed
    // leave (QueueStatusUpdate, isInQueue=false) opens it (wireQueueScreen).
    _returnToQueueModePicker = true;
    shell?.leaveQueue();
  });

  // ── PLAY menu (the single primary entry, task 191) ──────────────────
  // Play → reveal the GAME-MODE picker (Singleplayer / Multiplayer) IN THE
  // PLAY BUTTON'S PLACE (the button is hidden while it is open).
  //  - Singleplayer → the server starts an 8-player game (this player +
  //    7 bots) INSTANTLY (StartSoloMatchRequest — no queue, no loading);
  //    the normal MatchStarted broadcast flips the screen.
  //  - Multiplayer → reveal the MULTIPLAYER picker (the old Play menu):
  //    Tutorial (top) / Join Ranked Queue / Join Lobby / Create Game.
  //    - Join Ranked Queue → the fast/long queue-mode picker.
  //    - Join Lobby → the unified code input (resolves Original + Co-op).
  //    - Create Game → the Create Game setup screen (covers co-op too).
  // One sub-menu at a time (hideAllPickers swaps them; the server enforces
  // one lobby/queue per player).

  function showGameModePicker(): void {
    hideAllPickers();
    btnPlay.classList.add('hidden'); // hidden while the sub-menu is open
    gameModePickerEl.classList.remove('hidden');
  }

  btnPlay.addEventListener('click', showGameModePicker);

  // Singleplayer → the instant 8-player solo match (server-side; the
  // picker closes — the MatchStarted broadcast owns the screen change).
  btnSolo.addEventListener('click', () => {
    hideAllPickers();
    shell?.startSoloMatch();
  });

  // Multiplayer → the multiplayer picker (in the Multiplayer button's
  // place — the game-mode picker is hidden, the Play button stays hidden).
  btnMultiplayer.addEventListener('click', showPlayPicker);

  // Game Mechanics (task 202) → the nested Runes / Spells / Keywords
  // picker (in the Game Mechanics button's place — the game-mode picker
  // is hidden, the Play button stays hidden).
  function showMechanicsPicker(): void {
    hideAllPickers();
    btnPlay.classList.add('hidden');
    mechanicsPickerEl.classList.remove('hidden');
  }
  btnMechanics.addEventListener('click', showMechanicsPicker);

  // Back → the game-mode picker (Game Mechanics is a SUB-menu of Play's
  // picker — Back re-opens it, it does not leave the Play tree).
  btnMechanicsCancel.addEventListener('click', showGameModePicker);

  // Progress (task 203) → the achievements / stats / Top 100 sub-menu
  // (same nested convention as Game Mechanics).
  function showProgressPicker(): void {
    hideAllPickers();
    btnPlay.classList.add('hidden');
    progressPickerEl.classList.remove('hidden');
  }
  btnProgress.addEventListener('click', showProgressPicker);

  // Back → the game-mode picker (Progress is a SUB-menu of Play's
  // picker — Back re-opens it, it does not leave the Play tree).
  btnProgressCancel.addEventListener('click', showGameModePicker);

  btnGameModeCancel.addEventListener('click', hideAllPickers);

  function showPlayPicker(): void {
    hideAllPickers();
    btnPlay.classList.add('hidden'); // the sub-menu replaces the Play button
    playPickerEl.classList.remove('hidden');
    // The Join Lobby code row is ALWAYS visible in the multiplayer menu
    // (no sub-menu — the code input + Join button sit in the picker itself,
    // and Join joins directly).
    joinLobbyRowEl.classList.remove('hidden');
    lobbyCodeInputEl.value = '';
  }

  function hidePlayPicker(): void {
    playPickerEl.classList.add('hidden');
    joinLobbyRowEl.classList.add('hidden');
    if (shell?.gameState.queue.isInQueue) {
      return; // queued — Leave Queue is the active control (Play stays hidden)
    }
    btnPlay.classList.remove('hidden');
  }

  // Back → the game-mode picker (Multiplayer is a SUB-menu of Play's
  // picker — Back re-opens it, it does not leave the Play tree — same
  // convention as Game Mechanics / Progress).
  btnPlayCancel.addEventListener('click', showGameModePicker);

  // Join Ranked Queue → the fast/long queue-mode picker (existing flow).
  btnPlayRanked.addEventListener('click', () => {
    hidePlayPicker();
    showQueueModePicker();
  });

  // NOTE: the old "Join Lobby" option button (#btn-play-join-lobby) is
  // hidden — its code input + Join button row is now ALWAYS visible in the
  // multiplayer picker, and Join joins directly (no sub-menu).

  // Create Game → the Create Game setup screen.
  btnPlayCreate.addEventListener('click', () => {
    hidePlayPicker();
    showCreateGameScreen();
  });

  // Join Lobby submit → the unified JoinLobbyRequest (code resolves both
  // Original and Co-op lobbies; the server routes to the right screen).
  function submitLobbyJoin(): void {
    const code = lobbyCodeInputEl.value.trim();
    if (code.length === 0) return;
    shell?.joinLobby(code);
    hidePlayPicker(); // the server's reply (state update or error toast)
  }
  btnLobbyJoin.addEventListener('click', submitLobbyJoin);
  lobbyCodeInputEl.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') submitLobbyJoin();
  });

  // NOTE (task 191): the legacy CO-OP PICKER (the 🤝 Co-op button next to
  // Play) is GONE — co-op is reachable through Play → Multiplayer →
  // Create Game (the game type toggle) and Join Lobby (a co-op code
  // resolves to the coop lobby exactly as before).

  // Claim the 7-day login streak reward: the button is only ever enabled for
  // a server-reported streak >= STREAK_REWARD_DAYS. The SERVER validates and
  // performs the claim — it resets the cycle to 1/7 (anchored to the claim
  // day), and that reset is also the double-claim guard (a second press sees
  // streak 1 and is rejected). We always resync the counter from the
  // response, so a stale enabled UI self-heals instead of double-claiming.
  btnClaimStreak.addEventListener('click', () => {
    if (btnClaimStreak.disabled || _streakClaimInFlight) return;
    if (!currentToken || !authService) return;
    _streakClaimInFlight = true;
    authService.claimStreak(currentToken)
      .then((result) => {
        if (typeof result.streak === 'number') {
          currentStreak = result.streak; // claimed → 1 (new cycle); rejected → current value
          updateStreakPanel();
        }
        // The server is the only source of the reward: on a successful
        // claim it granted ONE random streak-pool skin the user did not
        // own yet (null when the user owns the whole pool — the claim
        // still reset the cycle, the player just received nothing). We
        // only display what the server reports.
        if (result.claimed) {
          if (result.reward) {
            showUnlockToast('7-Day Streak Reward', '🎁', `${result.reward.name} skin unlocked`);
          } else {
            showUnlockToast('7-Day Streak Reward', '🎁', 'No new reward — you already own all of them');
          }
        }
      })
      .catch(() => {
        // Network failure: keep the current display — the next
        // login/session verification resyncs the streak from the server.
      })
      .finally(() => {
        _streakClaimInFlight = false;
      });
  });

  btnLeaveMatch.addEventListener('click', () => {
    onLeaveMatch();
  });

  btnBackToQueue.addEventListener('click', () => {
    restartShellKeepingIdentity();
  });

  // Rotate-device hint (mobile, in-game): tapping retries the fullscreen
  // + landscape lock — the tap IS a user gesture (which requestFullscreen
  // requires), so this often succeeds where the match-start attempt (no
  // gesture) could not.
  rotateHintOverlay.addEventListener('click', () => {
    if (!_gameOrientationEnforced) return;
    void beginGameOrientationEnforcement();
  });

  // Buy-a-card button: sends the intention; the server is authoritative
  // (computes the Discount-reduced cost, validates gold + hand capacity).
  buyCardBtn.addEventListener('click', () => {
    // SOUND: buying a card (Apply.wav).
    soundService.play('apply');
    handUI?.buyCard();
  });

  // Rune shop (upper-left corner of the hand): opens the rune shop overlay
  // (every rune with its gold price). The purchase itself happens on a
  // shop row click (handUI.buyRune(runeId) — the server re-validates
  // everything).
  buyRuneBtn.addEventListener('click', () => {
    openRuneShop();
  });

  // Keywords reference overlay: open via the hand-row button, close via the
  // ✕ button, a backdrop click or Esc. Rows are built once (the ability
  // definitions are static). Combat start closes it automatically (see the
  // showCombat transition below).
  buildKeywordsOverlay();
  keywordsBtn.addEventListener('click', () => openKeywordsOverlay());

  // COOP teammate viewer: the eye button opens the overlay (preparation
  // only — updateCoopMatchUI keeps it [hidden] in regular matches and the
  // battle phase, so a stale click can't open it). Close via ✕, backdrop
  // click or Esc; combat start auto-closes it (see the showCombat
  // transition). The overlay re-renders live on every state change (see
  // wireHandUI's render callback).
  teammateEyeBtn.addEventListener('click', () => openTeammateOverlay());
  teammateCloseBtn.addEventListener('click', () => closeTeammateOverlay());
  teammateOverlay.addEventListener('click', (e) => {
    if (e.target === teammateOverlay) closeTeammateOverlay(); // backdrop click
  });

  // Ready button (lower-right corner of the hand): sends the toggle
  // intention; the server is authoritative (validates alive + preparation,
  // updates the count for the whole match, and starts combat immediately
  // once EVERY living player is ready).
  readyBtn.addEventListener('click', () => togglePreparationReady());

  keywordsClose.addEventListener('click', () => closeKeywordsOverlay());
  keywordsOverlay.addEventListener('click', (e) => {
    if (e.target === keywordsOverlay) closeKeywordsOverlay(); // backdrop click
  });
  // How to Play (wiki): the button was removed from the landing page
  // (outdated content — to be updated later); the overlay + open/close
  // functions remain for a future entry point. Combat start still
  // force-closes it (defensive).
  howToPlayClose.addEventListener('click', () => closeHowToPlayOverlay());

  // Tutorial (scripted in-game guide): available to EVERYONE — the button
  // is in #landing-screen, so both the logged-out identity-choices page
  // and the logged-in queue view reach it. Needs no server connectivity
  // (startTutorial shuts the real shell down first).
  btnTutorial.addEventListener('click', () => startTutorial());

  // In-game Settings (top-right ⚙️): opens the settings overlay (Resign).
  // The button is only clickable in the PREPARATION phase (main.ts hides
  // it during combat — you can not resign during combat) and the overlay
  // itself is auto-closed by the combat-start hook in onStateChange.
  btnSettings.addEventListener('click', () => {
    if (isSettingsActionAllowed()) openSettingsOverlay();
  });
  settingsClose.addEventListener('click', () => closeSettingsOverlay());
  settingsOverlay.addEventListener('click', (e) => {
    if (e.target === settingsOverlay) closeSettingsOverlay(); // backdrop click
  });
  btnResign.addEventListener('click', () => attemptResign());
  howToPlayOverlay.addEventListener('click', (e) => {
    if (e.target === howToPlayOverlay) closeHowToPlayOverlay(); // backdrop click
  });

  // Purchased-runes row (top-left, below the HUD): ONE delegated listener —
  // the row's innerHTML is rebuilt on every state change, so per-icon
  // listeners would leak. The overlay follows the shared close conventions
  // (✕ button, backdrop click, Esc below).
  purchasedRunesRow.addEventListener('click', (e) => {
    const icon = (e.target as HTMLElement).closest('.purchased-rune-icon');
    const runeId = icon?.getAttribute('data-rune-id');
    if (runeId) openPurchasedRuneOverlay(runeId);
  });
  // Spell-charges row (armed board spells) — the same delegated-listener
  // convention: the row's innerHTML is rebuilt on every state change, so
  // per-icon listeners would leak. Clicking an icon opens the shared
  // rune-style overlay with the spell's name + effect.
  spellChargesRow.addEventListener('click', (e) => {
    const icon = (e.target as HTMLElement).closest('.spell-charge-icon');
    const chargeKey = icon?.getAttribute('data-spell-charge');
    if (chargeKey) {
      const count = Number(icon?.getAttribute('data-spell-charge-count') ?? 0);
      openSpellChargeOverlay(chargeKey, count);
    }
  });
  purchasedRuneClose.addEventListener('click', () => closePurchasedRuneOverlay());
  purchasedRuneOverlay.addEventListener('click', (e) => {
    if (e.target === purchasedRuneOverlay) closePurchasedRuneOverlay(); // backdrop click
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeKeywordsOverlay();
      closeTeammateOverlay();
      closeHowToPlayOverlay();
      closeAchievementsOverlay();
      closeStatsOverlay();
      closeSkinsOverlay();
      closeToplistOverlay();
      closeShareOverlay();
      closeRuneViewer();
      closeSpellViewer();
      closeRuneShop();
      closePurchasedRuneOverlay();
      closeSettingsOverlay();
      closeUpgradePanel();
      closeClassProgressOverlay();
      closeClassProgressMobile();
    }
  });

  // The class-progression arrows track the layout (window resizes).
  window.addEventListener('resize', () => {
    if (!classProgressOverlay.classList.contains('hidden')) drawClassProgressArrows();
  });

  // Click OUTSIDE the upgrade options panel closes it (the panel has no
  // close button; a single click on anything outside it — battlefield,
  // hand, HUD, background — dismisses it and clears the selection).
  // Clicks inside the panel (the branch options) keep their own handlers.
  // Registered once in main() — the panel element and closeUpgradePanel
  // are stable across shell restarts.
  document.addEventListener('click', (e) => {
    if (!upgradePanel.classList.contains('visible')) return;
    const target = e.target as Node | null;
    if (target && upgradePanel.contains(target)) return;
    closeUpgradePanel();
  });

  // ─── Menu button clicks (BtnClick.wav, task: sounds) ───────────────────
  // ANY button in the game menus (landing/lobby screens + every overlay
  // dialog) plays a click. In-match action buttons (hand plates, board,
  // combat) are NOT menus — they keep their own action sounds.
  document.addEventListener('click', (e: MouseEvent) => {
    if (!(e.target instanceof Element)) return;
    if (e.target.closest(
      '#landing-screen button, #lobby-screen button, #coop-lobby-screen button, #create-game-screen button, #gameover-screen button, #eliminated-screen button, [id$="-overlay"] button',
    )) {
      soundService.play('btnClick');
    }
  }, true);

  // Achievements (logged-in users only, outside of a game): the request is
  // sent through the shell; the server answers with the sorted, redacted
  // list which opens the overlay (see the 'achievementsUpdate' event).
  btnAchievements.addEventListener('click', () => {
    if (shell && shell.isAuthenticated) shell.requestAchievements();
  });
  achievementsClose.addEventListener('click', () => closeAchievementsOverlay());
  achievementsOverlay.addEventListener('click', (e) => {
    if (e.target === achievementsOverlay) closeAchievementsOverlay(); // backdrop click
  });

  // Stats (logged-in users only, outside of a game): the request is sent
  // through the shell; the server answers with the authoritative stats
  // which open the overlay (see the 'statsUpdate' event).
  btnStats.addEventListener('click', () => {
    if (shell && shell.isAuthenticated) shell.requestStats();
  });
  statsClose.addEventListener('click', () => closeStatsOverlay());
  statsOverlay.addEventListener('click', (e) => {
    if (e.target === statsOverlay) closeStatsOverlay(); // backdrop click
  });

  // Skins (logged-in users only, outside of a game): the request is sent
  // through the shell; the server answers with the full catalogue plus the
  // per-user unlocked/selected state which opens the overlay (see the
  // 'skinsUpdate' event). Selecting a skin sends a SetSkinRequest; the
  // confirmation arrives as another SkinsResponse (overlay re-renders).
  btnSkins.addEventListener('click', () => {
    if (shell && shell.isAuthenticated) shell.requestSkins();
  });
  skinsClose.addEventListener('click', () => closeSkinsOverlay());

  // Toplist: available to EVERYONE (no isAuthenticated gate — guests view
  // the Top 100 too, they are just not placed on it). The request is sent
  // through the shell; the server answers with the authoritative list which
  // opens the overlay (see the 'toplistUpdate' event).
  // Top 100 — TWO access paths (task 203): the corner icon AND the
  // Progress sub-menu entry. Both request the authoritative list.
  const requestToplist = (): void => { if (shell) shell.requestToplist(); };
  btnToplist.addEventListener('click', requestToplist);
  btnToplistProgress.addEventListener('click', requestToplist);
  toplistClose.addEventListener('click', () => closeToplistOverlay());
  toplistOverlay.addEventListener('click', (e) => {
    if (e.target === toplistOverlay) closeToplistOverlay(); // backdrop click
  });

  // Share the app (login page + queue view): PURELY CLIENT-SIDE — no
  // auth or shell guard (guests share too, like they view the Top 100).
  // The overlay lists the platform share intents (new window, noopener)
  // plus a Copy-link button; TikTok (no documented web intent) copies
  // its caption to the clipboard + opens the site for a manual post.
  buildShareOverlay();
  // On CrazyGames the app lives INSIDE the platform — sharing a
  // battlebrew.online link is pointless (invites/joins go through the
  // platform's native UI). Hide both share buttons there.
  if (IS_CRAZYGAMES_MODE) {
    btnShareAuth.classList.add('hidden');
    btnShareQueue.classList.add('hidden');
  }
  btnShareAuth.addEventListener('click', () => openShareOverlay());
  btnShareQueue.addEventListener('click', () => openShareOverlay());
  shareClose.addEventListener('click', () => closeShareOverlay());
  shareOverlay.addEventListener('click', (e) => {
    if (e.target === shareOverlay) closeShareOverlay(); // backdrop click
  });
  shareCopyBtn.addEventListener('click', () => {
    void copyShareLink();
  });
  skinsOverlay.addEventListener('click', (e) => {
    if (e.target === skinsOverlay) closeSkinsOverlay(); // backdrop click
  });

  // Keywords reference on the landing page: same overlay as the in-game 📖
  // button — purely client-side (static keyword definitions), so it needs
  // no auth/shell guard. The overlay is global and auto-closes when combat
  // starts, so opening it from the queue is safe even if the player joins
  // right after.
  btnKeywordsQueue.addEventListener('click', () => {
    openKeywordsOverlay();
  });

  // Class Progression (task 210): the grid visualization of the class
  // ladders + transforms. Available to everyone (like Runes / Spells);
  // closes via its ✕ button, a backdrop click or Esc. Task 217: on
  // MOBILE the separate single-column overlay opens instead (the desktop
  // grid is left untouched — making it responsive would break it too
  // much); on desktop the grid opens.
  btnClassProgress.addEventListener('click', () => {
    if (isMobileLayout()) {
      openClassProgressMobile();
    } else {
      openClassProgressOverlay();
    }
  });
  classProgressClose.addEventListener('click', () => closeClassProgressOverlay());
  classProgressOverlay.addEventListener('click', (e) => {
    // A click anywhere OUTSIDE the chip dismisses the hint (the chip's
    // handler stops propagation); a click on the backdrop closes the page.
    hideClassProgressHint();
    if (e.target === classProgressOverlay) closeClassProgressOverlay();
  });
  // The MOBILE overlay (task 217): the same close conventions (✕,
  // backdrop, Esc — the Esc is in the shared handler below).
  cpmClose.addEventListener('click', () => closeClassProgressMobile());
  classProgressMobileOverlay.addEventListener('click', (e) => {
    hideCpmHint();
    if (e.target === classProgressMobileOverlay) closeClassProgressMobile();
  });

  // Runes (main menu): the 🪬 Runes button opens the READ-ONLY catalogue
  // viewer (a scrollable list of ALL runes — browse/scroll only; there is
  // no pre-match rune selection anymore). Available to everyone (guests
  // included). The overlay closes via its ✕ button, a backdrop click or
  // Esc (shared with the in-game rune shop).
  btnRunes.addEventListener('click', () => openRuneViewer());
  runeViewerClose.addEventListener('click', () => closeRuneViewer());
  runeViewerOverlay.addEventListener('click', (e) => {
    if (e.target === runeViewerOverlay) closeRuneViewer(); // backdrop click
  });

  // Spells (main menu): the Spells button opens the READ-ONLY catalogue
  // viewer (grouped by level, each group sorted by gold value). Same
  // conventions as Runes: available to everyone (guests included); the
  // overlay closes via its ✕ button, a backdrop click or Esc.
  btnSpells.addEventListener('click', () => openSpellViewer());
  spellViewerClose.addEventListener('click', () => closeSpellViewer());
  spellViewerOverlay.addEventListener('click', (e) => {
    if (e.target === spellViewerOverlay) closeSpellViewer(); // backdrop click
  });
  // Rune shop (in-game): the ✕ button and a backdrop click close it (Esc
  // is handled by the shared document-level Escape listener above). The
  // shop rows wire themselves up in renderRuneShop (the list is rebuilt
  // from the authoritative state on every state change while open).
  runeShopClose.addEventListener('click', () => closeRuneShop());
  runeShopOverlay.addEventListener('click', (e) => {
    if (e.target === runeShopOverlay) closeRuneShop(); // backdrop click
  });

  // ── 3. Start on the landing screen ─────────────────────────────────
  // CrazyGames subpage: NO login screen — the platform's user signs in
  // automatically (stored session first, then the SDK token flow).
  // Everywhere else: identity selection + stored-session resume.
  if (IS_CRAZYGAMES_MODE) {
    startCrazyGamesLanding();
  } else {
    showLandingView('choices');
    showScreen('landing');

    // ── 4. Resume a stored session if present (page reload) ─────
    void resumeSavedSession();
  }

  // ── 5. Ambient particle background (behind both landing views) ──────
  startLandingParticles();
  startLandingFires();
  hideLandscapeMobileBrowserBar();
}

// ─── Landing particle background ────────────────────────────────────────
// Ambient drifting particles behind the landing content: the identity
// choices ("main landing page") and the logged-in queue view both live
// inside #landing-screen, so ONE viewport-fixed canvas (#landing-particles,
// z-index:-1 under the screen's isolation:isolate stacking context) covers
// both. Purely decorative — pointer-events:none, soft glow in the game's
// palette, slow drift with edge wrap-around. Honors prefers-reduced-motion
// (one static frame, no animation loop) and idles while the landing screen
// is hidden (match / game over).
const PARTICLE_PALETTE = [
  '241, 196, 15',  // gold (title)
  '52, 152, 219',  // blue (primary)
  '26, 188, 156',  // teal (runes)
  '162, 155, 254', // violet (keywords)
];

interface LandingParticle {
  x: number;
  y: number;
  vx: number; // px/s
  vy: number; // px/s
  r: number;  // core radius (px)
  alpha: number;
  twinklePhase: number;
  twinkleSpeed: number; // rad/s
  color: string; // "r, g, b"
}

function startLandingParticles(): void {
  if (!landingParticlesCanvas) return;
  const canvas = landingParticlesCanvas;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // DPR-crisp viewport-sized canvas (the canvas is position:fixed, so it
  // covers exactly the viewport even while the screen scrolls).
  let width = 0;
  let height = 0;
  const resize = (): void => {
    const dpr = window.devicePixelRatio || 1;
    width = window.innerWidth;
    height = window.innerHeight;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };
  resize();
  window.addEventListener('resize', resize);

  // Sparse + calm: ~one particle per 22,000 px² of viewport, capped.
  const count = Math.min(90, Math.max(24, Math.round((width * height) / 22000)));
  const particles: LandingParticle[] = [];
  for (let i = 0; i < count; i++) {
    particles.push({
      x: Math.random() * width,
      y: Math.random() * height,
      // Slow drift: 4–18 px/s in a random direction.
      vx: (Math.random() - 0.5) * 18,
      vy: (Math.random() - 0.5) * 18,
      r: 1 + Math.random() * 2.2,             // 1–3.2 px core
      alpha: 0.12 + Math.random() * 0.3,      // soft
      twinklePhase: Math.random() * Math.PI * 2,
      twinkleSpeed: 0.3 + Math.random() * 0.8,
      color: PARTICLE_PALETTE[i % PARTICLE_PALETTE.length],
    });
  }

  const draw = (nowMs: number): void => {
    ctx.clearRect(0, 0, width, height);
    const t = nowMs / 1000;
    for (const p of particles) {
      const a = p.alpha * (0.7 + 0.3 * Math.sin(p.twinklePhase + t * p.twinkleSpeed));
      // Halo (larger, fainter) + core — cheaper than shadowBlur.
      ctx.fillStyle = `rgba(${p.color}, ${(a * 0.25).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r * 3.2, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(${p.color}, ${a.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fill();
    }
  };

  // prefers-reduced-motion: one static frame, no animation loop.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    draw(0);
    return;
  }

  let last = performance.now();
  const frame = (now: number): void => {
    requestAnimationFrame(frame);
    const dt = Math.min(0.1, (now - last) / 1000); // clamp tab-switch jumps
    last = now;
    // Idle while the landing screen is hidden (match / game over).
    if (landingScreen.classList.contains('hidden')) return;
    for (const p of particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      // Wrap with a margin so dots glide in/out of the edges.
      if (p.x < -10) p.x = width + 10; else if (p.x > width + 10) p.x = -10;
      if (p.y < -10) p.y = height + 10; else if (p.y > height + 10) p.y = -10;
    }
    draw(now);
  };
  requestAnimationFrame(frame);
}

// ─── Landscape mobile: hide the browser navigation bar ─────────────────────
// requestFullscreen is the only standard way to hide the mobile browser
// chrome (the address/navigation bar), and browsers require a USER GESTURE
// for it — so this is best-effort ("if possible"): one un-gestured attempt
// on load (works in some WebViews) + a retry on the first tap while the
// landing screen is visible in the phone-landscape band (w > h, w ≤ 1024,
// h ≤ 500 — the same band as isMobileLayout's landscape case). The
// CrazyGames subpage is skipped: the platform frame owns that chrome.
function hideLandscapeMobileBrowserBar(): void {
  if (IS_CRAZYGAMES_MODE) return;
  const isLandscapePhone = (): boolean => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    return w > h && w <= 1024 && h <= 500;
  };
  const tryEnter = (): void => {
    if (!isLandscapePhone()) return;
    if (landingScreen.classList.contains('hidden')) return;
    if (document.fullscreenElement) return;
    if (!fullscreenService) {
      // The service is normally created with the shell — create it on
      // demand (the same pair the tutorial path runs) and keep it for
      // later (it is a plain singleton, safe to reuse):
      fullscreenService = new FullscreenService();
      wireFullscreenService();
    }
    void fullscreenService.enter().catch(() => undefined);
  };
  tryEnter(); // best effort without a gesture (rejected on most browsers)
  window.addEventListener('pointerdown', tryEnter, { once: true });
}

// ─── Landing fire FX ─────────────────────────────────────────────────────────
// Two static fires burning at the screen's left and right edges (each
// vertically centered, 10% in from its edge) — bright yellow flames with
// rising smoke, drawn on #landing-fires between the WaterCastle layer and
// the Stones layer. PLAYS on desktop AND landscape (horizontal) mobile;
// HIDDEN on portrait (vertical) layouts. Honors prefers-reduced-motion
// (one static frame) and idles while the landing screen is hidden, like
// the particle canvas.
interface FireParticle {
  x: number;
  y: number;
  vx: number; // px/s base horizontal drift
  vy: number; // px/s (negative = rising)
  r: number;  // base radius (px)
  life: number; // remaining seconds
  maxLife: number;
  phase: number; // flicker phase (rad)
  speed: number; // flicker speed (rad/s)
  color: string; // "r, g, b"
  smoke: boolean; // smoke rises slower, grows and fades (no additive glow)
}

function startLandingFires(): void {
  if (!landingFiresCanvas) return;
  const canvas = landingFiresCanvas;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  let width = 0;
  let height = 0;
  // The fires play on desktop + LANDSCAPE (horizontal) mobile and are
  // hidden only on PORTRAIT (vertical) layouts (narrower than tall):
  const portrait = (): boolean => window.innerWidth < window.innerHeight;
  const resize = (): void => {
    const dpr = window.devicePixelRatio || 1;
    width = window.innerWidth;
    height = window.innerHeight;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // PORTRAIT HIDDEN — no fires on vertical layouts:
    canvas.style.display = portrait() ? 'none' : '';
    if (portrait()) ctx.clearRect(0, 0, width, height);
  };
  resize();
  window.addEventListener('resize', resize);

  // The two STATIC burn points: vertically centered, 10% in from each edge.
  const emitters = (): Array<{ x: number; y: number }> => [
    { x: width * 0.10, y: height * 0.50 },
    { x: width * 0.90, y: height * 0.50 },
  ];

  const flames: FireParticle[] = [];
  const SMOKE: FireParticle[] = [];
  let flameAccum = 0; // fractional spawn carry (rate-based spawning)
  let smokeAccum = 0;

  const spawnFlame = (e: { x: number; y: number }): void => {
    flames.push({
      x: e.x + (Math.random() - 0.5) * 26,
      y: e.y - Math.random() * 8,
      vx: (Math.random() - 0.5) * 14,
      vy: -(70 + Math.random() * 110), // rise fast
      r: 6 + Math.random() * 9,        // 6–15 px
      life: 0.6 + Math.random() * 0.8, // 0.6–1.4 s
      maxLife: 0, // set below
      phase: Math.random() * Math.PI * 2,
      speed: 6 + Math.random() * 8,
      // Bright yellow core → hot orange (brighter particles bias yellow):
      color: Math.random() < 0.45 ? '255, 236, 80' : '255, 170, 40',
      smoke: false,
    });
    flames[flames.length - 1].maxLife = flames[flames.length - 1].life;
  };
  const spawnSmoke = (e: { x: number; y: number }): void => {
    const life = 1.4 + Math.random() * 1.6; // 1.4–3.0 s
    SMOKE.push({
      x: e.x + (Math.random() - 0.5) * 20,
      y: e.y - 14 - Math.random() * 10,
      vx: (Math.random() - 0.5) * 10,
      vy: -(26 + Math.random() * 34), // slower rise
      r: 7 + Math.random() * 9,       // 7–16 px (grows while rising)
      life,
      maxLife: life,
      phase: Math.random() * Math.PI * 2,
      speed: 2 + Math.random() * 3,
      color: '128, 128, 134', // cool gray
      smoke: true,
    });
  };

  const baseGlow = (e: { x: number; y: number }): void => {
    // The static point itself: a warm radial glow under the flames.
    const g = ctx.createRadialGradient(e.x, e.y, 0, e.x, e.y, 46);
    g.addColorStop(0, 'rgba(255, 220, 90, 0.55)');
    g.addColorStop(0.5, 'rgba(255, 160, 40, 0.22)');
    g.addColorStop(1, 'rgba(255, 140, 20, 0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(e.x, e.y, 46, 0, Math.PI * 2);
    ctx.fill();
  };

  const draw = (nowMs: number): void => {
    ctx.clearRect(0, 0, width, height);
    const t = nowMs / 1000;
    // Smoke first (behind the flames, normal compositing):
    for (const p of SMOKE) {
      const a = p.life / p.maxLife; // 1 → 0
      const grow = 1 + 1.8 * (1 - a); // puffs expand while rising
      const alpha = 0.14 * Math.pow(a, 0.7);
      ctx.fillStyle = `rgba(${p.color}, ${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r * grow, 0, Math.PI * 2);
      ctx.fill();
    }
    // Flames on top with additive blending (overlap burns brighter):
    ctx.globalCompositeOperation = 'lighter';
    for (const e of emitters()) baseGlow(e);
    for (const p of flames) {
      const a = p.life / p.maxLife; // 1 → 0
      const flicker = 0.85 + 0.15 * Math.sin(p.phase + t * p.speed);
      const r = Math.max(0.5, p.r * a * flicker);
      const x = p.x + Math.sin(p.phase + t * p.speed * 0.6) * 5 * a; // sway
      // Outer hot halo + inner bright core (cheaper than shadowBlur):
      ctx.fillStyle = `rgba(${p.color}, ${(0.28 * a).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(x, p.y, r * 2.1, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = `rgba(${p.color}, ${(0.75 * a).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(x, p.y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
  };

  const step = (dt: number): void => {
    const es = emitters();
    // Rate-based spawning (per second, shared across both fires):
    flameAccum += 46 * dt;
    smokeAccum += 9 * dt;
    while (flameAccum >= 1) {
      flameAccum -= 1;
      spawnFlame(es[Math.floor(Math.random() * es.length)]);
    }
    while (smokeAccum >= 1) {
      smokeAccum -= 1;
      spawnSmoke(es[Math.floor(Math.random() * es.length)]);
    }
    const age = (list: FireParticle[]): void => {
      for (let i = list.length - 1; i >= 0; i--) {
        const p = list[i];
        p.life -= dt;
        if (p.life <= 0) { list.splice(i, 1); continue; }
        p.x += (p.vx + Math.sin(p.phase + p.life * p.speed) * 16) * dt;
        p.y += p.vy * dt;
        if (p.smoke) p.vy *= 1 - 0.25 * dt; // smoke slows as it cools
      }
    };
    age(flames);
    age(SMOKE);
    if (flames.length > 240) flames.splice(0, flames.length - 240);
    if (SMOKE.length > 90) SMOKE.splice(0, SMOKE.length - 90);
  };

  // prefers-reduced-motion: one static frame (a few pre-aged flames +
  // smoke puffs around each fire), no animation loop.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    if (!portrait()) {
      for (const e of emitters()) {
        for (let i = 0; i < 10; i++) {
          spawnFlame(e);
          flames[flames.length - 1].life = flames[flames.length - 1].maxLife * (0.3 + 0.7 * Math.random());
        }
        for (let i = 0; i < 4; i++) {
          spawnSmoke(e);
          SMOKE[SMOKE.length - 1].life = SMOKE[SMOKE.length - 1].maxLife * (0.3 + 0.7 * Math.random());
        }
      }
      draw(0);
    }
    return;
  }

  let last = performance.now();
  const frame = (now: number): void => {
    requestAnimationFrame(frame);
    const dt = Math.min(0.1, (now - last) / 1000); // clamp tab-switch jumps
    last = now;
    // Idle while the landing screen is hidden (match / game over) or on a
    // portrait layout (the canvas is hidden — nothing to draw).
    if (landingScreen.classList.contains('hidden') || portrait()) return;
    step(dt);
    draw(now);
  };
  requestAnimationFrame(frame);
}

/**
 * Creates the drag-and-drop service with the standard action routing:
 * play / reposition / sell intentions go to the module-level UI
 * components, which forward them to the CURRENT shell (the authority).
 * Because the callbacks read the module variables at call time, the SAME
 * service instance routes to the tutorial's TutorialShell while a tour
 * runs (startTutorial rebinds the components before the first drag) and
 * back to the real shell afterwards. Used by both the real shell init
 * and the tutorial (logged-out users have no service yet — startTutorial
 * creates one on demand).
 */
function createDragAndDropService(): DragAndDropService {
  return new DragAndDropService({
    isDragEnabled: false, // Will be enabled when in preparation phase
    onPlayCard: (instanceId: string, targetPosition?: number) => {
      // SOUND: the card lands on the battlefield — Impact (an Undead played
      // from the hand plays BeastSound instead — the specific wins).
      const played = handUI?.renderData.cards.find((c) => c.instanceId === instanceId);
      soundService.play(played?.minionType === 'undead' ? 'beast' : 'impact');
      handUI?.playCard(instanceId, targetPosition);
    },
    // SPELL CAST: the spell's effect goes into play and the card despawns
    // (it never lands on the board). Targeted spells carry the minion the
    // card was released on; battlefield spells have no target. releasePos is
    // where the card was released — the star-explosion cast FX plays there
    // (captured for the state-update confirmation, see _pendingSpellCastPos).
    // The server is authoritative (validates the cast + target, applies the
    // effect).
    onPlaySpell: (instanceId: string, targetInstanceId?: string, releasePos?: { x: number; y: number }) => {
      // SOUND: any spell cast plays PlayCard.
      soundService.play('playCard');
      _pendingSpellCastPos = releasePos ?? null;
      // Record the TARGET for the cast FX (the Ban Hammer's red X plays
      // OVER the target minion, not at the release point). The card is
      // still in the hand at this point (the server removes it in the
      // next state update), so the render data still has it.
      const spellBase = handUI?.renderData.cards.find(c => c.instanceId === instanceId)?.baseMinionId ?? '';
      _pendingSpellFx = { baseMinionId: spellBase, targetInstanceId };
      handUI?.playSpellCard(instanceId, targetInstanceId);
    },
    onReposition: (instanceId: string, targetIndex: number) => {
      // SOUND: a reorder drop also lands the card on the board (Impact).
      soundService.play('impact');
      if (!battlefieldUI) return;
      const renderData = battlefieldUI.renderData;
      const currentIds = renderData.minions.map(m => m.instanceId);
      const fromIndex = currentIds.indexOf(instanceId);
      if (fromIndex < 0) return;

      // Build new order by removing from old position and inserting at new
      const newIds = [...currentIds];
      newIds.splice(fromIndex, 1);
      // Adjust targetIndex if needed (since we removed an element)
      const adjustedTarget = targetIndex > fromIndex ? targetIndex - 1 : targetIndex;
      newIds.splice(adjustedTarget, 0, instanceId);
      battlefieldUI.repositionMinions(newIds);
    },
    onSellCard: (instanceId: string) => {
      // SOUND: selling (hand OR board) plays Sell.
      soundService.play('sell');
      // Drag-to-sell: the card may be in the hand OR on the battlefield —
      // read fresh state to route to the right UI entry point. The server
      // remains authoritative (validates phase + ownership, awards gold).
      const matchState = shell?.gameState.match;
      const me = matchState?.players.find(p => p.id === shell?.playerId);
      const inHand = me?.hand?.some(c => c.instanceId === instanceId) ?? false;
      if (inHand) {
        handUI?.sellCard(instanceId);
      } else {
        battlefieldUI?.sellMinion(instanceId);
      }
      // The sold minion can no longer be upgraded — clear any pending
      // upgrade selection/panel (same as the drag-to-sell path).
      upgradePanel.classList.remove('visible');
      upgradeChoiceUI?.deselectMinion();
    },
  });
}

/**
 * Create an AppShell for the current identity, instantiate all screen
 * components, and wire every callback. This is the ONLY place where an
 * AppShell is created — called from startApp() and
 * restartShellKeepingIdentity().
 */
function initShellAndComponents(): void {
  const serverUrl = getServerUrl();

  // A fresh shell is a fresh identity context: a DEFERRED startup lobby
  // action stashed by a PREVIOUS load/identity must never fire under the
  // new one (the startup flow that follows this call re-arms it for THIS
  // load — it runs AFTER the clear, so its own stash survives).
  _pendingCgStartupLobby = null;

  // ── 0. Dispose the previous shell's document-level services — they own
  // document-level listeners (sell drop zone, card long-press inspect)
  // that must not accumulate across shell restarts (re-login, restart
  // after game over).
  dragAndDropService?.dispose();
  cardInspectService?.dispose();

  // ── 1. Create and initialize AppShell ──────────────────────────────
  shell = new AppShell({
    serverUrl,
    playerId: currentPlayerId!,
    authToken: currentToken ?? undefined,
    // Unlimited reconnect attempts (the ConnectionService default) — an idle
    // background tab drops the socket; the game must recover, never give up
    // (the old cap of 5 left Play/Quick Play disabled forever).
    reconnectDelayMs: 1000,
  });

  // Recover the instant the tab returns to the foreground: a background
  // tab's timers are throttled, so a dropped socket may sit in its backoff
  // (or its pings stalled) while the user is away. Poking on
  // visibilitychange/focus reconnects immediately instead of waiting for
  // the next backoff tick — Play/Quick Play light up as soon as they look.
  // (module-scoped so a shell restart removes the previous pair first)
  if (_pokeOnForeground) {
    document.removeEventListener('visibilitychange', _pokeOnForeground);
    window.removeEventListener('focus', _pokeOnForeground);
  }
  _pokeOnForeground = () => {
    if (document.visibilityState === 'visible' && shell) shell.pokeConnection();
  };
  document.addEventListener('visibilitychange', _pokeOnForeground);
  window.addEventListener('focus', _pokeOnForeground);

  // ── 2. Create all screen components ────────────────────────────────
  queueScreen          = new QueueScreen(shell);
  matchHudComponent    = new MatchHUD(shell);
  handUI               = new HandUI(shell);
  battlefieldUI        = new BattlefieldUI(shell);
  combatBoardView      = new CombatBoardView(shell);
  preparationTimerUI   = new PreparationTimerUI(shell);
  upgradeChoiceUI      = new UpgradeChoiceUI(shell);
  combatAnimationView  = new CombatAnimationView(shell);
  simultaneousDamageAnimator = new SimultaneousDamageAnimator();
  combatGoldDisplay    = new CombatGoldDisplay(shell);
  combatResultUI       = new CombatResultUI(shell);
  spectatorModeUI      = new SpectatorModeUI(shell);
  eliminatedPlayerScreen = new EliminatedPlayerScreen(shell);
  winnerScreen         = new WinnerScreen(shell);
  reconnectUI          = new ReconnectUI(shell);

  // ── 2b. Create DragAndDropService ──────────────────────────────────
  dragAndDropService = createDragAndDropService();

  // ── 2c. Create FullscreenService ───────────────────────────────────
  // ── 2b2. Create CardInspectService ──────────────────────────────
  // Long-press any card (hand / battlefield / combat minion) to see a large,
  // non-interactive magnified view with the ability explanatory text;
  // it disappears as soon as the press is released.
  cardInspectService = new CardInspectService();

  fullscreenService = new FullscreenService();
  wireFullscreenService();

  // ── 3. Wire QueueScreen render updates ─────────────────────────────
  wireQueueScreen();

  // ── 4. Wire MatchHUD render updates ────────────────────────────────
  wireMatchHUD();

  // Pill history tooltip (hover / long-press, task 181).
  wirePillHistoryTooltip();

  // ── 5. Wire HandUI render updates ──────────────────────────────────
  wireHandUI();

  // ── 6. Wire BattlefieldUI render updates ───────────────────────────
  wireBattlefieldUI();

  // ── 7. Wire CombatBoardView render updates ─────────────────────────
  wireCombatBoardView();

  // ── 8. Wire PreparationTimerUI render updates ──────────────────────
  wirePreparationTimerUI();

  // ── 9. Wire CombatAnimationView render updates ─────────────────────
  wireCombatAnimationView();

  // ── 10. Wire CombatGoldDisplay render updates ──────────────────────
  wireCombatGoldDisplay();

  // ── 12. Wire SpectatorModeUI render updates ────────────────────────
  wireSpectatorModeUI();

  // ── 13. Wire EliminatedPlayerScreen render updates ─────────────────
  wireEliminatedPlayerScreen();

  // ── 14. Wire WinnerScreen render updates ───────────────────────────
  wireWinnerScreen();

  // ── 15. Wire ReconnectUI render updates ────────────────────────────
  wireReconnectUI();

  // ── 16. Wire UpgradeChoiceUI render updates ────────────────────────
  wireUpgradeChoiceUI();

  // ── 17. Listen for AppShell events (screen transitions) ────────────
  shell.onEvent((event: AppShellEvent) => {
    switch (event.type) {
      case 'connectionChange': {
        const data = event.data as { currentState: ConnectionState };
        _lastConnectionState = data.currentState;
        updateConnectionBanner(data.currentState);
        // Update the Play button based on connection state
        const connected = data.currentState === 'connected';
        const inQueue = queueScreen ? queueScreen.renderData.isInQueue : false;
        btnLeaveQueue.classList.toggle('hidden', !connected || !inQueue);
        // Play (the single primary entry, task 191): enabled when connected
        // and not queued; hidden while queued (Leave Queue is the control).
        btnPlay.disabled = !connected || inQueue;
        btnPlay.classList.toggle('hidden', inQueue);
        // While queued, every sub-menu is hidden (Leave Queue is the only
        // active control — one menu at a time).
        if (inQueue) hideAllPickers();
        // The mode picker is shown ONLY by the Join Ranked Queue click (the
        // showQueueModePicker flow) — a connection change must never reveal
        // it. While queued it must be hidden (one queue at a time — Leave
        // Queue is the control).
        if (inQueue) gameModePickerEl.classList.add('hidden');
        // Update queue status text
        if (connected && queueScreen) {
          queueStatusEl.textContent = queueScreen.renderData.statusMessage;
        }
        // Re-sync the server-side lobby after a shell restart (match over /
        // elimination). The client lost its lobby state when the shell was
        // rebuilt; if the party is still alive (POST_GAME) this brings the
        // lobby screen + the CrazyGames room back. If it is gone the server
        // rejects (harmless) and the state-driven room sync leftRooms.
        if (connected && _lastKnownLobbyId && shell) {
          shell.getLobbyState(_lastKnownLobbyId);
        }
        // The DEFERRED CrazyGames startup lobby action (the invited player's
        // join was stashed because the socket was not connected when
        // startApp fired it — see _pendingCgStartupLobby). This is the first
        // moment the request can actually reach the server.
        if (connected && _pendingCgStartupLobby && shell) {
          const pending = _pendingCgStartupLobby;
          _pendingCgStartupLobby = null;
          if (pending.kind === 'join') {
            shell.joinLobby(pending.code);
          } else {
            shell.createLobby({
              visibility: 'private',
              gameType: 'original',
              originalSize: 4,
              ranked: false,
            });
          }
        }
        break;
      }
      case 'screenChange': {
        const data = event.data as {
          previousScreen: string;
          currentScreen: 'landing' | 'queue' | 'match' | 'gameOver' | 'lobby' | 'createGame';
        };
        onScreenChange(data.previousScreen, data.currentScreen);
        break;
      }
      case 'stateChange': {
        onStateChange(event.data);
        break;
      }
      case 'authChange': {
        // Server confirmed our identity (playerId is the username). The UI
        // already displays the stored username — nothing to update here
        // (the runes are a purely client-side read-only reference now —
        // no server round-trip).
        break;
      }
      case 'authFailed': {
        // Server rejected the session token (logged out elsewhere or the
        // server restarted) — force the player back to the login screen.
        const data = event.data as { error: string };
        returnToAuthChoices(data.error);
        break;
      }
      case 'achievementsUpdate': {
        // Authoritative achievement list (sorted: unlocked first, then
        // closest to unlocking; hidden-unlocked redacted server-side).
        renderAchievements(event.data as AchievementInfo[]);
        achievementsOverlay.classList.remove('hidden');
        break;
      }
      case 'achievementUnlocked': {
        // Push notification the moment an achievement unlocks — toast, and
        // refresh the overlay if it is open (progress may have advanced).
        const data = event.data as AchievementUnlocked;
        showAchievementToast(data.icon, data.name);
        if (!achievementsOverlay.classList.contains('hidden')) {
          shell?.requestAchievements();
        }
        break;
      }
      case 'statsUpdate': {
        // Authoritative statistics for the logged-in user (always their
        // own — the server never returns another user's stats).
        renderStats(event.data as PlayerStats);
        statsOverlay.classList.remove('hidden');
        break;
      }
      case 'skinsUpdate': {
        // Authoritative skin catalogue with per-user unlocked/selected
        // state (served on open AND after each SetSkinRequest — the same
        // handler covers both, so the overlay always reflects the server).
        renderSkins(event.data as SkinInfo[]);
        skinsOverlay.classList.remove('hidden');
        break;
      }
      case 'toplistUpdate': {
        // Authoritative Top 100 + the requester's own row (null for guests).
        // Served to everyone — the overlay renders for both identities.
        const data = event.data as { entries: ToplistEntry[]; myPlacement: ToplistEntry | null };
        renderToplist(data.entries, data.myPlacement);
        toplistOverlay.classList.remove('hidden');
        break;
      }
    }
  });

  // ── 18. Start the application ──────────────────────────────────────
  // (Button listeners are wired exactly once in main().)
  shell.initialize();
}

// ─── Auth View / Identity Lifecycle ───────────────────────────────────────

/**
 * Renders the daily login streak panel (small info panel fixed to the
 * top-left of the landing page).
 *
 * Visible only for LOGGED-IN users whose server reported a streak — guests
 * (and an unreported streak) keep it hidden. The day count is the
 * server-authoritative consecutive-login-days value captured at
 * login/session resume; the frontend only formats it.
 *
 * The claim button shows the CYCLE progress as N/7 (1/7 on day 1, …, 7/7 at
 * the target — a streak beyond the target, i.e. the reward not claimed yet,
 * caps at 7/7) and enables once the streak reaches STREAK_REWARD_DAYS.
 * Pressing it claims server-side and the response (streak 1) re-renders it
 * back to 1/7 + disabled.
 */
function updateStreakPanel(): void {
  const visible = !!currentUsername && currentStreak !== null;
  streakPanelEl.classList.toggle('hidden', !visible);
  if (!visible) return;
  const streak = currentStreak as number;
  streakDaysEl.textContent = String(streak);
  // The x/7 counter (task 203 mobile corner row — icon + counter only):
  streakProgressEl.textContent = `${Math.min(streak, STREAK_REWARD_DAYS)}/${STREAK_REWARD_DAYS}`;
  const claimable = streak >= STREAK_REWARD_DAYS;
  streakStatusEl.classList.toggle('streak-claimable', claimable);
  btnClaimStreak.disabled = !claimable;
  const shown = Math.min(streak, STREAK_REWARD_DAYS);
  // Two labels, same authoritative N/7 progress: the full one for
  // desktop and the compact one for portrait mobile — the CSS
  // (.streak-claim-full/.streak-claim-short) toggles which is visible.
  btnClaimStreak.innerHTML =
    `<span class="streak-claim-full">Claim 7-Day Reward (${shown}/${STREAK_REWARD_DAYS})</span>` +
    `<span class="streak-claim-short">Claim (${shown}/${STREAK_REWARD_DAYS})</span>`;
}

/**
 * Show one of the landing screen sub-views:
 *  - 'choices': Login / Create Account / Play as Guest buttons
 *  - 'login' / 'register': the corresponding form
 *  - 'queue': queue info + Join/Leave buttons (after identity is selected)
 *
 * CrazyGames subpage: the login screen does not exist there — every mode
 * collapses to 'queue' (the platform's user is the identity).
 */
function showLandingView(mode: 'choices' | 'login' | 'register' | 'queue'): void {
  if (IS_CRAZYGAMES_MODE && mode !== 'queue') mode = 'queue';
  const isQueue = mode === 'queue';
  authViewEl.classList.toggle('hidden', isQueue);
  queueViewEl.classList.toggle('hidden', !isQueue);
  authChoicesEl.classList.toggle('hidden', mode === 'login' || mode === 'register');
  loginFormEl.classList.toggle('hidden', mode !== 'login');
  registerFormEl.classList.toggle('hidden', mode !== 'register');

  if (isQueue) {
    // Fresh queue view: the bot-fill countdown hint is hidden until the
    // next authoritative QueueStatusUpdate carries the countdown field.
    queueBotFillCountdownEl.classList.add('hidden');
    identityNameEl.textContent = displayUsername(currentUsername ?? 'Guest');
    // CrazyGames subpage: a real platform user has no "Log Out" (the
    // platform's sign-in IS the identity); a subpage GUEST keeps
    // "Switch Player" (it re-opens the guest/sign-in choices).
    btnLogout.classList.toggle('hidden', IS_CRAZYGAMES_MODE && !crazyGamesIdentityIsGuest);
    btnLogout.textContent = currentUsername ? 'Log Out' : 'Switch Player';
    // The CrazyGames guest/sign-in choices never show on the queue view.
    cgAuthViewEl.classList.add('hidden');
    // (Task 191: the legacy Co-op button is gone — co-op is reachable
    // through Play → Multiplayer → Create Game / Join Lobby.)
    // Achievements, stats and skins are logged-in-user features — hide for
    // guests (they always use default art, which needs no settings page).
    btnAchievements.classList.toggle('hidden', !currentUsername);
    btnStats.classList.toggle('hidden', !currentUsername);
    btnSkins.classList.toggle('hidden', !currentUsername);
    btnKeywordsQueue.classList.toggle('hidden', !currentUsername);
    // CREATE GAME (task 202): guests cannot host lobbies — the button is
    // grayed out + disabled, and its sub-line + title carry the hint.
    const isGuest = !currentUsername;
    btnPlayCreate.disabled = isGuest;
    btnPlayCreate.classList.toggle('guest-disabled', isGuest);
    btnPlayCreate.title = isGuest ? 'Only logged in users can host games' : '';
    const createSub = btnPlayCreate.querySelector('.play-option-sub');
    if (createSub) {
      createSub.textContent = isGuest ? 'Only logged in users can host games' : 'host a lobby';
    }
    // TASK 203: the queue-actions rows are gone (Achievements/Stats →
    // Progress, Skins → Play sub-menu) — the row-collapse loop is kept as
    // a harmless no-op for future rows. The guest rules above apply in
    // the pickers (id-based selectors).
    for (const row of document.querySelectorAll<HTMLElement>('#queue-actions .queue-actions-row')) {
      row.classList.toggle('hidden', !row.querySelector('.btn:not(.hidden)'));
    }
    // Daily login streak (top-left panel): logged-in users only, day count
    // rendered from the server-reported streak (see updateStreakPanel).
    updateStreakPanel();
    // Runes: the 🪬 Runes button is a READ-ONLY catalogue reference —
    // available to EVERYONE (guests included; there is no pre-match rune
    // selection anymore). The viewer renders from the shared
    // RUNE_DEFINITIONS catalogue (no server round-trip), so there is
    // nothing to request or re-evaluate here. Spells: the SAME convention
    // (the Spells button + viewer render from the shared
    // SPELL_DEFINITIONS catalogue — grouped by level, sorted by gold).
    btnRunes.classList.remove('hidden');
    btnSpells.classList.remove('hidden');
    // The viewers are closed each time the queue view is shown (a stale
    // open state would float over the fresh landing page).
    closeRuneViewer();
    closeSpellViewer();
  }
}

/**
 * Wire the landing page auth controls (login / create account / guest /
 * logout). Called once from main().
 */
function wireAuthView(): void {
  btnLogin.addEventListener('click', () => {
    loginErrorEl.textContent = '';
    showLandingView('login');
  });

  btnCreateAccount.addEventListener('click', () => {
    registerErrorEl.textContent = '';
    showLandingView('register');
  });

  btnPlayGuest.addEventListener('click', () => {
    // Guests keep the pre-existing behavior: generated ID, no account
    startApp(getPlayerId(), null, null);
  });

  btnBackFromLogin.addEventListener('click', () => showLandingView('choices'));
  btnBackFromRegister.addEventListener('click', () => showLandingView('choices'));

  loginFormEl.addEventListener('submit', async (e) => {
    e.preventDefault();
    loginErrorEl.textContent = '';
    const username = loginUsernameEl.value.trim();
    const password = loginPasswordEl.value;
    if (!username || !password || !authService) return;
    const result = await authService.login(username, password);
    if (result.success && result.username && result.token) {
      currentStreak = typeof result.streak === 'number' ? result.streak : null;
      startApp(result.username, result.token, result.username);
    } else {
      loginErrorEl.textContent = result.error ?? 'Login failed.';
    }
  });

  registerFormEl.addEventListener('submit', async (e) => {
    e.preventDefault();
    registerErrorEl.textContent = '';
    const username = registerUsernameEl.value.trim();
    const password = registerPasswordEl.value;
    const confirm = registerPasswordConfirmEl.value;
    if (!username || !password || !authService) return;
    if (password !== confirm) {
      registerErrorEl.textContent = 'Passwords do not match.';
      return;
    }
    const result = await authService.register(username, password);
    if (result.success && result.username && result.token) {
      currentStreak = typeof result.streak === 'number' ? result.streak : null;
      startApp(result.username, result.token, result.username);
    } else {
      registerErrorEl.textContent = result.error ?? 'Could not create account.';
    }
  });

  btnLogout.addEventListener('click', () => {
    returnToAuthChoices();
  });
}

/**
 * Resume a stored session if present (page reload). A stored token that no
 * longer validates server-side (e.g., server restart) is discarded.
 */
async function resumeSavedSession(): Promise<void> {
  const stored = authService?.getStoredSession();
  if (!stored || !authService) return;
  const verified = await authService.verifySession(stored.token);
  if (verified) {
    // Session resume advanced the streak server-side (idempotent per day).
    currentStreak = typeof verified.streak === 'number' ? verified.streak : null;
    startApp(verified.username, stored.token, verified.username);
  } else {
    clearSession();
  }
}

/**
 * Start (or restart) the game shell for the given identity.
 * - Guest: playerId is a generated guest ID, no token.
 * - Logged-in: playerId IS the username, token authenticates the socket.
 */
function startApp(playerId: string, token: string | null, username: string | null): void {
  currentPlayerId = playerId;
  currentToken = token;
  currentUsername = username;
  initShellAndComponents();
  showLandingView('queue');
  showScreen('landing');
  // Identity-dependent chrome: the QA login button (CrazyGames subpage only)
  // and the status-dot visibility (hidden for CG users + guests).
  // QA "Login with CrazyGames" button — HIDDEN by design (login works via
  // the silent auto-login / platform sign-in). The button + wiring are
  // KEPT (and only ever hidden) so QA can re-enable it by removing the
  // class — see the markup comment in index.html.
  btnCgQaLogin.classList.add('hidden');
  updateConnectionBanner(_lastConnectionState);
  // CrazyGames subpage: set up the multiplayer platform adapter + run the
  // platform startup (invite params / instant multiplayer). Standalone is a
  // no-op (the platform abstraction handles both worlds). Works for
  // SIGNED-IN users AND GUESTS.
  if (IS_CRAZYGAMES_MODE) {
    // The startup lobby (invite params / instant multiplayer) reads the
    // platform's `game` module — it must run AFTER the platform is
    // installed (initCrazyGamesPlatform is async: it awaits SDK init).
    void initCrazyGamesPlatform().then(() => handleCrazyGamesStartupLobby());
  }
}

// ─── CrazyGames multiplayer (platform adapter — CrazyGames only) ──────────
// The SAME game/matchmaking/database/Elo runs on the platform; only the
// lobby-join affordances differ (Invite button + join listener vs the code
// input). These helpers are NO-OPS for standalone (IS_CRAZYGAMES_MODE).

let _cgPlatformReady = false;
let _cgRemoveJoinRoomListener: (() => void) | null = null;
let _cgRemoveAuthListener: (() => void) | null = null;
// One login at a time: the platform RATE-LIMITS repeated auth calls ("Too
// many login requests"). A guest sign-in fires BOTH the QA button handler
// and the SDK auth listener — the flag makes the listener skip when the
// button is already running the login (and vice versa).
let _cgLoginInFlight = false;

/**
 * Set up the CrazyGames platform adapter (idempotent — one set of SDK
 * listeners per page load, never re-registered on rerenders). Called once
 * the shell exists (startApp). Works for SIGNED-IN users AND GUESTS — the
 * room layer is independent of platform sign-in.
 *
 * ASYNC: it first ensures the SDK script is loaded + `init()` has run
 * (the `game` module only exists after init). The stored-session fast path
 * can reach startApp before any SDK touch, so we must not assume init.
 */
async function initCrazyGamesPlatform(): Promise<void> {
  if (!IS_CRAZYGAMES_MODE || _cgPlatformReady) return;
  _cgPlatformReady = true;
  const service = (crazyGamesService ??= new CrazyGamesService());
  try {
    await service.ensureSdkInitialized();
  } catch (err) {
    console.warn('[CrazyGames] SDK init failed — multiplayer invites are disabled', err);
  }
  // Install the CrazyGames adapter WITH the SDK `game` module. Without it
  // createPlatform() would fall back to the standalone adapter and
  // updateRoom would NEVER be called (the platform's Invite UI would stay
  // on "Find a multiplayer mode").
  const game = getGlobalCrazyGamesGame();
  setPlatform(createPlatform({ game: game ?? undefined }));
  // NOTE: `environment` is a TOP-LEVEL SDK property (SDK.environment), not
  // a `game`-module property — reading game.environment logs undefined.
  console.log('[CrazyGames] environment:', getGlobalCrazyGames()?.environment);
  if (!game) {
    console.warn('[CrazyGames] SDK `game` module unavailable — invites are disabled (check SDK init / platform detection)');
  }
  // A friend accepts the Invite link WHILE we are already in the game:
  // the SDK fires the join listener with the invite params (lobby code).
  _cgRemoveJoinRoomListener = getPlatform().onJoinRoom((data) => {
    const code = data.lobbyCode;
    if (typeof code !== 'string' || code.length === 0) return;
    const st = shell?.gameState;
    // Join only when free (not already in a lobby or a match).
    if (st && st.lobby === null && !st.match.matchId) {
      shell?.joinLobby(code);
    }
  });
  // A GUEST signs in on the platform WHILE the game is running: switch
  // the current identity to that account (fresh token → server-verified).
  void service.addAuthListener(async (user) => {
    console.log('[CG] User authenticated:', user?.username);
    // Already an account user (signed in at startup) → nothing to switch.
    if (!user || currentUsername) return;
    // The startup flow owns pre-game logins (no shell yet).
    if (!shell) return;
    // The QA / sign-in button is already running THIS login — do NOT run a
    // second one (the platform rate-limits repeated auth calls).
    if (_cgLoginInFlight) return;
    try {
      const token = await service.getUserToken();
      const result = await authService?.crazyGamesLogin(token);
      if (result?.success && result.username && result.token) {
        console.log('[CG] Mid-game platform login → switching identity to', result.username);
        currentStreak = typeof result.streak === 'number' ? result.streak : null;
        crazyGamesIdentityIsGuest = false;
        startApp(result.username, result.token, result.username);
      }
    } catch (err) {
      console.error('[CG] Auth listener login failed', err);
    }
  }).then((cleanup) => {
    _cgRemoveAuthListener = cleanup;
  });
}

/**
 * Log in with a CrazyGames ACCOUNT (the QA "Login with CrazyGames" button):
 *  1. `getUser()` first — a visitor ALREADY signed in on the platform makes
 *     `showAuthPrompt()` REJECT with `userAlreadySignedIn`, so the prompt
 *     is only shown to visitors who are not signed in;
 *  2. a fresh `getUserToken()` (the server verifies the JWT — the platform
 *     says the token's userId is the link key to your own backend);
 *  3. `crazyGamesLogin(token)` → the player becomes a real (saved-progress)
 *     user. The `_cgLoginInFlight` guard keeps the auth listener from
 *     running a parallel login (double auth calls → platform rate limit).
 */
async function loginWithCrazyGamesAccount(): Promise<void> {
  if (!authService || _cgLoginInFlight) return;
  _cgLoginInFlight = true;
  const service = (crazyGamesService ??= new CrazyGamesService());
  try {
    let user: import('./frontend/services/CrazyGamesService').CrazyGamesUser | null = null;
    try {
      user = await service.getUser(); // silent — no prompt
    } catch {
      /* SDK hiccup → try the prompt below */
    }
    if (!user) {
      user = await service.showAuthPrompt(); // the official sign-in popup
    }
    if (!user) {
      console.log('[CG] Login cancelled (no user).');
      return;
    }
    console.log('[CG] Logged in:', user);
    const token = await service.getUserToken();
    const result = await authService.crazyGamesLogin(token);
    if (result.success && result.username && result.token) {
      currentStreak = typeof result.streak === 'number' ? result.streak : null;
      crazyGamesIdentityIsGuest = false;
      startApp(result.username, result.token, result.username);
    } else {
      console.error('[CG] Login failed (server):', result.error);
      const expMs = crazyGamesTokenExpiresAtMs(token);
      if (expMs !== null && expMs < Date.now()) {
        console.error(
          `[CG] The platform token is EXPIRED (${Math.round((Date.now() - expMs) / 1000)}s ago — the SDK served a stale cached token). RELOAD the page to mint a fresh one.`
        );
      }
    }
  } catch (err) {
    console.error('[CG] Login failed:', err);
  } finally {
    _cgLoginInFlight = false;
  }
}

// ─── CrazyGames room sync (the SINGLE authoritative path) ────────────────────
// Driven by AUTHORITATIVE state updates only (the server-confirmed lobby /
// coop / match state) — never by component mount/unmount. The room follows
// the BattleBrew lobby lifecycle:
//   lobby present   → updateRoom(joinability computed from the server state)
//   match running   → updateRoom(isJoinable:false) — NO leftRoom (the
//                     party survives the game: WAITING→COUNTDOWN→IN_GAME→
//                     POST_GAME→WAITING is ONE room)
//   nothing left    → leftRoom (left / kicked / host closed / destroyed)
let _cgMatchNonJoinableSent = false;

function cgSyncRoomState(state: {
  lobby: { id: string; code: string; state: string; players: unknown[]; playerCapacity: number; exists: boolean } | null;
  coopLobby: { lobbyCode: string; memberId: string | null; exists: boolean } | null;
  match: { matchId: string | null };
}): void {
  if (!IS_CRAZYGAMES_MODE) return;
  // The state → action decision lives in CrazyGamesRoom (pure, unit-tested);
  // this is the single place that acts on it (state-driven, never
  // component-driven).
  const action = crazyGamesRoomActionForState(
    state.lobby,
    state.coopLobby,
    !!state.match?.matchId
  );
  if (action.kind === 'sync') {
    // In (or back in) a joinable lobby — report the room with the
    // server-computed joinability (also resumes the room after a match).
    _cgMatchNonJoinableSent = false;
    crazyGamesSyncRoom(action.room);
    return;
  }
  if (action.kind === 'leave') {
    // Gameplay running (or the group is gone): LEAVE the room (leftRoom) —
    // there is no way to join an ongoing game, and isJoinable:false alone
    // does not deactivate the platform's Invite Friends button. The party
    // survives: the server-side lobby lives on (IN_GAME → POST_GAME) and the
    // next authoritative lobby state re-registers the room (the invite
    // button returns for the next round). Sent once per match (the
    // high-frequency state updates never spam the SDK; the leave is
    // idempotent anyway).
    if (!_cgMatchNonJoinableSent) {
      _cgMatchNonJoinableSent = true;
      crazyGamesLeaveRoom();
    }
    return;
  }
  // 'none': no room context — leave is a no-op safety (nothing tracked).
  crazyGamesLeaveRoom();
}

/**
 * The platform STARTUP lobby (once per page load, after sign-in/guest):
 *  - an Invite link with lobby code params → join that lobby + clear the
 *    one-time URL params (the server re-persists the code in the path).
 *  - instant multiplayer (the host started the room from the platform)
 *    → create a private 4-player original lobby.
 */
async function handleCrazyGamesStartupLobby(): Promise<void> {
  if (!IS_CRAZYGAMES_MODE || _cgStartupConsumed) return;
  // The boot path can run BEFORE the login flow's detection (and before
  // the platform delivered the launch context) — do our own (longer) poll
  // so a late inviteParams/isInstantMultiplayer is still caught here.
  if (!_cgStartupIntent) {
    _cgStartupIntent = await detectCrazyGamesStartupIntent(cgStartupPollMs());
  }
  const intent = _cgStartupIntent;
  if (!intent.inviteCode && !intent.instant) return; // normal launch
  if (!shell) {
    // The app has not started yet — KEEP the intent; the login flow
    // consumes it right after startApp (the join must not be lost).
    return;
  }
  _cgStartupIntent = null; // consumed (single join, no double-fire)
  _cgStartupConsumed = true; // the SDK keeps the params — block re-detection
  const platform = getPlatform();
  if (intent.inviteCode) {
    if (shell.connectionState === 'connected') {
      shell.joinLobby(intent.inviteCode);
    } else {
      // The socket is still handshaking — a JoinLobbyRequest sent NOW is
      // silently dropped (ConnectionService.send returns false). Defer to
      // the next connectionChange (see the 'connectionChange' handler).
      _pendingCgStartupLobby = { kind: 'join', code: intent.inviteCode };
    }
    platform.clearInviteParams();
    return;
  }
  if (shell.connectionState === 'connected') {
    shell.createLobby({
      visibility: 'private',
      gameType: 'original',
      originalSize: 4,
      ranked: false,
    });
  } else {
    _pendingCgStartupLobby = { kind: 'create' };
  }
  platform.clearInstantMultiplayer();
}

/**
 * Tell the platform we permanently left the room (logout / menu return).
 * Routed through the CrazyGamesRoom controller (the single authoritative
 * path — it resets the tracked room, so a later state-driven sync cannot
 * double-report).
 */
function notifyCrazyGamesLeftRoom(): void {
  if (!IS_CRAZYGAMES_MODE) return;
  crazyGamesLeaveRoom();
}

/**
 * Tear everything down and return to the Login / Create Account / Guest
 * screen. Used by the Log Out button and by the authFailed event (the
 * server rejected our session token).
 *
 * CrazyGames subpage: there is no login screen to return to — a failed
 * auth re-runs the CrazyGames sign-in flow (a fresh token fixes a stale
 * session; the platform re-signs-in when needed).
 */
function returnToAuthChoices(message?: string): void {
  cancelEliminationAutoDisconnect();

  if (IS_CRAZYGAMES_MODE) {
    notifyCrazyGamesLeftRoom(); // returning to the platform choices = room left
    if (shell) {
      shell.shutdown();
      shell = null;
    }
    currentPlayerId = null;
    currentToken = null;
    currentUsername = null;
    currentStreak = null;
    crazyGamesIdentityIsGuest = false;
    resetRuneState();
    hideMatchComponents();
    startCrazyGamesLanding();
    return;
  }

  // Best-effort server-side session invalidation
  if (currentToken && authService) {
    void authService.logout(currentToken);
  }

  // CrazyGames: returning to the (platform) choices permanently leaves the room.
  notifyCrazyGamesLeftRoom();

  if (shell) {
    shell.shutdown();
    shell = null;
  }

  currentPlayerId = null;
  currentToken = null;
  currentUsername = null;
  currentStreak = null;
  resetRuneState();

  hideMatchComponents();
  showScreen('landing');
  showLandingView(message ? 'login' : 'choices');
  if (message) {
    loginErrorEl.textContent = message;
  }
}

// ─── CrazyGames subpage (/crazygames) ─────────────────────────────────────
// The platform's user IS the identity: no login screen. Flow — a stored
// session (fast reload path) → otherwise the CrazyGames SDK (init → the
// platform's sign-in prompt when needed → a fresh user token) →
// POST /api/auth/crazygames, where the server VERIFIES the token (RS256
// public key + gameId + exp) and maps it to a REAL user account (created
// on first sight) → startApp with the returned session. The player is then
// a fully logged-in user: skins, stats, achievements, toplist, streak and
// ELO all work. The SDK's client-side `__dangerousUserId` is never sent —
// only the server-verifiable token is the credential.

/** Show the CrazyGames landing (queue view, "connecting" identity) and start the sign-in. */
function startCrazyGamesLanding(): void {
  showScreen('landing');
  showLandingView('queue');
  identityNameEl.textContent = 'Connecting…';
  // The platform frame owns that chrome — no status dot for CG visitors.
  connectionBanner.style.display = 'none';
  // CrazyGames handles fullscreen + device orientation in its own
  // environment: the fullscreen button and the rotate-device hint are
  // NEVER shown on the subpage (regular users keep both — see
  // beginGameOrientationEnforcement / updateRotateHintVisibility).
  btnFullscreen.classList.add('hidden');
  void crazyGamesLoginFlow();
}

/**
 * The one-time CrazyGames startup intent — WHO THIS PAGE LOAD IS FOR.
 * Set once by the detector (shared by the login flow and the boot-time
 * lobby handler) and consumed by handleCrazyGamesStartupLobby.
 */
let _cgStartupIntent: { inviteCode: string | null; instant: boolean } | null = null;

/**
 * One-time guard: once the startup intent has been CONSUMED (join/create
 * executed — or deferred to the next connection), the launcher context in
 * the SDK (`game.inviteParams` / `isInstantMultiplayer`) is NOT cleared
 * platform-side, so the boot-time handler would re-detect it and fire a
 * SECOND join ("already in a lobby" toast). This flag makes the join
 * happen exactly once per page load.
 */
let _cgStartupConsumed = false;

/**
 * A startup lobby action that was DEFERRED because the socket was not
 * connected when startApp fired it (ConnectionService.send() silently drops
 * pre-connection messages — the invited-player "lands on the landing page"
 * bug: the join request for the invited lobby was sent before the handshake
 * finished and vanished). Consumed by the next connectionChange event.
 */
let _pendingCgStartupLobby: { kind: 'join'; code: string } | { kind: 'create' } | null = null;

/**
 * Poll the SDK `game` module for the launch context (inviteParams /
 * isInstantMultiplayer). The platform delivers it ASYNCHRONOUSLY after
 * init() in the invite-link / friend-list flows — a single read right
 * after init misses it (the fresh-load "Join game" case that used to
 * strand the visitor on the landing page). Poll until found or the
 * deadline elapses (normal loads simply hit the deadline fast — 100 ms
 * ticks, no per-tick cost).
 */
/**
 * The poll deadline for the launch context (ms). Tests may set the global
 * seam `__CG_STARTUP_POLL_MS = 0` for a single synchronous check (their
 * microtask flushes cannot wait on real-time poll ticks).
 */
function cgStartupPollMs(): number {
  const g = globalThis as { __CG_STARTUP_POLL_MS?: unknown };
  return typeof g.__CG_STARTUP_POLL_MS === 'number' ? (g.__CG_STARTUP_POLL_MS as number) : 1500;
}

async function detectCrazyGamesStartupIntent(
  deadlineMs: number
): Promise<{ inviteCode: string | null; instant: boolean }> {
  const t0 = Date.now();
  for (;;) {
    const game = getGlobalCrazyGamesGame();
    const raw = game?.inviteParams?.lobbyCode;
    const intent = {
      inviteCode: typeof raw === 'string' && raw.length > 0 ? raw : null,
      instant: game?.isInstantMultiplayer === true,
    };
    if (intent.inviteCode || intent.instant) {
      console.log(
        `[CrazyGames] startup intent detected after ${Date.now() - t0} ms:`,
        intent
      );
      return intent;
    }
    if (Date.now() - t0 >= deadlineMs) return intent;
    await new Promise((r) => setTimeout(r, 100));
  }
}

/**
 * The one-time CrazyGames startup flags (read from the SDK `game` module —
 * requires SDK init). `inviteCode`: the visitor was INVITED into a lobby
 * (invite link / the platform's native invite UI); `instant`: launched via
 * Instant Multiplayer. Both mean "get this player into the game NOW".
 * The detected intent is STORED so the lobby join survives the race with
 * the boot-time handler.
 */
async function crazyGamesStartupFlags(): Promise<{ inviteCode: string | null; instant: boolean }> {
  const service = (crazyGamesService ??= new CrazyGamesService());
  try {
    await service.ensureSdkInitialized();
  } catch {
    return { inviteCode: null, instant: false }; // off-platform / SDK blocked
  }
  const intent = await detectCrazyGamesStartupIntent(cgStartupPollMs());
  _cgStartupIntent = intent;
  return intent;
}

/**
 * The CrazyGames sign-in flow (see the block comment above):
 *  - INVITED / instant-multiplayer player → straight into the game: silent
 *    auto-login when the platform visitor is signed in, otherwise a GUEST
 *    seat (NEVER the choices screen — the lobby join happens in
 *    handleCrazyGamesStartupLobby inside startApp)
 *  - a stored session (reload) → silent start
 *  - already signed in on the platform → SILENT auto-login (no prompt,
 *    no choices — the visitor is never bothered)
 *  - NOT signed in → the guest/sign-in CHOICES (no automatic popup):
 *    "Play as Guest" / "Sign in to save progress"
 *  - SDK unavailable (off-platform) → the choices with a note (guest works)
 */
async function crazyGamesLoginFlow(): Promise<void> {
  if (!authService) return;
  const service = (crazyGamesService ??= new CrazyGamesService());

  // 0. INVITED / INSTANT player: never show the login/choices page. Auto-
  // login when the platform visitor is signed in; otherwise go straight to
  // a guest seat (no prompt). The lobby itself is joined right after
  // (startApp → handleCrazyGamesStartupLobby → joinLobby(code)).
  const startup = await crazyGamesStartupFlags();
  if (startup.inviteCode || startup.instant) {
    try {
      const user = await service.getUser(); // silent — NO prompt
      if (user) {
        let token: string | null = null;
        try {
          token = await service.getUserToken();
        } catch (tokenErr) {
          if (!isCrazyGamesUserNotAuthenticated(tokenErr)) throw tokenErr;
          token = null; // unauthenticated → guest fallback
        }
        if (token) {
          const result = await authService.crazyGamesLogin(token);
          if (result.success && result.username && result.token) {
            currentStreak = typeof result.streak === 'number' ? result.streak : null;
            startApp(result.username, result.token, result.username);
            void handleCrazyGamesStartupLobby(); // join the invited lobby
            return;
          }
        }
      }
    } catch {
      /* SDK hiccup → guest fallback (the invite must not be lost) */
    }
    crazyGamesIdentityIsGuest = true;
    startApp(getPlayerId(), null, null);
    void handleCrazyGamesStartupLobby(); // join the invited lobby
    return;
  }

  // 1. Fast path: a session from a previous load (the server did not
  // restart since) — no SDK token round-trip needed.
  const stored = authService.getStoredSession();
  if (stored) {
    const verified = await authService.verifySession(stored.token);
    if (verified) {
      currentStreak = typeof verified.streak === 'number' ? verified.streak : null;
      startApp(verified.username, stored.token, verified.username);
      return;
    }
    clearSession();
  }

  // 2. Is the visitor already signed in on the platform?
  try {
    const user = await service.getUser(); // init + getUser — NO prompt
    if (user) {
      // Silent auto-login: fresh token → the server's verified account.
      // (The CrazyGames-recommended pattern: start from getUserToken() and
      // treat userNotAuthenticated as the guest path — if the token call
      // reports the visitor as unauthenticated, fall back to the choices.)
      let token: string;
      try {
        token = await service.getUserToken();
      } catch (tokenErr) {
        if (isCrazyGamesUserNotAuthenticated(tokenErr)) {
          showCgAuthChoices();
          return;
        }
        throw tokenErr;
      }
      const result = await authService.crazyGamesLogin(token);
      if (result.success && result.username && result.token) {
        currentStreak = typeof result.streak === 'number' ? result.streak : null;
        startApp(result.username, result.token, result.username);
      } else {
        console.error('CrazyGames sign-in failed:', result.error);
        showCgAuthChoices(result.error ?? 'Sign-in failed — try again.');
      }
      return;
    }
    // Not signed in → the visitor chooses (no automatic popup).
    showCgAuthChoices();
  } catch (err) {
    // No SDK / accounts unavailable (off-platform): guest stays playable.
    console.error('CrazyGames sign-in unavailable:', err);
    showCgAuthChoices('CrazyGames sign-in is unavailable — play as guest.');
  }
}

/**
 * Show the CrazyGames guest/sign-in choices (the /crazygames landing for
 * visitors who are NOT signed in on the platform). Everything else on the
 * landing (title, tagline, tutorial) stays visible.
 */
function showCgAuthChoices(note?: string): void {
  showScreen('landing');
  authViewEl.classList.add('hidden');
  queueViewEl.classList.add('hidden');
  cgAuthViewEl.classList.remove('hidden');
  cgAuthNoteEl.textContent = note ?? '';
  btnCgSignin.disabled = false;
}

/** Wire the CrazyGames subpage's guest / sign-in buttons (called once from main()). */
function wireCrazyGamesAuth(): void {
  // "Play as Guest" — a normal guest seat (generated id, no account, no
  // progress). "Switch Player" on the identity line re-opens these choices.
  btnCgGuest.addEventListener('click', () => {
    crazyGamesIdentityIsGuest = true;
    startApp(getPlayerId(), null, null);
  });

  // "Sign in to save progress" — the platform's OWN account popup
  // (showAuthPrompt); on success a fresh token is verified by the server
  // and the player becomes a real (saved-progress) user.
  btnCgSignin.addEventListener('click', async () => {
    if (!authService || btnCgSignin.disabled) return;
    btnCgSignin.disabled = true;
    cgAuthNoteEl.textContent = 'Opening the CrazyGames sign-in…';
    const service = (crazyGamesService ??= new CrazyGamesService());
    try {
      const { token } = await service.getLoginToken();
      const result = await authService.crazyGamesLogin(token);
      if (result.success && result.username && result.token) {
        currentStreak = typeof result.streak === 'number' ? result.streak : null;
        startApp(result.username, result.token, result.username);
      } else {
        cgAuthNoteEl.textContent = result.error ?? 'Sign-in failed — try again.';
        btnCgSignin.disabled = false;
      }
    } catch (err) {
      cgAuthNoteEl.textContent =
        err instanceof Error && /cancelled/.test(err.message)
          ? 'Sign-in cancelled.'
          : 'Sign-in failed — try again.';
      btnCgSignin.disabled = false;
    }
  });

  // TEMPORARY (QA builds): "Login with CrazyGames" — getUser() first (the
  // prompt is only shown when NOT already signed in on the platform), then
  // a fresh getUserToken() that the server verifies (JWT). Lets QA confirm
  // a real logged-in user end-to-end. Shown on the CG subpage only.
  btnCgQaLogin.addEventListener('click', () => {
    if (!authService || btnCgQaLogin.disabled) return;
    btnCgQaLogin.disabled = true;
    void loginWithCrazyGamesAccount().finally(() => {
      btnCgQaLogin.disabled = false;
    });
  });
}

/** True when the SDK reports the visitor is not signed in (guest path). */
function isCrazyGamesUserNotAuthenticated(err: unknown): boolean {
  const e = err as { code?: string; name?: string } | null;
  return e?.code === 'userNotAuthenticated' || e?.name === 'userNotAuthenticated';
}

/**
 * Display-only: hide the internal `cg_` prefix on CrazyGames usernames
 * (the server keeps the prefixed name for uniqueness — the GUI never
 * shows it). A standalone user who literally chose a `cg_` name loses the
 * prefix in display too (accepted: display-only, data is untouched).
 */
function displayUsername(name: string): string {
  return name.startsWith('cg_') ? name.slice('cg_'.length) : name;
}

/**
 * Decode the token's `exp` (seconds) WITHOUT verifying it (diagnostics only).
 * Returns null when undecodable. A server 401 on an already-expired token
 * means the SDK served a stale CACHED token — a page reload mints a fresh
 * one (the only reliable recovery the SDK exposes).
 */
function crazyGamesTokenExpiresAtMs(token: string): number | null {
  try {
    const payloadB64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(payloadB64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const payload = JSON.parse(new TextDecoder().decode(bytes)) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Restart the shell (after game over / elimination) while keeping the
 * current identity — logged-in users must keep their username, guests
 * keep the same guest ID.
 */
function restartShellKeepingIdentity(): void {
  if (!currentPlayerId) return;
  // NOTE (CrazyGames): the match is over but we did NOT leave the
  // platform's room — the lobby party survives the game (POST_GAME →
  // Play Again). The room stays registered (non-joinable during the game,
  // resynced when the group returns to the lobby); leftRoom fires only on
  // a real group exit (the state-driven cgSyncRoomState path).
  if (shell) {
    shell.shutdown();
    shell = null;
  }
  initShellAndComponents();
  showLandingView('queue');
  showScreen('landing');
}

// ─── COOP lobby screen (task 163) ────────────────────────────────────────
// The lobby renders PURELY from the authoritative CoopLobbyStateUpdate
// (mirrored in the game state as coopLobby): the invite code centered top,
// the mission briefing in the middle (the shared CoopMissions spec), and
// Leave + Start (host-only) at the bottom. When the lobby is gone
// (host left / match started) the server sends exists:false → the state
// clears → the screen flips back to the menu (AppShell screen logic).
// (Task 191: the legacy Co-op button + its 5 s post-lobby cooldown are
// gone with the button — co-op now lives under Play → Multiplayer.)

let _coopLobbyWasPresent = false;

function renderCoopLobby(): void {
  const lobby = shell?.gameState.coopLobby;
  if (!lobby) {
    // No lobby (shouldn't be on this screen then) — blank it safely.
    coopLobbyCodeEl.textContent = '';
    coopLobbyMissionNameEl.textContent = '';
    coopLobbyMissionDescEl.textContent = '';
    return;
  }

  // The identity line (the viewer's name).
  coopLobbyIdentityEl.textContent = displayUsername(currentUsername ?? currentPlayerId ?? 'Guest');

  // The invite code (centered top; click to copy).
  coopLobbyCodeEl.textContent = lobby.lobbyCode;
  coopLobbyCodeHintEl.classList.add('hidden');

  // The seats (both names once the teammate joins).
  coopLobbyHostNameEl.textContent = displayUsername(lobby.hostName || lobby.hostId);
  coopLobbyMemberNameEl.textContent = lobby.memberName
    ? displayUsername(lobby.memberName)
    : 'Waiting…';

  // The mission briefing (shared spec — the single source of truth).
  const mission = getCoopMissionById(lobby.missionId);
  coopLobbyMissionNameEl.textContent = mission ? `Mission ${mission.number}: ${mission.name}` : lobby.missionId;
  coopLobbyMissionDescEl.textContent = mission?.description ?? '';

  // The mission dropdown (host re-picks; the joiner sees a read-only
  // mirror of the host's pick — every state update keeps it in sync).
  renderCoopMissionSelect(lobby);

  // The combat-side picker (host chooses left/right; the teammate
  // automatically gets the other side).
  renderCoopSidePicker(lobby);

  // The Start button (HOST-ONLY): disabled + "Waiting for teammate…" while
  // the seat is empty; enabled + "Start!" once a teammate has joined.
  const isHost = lobby.viewerRole === 'host';
  btnCoopLobbyStart.classList.toggle('hidden', !isHost);
  if (isHost) {
    const full = lobby.memberId !== null;
    btnCoopLobbyStart.disabled = !full;
    btnCoopLobbyStart.textContent = full ? 'Start!' : 'Waiting for teammate…';
  }
}

function renderCoopMissionSelect(lobby: CoopLobbyClientState): void {
  const isHost = lobby.viewerRole === 'host';
  coopLobbyMissionSelectEl.innerHTML = '';
  for (const missionId of lobby.availableMissionIds) {
    const mission = getCoopMissionById(missionId);
    if (!mission) continue;
    const option = document.createElement('option');
    option.value = missionId;
    // The host's NEXT mission to complete is marked clearly in the label
    // (null when everything is cleared — every mission is a replay).
    option.textContent = missionId === lobby.nextMissionId
      ? `M${mission.number}: ${mission.name} — ⚑ NEXT TO COMPLETE`
      : `M${mission.number}: ${mission.name}`;
    option.dataset.next = missionId === lobby.nextMissionId ? 'true' : 'false';
    option.selected = missionId === lobby.missionId;
    coopLobbyMissionSelectEl.appendChild(option);
  }
  coopLobbyMissionSelectEl.disabled = !isHost;
  coopLobbyMissionSelectRowEl.classList.toggle('hidden', lobby.availableMissionIds.length === 0);
}

function renderCoopSidePicker(lobby: CoopLobbyClientState): void {
  const isHost = lobby.viewerRole === 'host';
  coopLobbySideRowEl.classList.remove('hidden');
  coopSideLeftEl.disabled = !isHost;
  coopSideRightEl.disabled = !isHost;
  coopSideLeftEl.classList.toggle('selected', lobby.hostSide === 'left');
  coopSideRightEl.classList.toggle('selected', lobby.hostSide === 'right');
  // The joiner sees whose side is whose (the host picks; they get the
  // other side — their minions' row half + who attacks first).
  if (isHost) {
    coopLobbySideNoteEl.classList.add('hidden');
  } else {
    coopLobbySideNoteEl.classList.remove('hidden');
    coopLobbySideNoteEl.textContent = lobby.hostSide === 'left'
      ? `${lobby.hostName || lobby.hostId} plays LEFT — you play RIGHT`
      : `${lobby.hostName || lobby.hostId} plays RIGHT — you play LEFT`;
  }
}

function wireCoopLobbyUI(): void {
  // Click the code to copy it (the invite flow).
  coopLobbyCodeEl.addEventListener('click', () => {
    const code = coopLobbyCodeEl.textContent ?? '';
    if (!code) return;
    void copyToClipboard(code).then((ok) => {
      if (ok) {
        coopLobbyCodeHintEl.classList.remove('hidden');
        setTimeout(() => coopLobbyCodeHintEl.classList.add('hidden'), 1500);
      }
    });
  });

  // Leave: sends LeaveCoopLobbyRequest; the authoritative state update
  // (exists:false) drives the return to the menu + the 5 s cooldown.
  btnCoopLobbyLeave.addEventListener('click', () => {
    shell?.leaveCoopLobby();
  });

  // Mission dropdown (host only): sends SelectCoopMissionRequest; the
  // authoritative state update resyncs BOTH clients (the joiner's
  // briefing + dropdown mirror the host's pick).
  coopLobbyMissionSelectEl.addEventListener('change', () => {
    const lobby = shell?.gameState.coopLobby;
    if (!lobby || lobby.viewerRole !== 'host') return;
    shell?.selectCoopMission(lobby.lobbyCode, coopLobbyMissionSelectEl.value);
  });

  // Combat-side picker (host only): sends SelectCoopSideRequest; the
  // authoritative state update resyncs both clients (the teammate gets
  // the other side).
  coopSideLeftEl.addEventListener('click', () => {
    const lobby = shell?.gameState.coopLobby;
    if (!lobby || lobby.viewerRole !== 'host' || lobby.hostSide === 'left') return;
    shell?.selectCoopSide(lobby.lobbyCode, 'left');
  });
  coopSideRightEl.addEventListener('click', () => {
    const lobby = shell?.gameState.coopLobby;
    if (!lobby || lobby.viewerRole !== 'host' || lobby.hostSide === 'right') return;
    shell?.selectCoopSide(lobby.lobbyCode, 'right');
  });

  // Start (host only): sends StartCoopLobbyRequest; the server creates the
  // match and both clients receive MatchStarted (the lobby is consumed).
  btnCoopLobbyStart.addEventListener('click', () => {
    const lobby = shell?.gameState.coopLobby;
    if (!lobby || lobby.viewerRole !== 'host' || lobby.memberId === null) return;
    shell?.startCoopLobby(lobby.lobbyCode);
  });
}

// ─── Unified lobby UI (Play menu: Create Game + Original lobby screen) ──

/** The Create Game screen's current (client-side, mirror) settings. */
const createGameSettings = {
  name: '',
  visibility: 'private' as 'public' | 'private',
  gameType: 'original' as 'original' | 'coop',
  originalSize: 4 as 4 | 8,
  ranked: false,
  /** Task 209: true = the size-based default (6 long / 5 short). */
  lifeDefault: true,
};

/** Read the active value from a .cg-choice-row group. */
function cgChoiceValue(group: HTMLElement): string | null {
  return group.querySelector<HTMLElement>('.cg-choice.active')?.dataset.value ?? null;
}

/** Set the active choice in a .cg-choice-row group + fire the change hook. */
function setCgChoice(group: HTMLElement, value: string, hook?: (v: string) => void): void {
  for (const el of Array.from(group.querySelectorAll<HTMLElement>('.cg-choice'))) {
    el.classList.toggle('active', el.dataset.value === value);
  }
  hook?.(value);
}

/** Show the Create Game screen (resets to sensible defaults). */
function showCreateGameScreen(): void {
  createGameSettings.name = '';
  createGameSettings.visibility = 'public';
  createGameSettings.gameType = 'original';
  createGameSettings.originalSize = 4;
  createGameSettings.ranked = true;
  createGameSettings.lifeDefault = true;
  cgNameInput.value = '';
  cgLifeDefaultEl.checked = true;
  cgLifeCustomEl.classList.add('hidden');
  cgLifeValueEl.value = '5';
  cgRankedCheckbox.disabled = false;
  cgRankedCheckbox.checked = true;
  setCgChoice(cgVisibilityEl, 'public', onCgVisibilityChange);
  setCgChoice(cgGameTypeEl, 'original', onCgGameTypeChange);
  setCgChoice(cgSizeEl, '4');
  updateCreateGameVisibility();
  showScreen('createGame');
}

function hideCreateGameScreen(): void {
  showScreen('landing');
}

// Visibility change: private → ranked is forced off (the server enforces
// the same rule; the client mirrors it for UX — the control is hidden).
function onCgVisibilityChange(value: string): void {
  createGameSettings.visibility = value as 'public' | 'private';
  if (value === 'private') {
    createGameSettings.ranked = false;
    cgRankedCheckbox.checked = false;
  }
  updateCreateGameVisibility();
}

// Game-type change: co-op hides the size + lifestart + ranked fields (the
// server forces ranked=false + capacity 2 for co-op; the client mirrors for
// UX; the starting-life setting is NOT offered for co-op — task 209).
function onCgGameTypeChange(value: string): void {
  createGameSettings.gameType = value as 'original' | 'coop';
  if (value === 'coop') {
    createGameSettings.ranked = false;
    cgRankedCheckbox.checked = false;
  }
  updateCreateGameVisibility();
}

// Starting-life change (task 209): unchecking the default reveals the 1–10
// input and FORCES the game unranked (a custom starting life can never be
// ranked — the server enforces the same invariant). Re-checking the default
// re-enables the Ranked toggle (its public-original visibility rule applies).
function onCgLifeDefaultChange(): void {
  createGameSettings.lifeDefault = cgLifeDefaultEl.checked;
  cgLifeCustomEl.classList.toggle('hidden', cgLifeDefaultEl.checked);
  if (!cgLifeDefaultEl.checked) {
    createGameSettings.ranked = false;
    cgRankedCheckbox.checked = false;
    cgRankedCheckbox.disabled = true;
  } else {
    cgRankedCheckbox.disabled = false;
    // Public original may be ranked again (the user re-checks explicitly).
    cgRankedCheckbox.checked = createGameSettings.visibility === 'public';
    createGameSettings.ranked = cgRankedCheckbox.checked;
  }
}

// Show/hide the size + lifestart + ranked fields based on the current choices.
function updateCreateGameVisibility(): void {
  const isOriginal = createGameSettings.gameType === 'original';
  cgSizeFieldEl.classList.toggle('hidden', !isOriginal);
  // The starting-life section is ORIGINAL only (never visible for co-op).
  cgLifeStartFieldEl.classList.toggle('hidden', !isOriginal);
  // Ranked is only offered for a PUBLIC ORIGINAL lobby.
  cgRankedFieldEl.classList.toggle(
    'hidden',
    !(isOriginal && createGameSettings.visibility === 'public')
  );
}

/** Read the form into createGameSettings and submit (Start Lobby). */
function submitCreateGame(): void {
  createGameSettings.name = cgNameInput.value.trim();
  createGameSettings.visibility = (cgChoiceValue(cgVisibilityEl) ?? 'private') as 'public' | 'private';
  createGameSettings.gameType = (cgChoiceValue(cgGameTypeEl) ?? 'original') as 'original' | 'coop';
  createGameSettings.originalSize = (Number(cgChoiceValue(cgSizeEl)) === 8 ? 8 : 4) as 4 | 8;
  createGameSettings.ranked =
    createGameSettings.gameType === 'original' &&
    createGameSettings.visibility === 'public' &&
    cgLifeDefaultEl.checked && // a custom starting life is never ranked (task 209)
    cgRankedCheckbox.checked;

  // Task 209: the starting life — omitted for the default (the server uses
  // the size-based 6 long / 5 short); a clamped 1–10 integer for custom.
  const isOriginal = createGameSettings.gameType === 'original';
  let startingLife: number | undefined;
  if (isOriginal && !cgLifeDefaultEl.checked) {
    const raw = Math.round(Number(cgLifeValueEl.value));
    startingLife = Math.min(10, Math.max(1, Number.isFinite(raw) ? raw : 5));
  }

  shell?.createLobby({
    name: createGameSettings.name || undefined,
    gameType: createGameSettings.gameType,
    visibility: createGameSettings.visibility,
    ranked: createGameSettings.ranked,
    // Co-op: no size (the server uses capacity 2); original: the chosen size.
    originalSize: isOriginal ? createGameSettings.originalSize : undefined,
    startingLife,
  });
  hideCreateGameScreen(); // the authoritative LobbyStateUpdate opens the lobby
}

function wireCreateGameUI(): void {
  cgVisibilityEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.cg-choice');
    if (!btn) return;
    setCgChoice(cgVisibilityEl, btn.dataset.value ?? 'private', onCgVisibilityChange);
  });
  cgGameTypeEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.cg-choice');
    if (!btn) return;
    setCgChoice(cgGameTypeEl, btn.dataset.value ?? 'original', onCgGameTypeChange);
  });
  cgSizeEl.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('.cg-choice');
    if (!btn) return;
    setCgChoice(cgSizeEl, btn.dataset.value ?? '4');
  });
  cgLifeDefaultEl.addEventListener('change', onCgLifeDefaultChange);
  cgRankedCheckbox.addEventListener('change', () => {
    createGameSettings.ranked = cgRankedCheckbox.checked;
  });
  btnCgCancel.addEventListener('click', hideCreateGameScreen);
  btnCgStart.addEventListener('click', submitCreateGame);
}

// ─── The Original lobby screen (driven by LobbyStateUpdate) ───────────────

let _lobbyCountdownTimer: ReturnType<typeof setInterval> | null = null;
// The server seconds value the local countdown is currently ANCHORED to.
// Re-anchoring happens ONLY when that value changes — the screen re-renders
// on EVERY state update (an unrelated QueueStatusUpdate every second while
// any queue is in its bot-fill window would re-anchor to the STALE stored
// value and freeze the display — the "stuck at 30 s" bug).
let _lobbyCdAnchorValue: number | null = null;
// Set by "Quick host lobby" (the queued-state button): the freshly created
// lobby auto-starts its countdown the moment the lobby screen renders it
// (create + start in one gesture, as if the player had pressed Start).
let _autoStartLobby = false;
// The last connection state (for re-running the banner visibility after
// an identity change — login/logout/guest swap the status-dot rule).
let _lastConnectionState: ConnectionState = 'connecting';
// The last lobby id we authoritatively saw (set on every LobbyStateUpdate).
// A shell restart (match over / elimination / back-to-menu) loses the
// client-side lobby state — on the next connection we re-request THIS lobby
// (GetLobbyStateRequest): if the server-side party is alive (POST_GAME) the
// lobby screen + the CrazyGames room resume; if it is gone, the server
// rejects and we stay on the menu (the room sync then leftRooms).
let _lastKnownLobbyId: string | null = null;

/** Render the lobby screen from the authoritative LobbyStateUpdate. */
function renderLobby(): void {
  const lobby = shell?.gameState.lobby;
  if (!lobby) {
    lobbyNameEl.textContent = '';
    lobbyPlayersListEl.innerHTML = '';
    lobbyCodeEl.textContent = '';
    stopLobbyCountdownTick();
    return;
  }

  lobbyIdentityEl.textContent = displayUsername(currentUsername ?? currentPlayerId ?? 'Guest');
  lobbyNameEl.textContent = lobby.name;
  lobbyChipTypeEl.textContent = lobby.gameType === 'coop' ? 'Co-op' : 'Original';
  lobbyChipSizeEl.textContent =
    lobby.gameType === 'original' ? `${lobby.originalSize ?? 4} Player` : '2 Player';
  lobbyChipVisEl.textContent = lobby.visibility === 'public' ? 'Public' : 'Private';
  lobbyChipRankedEl.textContent = lobby.ranked ? 'Ranked' : 'Unranked';
  lobbyChipRankedEl.classList.toggle('ranked', lobby.ranked);
  // Starting life (task 209) — original lobbies only (null for co-op).
  lobbyChipLifeEl.classList.toggle('hidden', lobby.startingLife === null);
  lobbyChipLifeEl.textContent =
    lobby.startingLife === null ? '' : `Starting Life ${lobby.startingLife}`;

  // The lobby code — ALWAYS shown (including on CrazyGames: the native
  // Invite Friends flow is the primary path, but the code is the fallback
  // a friend can type in). The in-app Invite button is intentionally never
  // rendered (it is hidden in the markup; CrazyGames' native UI covers it).
  lobbyCodeEl.textContent = lobby.code;
  lobbyCodeHintEl.textContent = '';
  lobbyInviteRowEl.classList.add('hidden');

  renderLobbyPlayers(lobby);

  // Start / Leave controls (host gets Start; everyone gets Leave). The Start
  // button is available in WAITING and POST_GAME (Play Again).
  const isHost = lobby.viewerRole === 'host';
  const canStart = isHost && (lobby.state === 'WAITING' || lobby.state === 'POST_GAME');
  btnLobbyStart.classList.toggle('hidden', !canStart);
  if (canStart) {
    btnLobbyStart.disabled = false;
    btnLobbyStart.textContent = lobby.state === 'POST_GAME' ? 'Play Again' : 'Start Game';
  }

  // Quick-host auto-start: "Quick host lobby" (the queued-state button) set
  // the flag — the lobby is fresh (WAITING, we are host) → fire the server
  // countdown immediately, as if Start had been pressed by hand.
  if (_autoStartLobby && isHost && lobby.state === 'WAITING') {
    _autoStartLobby = false;
    shell?.startLobbyCountdown(lobby.id);
  }

  // The server-authoritative countdown (rendered from the server-computed
  // remaining seconds — the match start itself stays server-side at
  // countdownEndsAt). Re-anchor only when the server value CHANGES (a fresh
  // LobbyStateUpdate carries a fresh value; unrelated state updates carry
  // the stale stored one and must not reset the local timer).
  if (lobby.state === 'COUNTDOWN' && lobby.countdownSecondsLeft !== undefined) {
    if (lobby.countdownSecondsLeft !== _lobbyCdAnchorValue) {
      _lobbyCdAnchorValue = lobby.countdownSecondsLeft;
      startLobbyCountdownTick(lobby.countdownSecondsLeft);
    }
  } else {
    _lobbyCdAnchorValue = null;
    stopLobbyCountdownTick();
    lobbyCountdownEl.textContent = '';
  }

  // NOTE: the CrazyGames room sync (updateRoom) is NOT here — it is driven
  // by the authoritative state updates in cgSyncRoomState (onStateChange),
  // so the room follows the lobby state even while the match screen is up
  // (rendering this screen must never create/clear the room).
}

/** Render the player list (host first; the host sees kick buttons). */
function renderLobbyPlayers(lobby: import('./frontend/services/GameStateService').LobbyClientState): void {
  lobbyPlayersListEl.innerHTML = '';
  const isHost = lobby.viewerRole === 'host';
  for (const p of lobby.players) {
    const row = document.createElement('div');
    row.className = 'lobby-player-row';
    const name = document.createElement('span');
    name.className = 'lp-name';
    name.textContent = displayUsername(p.displayName);
    row.appendChild(name);
    if (p.isHost) {
      const badge = document.createElement('span');
      badge.className = 'lp-host';
      badge.textContent = 'HOST';
      row.appendChild(badge);
    }
    // Kick buttons (host only, non-host targets) — available in WAITING
    // ONLY: the server forbids kicks during COUNTDOWN (the host must not
    // be able to kick the players dragged in from the queue) and IN_GAME.
    if (isHost && !p.isHost && lobby.state === 'WAITING') {
      const kick = document.createElement('button');
      kick.className = 'lp-kick';
      kick.type = 'button';
      kick.textContent = 'Kick';
      kick.addEventListener('click', () => {
        shell?.kickLobbyPlayer(lobby.id, p.userId);
      });
      row.appendChild(kick);
    }
    lobbyPlayersListEl.appendChild(row);
  }
  // Empty seats (dashed placeholders up to capacity).
  const empty = lobby.playerCapacity - lobby.players.length;
  for (let i = 0; i < empty; i++) {
    const row = document.createElement('div');
    row.className = 'lobby-player-row empty';
    row.textContent = 'Waiting for a player…';
    lobbyPlayersListEl.appendChild(row);
  }
}

/**
 * Smooth client countdown driven by the SERVER's remaining-seconds value
 * (countdownSecondsLeft — the server clock is not epoch time, so the client
 * receives the seconds pre-computed, the botFillSecondsLeft convention).
 * Anchored to the local clock on receipt; re-anchors on each
 * LobbyStateUpdate; the 500 ms interval keeps the display smooth. The
 * server's countdownEndsAt remains authoritative for the MATCH START.
 */
function startLobbyCountdownTick(secondsLeft: number): void {
  const anchorMs = Date.now();
  const render = (): void => {
    const left = Math.max(0, Math.ceil(secondsLeft - (Date.now() - anchorMs) / 1000));
    lobbyCountdownEl.textContent = left > 0 ? `Starting in ${left}s` : 'Starting…';
  };
  render();
  if (_lobbyCountdownTimer) clearInterval(_lobbyCountdownTimer);
  _lobbyCountdownTimer = setInterval(render, 500);
}

function stopLobbyCountdownTick(): void {
  if (_lobbyCountdownTimer) {
    clearInterval(_lobbyCountdownTimer);
    _lobbyCountdownTimer = null;
  }
}

/** Wire the Original lobby screen's controls (called once at startup). */
function wireLobbyUI(): void {
  // Click the code to copy it (the standalone invite flow).
  lobbyCodeEl.addEventListener('click', () => {
    const code = lobbyCodeEl.textContent ?? '';
    if (!code) return;
    void copyToClipboard(code).then((ok) => {
      if (ok) {
        lobbyCodeHintEl.textContent = 'Copied!';
        setTimeout(() => (lobbyCodeHintEl.textContent = ''), 1500);
      }
    });
  });

  // Leave: sends LeaveLobbyRequest; the authoritative state update
  // (exists:false) drives the return to the menu. CrazyGames: leaving the
  // lobby is a permanent room leave (leftRoom).
  btnLobbyLeave.addEventListener('click', () => {
    stopLobbyCountdownTick();
    // Leaving the lobby is a permanent room leave (leftRoom) — immediate,
    // via the controller; the server-confirmed exists:false snapshot that
    // follows re-runs the state-driven path (deduped — no double call).
    crazyGamesLeaveRoom();
    shell?.leaveLobby();
  });

  // Start (host only): sends StartLobbyCountdownRequest; the server runs the
  // 30 s countdown and creates the match at expiry (both clients get
  // MatchStarted; the lobby survives as POST_GAME for Play Again).
  btnLobbyStart.addEventListener('click', () => {
    const lobby = shell?.gameState.lobby;
    if (!lobby || lobby.viewerRole !== 'host') return;
    shell?.startLobbyCountdown(lobby.id);
  });

  // Invite (CrazyGames only): build the invite link (inviteLink) with the
  // lobby code as the routing param, then expose it via the share UI.
  btnLobbyInvite.addEventListener('click', () => {
    const lobby = shell?.gameState.lobby;
    if (!lobby) return;
    getPlatform()
      .createInviteLink({ lobbyCode: lobby.code })
      .then((link) => {
        void copyToClipboard(link).then((ok) => {
          if (ok) {
            lobbyCodeHintEl.textContent = 'Invite link copied!';
            setTimeout(() => (lobbyCodeHintEl.textContent = ''), 2000);
          }
        });
      })
      .catch(() => {
        lobbyCodeHintEl.textContent = 'Invites are unavailable right now.';
        setTimeout(() => (lobbyCodeHintEl.textContent = ''), 2000);
      });
  });
}

// ─── COOP match UI (task 164) ─────────────────────────────────────────────
// PREPARATION (the play phase) in a team mission renders EXACTLY like a
// regular match: ONLY the own board. The mission bot's pre-spawned minions
// and the teammate's minions are NOT shown in the play phase — the one
// preparation difference is the SPLIT sell zone: the right half GIVES a
// minion to the teammate for 5 gold (it despawns for the viewer and lands
// in the teammate's hand — the state broadcast re-renders the board).
// BATTLE (the combat phase): the combat board shows the COMBINED team row
// (own + teammate copies in the authoritative teamOrder layout) in the
// LOWER half vs the bot on top — the ONLY place the teammate's minions are
// visible (teammate's copies tinted yellow, .coop-teammine).
// Normal matches and mission 4 ('normal' mode) render exactly as before.

let _coopTeamInfo: CoopMatchInfo | null = null;

function updateCoopMatchUI(coop: CoopMatchInfo | null, players: PlayerData[], phase: string = ''): void {
  _coopTeamInfo = coop;
  const isTeamCoop = !!coop && coop.mode !== 'normal';
  const active = isTeamCoop && !!shell && shell.isInMatch && !!players.find((p) => p.id === shell!.playerId);

  // ── Teammate eye button: team coop + PREPARATION phase only (the viewer
  //     is never available in the battle phase; the overlay auto-closes at
  //     combat start). Runs before the early return so leaving a coop match
  //     (or entering combat) hides the button. ──
  teammateEyeBtn.hidden = !(active && phase === 'preparation');
  if (phase !== 'preparation' && _teammateOverlayOpen) {
    closeTeammateOverlay();
  }

  if (!active || !coop) {
    if (hudCoopTeamName) hudCoopTeamName.classList.add('hidden');
    if (dragAndDropService) {
      // Board capacity back to the standard 7 (the red "No space" drop
      // indicator keys off this limit — task 175).
      dragAndDropService.updateOptions({
        giveZoneEnabled: false,
        onGiveCard: undefined,
        boardSizeLimit: GameConfig.maxBoardSize,
      });
    }
    return;
  }

  // The teammate's name in the shared-life HUD = the VIEWER'S partner
  // (for the teammate's own client that is the HOST — coop.teammateId is
  // the absolute teammate seat, i.e. the teammate themselves here).
  const teammateId = coopPartnerId(shell!.playerId) ?? coop.teammateId;
  const teammate = players.find((p) => p.id === teammateId) ?? null;

  // ── Shared life HUD: the teammate's name next to the team life ────
  if (hudCoopTeamName) {
    hudCoopTeamName.classList.remove('hidden');
    hudCoopTeamName.textContent = `+ ${displayUsername(teammate?.displayName ?? teammateId)}`;
  }

  // ── Split sell|give zone (the right half gives for 5 gold) ────────
  // No enemy row / teammate half here on purpose: the play phase shows
  // only the own board (the teammates' minions appear in battle only).
  if (dragAndDropService) {
    dragAndDropService.updateOptions({
      giveZoneEnabled: true,
      // COOP team matches cap the HUMAN board at 5 — the "No space"
      // indicator must reflect that, not the standard 7 (task 175).
      boardSizeLimit: COOP_HUMAN_BOARD_SIZE,
      onGiveCard: (instanceId: string) => {
        // SOUND: transferring to a COOP teammate counts as a sell (Sell.wav).
        soundService.play('sell');
        const matchId = shell?.gameState.match.matchId;
        if (matchId) shell?.giveMinion(matchId, instanceId);
      },
    });
  }
}

// ─── Tutorial (scripted in-game guide — no server) ───────────────────────

/**
 * Starts the tutorial: a fully scripted 11-tip tour of the REAL match
 * screen. Available to everyone (logged-in queue view AND logged-out
 * identity-choices page) and needs no server connectivity — if a real
 * shell exists it is shut down first so live state cannot intrude, and
 * the match screen is then driven by a TutorialShell (the scripted
 * "server") with the REAL UI components bound to it: the tutorial
 * renders exactly like a live match and inherits every future
 * card/HUD change automatically.
 *
 * The scripted game: 3 cards in hand (a tank, a bard and a mage), 0 gold,
 * the preparation timer frozen at 42 s, infinite time (the phase never
 * locks and combat never starts). The tips (TUTORIAL_STEPS) must be
 * completed in order and the user may only perform the current tip's
 * action (the TutorialShell rejects everything else).
 */
function startTutorial(): void {
  if (tutorialShell) return; // a tour is already running

  // 1. The tutorial needs no server: shut the real shell down (queue /
  //    live matches included) so nothing can intrude into the scripted
  //    match. The shutdown's screenChange already ran hideMatchComponents.
  if (shell) {
    shell.shutdown();
    shell = null;
  }
  queueScreen = null; // re-created by the next initShellAndComponents

  // 2. The scripted "server" — authoritative for the tutorial match.
  tutorialShell = new TutorialShell(currentPlayerId ?? 'tutorial-player');
  // All of main.ts's render glue reads the module-level `shell`, so the
  // cast keeps every existing code path working unchanged.
  shell = tutorialShell as unknown as AppShell;

  // 3. Fresh components bound to the tutorial shell + re-run the wire
  //    glue on them (the previous instances are bound to the dead real
  //    shell). The wire* functions subscribe on the module-level
  //    component variables, so this repoints the whole render path.
  initTutorialComponents();

  // 4. Interaction services: logged-out users have none yet — create
  //    them on demand (a later real shell init disposes + recreates
  //    them through the usual initShellAndComponents path).
  if (!dragAndDropService) {
    dragAndDropService = createDragAndDropService();
  }
  if (!cardInspectService) {
    cardInspectService = new CardInspectService();
  }
  if (!fullscreenService) {
    // Same pair the real-shell init runs — the tutorial screen IS the
    // match screen, so the mobile landscape enforcement (fullscreen +
    // orientation lock + rotate hint) needs the service to be present.
    fullscreenService = new FullscreenService();
    wireFullscreenService();
  }

  // 5. Show the match screen, then the scripted initial state (3 cards in
  //    hand, 0 gold, round 1, default rune loadout) — rendered through
  //    the normal component path (hand, HUD, buy buttons, …).
  resetHoldState();
  showScreen('match');
  // The tutorial screen IS the match screen — enforce the same mobile
  // landscape rule the real game applies on screen→'match' (best-effort
  // fullscreen + orientation lock, rotate-hint overlay as fallback).
  void beginGameOrientationEnforcement();
  showMatchComponents();
  tutorialShell.emitInitial();

  // 6. The tutorial timer is FROZEN at 42 s: one write. The real
  //    PreparationTimerUI is deliberately NOT wired for the tour (its
  //    local countdown would drift away from the frozen value).
  timerValue.textContent = String(TUTORIAL_TIMER_SECONDS);
  timerValue.classList.remove('locked');

  // 7. The connection banner is meaningless offline — hide it for the tour.
  connectionBanner.style.display = 'none';

  // 8. The tour controller (dialog + spotlight + tip state machine).
  tutorial = new TutorialService({
    shell: tutorialShell,
    cardInspect: cardInspectService,
    onFinish: () => endTutorial(),
  });
  tutorial.start();
}

/**
 * Creates a fresh set of match UI components bound to the tutorial shell
 * and re-runs the render wiring on them (mirrors the component half of
 * initShellAndComponents). The old instances stay bound to the dead real
 * shell and are simply dropped; the wire* callbacks are re-registered on
 * the fresh ones, repointing the whole render path at the tutorial.
 */
function initTutorialComponents(): void {
  const s = shell!;
  matchHudComponent    = new MatchHUD(s);
  handUI               = new HandUI(s);
  battlefieldUI        = new BattlefieldUI(s);
  combatBoardView      = new CombatBoardView(s);
  preparationTimerUI   = new PreparationTimerUI(s);
  upgradeChoiceUI      = new UpgradeChoiceUI(s);
  combatAnimationView  = new CombatAnimationView(s);
  simultaneousDamageAnimator = new SimultaneousDamageAnimator();
  combatGoldDisplay    = new CombatGoldDisplay(s);
  combatResultUI       = new CombatResultUI(s);
  spectatorModeUI      = new SpectatorModeUI(s);
  eliminatedPlayerScreen = new EliminatedPlayerScreen(s);
  winnerScreen         = new WinnerScreen(s);
  reconnectUI          = new ReconnectUI(s);

  wireMatchHUD();
  wireHandUI();
  wireBattlefieldUI();
  wireCombatBoardView();
  // Deliberately NOT wirePreparationTimerUI: the tutorial timer is frozen
  // (one-time 42 s write in startTutorial) — the component's local
  // countdown would drift away from the frozen value.
  wireCombatAnimationView();
  wireCombatGoldDisplay();
  wireSpectatorModeUI();
  wireEliminatedPlayerScreen();
  wireWinnerScreen();
  wireReconnectUI();
  wireUpgradeChoiceUI();

  // The main stateChange handler (phase/drag enable, ready button, combat
  // area toggle, purchased-runes row) must run on the tutorial shell's
  // events, exactly like it does on the real shell.
  tutorialShell?.onEvent((event) => {
    if (event.type === 'stateChange') onStateChange(event.data);
  });
}

/**
 * Ends the tutorial and returns to the menu: restores the real shell for
 * the SAME identity (logged-in users keep their username, guests their
 * guest ID — the usual restart funnel) or the identity-choices page for
 * logged-out visitors.
 */
function endTutorial(): void {
  if (!tutorialShell) return;

  // 1. Tear down the tour (dialog, spotlight, interval, gating, the
  //    long-press listener) and drop the scripted shell.
  tutorial?.destroy();
  tutorial = null;
  tutorialShell = null;
  shell = null;

  // 2. Hide the match screen and reset all match UI tracking (also
  //    resets the drag service's visual state).
  hideMatchComponents();

  // 3. Restore the connection banner (it re-fills on (re)connect).
  connectionBanner.style.display = '';

  // 4. The interaction services stay installed for now — they route
  //    through the module-level components, which the next
  //    initShellAndComponents disposes + recreates as usual. Disarm the
  //    drag service until then.
  dragAndDropService?.updateOptions({ isDragEnabled: false });
  dragAndDropService?.reset();

  // 5. Back to the menu — the same funnel the real game uses. The landing
  //    screen is portrait-allowed, so release the landscape enforcement
  //    the tour began (mirrors onScreenChange's 'landing' case).
  void releaseGameOrientationEnforcement();
  if (currentPlayerId) {
    restartShellKeepingIdentity();
  } else {
    showScreen('landing');
    showLandingView('choices');
  }
}

// ─── Screen Change Handler ────────────────────────────────────────────────

// The screen currently shown (so state-change hooks can react only on a
// specific screen — the coop lobby re-render needs it, because a lobby
// update while ALREADY on the lobby screen never fires a screen change).
let _currentScreenId: 'landing' | 'match' | 'gameOver' | 'coopLobby' | 'lobby' | 'createGame' = 'landing';

type AnyScreen = 'landing' | 'queue' | 'match' | 'gameOver' | 'coopLobby' | 'lobby' | 'createGame';

function onScreenChange(_previous: string, current: AnyScreen): void {
  const screenId = current === 'queue' ? 'landing' : current;
  _currentScreenId = screenId;
  // Leaving the lobby screen: a stale quick-host auto-start must never fire
  // on an UNRELATED lobby (e.g. the player was kicked / the lobby closed
  // before the first render, then they join a friend's lobby later).
  if (screenId !== 'lobby') _autoStartLobby = false;
  showScreen(screenId);

  switch (current) {
    case 'landing':
      // Hide queue screen
      queueScreen?.hide();
      // Hide all match components
      hideMatchComponents();
      // Leave the game → the purchased-runes row is gone with it; close the
      // rune info overlay if it is still open (defensive — it is only openable
      // during a match).
      closePurchasedRuneOverlay();
      // Left the game — close the in-game settings overlay if it is still
      // open (it is only openable during a match).
      closeSettingsOverlay();
      // Release the mobile landscape enforcement (login/landing views
      // are allowed to be portrait)
      void releaseGameOrientationEnforcement();
      break;

    case 'queue':
      // Show queue screen
      queueScreen?.show();
      // The queue view is part of the landing screen — no enforcement here
      void releaseGameOrientationEnforcement();
      break;

    case 'coopLobby':
      // The private COOP lobby screen: the queue view is hidden (it is part
      // of the landing screen), and the lobby renders from the authoritative
      // CoopLobbyStateUpdate (renderCoopLobby — called from showScreen).
      queueScreen?.hide();
      hideMatchComponents();
      hideAllPickers(); // leaving the landing — no sub-menu survives
      void releaseGameOrientationEnforcement();
      break;

    case 'lobby':
      // The unified Original lobby screen: queue view hidden, no match
      // components; the lobby renders from the authoritative
      // LobbyStateUpdate (renderLobby — called from showScreen).
      queueScreen?.hide();
      hideMatchComponents();
      hideAllPickers(); // leaving the landing — no sub-menu survives
      void releaseGameOrientationEnforcement();
      break;

    case 'createGame':
      // The Create Game setup screen: a plain landing-level screen.
      queueScreen?.hide();
      hideMatchComponents();
      hideAllPickers(); // leaving the landing — no sub-menu survives
      void releaseGameOrientationEnforcement();
      break;

    case 'match':
      // The landing screen (and its How-to-Play button) is gone — close the
      // wiki overlay if it is still open (the combat-start hook below also
      // closes it, defensively).
      closeHowToPlayOverlay();
      hideAllPickers(); // leaving the landing — no sub-menu survives
      // Mobile: enforce landscape while the game is active (best-effort
      // fullscreen + orientation lock, rotate-hint overlay as fallback)
      void beginGameOrientationEnforcement();
      // Reset hold/selection state before showing match components
      // (prevents stale card/minion references from persisting across match start)
      resetHoldState();
      // Show all match components
      showMatchComponents();
      break;

    case 'gameOver':
      // The How-to-Play wiki only makes sense on the landing screen — close
      // it on the way to the game-over view (already closed at match start;
      // this is the idempotent backstop).
      closeHowToPlayOverlay();
      // Same for the in-game settings overlay (the match is over — there is
      // nothing left to resign).
      closeSettingsOverlay();
      hideAllPickers(); // leaving the landing — no sub-menu survives
      // Hide non-combat match components but keep combat area visible
      // so animations can complete. The winner screen overlays the combat area.
      matchHudComponent?.hide();
      handUI?.hide();
      battlefieldUI?.hide();
      preparationTimerUI?.hide();
      upgradeChoiceUI?.hide();
      spectatorModeUI?.hide();
      eliminatedPlayerScreen?.hide();
      // Don't hide combatBoardView/combatAnimationView — let animations finish
      // Show game over data
      updateGameOverScreen();
      break;
  }
}

function showMatchComponents(): void {
  // Reset FX diff/one-shot state for the new match
  _lastHudGold = null;
  _lastHudLife = null;
  _lastBattlefieldLevels = new Map();
  _lastBattlefieldStats = new Map();
  _pendingSpellFx = null;
  _pendingSpellCastPos = null;
  _lastSpellDiffHandSpells = new Map();
  _lastSpellDiffGold = null;
  _fxSeenBattlefieldMinionIds = new Set();
  _victoryFxPlayed = false;
  _defeatFxPlayed = false;
  _eliminationFxPlayed = false;
  matchHudComponent?.show();
  handUI?.show();
  battlefieldUI?.show();
  combatBoardView?.show();
  preparationTimerUI?.show();
  upgradeChoiceUI?.show();
  combatAnimationView?.show();
  combatGoldDisplay?.show();
  combatResultUI?.show();
  spectatorModeUI?.show();
  eliminatedPlayerScreen?.show();
  winnerScreen?.show();
  reconnectUI?.show();
}

function hideMatchComponents(): void {
  matchHudComponent?.hide();
  handUI?.hide();
  battlefieldUI?.hide();
  combatBoardView?.hide();
  preparationTimerUI?.hide();
  upgradeChoiceUI?.hide();
  combatAnimationView?.hide();
  combatGoldDisplay?.hide();
  combatResultUI?.hide();
  spectatorModeUI?.hide();
  eliminatedPlayerScreen?.hide();
  winnerScreen?.hide();
  reconnectUI?.hide();

  // Reset hold/selection state to prevent stale references
  resetHoldState();

  // Reset UI state
  upgradePanel.classList.remove('visible');
  eliminatedScreen.classList.remove('visible');
  combatResultEl.classList.remove('visible');
  combatGoldDisplayEl.classList.remove('visible');
  spectatorBanner.classList.remove('visible');
  roundTransitionOverlay.classList.remove('visible');
  _combatResultShown = false;
  _treasureBannerShownForCombat = false;
  _h2hHistory.clear(); // a new match starts — the head-to-head list resets
  _pendingUpgradeMinionId = null;
  _pendingHighStakesMinionId = null;
  _combatAnimationsRunning = false;

  // Reset DOM tracking so next show rebuilds fresh
  _lastHandCardIds = [];
  _lastBattlefieldMinionIds = [];
  _lastBattlefieldMinionTypes = [];
  _lastBattlefieldPlayerGold = -1;
  _lastBattlefieldLevels = new Map();
  _lastBattlefieldStats = new Map();
  _pendingSpellFx = null;
  _pendingSpellCastPos = null;
  _lastSpellDiffHandSpells = new Map();
  _lastSpellDiffGold = null;

  // Clear combat minion DOM + element tracking (next match starts fresh)
  clearCombatMinionElements();
  clearSuddenDeathVisuals();

  // Reset drop zone setup flag so next match re-registers listeners with fresh closures
  _battlefieldDropZoneSetup = false;

  // Clear auto-disconnect timer
  cancelEliminationAutoDisconnect();
}

// ─── State Change Handler ─────────────────────────────────────────────────

function onStateChange(data: unknown): void {
  const state = data as {
    match: { matchId: string | null; phase: string; gameOver: boolean; combatStarted: any; roundTransition: any; players: any[]; preparationLocked: boolean; combatStream: { events: any[] } | null; coop: CoopMatchInfo | null };
    coopLobby: CoopLobbyClientState | null;
    lobby: import('./frontend/services/GameStateService').LobbyClientState | null;
  };
  if (!state?.match) return;

  // ── CrazyGames room sync (the single authoritative path) ─────────────────
  // Runs on EVERY authoritative state update (server-confirmed) — the room
  // follows the lobby lifecycle, never the component lifecycle.
  cgSyncRoomState(state);
  // Remember the last known lobby id so a shell restart (match over /
  // elimination) can re-sync the server-side lobby (POST_GAME) — the
  // CrazyGames room resumes with it (or leftRoom fires if it is gone).
  if (state.lobby && state.lobby.exists) _lastKnownLobbyId = state.lobby.id;

  // ── COOP lobby lifecycle (task 163): when the viewer's lobby DISAPPEARS
  //     (leave, host left, or the match started), the state clears and the
  //     screen flips back to the menu (AppShell screen logic). The old
  //     Co-op button's 5 s cooldown started here — gone with the button
  //     (task 191). The was-present tracking stays (harmless, cheap) in
  //     case a future entry point needs the "just left a coop lobby" edge.
  const lobbyNow = state.coopLobby;
  _coopLobbyWasPresent = lobbyNow !== null;

  // Re-render the lobby on EVERY authoritative update while on the lobby
  // screen: a seat filling (or a mission re-pick) does NOT change the
  // screen, so the showScreen render never fires again — without this the
  // host keeps seeing "Waiting for teammate…" and a disabled Start button
  // even after the teammate has joined.
  if (lobbyNow !== null && _currentScreenId === 'coopLobby') {
    renderCoopLobby();
  }

  // Re-render the unified ORIGINAL lobby on EVERY authoritative update while
  // on the lobby screen (a seat filling / kick / countdown change does not
  // change the screen — same convention as the co-op lobby above).
  if (state.lobby !== null && _currentScreenId === 'lobby') {
    renderLobby();
  }

  // ── COOP match UI (task 164): the team board / overlays / shared life.
  updateCoopMatchUI(state.match.coop ?? null, state.match.players, state.match.phase);

  // Keep the Ready button (count + own red/green state) in sync. Every state
  // change flows through here, including the ReadyStateUpdate broadcasts and
  // the post-combat reset (an empty ReadyStateUpdate at round start flips it
  // back to red/not-ready).
  updateReadyButton();

  // The purchased-runes row (top-left, below the HUD) mirrors the
  // authoritative PlayerData.runes.purchased — every state broadcast
  // (including the buy-rune confirmation and the reload resync) flows
  // through here, so the icons appear the moment a rune is bought.
  updatePurchasedRunes(state.match.players);

  // The active field-spell charges row (just below the rune icons) mirrors
  // the authoritative PlayerData.spellCharges — the board spells with a
  // battlefield effect (Healing Rain / Acid Rain / Blizzard) show as small
  // icons while armed; the icons vanish the moment a charge is spent.
  updateSpellCharges(state.match.players);

  const phase = state.match.phase;
  const combatStarted = state.match.combatStarted;
  const roundTransition = state.match.roundTransition;
  const isLocked = state.match.preparationLocked;

  // Handle round transition (waiting for other players)
  if (roundTransition && roundTransition.message) {
    roundTransitionMessage.textContent = roundTransition.message;
    roundTransitionOverlay.classList.add('visible');
  } else {
    roundTransitionOverlay.classList.remove('visible');
  }

  // Track preparation phase for drag enable/disable
  // Actions are disabled when: not in preparation phase, combat started, OR timer is locked
  const wasInPreparation = _isInPreparation;
  _isInPreparation = phase === 'preparation' && !combatStarted && !isLocked;

  // Update drag-and-drop service with current phase
  if (dragAndDropService) {
    dragAndDropService.updateOptions({ isDragEnabled: _isInPreparation });
  }

  // Keep the native `draggable` attribute OFF on hand cards and battlefield
  // minions (the desktop drag is pure JS — the native HTML5 drag initiation
  // was the 50 % "nothing happens" bug; mobile's native long-press drag is
  // unwanted too). Drag availability is gated by isDragEnabled, which the
  // updateOptions call above already handled.
  if (wasInPreparation !== _isInPreparation) {
    const handCards = handContainer.querySelectorAll('.hand-card');
    for (const card of handCards) {
      (card as HTMLElement).draggable = false;
    }
    const bfMinions = battlefield.querySelectorAll('.battlefield-minion');
    for (const m of bfMinions) {
      (m as HTMLElement).draggable = false;
    }

    // When returning to preparation from combat, reset tracking so DOM rebuilds fresh
    // (cards may have been drawn, minions played/sold during the round transition)
    if (_isInPreparation && !wasInPreparation) {
      _lastHandCardIds = [];
      _lastBattlefieldMinionIds = [];
      _lastBattlefieldMinionTypes = [];
      _pendingHighStakesMinionId = null;
      _lastBattlefieldPlayerGold = -1;
    }
  }

  // Toggle preparation vs combat area based on phase, combatStarted data, or gameOver.
  // Keep combat area visible during game over so animations can complete.
  // Also keep visible while combat stream animations are still playing — the
  // server sends GameStateUpdate (phase='preparation') after a short delay that
  // may be shorter than total animation time, so we use _combatAnimationsRunning
  // to prevent premature hiding.
  const combatStreamActive = state.match.combatStream && state.match.combatStream.events.length > 0;
  if (combatStreamActive && !combatAnimationView?.combatHasEnded) {
    _combatAnimationsRunning = true;
  }
  if (combatAnimationView?.combatHasEnded && _combatAnimationsRunning) {
    _combatAnimationsRunning = false;
  }

  const showCombat = phase === 'combat' || combatStarted || state.match.gameOver || _combatAnimationsRunning;
  if (showCombat) {
    // ── Bug Fix: Hide upgrade panel when combat starts ──
    upgradePanel.classList.remove('visible');
    if (upgradeChoiceUI) {
      upgradeChoiceUI.deselectMinion();
    }

    // ── Auto-close the keyword reference + how-to-play + rune shop +
    //     teammate viewer overlays when combat starts ──
    closeKeywordsOverlay();
    closeTeammateOverlay();
    closeHowToPlayOverlay();
    closeRuneShop();

    // ── Auto-close the in-game settings overlay when combat starts ("you
    //     can not resign during combat" — the menu disappears with it). ──
    closeSettingsOverlay();

    // ── Bug Fix: Reset drag state when combat starts (clears held cards) ──
    if (dragAndDropService) {
      dragAndDropService.reset();
    }

    if (!combatArea.classList.contains('active')) {
      console.log('[main] Showing combat area (phase=', phase, 'combatStarted=', !!combatStarted, 'gameOver=', state.match.gameOver, 'animationsRunning=', _combatAnimationsRunning, ')');
    }
    preparationArea.classList.add('hidden');
    combatArea.classList.add('active');
    // Reset the lunge chain on combat transition — a queued lunge from the
    // previous combat must not run against the fresh board's elements.
    _attackAnimationChain = Promise.resolve();

    // COOP TREASURE TURN: when the combat board loads (battle phase start)
    // for a treasure fight, flash the "Treasure fight!" banner for 2
    // seconds — the same overlay as the win/lose/tie result message.
    if (combatStarted?.treasureFight && !_treasureBannerShownForCombat) {
      showTreasureFightBanner();
    }

    // ACID RAIN / BLIZZARD (spells): start the screen-wide ambient weather
    // from the authoritative CombatStarted flags — green raindrops falling
    // down the entire screen (Acid Rain) and/or snowflakes whirling around
    // the screen (Blizzard). Idempotent: the flags are stable for the whole
    // combat, and the hide branch below stops the weather. Reduced motion
    // suppresses the spawns inside FxCanvas (no-op).
    const ambientKinds: FxKind[] = [];
    if (combatStarted?.acidRain) ambientKinds.push('acidRain');
    if (combatStarted?.blizzard) ambientKinds.push('blizzard');
    getFx().setAmbient(ambientKinds.length > 0 ? ambientKinds : null);
  } else {
    if (combatArea.classList.contains('active')) {
      console.log('[main] Hiding combat area (phase=', phase, 'combatStarted=', !!combatStarted, 'gameOver=', state.match.gameOver, 'animationsRunning=', _combatAnimationsRunning, ')');
      // Combat fully over (animation window elapsed) — clear the combat
      // minion DOM and element tracking so the next combat starts fresh
      // instead of stacking on top of the previous combat's elements.
      clearCombatMinionElements();
      // Also clear the sudden-death red background + indicator.
      clearSuddenDeathVisuals();
      // The combat is fully over — stop the ambient spell weather (the
      // Acid Rain / Blizzard screen effects) and let the next combat show
      // its own again.
      getFx().setAmbient(null);
      // The combat is fully over — the next combat may show its own
      // treasure banner again.
      _treasureBannerShownForCombat = false;
    }
    preparationArea.classList.remove('hidden');
    combatArea.classList.remove('active');
  }

  // Check for player elimination and start auto-disconnect timer
  if (state.match.players) {
    const currentPlayer = state.match.players.find((p: any) => p.id === shell?.playerId);
    if (currentPlayer && !currentPlayer.isAlive && currentPlayer.phase === 'eliminated') {
      startEliminationAutoDisconnect();
    }
  }
}

// ─── Wire QueueScreen ─────────────────────────────────────────────────────

function wireQueueScreen(): void {
  queueScreen?.onRenderChange((data: QueueScreenRenderData) => {
    queueStatusEl.textContent = data.statusMessage;
    // The queue message box is visible ONLY while queued ("Looking for
    // games…" + the Quick host lobby button); hidden on the landing page
    // otherwise (no "choose a queue" box). The per-queue counters
    // (fast: x/4 · long: x/8) are gone — the box is a single line.
    queueInfoEl.classList.toggle('hidden', !data.isInQueue);
    queueStatsEl.textContent = '';
    // While queued the landing shows ONLY "Leave Queue" (plus the box) —
    // Play (and every sub-menu) is hidden (task 191).
    btnPlay.classList.toggle('hidden', data.isInQueue);
    // Bot-fill countdown hint: shown only while the server reports the
    // remaining seconds (from 2 s after a join, the whole rest of the
    // 30 s window). A new join resets the timer → the field is absent for
    // the first 2 s → the hint hides until the window opens again.
    if (data.botFillSecondsLeft !== undefined) {
      queueBotFillCountdownEl.textContent =
        `Starting with Bots in: ${data.botFillSecondsLeft} seconds`;
      queueBotFillCountdownEl.classList.remove('hidden');
    } else {
      queueBotFillCountdownEl.classList.add('hidden');
    }
    btnLeaveQueue.classList.toggle('hidden', !data.canLeave);
    // The pickers are never visible while queued (one queue at a time) —
    // the server's status is authoritative for the Play/Leave swap. The
    // NOT-queued side needs no work: the Play toggle above already
    // restores it (a status broadcast must never close an open picker —
    // queue changes by OTHER players are common).
    if (data.isInQueue) {
      hideAllPickers();
    } else if (_returnToQueueModePicker) {
      // BACK semantics: the Leave Queue click consumed its leave — return
      // to the PREVIOUS sub-menu (the fast/long ranked-queue picker), not
      // the root Play menu. Consumed on the first NOT-queued render.
      _returnToQueueModePicker = false;
      showQueueModePicker();
    }
  });

  // Quick host lobby (queued-state only): one gesture = leave the queue +
  // create a PUBLIC RANKED original lobby with the CURRENT queue's size
  // (fast = 4, long = 8) + start the countdown immediately (the
  // _autoStartLobby flag fires from renderLobby). It is a normal lobby —
  // the server drags waiting queue players into it (and it drags them out
  // of the queue, like any public ranked lobby).
  btnQuickHost.addEventListener('click', () => {
    if (!shell) return;
    const mode = shell.gameState.queue.queueMode ?? 'fast';
    _autoStartLobby = true;
    // Leave the queue FIRST — the server rejects lobby creation while the
    // player is queued ("Leave the queue before creating a lobby").
    shell.leaveQueue();
    shell.createLobby({
      name: 'Quick Host',
      gameType: 'original',
      visibility: 'public',
      ranked: true,
      originalSize: mode === 'long' ? 8 : 4,
    });
  });
}

// ─── Wire MatchHUD ────────────────────────────────────────────────────────

function wireMatchHUD(): void {
  matchHudComponent?.onRenderChange((data: MatchHUDRenderData) => {
    // FX: gold-gain pop / life-loss sting (diffed against the previous render)
    if (_lastHudGold !== null && data.gold > _lastHudGold) {
      floatText(hudGold, `+${data.gold - _lastHudGold}`, 'gold');
      retriggerClass(hudGold, 'fx-gold-pop');
      getFx().burstEl(hudGold, 'sparks', { count: 5, colors: FX_GOLD_COLORS });
    }
    if (_lastHudLife !== null && data.life < _lastHudLife) {
      floatText(hudLife, `-${_lastHudLife - data.life}`, 'dmg', _lastHudLife - data.life);
      retriggerClass(hudLife, 'fx-life-hurt');
    }
    _lastHudGold = data.gold;
    _lastHudLife = data.life;

    hudLife.textContent = String(data.life);
    hudGold.textContent = String(data.gold);
    hudRound.textContent = String(data.currentRound);
    // The floating timer box is PREPARATION-only (playtest: "should not be
    // visible during combat").
    timerBox.classList.toggle('hidden', data.phase === 'combat');
    // Settings (⚙️, top-right): visible in the PREPARATION phase only —
    // hidden during combat (you can not resign during combat; the overlay
    // auto-closes there too) and once the match is over.
    btnSettings.classList.toggle(
      'hidden',
      data.isGameOver || data.phase !== 'preparation'
    );
    renderPlayerPills(data.allPlayers, playerIdFromShell(), data.nextOpponentId);
  });
}

// ─── Wire HandUI ──────────────────────────────────────────────────────────

/**
 * Sends the Ready-button toggle intention. The desired state is computed
 * from the authoritative count (is my ID in readyPlayerIds?) — the client
 * never keeps its own ready flag. Outside preparation (no match, combat,
 * game over) the button is inert; the server re-validates regardless.
 */
function togglePreparationReady(): void {
  const match = shell?.gameState.match;
  if (!match?.matchId || match.gameOver || match.phase !== 'preparation') return;
  const myId = shell?.playerId;
  const isReady = !!myId && match.readyPlayerIds.includes(myId);
  shell?.markReady(match.matchId, !isReady);
}

/**
 * Renders the Ready button from the authoritative ready state:
 *  - count "x/n" from ReadyStateUpdate (readyTotalPlayers is the n; before
 *    the first broadcast the player list is the fallback so it never shows
 *    "0/0" while in a match) — kept on the plate in its lower-right frame;
 *  - OWN state as the plate image (no text): UnRdy.png = not ready (the
 *    post-combat reset state), Rdy.png = ready (.ready class).
 * Purely reflective — the server owns the state; this just paints it.
 */
function updateReadyButton(): void {
  const match = shell?.gameState.match;
  const inPreparation = !!match?.matchId && !match.gameOver && match.phase === 'preparation';
  if (!inPreparation) {
    // Outside preparation the button shows the neutral not-ready plate (it
    // is hidden with the whole hand area during combat anyway).
    readyBtn.classList.remove('ready');
    readyImgEl.src = '/UnRdy.png';
    readyCountEl.textContent = '0/0';
    readyBtn.title = 'Ready — combat starts when everyone is ready';
    return;
  }
  const myId = shell?.playerId;
  const isReady = !!myId && match.readyPlayerIds.includes(myId);
  const total = match.readyTotalPlayers > 0
    ? match.readyTotalPlayers
    : match.players.filter((p: any) => p.isAlive).length;
  readyBtn.classList.toggle('ready', isReady);
  readyImgEl.src = isReady ? '/Rdy.png' : '/UnRdy.png';
  readyCountEl.textContent = `${match.readyPlayerIds.length}/${total}`;
  readyBtn.title = isReady
    ? `Ready — click to cancel (${match.readyPlayerIds.length}/${total} ready)`
    : `Ready — combat starts when everyone is ready (${match.readyPlayerIds.length}/${total} ready)`;
}

/**
 * Syncs the Buy-a-card button (upper-right corner of the hand — the
 * Draw.png plate) with the current hand render data: the gold cost is kept
 * on the plate (centered to the right, in #buy-card-cost) showing the
 * current (Discount-reduced) cost, and the button is enabled only during
 * (unlocked) preparation when the player can afford the cost and the hand
 * has a free slot. Called on EVERY hand render (before the rebuild
 * early-return) because gold/discount can change without the hand IDs
 * changing. Purely cosmetic — the server re-validates everything on the
 * request.
 */
function updateBuyCardButton(data: HandUIRenderData): void {
  buyCardBtn.disabled = !data.canBuy;
  // Gold cost only — it sits in the plate's right-side slot, where there is
  // no room for a coin glyph (the Draw plate already carries the coin art).
  buyCardCostEl.textContent = `${data.purchaseCost}g`;
  if (data.canBuy) {
    buyCardBtn.title = data.discountCount > 0
      ? `Buy a random card for ${data.purchaseCost}g (−${data.discountCount} from ${data.discountCount} Discount in play)`
      : 'Buy a random card';
  } else if (data.isFull) {
    buyCardBtn.title = 'Hand is full (8/8)';
  } else {
    buyCardBtn.title = `Need ${data.purchaseCost}g and a free hand slot`;
  }
}

/**
 * Syncs the rune-shop button (upper-left corner of the hand) with the
 * current hand render data: the button opens the rune shop overlay
 * (every rune with its gold price). It is VISIBLE during the preparation
 * phase while something is still purchasable, DISABLED while preparation
 * is locked (the server rejects actions then). Purely cosmetic — the
 * server re-validates everything on the request.
 */
function updateBuyRuneButton(data: HandUIRenderData): void {
  // The button opens the rune shop overlay (every rune with its gold
  // price). It stays visible through the preparation phase (the cap is
  // DYNAMIC — a RuneKeeper can still be played, and at/over the cap the
  // label shows the "(x/N)" state, e.g. "(4/3)" after a RuneKeeper leaves
  // — the runes stay, the limit just dropped); DISABLED while preparation
  // is locked (the server rejects actions then). The label carries the
  // progress counter "(x/N)": x = runes I already bought (authoritative
  // PlayerData.runes.purchased), N = the EFFECTIVE cap (base 3 + one per
  // RuneKeeper in play) — so the player always sees how many more runes
  // they can buy.
  // COOP: the counter is PERSONAL ("each player can buy 3" + own RuneKeeper
  // bonuses) — the shared union lives in data.activeRunes (Owned badges).
  buyRuneBtn.textContent =
    `🪬 Buy Rune (${data.personalRunes.length}/${data.runeCap})`;
  buyRuneBtn.hidden = !data.runeShopVisible;
  buyRuneBtn.disabled = !data.runeShopEnabled;
  buyRuneBtn.title = data.runeShopEnabled
    ? 'Open the rune shop — every rune, at its gold price'
    : 'Runes can only be bought during the preparation phase';

  // Keep an OPEN shop in sync with the authoritative state (a purchase
  // lands here via the GameStateUpdate: gold, the Owned badges and the
  // affordability dimming must all refresh). Closing on combat start is
  // handled by the showCombat transition.
  if (!runeShopOverlay.classList.contains('hidden')) {
    renderRuneShop();
  }
}

/**
 * The ACTIVE (purchased) runes for a combat side, from the authoritative
 * CombatStarted message (playerARunes/playerBRunes — the pair's sides,
 * first attacker = A). Only runes actually purchased during the current
 * game are listed; an absent field (older server) means none.
 */
function combatSideRunes(playerId: string): string[] {
  const cs = shell?.gameState.match.combatStarted;
  if (!cs) return [];
  // COOP: the TEAM side's runes are the SHARED union (the server sends them
  // under the seat's id) — BOTH members render them (the seat is not the
  // teammate, so the plain id lookup would show the teammate nothing).
  const coop = _coopTeamInfo;
  if (
    coop && coop.mode !== 'normal' &&
    (playerId === coop.hostId || playerId === coop.teammateId)
  ) {
    const teamIsA = cs.playerAId === coop.hostId || cs.playerAId === coop.teammateId;
    return teamIsA ? (cs.playerARunes ?? []) : (cs.playerBRunes ?? []);
  }
  if (playerId === cs.playerAId) return cs.playerARunes ?? [];
  if (playerId === cs.playerBId) return cs.playerBRunes ?? [];
  return [];
}

/**
 * Renders the active rune icons to the right of a combat side's player
 * name. One icon per purchased rune (image + tooltip with name/effect).
 */
function renderCombatSideRunes(container: HTMLElement, runeIds: string[]): void {
  container.innerHTML = '';
  for (const id of runeIds) {
    const def = getRuneById(id);
    if (!def) continue; // unknown rune ID — skip (never block the label)
    const img = document.createElement('img');
    img.src = def.image;
    img.alt = def.name;
    img.title = `${def.name} — ${def.description}`;
    img.draggable = false;
    container.appendChild(img);
  }
}

/**
 * Builds the keyword-reference overlay UI ONCE from the shared
 * GAME_ABILITY_DEFINITIONS (single source of truth) plus the shared
 * MINION_TYPE_* display constants. Nothing about any specific keyword is
 * hard-coded:
 *  - the class filter buttons are the union of all definition types (an
 *    "All" button + one per class), so a new keyword for a new class gets
 *    its button automatically;
 *  - rows are sorted by level requirement (ties keep definition order), so
 *    it's obvious which ability is available at which level;
 *  - names, descriptions, class badges and level labels all come straight
 *    from the definitions and MINION_TYPE_* constants.
 */

// null = show every class (the "All" button); otherwise the selected class.
let keywordsClassFilter: string | null = null;

function keywordRowHtml(def: AbilityDefinition): string {
  const classBadges = def.types.map((t) => {
    const key = t as MinionType;
    const color = MINION_TYPE_COLORS[key] ?? '#888';
    const icon = MINION_TYPE_ICONS[key] ?? '❓';
    const name = MINION_TYPE_NAMES[key] ?? t;
    return `<span class="keyword-class-badge" style="background:${color}">${icon} ${name}</span>`;
  }).join('');
  return `
    <div class="keyword-row">
      <div class="keyword-top">
        <span class="keyword-name">${def.name}</span>
        ${classBadges}
        <span class="keyword-level">${formatLevelRequirement(def.availableAtLevel)}</span>
      </div>
      <div class="keyword-desc">${def.description}</div>
    </div>
  `;
}

/** Renders the keyword list honoring the active class filter (level-sorted). */
function renderKeywordsList(): void {
  const defs = filterKeywordsByClass(
    sortKeywordsByLevel(GAME_ABILITY_DEFINITIONS),
    keywordsClassFilter
  );
  keywordsList.innerHTML = defs.map((def) => keywordRowHtml(def)).join('');
}

function buildKeywordsOverlay(): void {
  if (keywordsFilters.childElementCount > 0) return; // already built

  // "All" button + one button per class that has at least one keyword.
  const classes = keywordClasses(GAME_ABILITY_DEFINITIONS);
  const buttons: Array<{ label: string; value: string | null }> = [
    { label: 'All', value: null },
    ...classes.map((c) => ({ label: keywordClassLabel(c), value: c })),
  ];
  keywordsFilters.innerHTML = buttons.map((b) =>
    `<button class="keyword-filter-btn${b.value === null ? ' active' : ''}" data-class="${b.value ?? ''}">${b.label}</button>`
  ).join('');

  keywordsFilters.querySelectorAll('.keyword-filter-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const value = (btn as HTMLElement).dataset.class ?? '';
      keywordsClassFilter = value === '' ? null : value;
      keywordsFilters.querySelectorAll('.keyword-filter-btn').forEach((b) =>
        b.classList.toggle('active', b === btn)
      );
      renderKeywordsList();
    });
  });

  renderKeywordsList();
}

function openKeywordsOverlay(): void {
  keywordsOverlay.classList.remove('hidden');
}

function closeKeywordsOverlay(): void {
  keywordsOverlay.classList.add('hidden');
}

// ─── Class Progression (task 210) ─────────────────────────────────────────
// The grid visualization of how the classes progress:
//   - BOTTOM row: one CARD per column — the five STANDARD draw-pile classes
//     (testMinions) AND the transform targets (Blightcaster / Buffer /
//     Runekeeper / Spellmaker — a transform resets the minion to a fresh
//     level-1 of the target class, so the targets get their own bottom
//     column too, in transform order).
//   - ROWS level 1..8 (labelled on the left): each cell lists the KEYWORDS
//     that become available for that class at that level — data-driven from
//     GAME_ABILITY_DEFINITIONS (types + availableAtLevel), so a class
//     availability change updates the grid automatically.
//   - The level where a transform is offered shows the TARGET CARD with a
//     GOLDEN frame, and a bezier ARROW (SVG) runs from that cell to the
//     target's bottom-row column.
// The cards are the real MinionCard (createMinionCard, inert — the Play
// button is hidden via CSS), so card changes flow through here.

const classProgressOverlay  = document.getElementById('class-progress-overlay')!;
const classProgressClose    = document.getElementById('class-progress-close')! as HTMLButtonElement;
const classProgressGrid     = document.getElementById('class-progress-grid')!;
const classProgressArrows   = document.getElementById('class-progress-arrows')! as unknown as SVGSVGElement;
const classProgressRunePanel = document.getElementById('class-progress-runes')!;
const btnClassProgress      = document.getElementById('btn-class-progress')! as HTMLButtonElement;
// The MOBILE overlay (task 217) — a separate, single-column page (the
// desktop grid above is untouched): class selector on top, the selected
// class's ladder below (card + level rows with INLINE labels). No rune
// panel, no arrows.
const classProgressMobileOverlay = document.getElementById('class-progress-mobile-overlay')!;
const cpmClose            = document.getElementById('cpm-close')! as HTMLButtonElement;
const cpmSelector         = document.getElementById('cpm-selector')!;
const cpmTree             = document.getElementById('cpm-tree')!;
const cpmHint             = document.getElementById('cpm-hint')!;

/** The highest level row rendered (the grid shows level 0..8). */
const CP_MAX_LEVEL = 8;

/**
 * The grid's COLUMNS: the five standard draw-pile classes (base-pool order),
 * the transform targets (transform order) AND the two token/summon classes
 * (Undead + Swarmer — they reach the board via keywords/runes, so they get
 * a level-0 column too; task 212). Each column carries the class's card on
 * the bottom row + the class's keyword ladder above it.
 */
function classProgressColumns(): { def: MinionDefinition; isTarget: boolean }[] {
  const cols: { def: MinionDefinition; isTarget: boolean }[] = testMinions.map((def) => ({
    def,
    isTarget: false,
  }));
  for (const t of TRANSFORM_DEFINITIONS) {
    const targetDef = getTransformTargetDefinition(t.targetClass);
    if (!targetDef) continue;
    if (!cols.some((c) => c.def.minionType === t.targetClass)) {
      cols.push({ def: targetDef, isTarget: true });
    }
  }
  // The token/summon classes (task 212): their level-0 column is where the
  // purple unlock arrows (SwarmLord → Swarmer, Gravedigger/Necromancy +
  // the necromancer runes → Undead) land.
  for (const def of [UNDEAD_MINION_DEFINITION, SWARMER_MINION_DEFINITION]) {
    if (!cols.some((c) => c.def.minionType === def.minionType)) {
      cols.push({ def, isTarget: true });
    }
  }
  return cols;
}

/**
 * The grid ROW (index) for a keyword (task 215 — the HAVING-level
 * mapping): a row is an IN-GAME LEVEL (the row labelled "Level L" shows
 * what a minion can HAVE at level L; the card row is index 0 = "Level
 * 1"). A keyword with availableAtLevel K is granted by the K → K+1
 * upgrade, so it is first had at level K+1 → row index K (levels 0/1
 * merge into the "Level 2" row — "available from the start"). Second
 * Chance (K 6, learned at 6→7) → the Level 7 row (index 6) — the
 * "Requires a Grandmaster" marker row; you can't have it at level 6.
 */
/**
 * The grid ROW (index) for a LEARNED keyword (task 216): the row INDEX
 * IS the in-game level (the bottom row is index 0 — the spawn cards, no
 * label — and "Level L" is index L). A keyword with availableAtLevel K
 * is granted by the K → K+1 upgrade (K 0/1 → the first upgrade), so it
 * is first had at level K+1 → index K+1 (levels 0/1 merge into the
 * Level 2 row). Second Chance (K 6, learned at 6→7) → index 7 = the
 * Level 7 row ("Grandmaster"); you can't have it at level 6. (The SPAWN
 * keywords — the class's baseAbilities — are not "learned": they sit on
 * the Level 1 row — see classProgressKeywordsFor.)
 */
function classProgressRowFor(def: { id: string; availableAtLevel: number }): number {
  return Math.min(CP_MAX_LEVEL, Math.max(2, def.availableAtLevel + 1));
}

/**
 * The keywords that become available for `minionType` on the grid row for
 * level `level` (1..CP_MAX_LEVEL). Data-driven from the catalogue; catalog
 * -only keywords (no types) never appear.
 */
/**
 * The keywords for a class's row (task 216): the LEARNED keywords at their
 * first-had level (classProgressRowFor) PLUS the SPAWN keywords — the
 * class's baseAbilities (Shield / Buff Vitality / Heal / Runekeeper /
 * Spellmaker / Improved / Fragile), which come WITH the class card and
 * therefore sit on the LEVEL 1 row (index 1).
 */
function classProgressKeywordsFor(minionType: string, level: number, baseAbilities: readonly string[] = []): string[] {
  return GAME_ABILITY_DEFINITIONS.filter(
    (d) => d.types.includes(minionType as MinionType)
      && (baseAbilities.includes(d.id) ? 1 : classProgressRowFor(d)) === level
  ).map((d) => d.id);
}

/**
 * The RUNES that unlock/affect a unit class (task 212) — the right-side
 * rune column (stacked: icon + name + price; click → description; purple
 * arrow to the affected unit's bottom-row card).
 */
function classProgressRunes() {
  return Object.keys(RUNE_UNIT_EFFECTS).map((id) => getRuneById(id)).filter((r) => r !== null);
}

/** One inert MinionCard from a base definition (level 1, base stats). */
function classProgressCardData(def: MinionDefinition): MinionCardData {
  const abilityNames = def.baseAbilities.map((id) => GAME_ABILITY_DEFINITIONS.find((d) => d.id === id)?.name ?? id);
  return {
    instanceId: `cp-${def.id}`,
    baseMinionId: def.id,
    currentHp: def.startingHp,
    attack: def.attack,
    minionType: def.minionType,
    upgradeCount: 0,
    level: 1,
    abilityNames,
    abilityDescriptions: def.baseAbilities.map(
      (id) => GAME_ABILITY_DEFINITIONS.find((d) => d.id === id)?.description ?? ''
    ),
    hasAbilities: def.baseAbilities.length > 0,
  };
}

/** Build the grid (bottom row = the cards; above = levels 8..1). */
function renderClassProgression(): void {
  const cols = classProgressColumns();
  classProgressGrid.innerHTML = '';
  // Label column + one column per class. (The rune panel is a FLOATING
  // sibling on the right — NOT a grid item, task 212/fix.)
  classProgressGrid.style.gridTemplateColumns = `70px repeat(${cols.length}, 112px)`;

  // The class-header row (icon + name per column) + the level labels.
  const headerLabel = document.createElement('div');
  classProgressGrid.appendChild(headerLabel);
  for (const c of cols) {
    const h = document.createElement('div');
    h.className = 'cp-class-header';
    const icon = document.createElement('span');
    icon.className = 'cp-class-icon';
    icon.textContent = MINION_TYPE_ICONS[c.def.minionType as MinionType] ?? '❓';
    const name = document.createElement('span');
    name.textContent = MINION_TYPE_NAMES[c.def.minionType as MinionType] ?? c.def.minionType;
    h.append(icon, name);
    classProgressGrid.appendChild(h);
  }

  // The level rows (8 → 1) + the bottom card row (level 0).
  for (let level = CP_MAX_LEVEL; level >= 0; level--) {
    // The row INDEX is the in-game level (task 216). The bottom row
    // (index 0) is the SPAWN row — the cards only — and gets NO label
    // ("level 0"); every row above is labelled "Level L" = index L.
    const label = document.createElement('div');
    label.className = 'cp-level-label';
    if (level > 0) label.textContent = `Level ${level}`;
    // The Grandmaster-context row markers (task 216): the WHOLE Level 7
    // row requires a Grandmaster in play (the first over-max level — the
    // base cap is level 6), Level 8 two of them — the label says so and
    // the row's cells are tinted.
    if (level === GRANDMASTER_ROW) {
      const note = document.createElement('span');
      note.className = 'cp-level-note';
      note.textContent = 'Grandmaster';
      label.appendChild(note);
    } else if (level === DOUBLE_GRANDMASTER_ROW) {
      const note = document.createElement('span');
      note.className = 'cp-level-note';
      note.textContent = '2 Grandmasters';
      label.appendChild(note);
    }
    classProgressGrid.appendChild(label);
    cols.forEach((c, col) => {
      const cell = document.createElement('div');
      cell.className = 'cp-cell';
      if (level === GRANDMASTER_ROW || level === DOUBLE_GRANDMASTER_ROW) {
        cell.classList.add('cp-row-gm');
      }
      if (level === 0) {
        // THE CARD (the standard draw-pile classes + the transform targets).
        cell.classList.add('cp-card-cell');
        const card = createMinionCard(classProgressCardData(c.def), {
          variant: 'hand',
          draggable: false,
        });
        card.dataset.cpCard = c.def.minionType;
        if (c.isTarget) {
          // The transform targets get the GOLDEN frame (the cards we can
          // envolve into).
          const gold = document.createElement('div');
          gold.className = 'cp-card-gold';
          gold.appendChild(card);
          cell.appendChild(gold);
        } else {
          cell.appendChild(card);
        }
      } else {
        // The keywords that become available at this level (data-driven).
        for (const kwId of classProgressKeywordsFor(c.def.minionType, level, c.def.baseAbilities)) {
          const kw = GAME_ABILITY_DEFINITIONS.find((d) => d.id === kwId)!;
          const chip = document.createElement('span');
          chip.className = 'cp-keyword';
          // The class's BASE keywords (it starts with them): GREEN frame
          // (task 211) — and they never show dependency arrows.
          if (c.def.baseAbilities.includes(kwId)) chip.classList.add('cp-keyword-base');
          chip.dataset.cpKeyword = kwId;
          chip.dataset.cpCol = String(col);
          chip.dataset.cpRow = String(level);
          // The UNLOCK keywords (task 212): the keyword brings the class's
          // units to the board (SwarmLord → Swarmer, Gravedigger/Necromancy
          // → Undead) — purple mark + a purple arrow to that class's
          // level-0 column.
          if ((KEYWORD_CLASS_UNLOCKS[kwId] ?? []).length > 0) chip.classList.add('cp-keyword-unlock');
          chip.textContent = kw.name;
          chip.title = kw.description;
          chip.addEventListener('click', (ev) => {
            ev.stopPropagation();
            showClassProgressHint(chip, kw);
          });
          cell.appendChild(chip);
        }
        // The TRANSFORM level (task 216): the transform is LEARNED on the
        // 3 → 4 upgrade, so the badge sits on the LEVEL 4 row (index
        // spec.level + 1). Only the CLASS BADGE (the NAME — no icon,
        // task 212) with the golden frame; the full target card lives on
        // the bottom (spawn) row and is where the arrow lands.
        const spec = TRANSFORM_DEFINITIONS.find((t) => t.sourceClasses.includes(c.def.minionType as MinionType));
        if (spec && level === spec.level + 1) {
          const badge = document.createElement('div');
          badge.className = 'cp-transform-badge';
          badge.dataset.cpTransformSource = c.def.minionType;
          badge.dataset.cpCard = spec.targetClass;
          const name = document.createElement('span');
          name.textContent = MINION_TYPE_NAMES[spec.targetClass as MinionType] ?? spec.targetClass;
          badge.appendChild(name);
          cell.appendChild(badge);
        }
      }
      classProgressGrid.appendChild(cell);
    });
  }

  renderClassProgressRunePanel();

  drawClassProgressArrows();
}

/**
 * The RUNE PANEL (task 212/fix): the runes that unlock/affect a unit
 * class, stacked (icon + name + price). It FLOATS on the right of the
 * grid — a sticky sibling element, deliberately NOT a grid item (so it
 * can never distort the grid's geometry). Click → the description hint
 * (like the keywords); a purple arrow runs to each affected unit's
 * bottom-row card.
 */
function renderClassProgressRunePanel(): void {
  const panel = classProgressRunePanel;
  panel.innerHTML = '';
  const runeTitle = document.createElement('div');
  runeTitle.className = 'cp-rune-title';
  runeTitle.textContent = 'Runes';
  panel.appendChild(runeTitle);
  for (const rune of classProgressRunes()) {
    const chip = document.createElement('div');
    chip.className = 'cp-rune';
    chip.dataset.cpRune = rune.id;
    chip.title = rune.description;
    const icon = document.createElement('img');
    icon.src = rune.image;
    icon.alt = '';
    const name = document.createElement('span');
    name.className = 'cp-rune-name';
    name.textContent = rune.name;
    const price = document.createElement('span');
    price.className = 'cp-rune-price';
    price.textContent = `${rune.goldCost}g`;
    chip.append(icon, name, price);
    chip.addEventListener('click', (ev) => {
      ev.stopPropagation();
      showClassProgressHint(chip, { id: rune.id, name: rune.name, description: rune.description });
    });
    panel.appendChild(chip);
  }
}

/**
 * The bezier arrows: one per transform, from the transform cell's card
 * (source column, the spec level) down to the TARGET's bottom-row card
 * (the column the minion becomes). Recomputed on open + resize.
 */
function drawClassProgressArrows(): void {
  const body = classProgressArrows.parentElement!;
  const bodyRect = body.getBoundingClientRect();
  const svgNS = 'http://www.w3.org/2000/svg';
  classProgressArrows.innerHTML = '';

  // The arrowhead markers (defined once per render: gold for the transform
  // arrows, green for the keyword-dependency arrows, purple for the
  // class-unlock / rune arrows — task 212).
  const defs = document.createElementNS(svgNS, 'defs');
  for (const [id, color] of [
    ['cp-arrowhead', '#f1c40f'],
    ['cp-dep-arrowhead', 'rgba(46, 204, 113, 0.9)'],
    ['cp-unlock-arrowhead', '#9b59b6'],
  ] as const) {
    const marker = document.createElementNS(svgNS, 'marker');
    marker.setAttribute('id', id);
    marker.setAttribute('markerWidth', '7');
    marker.setAttribute('markerHeight', '7');
    marker.setAttribute('refX', '5.5');
    marker.setAttribute('refY', '3.5');
    marker.setAttribute('orient', 'auto');
    const tip = document.createElementNS(svgNS, 'path');
    tip.setAttribute('d', 'M0,0 L7,3.5 L0,7 Z');
    tip.setAttribute('fill', color);
    marker.appendChild(tip);
    defs.appendChild(marker);
  }
  classProgressArrows.appendChild(defs);

  const toBody = (el: Element) => {
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2 - bodyRect.left, yTop: r.top - bodyRect.top, yBottom: r.top + r.height - bodyRect.top };
  };

  // ── The keyword DEPENDENCY arrows (task 211): from a keyword chip to
  //    each keyword it REQUIRES (KEYWORD_DEPENDENCIES — the same "requires"
  //    gates as the upgrade eligibility). Only when the dependency is
  //    rendered in the SAME column (the arrow points at the actual chip).
  //    BASE keywords (the class starts with them) never show dependencies.
  const chips = Array.from(classProgressGrid.querySelectorAll<HTMLElement>('[data-cp-keyword]'));
  for (const chip of chips) {
    if (chip.classList.contains('cp-keyword-base')) continue;
    // The marker rows (Level 7/8) carry the Grandmaster requirement for
    // the WHOLE row — their keywords never draw the dependency arrow
    // (task 212, confirmed task 215: Second Chance sits on Level 7).
    if (Number(chip.dataset.cpRow ?? 0) >= GRANDMASTER_ROW) continue;
    const deps = KEYWORD_DEPENDENCIES[chip.dataset.cpKeyword ?? ''] ?? [];
    if (deps.length === 0) continue;
    const s = toBody(chip);
    for (const depId of deps) {
      const dep = chips.find(
        (c) => c.dataset.cpKeyword === depId && c.dataset.cpCol === chip.dataset.cpCol
      );
      if (!dep) continue;
      const d = toBody(dep);
      // A short curved arrow from the keyword's bottom to the dependency's
      // top (the dependencies always sit lower — the requires gate).
      const midY = (s.yBottom + d.yTop) / 2;
      const bow = chip.offsetLeft < dep.offsetLeft ? 10 : -10;
      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('class', 'cp-dep-arrow');
      // Task 213: the hover/click reveal keys (from the keyword to its
      // dependency):
      path.dataset.cpFrom = chip.dataset.cpKeyword ?? '';
      path.dataset.cpTo = depId;
      path.setAttribute('d', `M ${s.x + bow} ${s.yBottom} Q ${s.x + bow * 2} ${midY}, ${d.x} ${d.yTop - 2}`);
      path.setAttribute('marker-end', 'url(#cp-dep-arrowhead)');
      classProgressArrows.appendChild(path);
    }
  }

  const nCols = classProgressColumns().length;

  /** The BOTTOM-row (level 0) card of the given class column. */
  const bottomCardOf = (minionType: string): HTMLElement | null => {
    const col = classProgressColumns().findIndex((c) => c.def.minionType === minionType);
    if (col < 0) return null;
    return classProgressGrid.children[
      (CP_MAX_LEVEL + 1) * (nCols + 1) + 1 + col
    ]?.querySelector<HTMLElement>('.hand-card') ?? null;
  };

  /** The right edge / left edge of an element (for the cross-column arrows). */
  const toBodyEdge = (el: Element, side: 'left' | 'right') => {
    const r = el.getBoundingClientRect();
    return {
      x: (side === 'left' ? r.left : r.right) - bodyRect.left,
      y: r.top + r.height / 2 - bodyRect.top,
    };
  };

  // ── The UNLOCK arrows (task 212): from each purple UNLOCK keyword chip
  //    (SwarmLord / Gravedigger / Necromancy) to the unlocked class's
  //    BOTTOM-row card. A purple bezier (the chip leaves from its side
  //    facing the target column).
  const unlockChips = Array.from(classProgressGrid.querySelectorAll<HTMLElement>('.cp-keyword-unlock'));
  for (const chip of unlockChips) {
    const targets = KEYWORD_CLASS_UNLOCKS[chip.dataset.cpKeyword ?? ''] ?? [];
    const sourceCol = Number(chip.dataset.cpCol ?? -1);
    for (const targetType of targets) {
      const target = bottomCardOf(targetType);
      if (!target) continue;
      const targetCol = classProgressColumns().findIndex((c) => c.def.minionType === targetType);
      const s = toBodyEdge(chip, targetCol > sourceCol ? 'right' : 'left');
      const t = toBodyEdge(target, targetCol > sourceCol ? 'left' : 'right');
      const bow = (t.x - s.x) * 0.45;
      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('class', 'cp-unlock-arrow');
      path.dataset.cpFrom = chip.dataset.cpKeyword ?? '';
      path.dataset.cpTo = targetType;
      path.setAttribute('d', `M ${s.x} ${s.y} C ${s.x + bow} ${s.y}, ${t.x - bow} ${t.y}, ${t.x} ${t.y}`);
      path.setAttribute('marker-end', 'url(#cp-unlock-arrowhead)');
      classProgressArrows.appendChild(path);
    }
  }

  // ── The RUNE arrows (task 212): from each rune chip (right column) to
  //    the BOTTOM-row card of every unit class the rune unlocks/affects.
  // The rune chips live in the FLOATING panel (a grid sibling — task
  // 212b), NOT in the grid: query the panel, not the grid.
  const runeChips = Array.from(
    classProgressRunePanel.querySelectorAll<HTMLElement>('[data-cp-rune]')
  );
  for (const chip of runeChips) {
    const targets = RUNE_UNIT_EFFECTS[chip.dataset.cpRune ?? ''] ?? [];
    for (const targetType of targets) {
      const target = bottomCardOf(targetType);
      if (!target) continue;
      const s = toBodyEdge(chip, 'left');
      const t = toBodyEdge(target, 'right');
      const bow = Math.max(40, (s.x - t.x) * 0.4);
      // Task 213: the rune → class arrows are DOTTED purple (a distinct
      // style from the solid keyword/unlock arrows).
      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('class', 'cp-rune-arrow');
      path.dataset.cpFrom = chip.dataset.cpRune ?? '';
      path.dataset.cpTo = targetType;
      path.setAttribute('d', `M ${s.x} ${s.y} C ${s.x - bow} ${s.y}, ${t.x + bow} ${t.y}, ${t.x} ${t.y}`);
      path.setAttribute('marker-end', 'url(#cp-unlock-arrowhead)');
      classProgressArrows.appendChild(path);
    }
  }

  // ── The TRANSFORM arrows: from the golden class badge (the transform
  //    cell) down to the target's BOTTOM-row card (the level-1 reset).
  const transformBadges = Array.from(
    classProgressGrid.querySelectorAll<HTMLElement>('[data-cp-transform-source]')
  );
  for (const source of transformBadges) {
    const targetType = source.dataset.cpCard;
    const targetCard = bottomCardOf(targetType ?? '');
    if (!targetCard) continue;

    const s = toBody(source);
    const t = toBody(targetCard);
    // A bezier that leaves the badge bottom and arrives at the target card
    // top (the sag scales with the vertical distance).
    const sag = Math.max(40, (t.yTop - s.yBottom) * 0.45);
    const path = document.createElementNS(svgNS, 'path');
    path.setAttribute('class', 'cp-arrow');
    path.dataset.cpFrom = source.dataset.cpTransformSource ?? '';
    path.dataset.cpTo = targetType ?? '';
    path.setAttribute('d', `M ${s.x} ${s.yBottom} C ${s.x} ${s.yBottom + sag}, ${t.x} ${t.yTop - sag}, ${t.x} ${t.yTop - 2}`);
    path.setAttribute('marker-end', 'url(#cp-arrowhead)');
    classProgressArrows.appendChild(path);
  }

  // Re-apply the PINNED arrows (task 213 — the draw wiped the DOM).
  for (const p of classProgressArrows.querySelectorAll<SVGPathElement>('path')) {
    const key = `${p.dataset.cpFrom}>${p.dataset.cpTo}`;
    if (_cpPinnedArrows.has(key)) p.classList.add('cp-shown', 'cp-pinned');
  }
}

// ─── The arrow reveal (task 213) ────────────────────────────────────────────
// The arrows are HIDDEN by default; hovering (or clicking) a KEYWORD chip,
// a CLASS BADGE, a CLASS CARD (level 0) or a RUNE chip shows the arrows
// connected to it. Clicking PINS the arrows (they stay after the pointer
// leaves, until re-clicked; re-rendering / closing clears the pins).

const _cpPinnedArrows = new Set<string>();

/** Show (or pin) the arrows connected to the given element. */
function _cpArrowsFor(el: HTMLElement, pin: boolean): void {
  const isKeyword = el.classList.contains('cp-keyword');
  const isBadge = el.classList.contains('cp-transform-badge');
  const isCard = el.classList.contains('hand-card') && Boolean(el.dataset.cpCard);
  const isRune = el.classList.contains('cp-rune');
  if (!isKeyword && !isBadge && !isCard && !isRune) return;
  for (const p of classProgressArrows.querySelectorAll<SVGPathElement>('path')) {
    const from = p.dataset.cpFrom ?? '';
    const to = p.dataset.cpTo ?? '';
    const connected = isKeyword
      ? from === (el.dataset.cpKeyword ?? '')
      : isBadge
        ? from === (el.dataset.cpTransformSource ?? '')
        : isRune
          ? from === (el.dataset.cpRune ?? '')
          : to === (el.dataset.cpCard ?? '');
    if (!connected) continue;
    const key = `${from}>${to}`;
    if (pin) {
      _cpPinnedArrows.add(key);
      p.classList.add('cp-shown', 'cp-pinned');
    } else {
      p.classList.add('cp-shown');
    }
  }
}

/** Hide the (unpinned) arrows connected to the given element. */
function _cpUnshowArrowsFor(el: HTMLElement): void {
  for (const p of classProgressArrows.querySelectorAll<SVGPathElement>('path')) {
    if (p.classList.contains('cp-pinned')) continue;
    p.classList.remove('cp-shown');
  }
}

/** Wire the hover/click reveal for the chips, badges, cards + runes. */
function wireClassProgressArrowReveal(): void {
  for (const el of Array.from(
    classProgressOverlay.querySelectorAll<HTMLElement>(
      '.cp-keyword, .cp-transform-badge, .cp-rune, .hand-card[data-cp-card]'
    )
  )) {
    el.addEventListener('mouseenter', () => _cpArrowsFor(el, false));
    el.addEventListener('mouseleave', () => _cpUnshowArrowsFor(el));
    el.addEventListener('click', (ev) => {
      // Toggle the pin (a second click unpins the connected arrows).
      const allPinned = Array.from(
        classProgressArrows.querySelectorAll<SVGPathElement>('path.cp-pinned')
      ).every((p) => {
        const from = p.dataset.cpFrom ?? '';
        const to = p.dataset.cpTo ?? '';
        return (
          (el.dataset.cpKeyword && from === el.dataset.cpKeyword) ||
          (el.dataset.cpTransformSource && from === el.dataset.cpTransformSource) ||
          (el.dataset.cpRune && from === el.dataset.cpRune) ||
          (el.dataset.cpCard && to === el.dataset.cpCard)
        );
      });
      if (allPinned) {
        for (const p of classProgressArrows.querySelectorAll<SVGPathElement>('path.cp-pinned')) {
          const from = p.dataset.cpFrom ?? '';
          const to = p.dataset.cpTo ?? '';
          const connected =
            (el.dataset.cpKeyword && from === el.dataset.cpKeyword) ||
            (el.dataset.cpTransformSource && from === el.dataset.cpTransformSource) ||
            (el.dataset.cpRune && from === el.dataset.cpRune) ||
            (el.dataset.cpCard && to === el.dataset.cpCard);
          if (connected) {
            _cpPinnedArrows.delete(`${from}>${to}`);
            p.classList.remove('cp-pinned', 'cp-shown');
          }
        }
      } else {
        _cpArrowsFor(el, true);
      }
      ev.stopPropagation();
    });
  }
}

// ─── The keyword click-hint (task 211) ─────────────────────────────────────

const classProgressHint = document.getElementById('class-progress-hint')!;

/** Show the keyword's explanation near the clicked chip. */
function showClassProgressHint(chip: HTMLElement, kw: { id: string; name: string; description: string }): void {
  classProgressHint.innerHTML = '';
  const name = document.createElement('span');
  name.className = 'cp-hint-name';
  name.textContent = kw.name;
  const deps = KEYWORD_DEPENDENCIES[kw.id ?? ''] ?? [];
  const desc = document.createElement('span');
  desc.textContent = kw.description;
  classProgressHint.append(name, desc);
  if (deps.length > 0) {
    const depNote = document.createElement('span');
    depNote.textContent = `Requires: ${deps
      .map((id) => GAME_ABILITY_DEFINITIONS.find((d) => d.id === id)?.name ?? id)
      .join(' + ')}`;
    depNote.style.color = '#2ecc71';
    classProgressHint.appendChild(depNote);
  }
  classProgressHint.classList.remove('hidden');
  // Position near the chip (clamped to the viewport).
  const r = chip.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = r.left + r.width / 2 - 130; // the hint is 260px wide
  left = Math.max(8, Math.min(left, vw - 268));
  let top = r.bottom + 8;
  if (top + 90 > vh) top = r.top - 8 - 90; // flip above the chip
  classProgressHint.style.left = `${left}px`;
  classProgressHint.style.top = `${top}px`; // (height clamp — content is short)
}

function hideClassProgressHint(): void {
  classProgressHint.classList.add('hidden');
}

function openClassProgressOverlay(): void {
  _cpPinnedArrows.clear(); // a fresh render — the pins start empty
  renderClassProgression();
  classProgressOverlay.classList.remove('hidden');
  // The arrows need the LAYOUTED positions — draw after the frame paints,
  // then wire the hover/click reveal (task 213) on the fresh elements.
  requestAnimationFrame(() => {
    drawClassProgressArrows();
    wireClassProgressArrowReveal();
  });
}

function closeClassProgressOverlay(): void {
  hideClassProgressHint();
  classProgressOverlay.classList.add('hidden');
}

// ─── Class Progression — MOBILE (task 217) ───────────────────────────
// A SEPARATE overlay for small screens (the desktop grid is left
// untouched — making it responsive would break it too much). ONE class
// at a time: a class selector row on top; the selected class's ladder
// below — the class card at the top, then the level rows with the
// "Level X" labels INLINE. The keyword display is the SAME as the
// desktop page (same chip classes): green spawn chips on Level 1,
// learned keywords at their first-had level, the golden transform badge
// on Level 4 (tapping it SELECTS the target class), the Grandmaster
// marker rows. No rune panel, no arrows.

/** The class currently shown in the mobile overlay (minion type). */
let _cpmSelectedClass: string = 'warrior';

/** Select a class in the mobile overlay and re-render its ladder. */
export function selectClassProgressMobileClass(minionType: string): void {
  _cpmSelectedClass = minionType;
  renderClassProgressMobile();
}

/** Build the mobile overlay: the class selector + the selected ladder. */
export function renderClassProgressMobile(): void {
  const cols = classProgressColumns();
  // The class selector (all 11 columns' classes — the same set as the
  // desktop grid): a horizontally scrollable row of icon + name chips.
  cpmSelector.innerHTML = '';
  for (const c of cols) {
    const chip = document.createElement('button');
    chip.className = 'cpm-class-chip';
    if (c.def.minionType === _cpmSelectedClass) chip.classList.add('active');
    chip.dataset.cpmClass = c.def.minionType;
    const icon = document.createElement('span');
    icon.className = 'cpm-class-icon';
    icon.textContent = MINION_TYPE_ICONS[c.def.minionType as MinionType] ?? '❓';
    const name = document.createElement('span');
    name.textContent = MINION_TYPE_NAMES[c.def.minionType as MinionType] ?? c.def.minionType;
    chip.append(icon, name);
    chip.addEventListener('click', () => selectClassProgressMobileClass(c.def.minionType));
    cpmSelector.appendChild(chip);
  }

  // The selected class's ladder (card on top, then levels 1 → 8 — task 218).
  const sel = cols.find((c) => c.def.minionType === _cpmSelectedClass) ?? cols[0];
  cpmTree.innerHTML = '';
  // The class card (the real MinionCard — same data as the desktop page).
  const cardRow = document.createElement('div');
  cardRow.className = 'cpm-card-row';
  const slot = document.createElement('div');
  slot.className = 'cpm-card-slot';
  const card = createMinionCard(classProgressCardData(sel.def), { variant: 'hand', draggable: false });
  card.dataset.cpCard = sel.def.minionType;
  slot.appendChild(card);
  cardRow.appendChild(slot);
  cpmTree.appendChild(cardRow);
  // The level rows (the inline label + the chips — same logic as the
  // desktop grid's cells). Task 218: the order is FLIPPED vs the desktop
  // grid — Level 1 at the TOP, Level 8 at the bottom (reading the
  // ladder top-to-bottom feels more natural on a phone).
  for (let level = 1; level <= CP_MAX_LEVEL; level++) {
    const row = document.createElement('div');
    row.className = 'cpm-row';
    if (level === GRANDMASTER_ROW || level === DOUBLE_GRANDMASTER_ROW) {
      row.classList.add('cpm-row-gm');
    }
    const label = document.createElement('div');
    label.className = 'cpm-level-label';
    label.textContent = `Level ${level}`;
    if (level === GRANDMASTER_ROW) {
      const note = document.createElement('span');
      note.className = 'cp-level-note';
      note.textContent = 'Grandmaster';
      label.appendChild(note);
    } else if (level === DOUBLE_GRANDMASTER_ROW) {
      const note = document.createElement('span');
      note.className = 'cp-level-note';
      note.textContent = '2 Grandmasters';
      label.appendChild(note);
    }
    const cells = document.createElement('div');
    cells.className = 'cpm-row-cells';
    for (const kwId of classProgressKeywordsFor(sel.def.minionType, level, sel.def.baseAbilities)) {
      const kw = GAME_ABILITY_DEFINITIONS.find((d) => d.id === kwId)!;
      const chip = document.createElement('span');
      chip.className = 'cp-keyword';
      if (sel.def.baseAbilities.includes(kwId)) chip.classList.add('cp-keyword-base');
      chip.dataset.cpKeyword = kwId;
      chip.dataset.cpRow = String(level);
      if ((KEYWORD_CLASS_UNLOCKS[kwId] ?? []).length > 0) chip.classList.add('cp-keyword-unlock');
      chip.textContent = kw.name;
      chip.title = kw.description;
      chip.addEventListener('click', (ev) => {
        ev.stopPropagation();
        showCpmHint(chip, kw);
      });
      cells.appendChild(chip);
    }
    // The TRANSFORM badge on Level 4 (task 216): tapping it SELECTS the
    // target class (the user asked for: looking at Tank → tap the
    // "Runekeeper" badge → the Runekeeper's tree is shown instead).
    const spec = TRANSFORM_DEFINITIONS.find((t) => t.sourceClasses.includes(sel.def.minionType as MinionType));
    if (spec && level === spec.level + 1) {
      const badge = document.createElement('div');
      badge.className = 'cp-transform-badge';
      badge.dataset.cpTransformSource = sel.def.minionType;
      badge.dataset.cpCard = spec.targetClass;
      const name = document.createElement('span');
      name.textContent = MINION_TYPE_NAMES[spec.targetClass as MinionType] ?? spec.targetClass;
      badge.appendChild(name);
      badge.addEventListener('click', () => selectClassProgressMobileClass(spec.targetClass));
      cells.appendChild(badge);
    }
    row.append(label, cells);
    cpmTree.appendChild(row);
  }
}

/** The mobile keyword hint (mirrors the desktop's showClassProgressHint). */
function showCpmHint(chip: HTMLElement, kw: { id: string; name: string; description: string }): void {
  cpmHint.innerHTML = '';
  const name = document.createElement('span');
  name.className = 'cp-hint-name';
  name.textContent = kw.name;
  const desc = document.createElement('span');
  desc.textContent = kw.description;
  cpmHint.append(name, desc);
  const deps = KEYWORD_DEPENDENCIES[kw.id ?? ''] ?? [];
  if (deps.length > 0) {
    const depNote = document.createElement('span');
    depNote.textContent = `Requires: ${deps
      .map((id) => GAME_ABILITY_DEFINITIONS.find((d) => d.id === id)?.name ?? id)
      .join(' + ')}`;
    depNote.style.color = '#2ecc71';
    cpmHint.appendChild(depNote);
  }
  cpmHint.classList.remove('hidden');
  const r = chip.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = r.left + r.width / 2 - 120;
  left = Math.max(8, Math.min(left, vw - 248));
  let top = r.bottom + 8;
  if (top + 90 > vh) top = r.top - 8 - 90;
  cpmHint.style.left = `${left}px`;
  cpmHint.style.top = `${top}px`;
}

function hideCpmHint(): void {
  cpmHint.classList.add('hidden');
}

export function openClassProgressMobile(): void {
  renderClassProgressMobile();
  classProgressMobileOverlay.classList.remove('hidden');
}

export function closeClassProgressMobile(): void {
  hideCpmHint();
  classProgressMobileOverlay.classList.add('hidden');
}

// ─── COOP Teammate Viewer (eye button + overlay) ─────────────────────────
// In a team coop match the teammate's full PlayerData (hand + board) is
// part of the same authoritative broadcast as the viewer's own, so the
// overlay needs no new server traffic — it just renders the teammate's
// data. The eye button is preparation-only (never in the battle phase) and
// the overlay auto-closes when combat starts (see the showCombat transition
// and updateCoopMatchUI's visibility toggle).

let _teammateOverlayOpen = false;

const TEAMMATE_ABILITY_SUMMARIES = GAME_ABILITY_DEFINITIONS.map(
  (def) => ({ id: def.id, name: def.name, description: def.description, types: def.types, availableAtLevel: def.availableAtLevel })
);

/**
 * The viewer's PARTNER's id — the other human of the team. CoopMatchInfo
 * is MISSION-level: `teammateId` is the absolute teammate seat, so for the
 * TEAMMATE's own client "the other player" is the HOST (the same
 * viewerId === hostId ? teammateId : hostId resolution the combat-side
 * helpers use). Null outside a team coop match.
 */
function coopPartnerId(viewerId: string): string | null {
  const coop = _coopTeamInfo;
  if (!coop || coop.mode === 'normal') return null;
  return viewerId === coop.hostId ? coop.teammateId : coop.hostId;
}

/** The viewer's teammate's authoritative PlayerData (null outside a team
 *  coop match, or when the teammate's data isn't present yet). */
function getTeammatePlayerData(): PlayerData | null {
  const coop = _coopTeamInfo;
  if (!coop || coop.mode === 'normal') return null;
  if (!shell) return null;
  const partnerId = coopPartnerId(shell.playerId);
  if (!partnerId) return null;
  const players = shell.gameState.match.players;
  return players.find((p: PlayerData) => p.id === partnerId) ?? null;
}

/**
 * (Re)builds the overlay contents from the teammate's CURRENT authoritative
 * hand + board. Called on open and on every state change while open (the
 * hand render callback flows through every broadcast — the teammate's
 * numbers stay live while the overlay is up).
 */
function renderTeammateOverlay(): void {
  const teammate = getTeammatePlayerData();
  if (!teammate) {
    closeTeammateOverlay();
    return;
  }

  teammateNameEl.textContent = displayUsername(teammate.displayName ?? teammate.id);

  // The wire→render mapping (identical to HandUI / BattlefieldUI): resolve
  // the ability names + descriptions from the shared ability catalogue.
  const toCardRender = (card: MinionInHandData): HandCardRenderData => {
    const names = resolveAbilityNames(card.abilities, TEAMMATE_ABILITY_SUMMARIES);
    const descs = card.abilities.map((id) => {
      const def = TEAMMATE_ABILITY_SUMMARIES.find((d) => d.id === id);
      return def ? def.description : '';
    });
    const owner = teammate;
    return {
      instanceId: card.instanceId,
      baseMinionId: card.baseMinionId,
      currentHp: card.currentHp,
      attack: card.attack,
      minionType: card.minionType,
      upgradeCount: card.upgradeCount,
      level: card.upgradeCount + 1,
      abilities: [...card.abilities],
      abilityNames: names,
      abilityDescriptions: descs,
      hasAbilities: card.abilities.length > 0,
      // ENRICHMENT (task 197): the per-card counter (dynamic description).
      ...(card.enrichmentTurns !== undefined
        ? { enrichmentTurns: card.enrichmentTurns }
        : {}),
      // UPGRADED (task 196): the OWNER's cast count (dynamic description).
      ...(card.baseMinionId === UPGRADED_SPELL_ID && owner
        ? { ownerUpgradedCasts: owner.upgradedSpellCasts ?? 0 }
        : {}),
    };
  };
  const toBoardRender = (minion: MinionOnBoardData): BattlefieldMinionRenderData => ({
    ...toCardRender(minion),
    battlefieldPosition: minion.battlefieldPosition,
    upgradePathState: minion.upgradePathState,
  });

  // Hand (INERT cards: no drag, no callbacks — the Play button is hidden
  // via CSS in the overlay).
  teammateHandEl.innerHTML = '';
  for (const card of teammate.hand) {
    teammateHandEl.appendChild(
      createMinionCard(toCardRender(card), { variant: 'hand', draggable: false })
    );
  }
  teammateHandCountEl.textContent = String(teammate.hand.length);

  // Board (sorted left-to-right; no sell/upgrade — the buttons are hidden).
  teammateBoardEl.innerHTML = '';
  const sortedBoard = [...teammate.battlefield].sort(
    (a, b) => a.battlefieldPosition - b.battlefieldPosition
  );
  for (const minion of sortedBoard) {
    teammateBoardEl.appendChild(
      createMinionCard(toBoardRender(minion), { variant: 'battlefield', draggable: false })
    );
  }
  teammateBoardCountEl.textContent = String(sortedBoard.length);
}

function openTeammateOverlay(): void {
  _teammateOverlayOpen = true;
  teammateOverlay.classList.remove('hidden');
  renderTeammateOverlay();
}

function closeTeammateOverlay(): void {
  _teammateOverlayOpen = false;
  teammateOverlay.classList.add('hidden');
}

// ─── Purchased-runes row + rune info overlay ────────────────────────────

/**
 * Syncs the purchased-runes row (top-left, directly below the HUD — just
 * under the Life/gold pills) with the authoritative PlayerData.runes.purchased
 * of the current player: one clickable icon per purchased rune, stacking
 * horizontally in purchase order. The row is hidden when nothing is
 * purchased (the :empty CSS rule). Icons carry data-rune-id and are matched
 * by the single delegated click listener on the row (the innerHTML is
 * rebuilt on every state change, so no per-icon listeners). Purely
 * cosmetic — the server owns the purchase state.
 */
function updatePurchasedRunes(players: Array<{ id: string; runes?: { purchased?: string[] } }>): void {
  const me = players?.find((p) => p.id === playerIdFromShell());
  const purchased = me?.runes?.purchased ?? [];
  purchasedRunesRow.innerHTML = purchased
    .map((id) => {
      // Unknown id (shouldn't happen — the server validates) → no icon.
      const def = getRuneById(id);
      if (!def) return '';
      return (
        `<button class="purchased-rune-icon" data-rune-id="${def.id}" title="${def.name}">` +
        `<img src="${def.image}" alt="${def.name}" draggable="false"></button>`
      );
    })
    .join('');
}

// ─── Active field-spell charges row (just below the rune icons) ──────

/**
 * Syncs the active field-spell charges row (top-left, JUST BELOW the
 * purchased-runes row — the same small horizontally-aligned icon style)
 * with the authoritative PlayerData.spellCharges of the current player:
 * one icon per armed board spell — the spells with a BATTLEFIELD effect
 * (Healing Rain / Acid Rain / Blizzard). Acid Rain's icon carries its
 * remaining ATTACK count (×N badge — the charges are attacks, not combats,
 * and can span several combats). COOP: the server sends the TEAM UNION
 * (the arming convention), so both seats render the same row. The row is
 * hidden when nothing is armed (the :empty CSS rule). Purely cosmetic —
 * the server owns the charges (tooltip only, no click).
 */
function updateSpellCharges(
  players: Array<{ id: string; spellCharges?: { healingRain: boolean; acidRainAttacks: number; blizzard: boolean; infestation?: boolean } }>
): void {
  const me = players?.find((p) => p.id === playerIdFromShell());
  const charges = me?.spellCharges;
  const icons: string[] = [];
  if (charges) {
    if (charges.healingRain) {
      icons.push(
        `<span class="spell-charge-icon" data-spell-charge="healingRain" title="Healing Rain armed: in your next combat, every time one of your minions attacks, all your minions restore 2 HP">🌧️</span>`
      );
    }
    if (charges.acidRainAttacks > 0) {
      icons.push(
        `<span class="spell-charge-icon" data-spell-charge="acidRain" data-spell-charge-count="${charges.acidRainAttacks}" title="Acid Rain armed: the next ${charges.acidRainAttacks} attack(s) deal 1 damage to ALL minions">` +
        `🧪<span class="spell-charge-count">×${charges.acidRainAttacks}</span></span>`
      );
    }
    if (charges.blizzard) {
      icons.push(
        `<span class="spell-charge-icon" data-spell-charge="blizzard" title="Blizzard armed: in your next combat, each attacking minion has a 25% chance to be Frozen and skip its attack">❄️</span>`
      );
    }
    if (charges.infestation) {
      icons.push(
        `<span class="spell-charge-icon" data-spell-charge="infestation" title="Infestation armed: in your next combat, whenever ANY minion dies a Fragile Swarmer replaces it and attacks immediately">🕷️</span>`
      );
    }
  }
  spellChargesRow.innerHTML = icons.join('');
}

/** The armed-spell descriptions for the charge-icon overlay (the same text
 *  the tooltips carry — one source, no drift). */
const SPELL_CHARGE_OVERLAYS: Record<string, { name: string; emoji: string; effect: string }> = {
  healingRain: {
    name: 'Healing Rain (armed)',
    emoji: '🌧️',
    effect: 'In your next combat, every time one of your minions attacks, all your minions restore 2 HP.',
  },
  acidRain: {
    name: 'Acid Rain (armed)',
    emoji: '🧪',
    effect: 'The next {count} attack(s) deal 1 damage to ALL minions. (The charges are attacks, not combats — they can span several combats.)',
  },
  blizzard: {
    name: 'Blizzard (armed)',
    emoji: '❄️',
    effect: 'In your next combat, each attacking minion has a 25% chance to be Frozen and skip its attack.',
  },
  infestation: {
    name: 'Infestation (armed)',
    emoji: '🕷️',
    effect: 'In your next combat, whenever ANY minion dies a Fragile Swarmer replaces it and attacks immediately.',
  },
};

/**
 * Opens the shared rune-style overlay for a spell-charge icon (the same
 * panel the purchased-runes row uses — name + image + effect): the icon's
 * emoji is drawn as a small SVG data URL in the image slot, and the effect
 * text explains the armed spell. Close conventions are inherited (✕
 * button, backdrop click, Esc — closePurchasedRuneOverlay is in the Esc
 * handler).
 */
function openSpellChargeOverlay(chargeKey: string, chargeCount: number): void {
  const info = SPELL_CHARGE_OVERLAYS[chargeKey];
  if (!info) return;
  purchasedRuneName.textContent = info.name;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'>` +
    `<text x='50' y='54' font-size='56' text-anchor='middle' dominant-baseline='middle'>${info.emoji}</text></svg>`;
  purchasedRuneImage.src = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  purchasedRuneImage.alt = info.name;
  // Acid Rain's remaining charge count is live (charges can be partially
  // spent across combats) — substitute it into the text.
  purchasedRuneEffect.textContent = chargeKey === 'acidRain'
    ? info.effect.replace('{count}', String(chargeCount))
    : info.effect;
  purchasedRuneOverlay.classList.remove('hidden');
}

/**
 * Opens the purchased-rune overlay for the given rune id: art + name +
 * effect (description) from the shared RUNE_DEFINITIONS — the client never
 * computes the effect, it only displays the shared definition for an id
 * the server confirmed was purchased.
 */
function openPurchasedRuneOverlay(runeId: string): void {
  const def = getRuneById(runeId);
  if (!def) return;
  purchasedRuneName.textContent = def.name;
  purchasedRuneImage.src = def.image;
  purchasedRuneImage.alt = def.name;
  purchasedRuneEffect.textContent = def.description;
  purchasedRuneOverlay.classList.remove('hidden');
}

/** Closes the purchased-rune overlay (✕ button, backdrop click or Esc). */
function closePurchasedRuneOverlay(): void {
  purchasedRuneOverlay.classList.add('hidden');
}

// ─── How to Play (wiki) overlay ────────────────────────────────────────────

/**
 * Build the How-to-Play wiki body ONCE (idempotent). All game data comes
 * from the shared single sources of truth (HowToPlay.buildHowToPlayContent),
 * so the page can never drift from the rules. Built lazily on first open —
 * same "build once" convention as the keyword overlay.
 */
function buildHowToPlayOverlay(): void {
  if (howToPlayContent.childElementCount > 0) return; // already built
  buildHowToPlayContent(howToPlayToc, howToPlayContent);
}

function openHowToPlayOverlay(): void {
  buildHowToPlayOverlay();
  howToPlayOverlay.classList.remove('hidden');
  // Reset the scroll to the top on every open (a revisit shouldn't drop the
  // reader mid-page).
  howToPlayBody.scrollTop = 0;
}

function closeHowToPlayOverlay(): void {
  howToPlayOverlay.classList.add('hidden');
}

// ─── In-game Settings overlay (Resign) ────────────────────────────────────

/**
 * Whether the settings menu may be OPENED right now: only inside a live
 * match and only in the PREPARATION phase. The button is hidden during
 * combat (see wireMatchHUD) and this guard is the second layer — the
 * server re-validates the phase on the ResignMatchRequest itself.
 */
function isSettingsActionAllowed(): boolean {
  const match = shell?.gameState.match;
  return !!match?.matchId && !match.gameOver && match.phase === 'preparation';
}

function openSettingsOverlay(): void {
  settingsOverlay.classList.remove('hidden');
}

/**
 * Closes the settings overlay. Called from the ✕ button, backdrop click,
 * Esc, screen changes AND the combat-start hook in onStateChange ("the
 * menu is auto-closed when combat starts if it was open").
 */
function closeSettingsOverlay(): void {
  settingsOverlay.classList.add('hidden');
}

/**
 * Resign the current match (forfeit). Sends the RESIGN INTENTION — the
 * server is authoritative (preparation phase + alive check, elimination,
 * re-pairing, the Elo loss). On a successful send the player is taken
 * straight back to the main landing page (identity preserved — logged-in
 * users keep their username, guests keep their guest ID); the authoritative
 * state update is still broadcast to the remaining players.
 *
 * A rejected resign (e.g. combat started in the same instant) leaves the
 * player in the match: the next queue join / reload re-attaches them to it
 * (the server's "already in a game" guard), so nothing is lost.
 */
function attemptResign(): void {
  const match = shell?.gameState.match;
  if (!match?.matchId || match.gameOver || match.phase !== 'preparation') return;
  closeSettingsOverlay();
  // The TutorialShell implements resignMatch as a no-op (resigning is not a
  // tutorial action) — `sent` stays false and the tour continues untouched.
  const sent = shell?.resignMatch(match.matchId);
  if (sent) {
    // Back to the main landing page (queue view for logged-in users and
    // guests alike — the same funnel the elimination flow uses).
    restartShellKeepingIdentity();
  }
}

// ─── Achievements overlay ─────────────────────────────────────────────────

function closeAchievementsOverlay(): void {
  achievementsOverlay.classList.add('hidden');
}

/**
 * Render the authoritative achievement list. The server already sorted it
 * (unlocked first, then closest to unlocking) and redacted hidden-unlocked
 * entries (name '???', no progress) — the client only renders.
 */
function renderAchievements(list: AchievementInfo[]): void {
  achievementsList.innerHTML = '';
  for (const achievement of list) {
    const row = document.createElement('div');
    row.className =
      'achievement-row' + (achievement.unlocked ? ' unlocked' : achievement.hidden ? ' mystery' : '');

    const icon = document.createElement('span');
    icon.className = 'achievement-icon';
    icon.textContent = achievement.icon;

    const body = document.createElement('div');
    body.className = 'achievement-body';
    const name = document.createElement('div');
    name.className = 'achievement-name';
    name.textContent = achievement.name;
    const desc = document.createElement('div');
    desc.className = 'achievement-desc';
    desc.textContent = achievement.description;
    body.append(name, desc);

    const meta = document.createElement('div');
    meta.className = 'achievement-meta';
    if (achievement.unlocked) {
      const check = document.createElement('div');
      check.className = 'achievement-check';
      check.textContent = '✓ Unlocked';
      meta.appendChild(check);
      if (achievement.unlockedAt) {
        const date = document.createElement('div');
        date.className = 'achievement-progress-text';
        date.textContent = new Date(achievement.unlockedAt).toLocaleDateString();
        meta.appendChild(date);
      }
    } else if (!achievement.hidden) {
      // Locked + revealed: show progress towards the target.
      const text = document.createElement('div');
      text.className = 'achievement-progress-text';
      text.textContent = `${achievement.progress} / ${achievement.target}`;
      meta.appendChild(text);
      const bar = document.createElement('div');
      bar.className = 'achievement-bar';
      const fill = document.createElement('div');
      fill.className = 'achievement-bar-fill';
      fill.style.width = `${Math.min(100, Math.round((achievement.progress / achievement.target) * 100))}%`;
      bar.appendChild(fill);
      meta.appendChild(bar);
    }
    // Hidden + locked: no progress shown (the server redacts it).

    row.append(icon, body, meta);
    achievementsList.appendChild(row);
  }
}

// ─── Stats overlay ─────────────────────────────────────────────────────────

function closeStatsOverlay(): void {
  statsOverlay.classList.add('hidden');
}

/**
 * Static display order for the stats rows (label, key into PlayerStats).
 * The VALUES always come from the authoritative StatsResponse — only the
 * human-readable labels and their order live here.
 */
const STATS_ROWS: ReadonlyArray<{ label: string; key: keyof PlayerStats }> = [
  { label: 'Games Played', key: 'gamesPlayed' },
  { label: 'Games Won', key: 'wins' },
  { label: 'Cards Played', key: 'cardsPlayed' },
  { label: 'Cards Upgraded', key: 'cardsUpgraded' },
  { label: 'Cards Sold', key: 'cardsSold' },
  { label: 'Enemy Cards Killed', key: 'enemyCardsKilled' },
  { label: 'Players Eliminated', key: 'playersEliminated' },
];

/**
 * Render the authoritative player statistics. The server returns the
 * counters (read from the per-user SQLite stats) — the client only formats
 * them into label/value rows.
 */
function renderStats(stats: PlayerStats): void {
  statsList.innerHTML = '';
  for (const rowDef of STATS_ROWS) {
    const row = document.createElement('div');
    row.className = 'stat-row';

    const label = document.createElement('span');
    label.className = 'stat-label';
    label.textContent = rowDef.label;

    const value = document.createElement('span');
    value.className = 'stat-value';
    value.textContent = String(stats[rowDef.key] ?? 0);

    row.append(label, value);
    statsList.appendChild(row);
  }
}

// ─── Toplist overlay ────────────────────────────────────────────────────

function closeToplistOverlay(): void {
  toplistOverlay.classList.add('hidden');
}

/** Builds one rank/username/rating row (shared by the list and the "mine" row). */
function buildToplistRow(entry: ToplistEntry, rankLabel: string): HTMLDivElement {
  const row = document.createElement('div');
  row.className = 'toplist-row';

  const rank = document.createElement('span');
  rank.className = 'toplist-rank';
  rank.textContent = rankLabel;

  const name = document.createElement('span');
  name.className = 'toplist-name';
  name.textContent = displayUsername(entry.username);

  const rating = document.createElement('span');
  rating.className = 'toplist-rating';
  rating.textContent = String(entry.rating);

  row.append(rank, name, rating);
  return row;
}

/**
 * Render the authoritative toplist. `entries` are the Top 100 rows
 * (server-sorted — the client never reorders); `myPlacement` (null for
 * guests) is the logged-in user's own row, pinned at the BOTTOM of the
 * panel so it stays visible even when the user is far outside the Top 100.
 * All values (rank, username, rating) come from the ToplistResponse.
 */
function renderToplist(entries: ToplistEntry[], myPlacement: ToplistEntry | null): void {
  toplistList.innerHTML = '';
  for (const entry of entries) {
    toplistList.appendChild(buildToplistRow(entry, String(entry.rank)));
  }

  if (myPlacement) {
    toplistMine.innerHTML = '';
    const row = buildToplistRow(myPlacement, String(myPlacement.rank));
    // Prefix a "You" badge (the highlighted row styling in CSS marks it as
    // the requester's own placement — rank/username/rating stay as-is).
    const label = document.createElement('span');
    label.className = 'toplist-mine-label';
    label.textContent = 'You';
    row.insertBefore(label, row.firstElementChild);

    toplistMine.appendChild(row);
    toplistMine.classList.remove('hidden');
  } else {
    // Guest (or no placement from the server): no bottom row.
    toplistMine.innerHTML = '';
    toplistMine.classList.add('hidden');
  }
}

// ─── Share overlay ────────────────────────────────────────────────────

function openShareOverlay(): void {
  shareNote.textContent = '';
  shareOverlay.classList.remove('hidden');
}

function closeShareOverlay(): void {
  shareOverlay.classList.add('hidden');
}

/** Build the platform buttons from the shared SHARE_TARGETS catalogue.
 *  Called once at startup (the catalogue is static — adding a platform
 *  is a ShareService change, picked up here automatically). */
function buildShareOverlay(): void {
  for (const target of SHARE_TARGETS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'share-target-btn';
    btn.dataset.shareTarget = target.id;

    const icon = document.createElement('span');
    icon.className = 'share-target-icon';
    icon.textContent = target.icon;
    const label = document.createElement('span');
    label.className = 'share-target-label';
    label.textContent = target.label;
    btn.append(icon, label);

    btn.addEventListener('click', () => {
      void shareToTarget(target.id);
    });
    shareTargetsEl.appendChild(btn);
  }
}

/**
 * Share to one platform: intent URL → new window (noopener). Targets
 * without a documented web intent (TikTok): copy the caption to the
 * clipboard and open the site so the player can paste it into a new
 * post — if the clipboard is unavailable the caption is shown in the
 * note so it can be copied manually.
 */
async function shareToTarget(id: ShareTargetId): Promise<void> {
  const target = getShareTarget(id);
  if (!target) return;
  const appUrl = window.location.href;
  const intentUrl = target.buildShareUrl(appUrl);
  if (intentUrl) {
    openShareWindow(intentUrl);
    return;
  }
  const ok = await copyToClipboard(buildShareCaption(appUrl));
  openShareWindow(target.fallbackOpenUrl ?? appUrl);
  shareNote.textContent = ok
    ? 'Caption copied — paste it into your TikTok post.'
    : `Copy failed. Paste this instead: ${buildShareCaption(appUrl)}`;
}

/** Copy the tagline + app URL (the "Copy link" button). */
async function copyShareLink(): Promise<void> {
  const ok = await copyToClipboard(buildShareCaption(window.location.href));
  shareNote.textContent = ok
    ? 'Link copied!'
    : `Copy failed. Paste this instead: ${buildShareCaption(window.location.href)}`;
}

// ─── Skins overlay ─────────────────────────────────────────────────────────

function closeSkinsOverlay(): void {
  skinsOverlay.classList.add('hidden');
}

/**
 * The small unlock-criteria caption shown beneath LOCKED skins. The text
 * is authoritative (the server resolves it per user — criteria + progress);
 * the client only formats it: measurable progress gets a (X/Y) suffix.
 */
function skinUnlockLabel(skin: SkinInfo): string {
  const criteria = skin.unlockCriteria ?? 'Unlock method coming soon.';
  if (skin.unlockProgress !== undefined && skin.unlockTarget !== undefined) {
    return `${criteria} (${skin.unlockProgress}/${skin.unlockTarget})`;
  }
  return criteria;
}

/**
 * Renders the authoritative skin catalogue (SkinInfo[] from the server)
 * grouped into one section per class. Fully dynamic:
 *  - classes present in the data get a section — known classes in their
 *    canonical order (VALID_MINION_TYPES), unknown (future) classes appended
 *    after them, so a new class needs zero UI changes;
 *  - each skin is a tile with its image, name and state badges;
 *  - locked skins (unlocked === false) are disabled + lock badge + the
 *    server-resolved unlock-criteria caption (small text beneath the name)
 *    — the SERVER decides selectability and the criteria/progress, the
 *    client only renders and forwards the SetSkinRequest intention for
 *    clicks on unlocked, unselected skins.
 */
function renderSkins(skins: SkinInfo[]): void {
  skinsList.innerHTML = '';

  // Group skins by class (catalogue order preserved within each class).
  const byClass = new Map<string, SkinInfo[]>();
  for (const skin of skins) {
    const list = byClass.get(skin.minionType) ?? [];
    list.push(skin);
    byClass.set(skin.minionType, list);
  }

  // Class order: known classes in canonical order, then any classes that
  // appear in the data but are not in VALID_MINION_TYPES yet (future class)
  // — shared pure helper, fully dynamic.
  const classOrder: string[] = orderSkinClasses(byClass.keys());

  for (const type of classOrder) {
    const section = document.createElement('div');
    section.className = 'skin-class-section';

    const key = type as MinionType;
    const title = document.createElement('div');
    title.className = 'skin-class-title';
    const icon = MINION_TYPE_ICONS[key] ?? '❓';
    const name = MINION_TYPE_NAMES[key] ?? type;
    title.textContent = `${icon} ${name}`;
    section.appendChild(title);

    const grid = document.createElement('div');
    grid.className = 'skin-grid';
    for (const skin of byClass.get(type)!) {
      const option = document.createElement('button');
      option.type = 'button';
      option.className =
        'skin-option'
        + (skin.selected ? ' selected' : '')
        + (skin.unlocked ? '' : ' locked');
      option.title = !skin.unlocked
        ? `${skin.name} — locked`
        : skin.selected ? `${skin.name} (current)` : `Use ${skin.name}`;
      option.innerHTML = `
        <img class="skin-preview" src="${skin.imageUrl}" alt="${skin.name}" draggable="false">
        <span class="skin-name">${skin.name}</span>
        ${skin.isDefault ? '<span class="skin-badge">Default</span>' : ''}
        ${skin.unlocked ? '' : `<span class="skin-criteria">${skinUnlockLabel(skin)}</span>`}
        ${skin.unlocked ? '' : '<span class="skin-lock" title="Locked">🔒</span>'}
      `;
      // Locked skins are not selectable (the server would reject them too —
      // defence in depth). Clicking the already-selected skin is a no-op
      // (no redundant round trip).
      option.disabled = !skin.unlocked;
      if (skin.unlocked && !skin.selected) {
        option.addEventListener('click', () => {
          shell?.setSkin(skin.minionType, skin.id);
        });
      }
      grid.appendChild(option);
    }
    section.appendChild(grid);
    skinsList.appendChild(section);
  }
}

// ─── Rune loadout (main page, logged-in users only) ──────────────────────

/**
 * Runes: the READ-ONLY catalogue viewer (main menu) and the in-game rune
 * SHOP. Both render from the SHARED RUNE_DEFINITIONS catalogue — the
 * single source of truth for rune content (the same array that seeds the
 * server's `runes` table, so the client can never drift from what the
 * server validates against). No catalogue data crosses the wire.
 *
 * The viewer is purely presentational (browse/scroll — there is no
 * pre-match rune selection anymore). The shop is the purchase surface:
 * each row is clickable unless the rune is already owned (a rune is
 * buyable ONCE per game — no fixed cap, gold is the constraint) or not
 * affordable right now. Clicking sends the BuyRuneRequest through
 * handUI.buyRune(runeId); the server re-validates the rune, the ownership
 * and the gold, and the open shop re-renders from the authoritative state
 * on the resulting GameStateUpdate (updateBuyRuneButton).
 */

/** Shared row markup (icon + name + description + gold cost [+ badge]). */
function runeRowMarkup(
  rune: { name: string; description: string; image: string; goldCost: number },
  badge: string,
  costHtml?: string
): string {
  return `
      <img src="${rune.image}" alt="" draggable="false">
      <div class="rune-row-info">
        <div class="rune-row-name">${rune.name}</div>
        <div class="rune-row-desc">${rune.description}</div>
      </div>
      <div class="rune-row-meta">
        ${costHtml ?? `<span class="rune-row-cost">${rune.goldCost}g</span>`}
        ${badge}
      </div>
    `;
}

/**
 * The shop's cost cell: the CURRENT price, or — while a RuneFeast is in
 * play — the discounted price with the base price struck through
 * ("7g <s>10g</s>"). The discounted price is the shared
 * getRuneGoldCost (floored 25% off) — the same value the server charges.
 */
function runeCostHtml(rune: { goldCost: number }, price: number): string {
  if (price === rune.goldCost) {
    return `<span class="rune-row-cost">${price}g</span>`;
  }
  return `<span class="rune-row-cost">${price}g <s class="rune-row-cost-base">${rune.goldCost}g</s></span>`;
}

/**
 * Opens the READ-ONLY rune viewer overlay (main menu, everyone — guests
 * included): a vertically scrollable list of ALL runes (icon + name +
 * description + gold cost). Nothing to click — it is a reference.
 */
function openRuneViewer(): void {
  runeViewerContext.textContent = 'Every rune in the game — read-only reference';
  renderRuneViewer();
  runeViewerOverlay.classList.remove('hidden');
}

/** Closes the rune viewer overlay (✕ button, backdrop click or Esc). */
function closeRuneViewer(): void {
  runeViewerOverlay.classList.add('hidden');
}

/** Renders the viewer list: every rune in the shared catalogue, in order. */
function renderRuneViewer(): void {
  runeViewerList.innerHTML = '';
  for (const rune of RUNE_DEFINITIONS) {
    const row = document.createElement('div');
    row.className = 'rune-row rune-row-static';
    row.innerHTML = runeRowMarkup(rune, '');
    runeViewerList.appendChild(row);
  }
}

/**
 * Opens the READ-ONLY spell viewer overlay (main menu, everyone — guests
 * included): the shared SPELL_DEFINITIONS catalogue GROUPED by spell level
 * (Level 1 / Level 2 / …), each group SORTED by gold value (the stable
 * sort keeps the catalogue order within a price tier). Nothing to click —
 * it is a reference.
 */
function openSpellViewer(): void {
  spellViewerContext.textContent = 'Every spell in the game — read-only reference';
  renderSpellViewer();
  spellViewerOverlay.classList.remove('hidden');
}

/** Closes the spell viewer overlay (✕ button, backdrop click or Esc). */
function closeSpellViewer(): void {
  spellViewerOverlay.classList.add('hidden');
}

/**
 * Renders the spell viewer list: one group per spell level (ascending),
 * each group a header + the spells of that level sorted by gold value
 * (cheapest first — the stable sort keeps the catalogue order within a
 * price tier, like the rune shop).
 */
function renderSpellViewer(): void {
  spellViewerList.innerHTML = '';
  const levels = [...new Set(SPELL_DEFINITIONS.map((s) => s.level))].sort((a, b) => a - b);
  for (const level of levels) {
    const header = document.createElement('div');
    header.className = 'spell-viewer-level';
    header.textContent = `Level ${level}`;
    spellViewerList.appendChild(header);
    const spells = SPELL_DEFINITIONS.filter((s) => s.level === level)
      .sort((a, b) => a.sellValue - b.sellValue);
    for (const spell of spells) {
      const row = document.createElement('div');
      row.className = 'spell-row spell-row-static';
      row.innerHTML = spellRowMarkup(spell);
      spellViewerList.appendChild(row);
    }
  }
}

/** One spell viewer row: name + target badge + description + gold value. */
function spellRowMarkup(spell: {
  name: string;
  description: string;
  target: 'minion' | 'battlefield';
  sellValue: number;
}): string {
  return `
      <div class="spell-row-info">
        <div class="spell-row-name">${spell.name}
          <span class="spell-row-target">${spell.target === 'minion' ? 'targets a minion' : 'battlefield'}</span>
        </div>
        <div class="spell-row-desc">${spell.description}</div>
      </div>
      <div class="spell-row-meta">
        <span class="spell-row-cost">${spell.sellValue}g</span>
      </div>
    `;
}

/**
 * Opens the in-game rune shop overlay (the 🪬 Buy Rune button —
 * preparation phase). Lists EVERY rune with its gold price; clicking a
 * row buys it (handUI.buyRune(runeId) — the server re-validates the rune,
 * the ownership and the gold).
 */
function openRuneShop(): void {
  renderRuneShop();
  runeShopOverlay.classList.remove('hidden');
}

/** Closes the rune shop overlay (✕ button, backdrop click, Esc or combat start). */
function closeRuneShop(): void {
  runeShopOverlay.classList.add('hidden');
}

/**
 * Renders the shop list from the SHARED catalogue + the AUTHORITATIVE
 * player state (purchased runes + gold + battlefield from PlayerData).
 * The battlefield drives both modifiers (the same shared RunePurchaseRules
 * the server validates with): the effective cap (base + one per RuneKeeper
 * in play) and the 25% RuneFeast discount on every price. Row states:
 *  - Owned (grayed out + badge) — the rune is already active this game;
 *  - Limit (grayed out + badge) — the cap is reached; the row unlocks if a
 *    RuneKeeper comes into play (the cap is dynamic);
 *  - dimmed (locked style) — not affordable with the current gold;
 *  - plain — buyable: a click sends the BuyRuneRequest.
 * The function is re-invoked on every state change while the shop is open
 * (updateBuyRuneButton) so the badges always mirror the server.
 */
function renderRuneShop(): void {
  const me = shell?.gameState.match.players.find((p) => p.id === shell?.playerId);
  // COOP: `purchased` is the SHARED team union (the Owned badges) and the
  // cap counts against that same union (the team's shared budget — the
  // server's purchase gate uses the team union too).
  const purchased = new Set(me?.runes?.purchased ?? []);
  const gold = me?.gold ?? 0;
  // The battlefield modifiers, read over the broadcast state (the client
  // mirrors the server; it never prices or validates anything itself).
  // COOP: the TEAM's union — a RuneKeeper / RuneFeast on EITHER member's
  // board applies to both (the user's team-wide rule; matches the server's
  // buyRune, which prices and caps over the union).
  const matchState = shell?.gameState.match;
  const coopInfo = matchState?.coop;
  const inPlayMembers =
    coopInfo && coopInfo.mode !== 'normal'
      ? (matchState?.players ?? []).filter(
          (p) => p.id === coopInfo.hostId || p.id === coopInfo.teammateId
        )
      : [me].filter((p): p is NonNullable<typeof p> => !!p);
  const inPlay = inPlayMembers.flatMap((m) => (m?.battlefield ?? []).map((b) => b.abilities));
  const runeCap = getEffectiveRuneCap(inPlay);
  const feastActive = isRuneFeastActive(inPlay);
  const capReached = purchased.size >= runeCap;

  runeShopContext.textContent = capReached
    ? `You can buy ${runeCap} runes per game — you have reached the cap`
    : feastActive
      ? `RuneFeast in play — all runes are 25% off (prices floored). You have ${gold}g`
      : `Each rune can be bought once, at its gold price — you have ${gold}g`;

  runeShopList.innerHTML = '';
  // The shop lists runes CHEAPEST FIRST (sorted by gold cost — the stable
  // sort keeps the catalogue order within a price tier). The read-only
  // main-menu viewer keeps the raw catalogue order (it is a reference, not
  // a purchase surface).
  const sortedRunes = [...RUNE_DEFINITIONS].sort((a, b) => a.goldCost - b.goldCost);
  for (const rune of sortedRunes) {
    const owned = purchased.has(rune.id);
    const atCap = !owned && capReached;
    // The CURRENT price (25% off, floored, while a RuneFeast is in play).
    const price = getRuneGoldCost(rune, inPlay);
    const affordable = !owned && !capReached && gold >= price;
    const row = document.createElement('div');
    row.className = 'rune-row';
    if (owned || atCap || !affordable) row.classList.add('rune-row-locked');
    row.title = owned
      ? `${rune.name} — already owned (active for the rest of the game)`
      : atCap
        ? `Cap reached — you can buy ${runeCap} runes per game (a RuneKeeper in play raises it)`
        : affordable
          ? `Buy ${rune.name} for ${price}g${feastActive ? ' (25% off)' : ''} (active for the rest of the game)`
          : `Not enough gold — ${rune.name} costs ${price}g${feastActive ? ' (25% off)' : ''}`;
    row.innerHTML = runeRowMarkup(
      rune,
      owned
        ? '<span class="rune-row-badge">Owned</span>'
        : atCap
          ? '<span class="rune-row-badge">Limit</span>'
          : '',
      feastActive ? runeCostHtml(rune, price) : undefined
    );
    if (affordable) {
      row.addEventListener('click', () => {
        // SOUND: buying a rune (BuyRune.wav).
        soundService.play('buyRune');
        handUI?.buyRune(rune.id);
        // The resulting GameStateUpdate re-renders this still-open shop
        // (the Owned badge + the gold line) — no local mutation (the
        // server is authoritative for the purchase).
      });
    }
    runeShopList.appendChild(row);
  }
}

/**
 * Resets the local rune state (identity switch / logout): closes both
 * overlays. The catalogue itself is the static shared RUNE_DEFINITIONS —
 * nothing else to reset.
 */
function resetRuneState(): void {
  closeRuneViewer();
  closeSpellViewer();
  closeRuneShop();
}

/**
 * Shows the unlock toast (auto-hides after a few seconds). The title
 * defaults to the achievement wording — other unlock sources (e.g. the
 * 7-day streak skin reward) pass their own.
 */
function showUnlockToast(title: string, icon: string, name: string): void {
  achievementToastTitle.textContent = title;
  achievementToastIcon.textContent = icon;
  achievementToastName.textContent = name;
  achievementToast.classList.remove('hidden');
  if (achievementToastTimer) clearTimeout(achievementToastTimer);
  achievementToastTimer = setTimeout(() => {
    achievementToast.classList.add('hidden');
  }, 4000);
}

/** Shows the achievement-unlock toast (auto-hides after a few seconds). */
function showAchievementToast(icon: string, name: string): void {
  showUnlockToast('Achievement Unlocked', icon, name);
}

let achievementToastTimer: ReturnType<typeof setTimeout> | null = null;

// ─── Adaptive card-row scaling (mobile fit + desktop fill) ──────────────
// The hand row has fixed side columns, so on a narrow landscape phone the
// fixed-size cards overflow the frame (past ~4-5 cards they are pushed out
// of view). updateHandCardScale measures the available #hand width and the
// live (unzoomed) card width + gap, asks computeHandCardScale for the
// largest uniform scale that fits, and publishes it on #hand-area as
// --hand-card-scale. The CSS then zooms every .hand-card (and the gap, the
// strip height and the hand area's bottom padding) by that factor — `zoom`
// scales the whole card subtree uniformly, so the 5:7 aspect ratio, the
// corner stat badges and every font size scale proportionally (see
// HandScaleService + the .hand-card / #hand rules).
//
// DESKTOP: the same mechanism scales rows UP (maxScaleForBaseWidth grants
// CARD_MAX_SCALE for the desktop card geometry, 1 for the mobile one), so
// a full 7-card board — and a full hand — fills the viewport width instead
// of sitting small in the middle. The battlefield and combat rows use the
// same math (updateBoardCardScale / updateCombatCardScale) with their own
// custom properties. Mobile geometry always caps at 1 → unchanged layout.
let _handCardScale = 1;
let _boardCardScale = 1;
let _combatCardScale = 1;
let _handScaleListenersWired = false;

// ─── Base-width probes (zoom-independent card measurement) ─────────────────
// The row cards are ZOOMED (zoom: var(--row-card-scale)), and how
// `offsetWidth` reflects that zoom is ENGINE-DEPENDENT (Chromium reports
// the PRE-zoom width, other engines the post-zoom one). "Measure the zoomed
// card and divide by the current scale" therefore is NOT a stable fixed
// point: on Chromium the de-zoomed base wobbles (100÷s, not 100) and the
// refit multiplies the scale by the fit factor on every re-measure — the
// rows pulse large/small on every ~1 s timer tick while they hold cards
// (the 2026-09-29 playtest bug, round 2; round 1 fixed the
// getComputedStyle variant, which carries the zoom there).
//
// The probes sidestep the ambiguity entirely: off-screen invisible CLONES
// of each card class with zoom pinned to 1. A zoom:1 element has no zoom to
// de-zoom — its offsetWidth is the CSS base width under BOTH engine
// behaviors. The clones live on <body>, so the SAME selectors, media bands
// (70px landscape) and :fullscreen mobile rules (70px) apply to them as to
// the live cards — the current band's base is always read live. The combat
// clone sits in a .combat-minions wrapper whose .wide class is synced to
// the live rows, so the COOP 56px-wide cards are measured too.
let _scaleProbeRoot: HTMLElement | null = null;
let _probeHand: HTMLElement | null = null;
let _probeBoard: HTMLElement | null = null;
let _probeCombat: HTMLElement | null = null;
let _probeCombatWrap: HTMLElement | null = null;

function ensureScaleProbes(): void {
  if (_scaleProbeRoot) return;
  _scaleProbeRoot = document.createElement('div');
  _scaleProbeRoot.id = 'scale-probe-root';
  _scaleProbeRoot.style.cssText =
    'position:fixed;left:-10000px;top:0;visibility:hidden;pointer-events:none;';
  _probeHand = document.createElement('div');
  _probeHand.className = 'hand-card';
  _probeHand.style.zoom = '1';
  _probeBoard = document.createElement('div');
  _probeBoard.className = 'battlefield-minion';
  _probeBoard.style.zoom = '1';
  _probeCombatWrap = document.createElement('div');
  _probeCombatWrap.className = 'combat-minions';
  _probeCombatWrap.style.zoom = '1';
  _probeCombat = document.createElement('div');
  _probeCombat.className = 'combat-minion';
  _probeCombat.style.zoom = '1';
  _probeCombatWrap.appendChild(_probeCombat);
  _scaleProbeRoot.append(_probeHand, _probeBoard, _probeCombatWrap);
  document.body.appendChild(_scaleProbeRoot);
}

/** The current base (unzoomed) width (px) of one card of the given row. */
function probeBaseCardWidth(row: 'hand' | 'board' | 'combat'): number {
  ensureScaleProbes();
  if (row === 'combat' && _probeCombat && _probeCombatWrap) {
    // Sync the COOP-wide override (56px cards) onto the probe's wrapper.
    const wide =
      combatMinionsA.classList.contains('wide')
      || combatMinionsB.classList.contains('wide');
    _probeCombatWrap.classList.toggle('wide', wide);
    return _probeCombat.offsetWidth;
  }
  return row === 'hand' ? (_probeHand?.offsetWidth ?? 0) : (_probeBoard?.offsetWidth ?? 0);
}

/**
 * MOBILE LAYOUT (task 185): the viewport bands that ARE the mobile layout —
 * the max-width:768px media band (portrait phones + narrow windows) OR the
 * compact phone-landscape band (landscape, <= 1024 wide, <= 500 tall —
 * the same gate as the CSS band for the 56px COOP cards). The hand/board/
 * combat rows fit the ACTUAL card count here and may scale UP to
 * CARD_MAX_SCALE (a lone card becomes large — "on mobile cards should
 * update their scaling based on the number of cards in the hand"). Real
 * desktop viewports (wide AND tall) keep the reference-count fit (strip
 * stability).
 */
function isMobileLayout(): boolean {
  if (typeof window === "undefined") return false;
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (w <= 768) return true;
  return w > h && w <= 1024 && h <= 500;
}

function updateHandCardScale(): void {
  // OVERDRAW GHOSTS (task 201) are .hand-card for styling but are NOT part
  // of the hand — exclude them so the 8 real cards never rescale while a
  // ghost card is sliding in / bursting.
  const cards = handContainer.querySelectorAll<HTMLElement>('.hand-card:not([data-overdraw-ghost])');
  const n = cards.length;
  // NO empty-row reset: an empty hand keeps the REFERENCE fit, so the
  // strip (152 × scale) is the SAME size with cards and without (playtest:
  // "hand changes size when holding cards vs empty" — the old n=0 → 1
  // reset shrank the strip every time the hand emptied).
  const available = handContainer.clientWidth;
  // Base (unzoomed) card width from the zoom:1 PROBE (see
  // probeBaseCardWidth): measuring a zoomed card and de-zooming it
  // arithmetically is engine-dependent (offsetWidth is pre-zoom on
  // Chromium) and oscillates the fit — the probe always reads the base.
  const baseWidth = probeBaseCardWidth('hand');
  // Base gap: the CSS gap is calc(base * --hand-card-scale), so the
  // computed px value must be divided back by the current scale.
  const baseGap =
    parseFloat(getComputedStyle(handContainer).columnGap) / _handCardScale;
  if (!(available > 0) || !(baseWidth > 0)) {
    return; // hand hidden (e.g. during combat) — keep the last scale
  }
  // DESKTOP rows scale UP to fill the viewport (CARD_MAX_SCALE cap via the
  // base-width gate); MOBILE rows (task 185) scale by the ACTUAL count,
  // BOUNDED (task 187 — playtest: "cards on mobile are now WAY TOO LARGE"):
  // the mobile max card size is the size at MOBILE_HAND_MAX_FIT_COUNT (5)
  // cards — the 5-card fit scale is the cap, so a 1-4 card hand never
  // grows past the 5-card size and a 6+ card hand only shrinks from it.
  const maxScale = maxScaleForBaseWidth(baseWidth, HAND_CARD_BASE_WIDTH);
  const mobileLayout = isMobileLayout();
  const fitCount = mobileLayout ? n : Math.max(HAND_CARD_REF_COUNT, n);
  const mobileMaxScale = mobileLayout
    ? computeHandCardScale(available * HAND_CARD_FIT_FACTOR, baseWidth, baseGap, MOBILE_HAND_MAX_FIT_COUNT, CARD_MAX_SCALE)
    : maxScale;
  // "A bit smaller" (playtest): the hand fills 90% of its row — the board
  // and combat rows keep the exact 100% fill.
  const scale = computeHandCardScale(
    available * HAND_CARD_FIT_FACTOR, baseWidth, baseGap, fitCount, mobileMaxScale
  );
  if (scale === _handCardScale) {
    return;
  }
  _handCardScale = scale;
  // Published on #hand-area (NOT #hand) so the hand area's own bottom
  // padding calc (badge clearance under the scaled cards) sees the value
  // too — #hand and the cards inherit it.
  handArea.style.setProperty(HAND_CARD_SCALE_PROPERTY, String(scale));
  // STRIP scale (task 188 — the hand strip is a STATIC height, unaffected
  // by the card count): mobile publishes the scale-UP CAP (the 5-card fit
  // = the "1 card in the hand" size) so the strip keeps that height at
  // every count — including the EMPTY hand (the n=0 → identity scale-1
  // reset used to balloon the strip back to its base height); desktop
  // publishes the (already constant) reference-fit scale.
  handArea.style.setProperty(HAND_STRIP_SCALE_PROPERTY, String(mobileLayout ? mobileMaxScale : scale));
  // The four hand-plate buttons zoom with the cards (max(1, scale) — a
  // 2× card row with 100px plates looked dwarfed, playtest "UI buttons in
  // the hand look really small"; scale ≤ 1 keeps them byte-identical).
  handArea.style.setProperty('--hand-btn-scale', String(Math.max(1, scale)));
}

/** Battlefield (preparation board) fit: a full 7-card board fills the
 *   viewport width on desktop (same zoom mechanism, --board-card-scale on
 *   #battlefield). The available width is the battlefield's content box
 *   (#battlefield is full-width on desktop — see the min-width:769px rule
 *   in index.html; its 8px padding is subtracted). */
function updateBoardCardScale(): void {
  const cards = battlefield.querySelectorAll<HTMLElement>('.battlefield-minion');
  const n = cards.length;
  // NO empty-row reset — see updateHandCardScale (the row size is constant
  // while cards come and go).
  // #battlefield's 8px horizontal padding leaves the content box for cards.
  const available = battlefield.clientWidth - 16;
  // Base width from the zoom:1 probe (see probeBaseCardWidth — the
  // de-zoom arithmetic oscillates the fit on Chromium).
  const baseWidth = probeBaseCardWidth('board');
  const baseGap =
    parseFloat(getComputedStyle(battlefield).columnGap) / _boardCardScale;
  if (!(available > 0) || !(baseWidth > 0)) {
    return; // battlefield hidden (e.g. during combat) — keep the last scale
  }
  const maxScale = maxScaleForBaseWidth(baseWidth, BOARD_CARD_BASE_WIDTH);
  // DESKTOP: REFERENCE count (the row size is constant while cards come
  // and go); MOBILE layout: the ACTUAL count, BOUNDED (task 187 —
  // playtest: "cards on the battlefield are now way too large"): the
  // mobile max card size is the size at a FULL 7-card board — the 7-card
  // fit scale is the cap, so a 1-6 minion board never grows past the
  // 7-minion size and a full board sits exactly at it.
  const mobileLayout = isMobileLayout();
  const fitCount = mobileLayout ? n : Math.max(BOARD_CARD_REF_COUNT, n);
  const mobileMaxScale = mobileLayout
    ? computeHandCardScale(available, baseWidth, baseGap, BOARD_CARD_REF_COUNT, CARD_MAX_SCALE)
    : maxScale;
  const scale = computeHandCardScale(available, baseWidth, baseGap, fitCount, mobileMaxScale);
  if (scale === _boardCardScale) {
    return;
  }
  _boardCardScale = scale;
  battlefield.style.setProperty(BOARD_CARD_SCALE_PROPERTY, String(scale));
}

/** Combat board fit: a full 7-card row fills the viewport width on
 *   desktop (same zoom mechanism, --combat-card-scale on #combat-board).
 *   Both rows share ONE scale — the fit uses the LARGER row (COOP rows
 *   carry up to 10 minions) so the wide row never overflows. */
function updateCombatCardScale(): void {
  const cardsA = combatMinionsA.querySelectorAll<HTMLElement>('.combat-minion');
  const cardsB = combatMinionsB.querySelectorAll<HTMLElement>('.combat-minion');
  const n = Math.max(cardsA.length, cardsB.length);
  // NO empty-row reset — see updateHandCardScale (the row size is constant
  // while cards come and go).
  // #combat-board's 16px horizontal padding leaves the content box for the
  // rows (the .combat-side columns are full-width and center their rows).
  const available = combatBoard.clientWidth - 32;
  const first = (cardsA[0] ?? cardsB[0])!;
  // Base width from the zoom:1 probe (see probeBaseCardWidth — the
  // de-zoom arithmetic oscillates the fit on Chromium).
  const baseWidth = probeBaseCardWidth('combat');
  const firstRow = cardsA.length > 0 ? combatMinionsA : combatMinionsB;
  const baseGap =
    parseFloat(getComputedStyle(firstRow).columnGap) / _combatCardScale;
  if (!(available > 0) || !(baseWidth > 0)) {
    return; // combat hidden — keep the last scale
  }
  const maxScale = maxScaleForBaseWidth(baseWidth, COMBAT_CARD_BASE_WIDTH);
  // DESKTOP: REFERENCE count + the actual LARGER row (COOP 10-wide) — the
  // scale is constant during the combat (cards dying never re-scale the
  // row) and a full row fills the width; MOBILE layout: the ACTUAL larger
  // row, BOUNDED (task 188 — playtest: "minions are still too large during
  // combat"): the mobile max combat card size is the size at a full
  // 7-card row (COMBAT_CARD_REF_COUNT) — the 7-row fit scale is the cap, so
  // a 1-6 minion row never grows past the full-row size (the same bound
  // as the preparation board; a COOP 10-wide row shrinks from it).
  const mobileLayout = isMobileLayout();
  const fitCount = mobileLayout ? n : Math.max(COMBAT_CARD_REF_COUNT, n);
  const mobileMaxScale = mobileLayout
    ? computeHandCardScale(available, baseWidth, baseGap, COMBAT_CARD_REF_COUNT, CARD_MAX_SCALE)
    : maxScale;
  const scale = computeHandCardScale(available, baseWidth, baseGap, fitCount, mobileMaxScale);
  if (scale === _combatCardScale) {
    return;
  }
  _combatCardScale = scale;
  combatBoard.style.setProperty(COMBAT_CARD_SCALE_PROPERTY, String(scale));
}

/** Registers the resize/orientation listeners exactly once (wireHandUI
 *   runs per match lifecycle, so it must stay idempotent). */
function ensureHandScaleListeners(): void {
  if (_handScaleListenersWired) {
    return;
  }
  _handScaleListenersWired = true;
  window.addEventListener('resize', updateHandCardScale);
  window.addEventListener('orientationchange', updateHandCardScale);
  window.addEventListener('resize', updateBoardCardScale);
  window.addEventListener('orientationchange', updateBoardCardScale);
  window.addEventListener('resize', updateCombatCardScale);
  window.addEventListener('orientationchange', updateCombatCardScale);
}

function wireHandUI(): void {
  ensureHandScaleListeners();
  handUI?.onRenderChange((data: HandUIRenderData) => {
    handCount.textContent = String(data.handSize);

    // CARD FX EVENTS (task 186, consumed through CardFxEvents — task 196):
    // one-shot events attached to the latest GameStateUpdate — 'overdraw'
    // (RED CARD-LIKE EXPLOSION over the hand — ANY overdraw, rune or not)
    // and 'cardDestroyed' (BURNING PARTICLES at the card's last position).
    // Each server batch is consumed exactly once (reference guard). The
    // rects are captured NOW — before renderTeammateOverlay / the main
    // rebuild below remove the destroyed card's element from the DOM.
    _lastCardEventsRef = consumeCardFxEvents(
      shell?.gameState.match.cardEvents,
      _lastCardEventsRef,
      { fx: getFx(), play: (n) => soundService.play(n) },
      {
        handEl: handContainer,
        myId: shell?.playerId ?? null,
        handScale: _handCardScale,
        teammateId: getTeammatePlayerData()?.id ?? null,
        teammateHandEl: _teammateOverlayOpen ? teammateHandEl : null,
        teammateOverlayOpen: _teammateOverlayOpen,
      },
      (instanceId) =>
        document.querySelector<HTMLElement>(
          `.hand-card[data-instance-id="${CSS.escape(instanceId)}"]`
        ),
    );

    // COOP teammate viewer: while the overlay is open, keep the teammate's
    // hand + board live on every state change (this callback fires on every
    // authoritative broadcast — the same flow that re-renders the viewer's
    // own hand).
    if (_teammateOverlayOpen) {
      renderTeammateOverlay();
    }

    // Keep the Buy buttons (cost + enabled state + rune offer) fresh on
    // every state change (gold can change without the hand IDs changing)
    updateBuyCardButton(data);
    updateBuyRuneButton(data);

    // Only rebuild DOM if hand cards actually changed (avoid rebuilding on timer ticks)
    const currentIds = data.cards.map(c => c.instanceId);
    if (arraysEqual(currentIds, _lastHandCardIds)) {
      // No DOM rebuild, but re-measure the fit scale anyway (the hand can
      // reappear after combat, or the viewport changed while it was hidden).
      updateHandCardScale();
      // OVERDRAW GHOSTS (task 201): an overdraw draw does NOT change the
      // hand IDs (the card never entered the hand) — this is the path that
      // spawns the ghost when no rebuild happens.
      queueOverdrawGhosts(shell?.gameState.match.cardEvents, shell?.playerId ?? null);
      return;
    }
    const newCardIds = new Set(currentIds.filter((id) => !_lastHandCardIds.includes(id)));
    _lastHandCardIds = [...currentIds];

    handContainer.innerHTML = '';
    for (let i = 0; i < data.cards.length; i++) {
      const card = data.cards[i];
      const el = createHandCardElement(card);
      // Double-click / double-tap the card body to play it — replaces the
      // (now hidden) Play button, mirroring the battlefield's
      // dblclick-to-upgrade gesture. Presses that land on the (hidden)
      // button itself are left to the button's own handler. The server
      // stays authoritative and rejects plays outside preparation. SPELLS:
      // a double-click CASTS a field spell (the drag stays the only path
      // for targeted spells) — the release position is the card's center
      // (the star explosion plays there on the cast confirmation).
      el.addEventListener('dblclick', (e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        e.preventDefault();
        if (card.minionType === SPELL_MINION_TYPE) {
          const spec = getSpellDefinition(card.baseMinionId);
          if (spec?.target === 'battlefield') {
            const r = el.getBoundingClientRect();
            _pendingSpellCastPos = r.width > 0
              ? { x: r.left + r.width / 2, y: r.top + r.height / 2 }
              : null;
            handUI?.playSpellCard(card.instanceId);
            return;
          }
          return; // targeted: drag onto a minion is the only cast path
        }
        handUI?.playCard(card.instanceId);
      });
      // Set up drag from hand to battlefield (desktop + mobile touch).
      // The card identity routes SPELL drags: a spell casts on board
      // release (onPlaySpell) instead of playing (onPlayCard).
      if (dragAndDropService) {
        dragAndDropService.makeHandCardDraggable(el, card.instanceId, i, {
          baseMinionId: card.baseMinionId,
          minionType: card.minionType,
        });
      }
      handContainer.appendChild(el);
      // FX: newly drawn cards rise into place. The class is a ONE-SHOT
      // trigger — remove it the moment the slide-in finishes: a lingering
      // class is deep-cloned by every copy of the card (the drag ghost
      // replays the 170px slide at the pointer, offsetting the dragged
      // card from the cursor — the rightmost card, the latest draw,
      // carries it).
      if (newCardIds.has(card.instanceId)) {
        retriggerClass(el, 'fx-card-draw');
        el.addEventListener('animationend', () => el.classList.remove('fx-card-draw'), { once: true });
      }
    }

    // OVERDRAW GHOSTS (task 201): queued AFTER the rebuild so the ghost
    // element survives it (the rebuild wipes handContainer); several
    // overdraws play sequentially (draw → destroy → next → …).
    queueOverdrawGhosts(shell?.gameState.match.cardEvents, shell?.playerId ?? null);

    // SPELL CAST FX: detect a spell card that VANISHED from the hand since
    // the previous render. One action arrives per state update, so the
    // gold delta is unambiguous: a vanished spell is SOLD when the gold rose
    // by its sell value, and CAST otherwise (the effect went into play —
    // queue the magic burst; wireBattlefieldUI consumes _pendingSpellFx on
    // the same update, after the board rebuild).
    if (_lastSpellDiffHandSpells.size > 0) {
      const meGold = shell?.gameState.match.players.find(p => p.id === shell?.playerId)?.gold ?? null;
      for (const [spellId, spellBaseId] of _lastSpellDiffHandSpells) {
        if (!data.cards.some(c => c.instanceId === spellId)) {
          const sellValue = getSpellDefinition(spellBaseId)?.sellValue ?? 0;
          const goldDelta = _lastSpellDiffGold !== null && meGold !== null ? meGold - _lastSpellDiffGold : 0;
          if (goldDelta < sellValue) {
            // Preserve the target recorded at release time (onPlaySpell) —
            // the Ban Hammer's FX plays over the target minion.
            _pendingSpellFx = {
              baseMinionId: spellBaseId,
              targetInstanceId: _pendingSpellFx?.baseMinionId === spellBaseId ? _pendingSpellFx.targetInstanceId : undefined,
            };
          }
        }
      }
    }
    {
      const nowSpells = new Map<string, string>();
      for (const c of data.cards) {
        if (c.minionType === SPELL_MINION_TYPE) nowSpells.set(c.instanceId, c.baseMinionId);
      }
      _lastSpellDiffHandSpells = nowSpells;
      const meGold = shell?.gameState.match.players.find(p => p.id === shell?.playerId)?.gold ?? null;
      if (meGold !== null) _lastSpellDiffGold = meGold;
    }

    // Fit the (re)built hand to the available row width (mobile/landscape).
    updateHandCardScale();
  });
}

// ─── Wire BattlefieldUI ───────────────────────────────────────────────────

function wireBattlefieldUI(): void {
  // Set up the battlefield drop zone ONCE per match lifecycle (not per-render).
  // The drop zone lives on the container, which persists across renders.
  // Re-adding listeners each render accumulates stale closures that break drag/drop
  // when the battlefield is empty (old closures reference dead shell state).
  if (dragAndDropService && !_battlefieldDropZoneSetup) {
    _battlefieldDropZoneSetup = true;

    // Replace battlefield with a fresh clone to strip any accumulated event listeners
    // from previous match lifecycles (e.g., after initShellAndComponents).
    // The clone preserves ID, classes, and inline styles but has zero event listeners.
    const freshBattlefield = battlefield.cloneNode(false) as HTMLElement;
    battlefield.parentElement?.replaceChild(freshBattlefield, battlefield);
    battlefield = freshBattlefield;

    dragAndDropService.setupBattlefieldDropZone(battlefield,
      () => shell?.gameState.match.players
        .find(p => p.id === shell?.playerId)?.hand?.map(h => h.instanceId) ?? [],
      [], // currentBattlefieldIds is unused by the drop zone logic
    );
  }

  battlefieldUI?.onRenderChange((data: BattlefieldUIRenderData) => {
    // Only rebuild DOM if battlefield minions actually changed (avoid rebuilding on timer ticks)
    // Check both IDs AND stats hash — stats can change (e.g., upgrade) without ID change.
    // Player gold is part of the check as well: the upgrade-affordability
    // indicator on each minion depends on it, and gold can change without any
    // minion changing (e.g. buying a card mid-preparation).
    const currentIds = data.minions.map(m => m.instanceId);
    const currentStatsHash = getBattlefieldStatsHash(data.minions);
    // Current player's gold (upgrade affordability check + indicator)
    const me = shell?.gameState.match.players
      .find(p => p.id === shell?.playerId);
    const playerGold = me?.gold ?? 0;
    // Grandmaster-aware upgrade cap for the per-card indicator — the same
    // mirror as UpgradeChoiceUI.currentMaxUpgrades (the server re-validates
    // authoritatively). Changes in the Grandmaster count always coincide
    // with an id change (play/sell) or a stats-hash change (upgrade), so
    // the rebuild guard above already covers it.
    const maxUpgrades = GameConfig.maxUpgrades
      + (me?.battlefield ?? []).filter(m => (m.abilities ?? []).includes(GRANDMASTER_ABILITY_ID)).length;
    // SPELL CAST FX (field spells — Healing Rain): consume BEFORE the
    // rebuild guard — a field spell cast changes NO board state, so the
    // guard would return early and the burst would never play. The board
    // center is stable (no rebuild needed).
    if (_pendingSpellFx && getSpellDefinition(_pendingSpellFx.baseMinionId)?.target === 'battlefield') {
      // ENRICHMENT (task 197): the cast pays GOLD — a GOLDEN RAIN of coins
      // over the ENTIRE playfield (screen-wide, no board anchor) + the
      // coin sound. No mystic burst / no board flash (the rain is the
      // cast's signature).
      if (_pendingSpellFx.baseMinionId === ENRICHMENT_SPELL_ID) {
        _pendingSpellCastPos = null;
        getFx().burstAt(0, 0, 'goldRain');
        soundService.play('apply');
        _pendingSpellFx = null;
      } else {
        // EXPLODING MYSTIC at the RELEASE position (the cast card's center
        // for button/double-click casts; the board center is the fallback)
        // + the whole PLAYFIELD flashes baby blue/yellow (the cast is
        // board-wide — sells never reach this site, so only casts flash).
        // (Playtest: battlefield spells get the mystic explosion, NOT the
        // star — the star stays with the minion-targeting casts below.)
        const pos = _pendingSpellCastPos;
        _pendingSpellCastPos = null;
        if (pos) {
          getFx().burstAt(pos.x, pos.y, 'mystic', { scale: _boardCardScale });
        } else {
          const r = battlefield.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) {
            getFx().burstAt(r.left + r.width / 2, r.top + r.height / 2, 'mystic', { scale: _boardCardScale });
          }
        }
        flashBattlefield();
        _pendingSpellFx = null;
      }
    }

    // MINION-TARGET SPELL CAST FX (Ban Hammer — task 196): a minion-target
    // cast that changes NO board state (Ban Hammer — the target is
    // untouched, no stats, no gold) would hit the rebuild guard's early
    // return and NEVER play its FX. Consume it HERE (the target element is
    // the existing one — no rebuild needed) before the guard. Board-
    // CHANGING minion spells keep the post-rebuild site (fresh elements).
    if (
      _pendingSpellFx
      && _pendingSpellFx.baseMinionId !== ''
      && getSpellDefinition(_pendingSpellFx.baseMinionId)?.target === 'minion'
      && arraysEqual(currentIds, _lastBattlefieldMinionIds)
      && currentStatsHash === _lastBattlefieldMinionStatsHash
      && playerGold === _lastBattlefieldPlayerGold
    ) {
      const pos = _pendingSpellCastPos;
      _pendingSpellCastPos = null;
      if (_pendingSpellFx.baseMinionId === BAN_HAMMER_SPELL_ID && _pendingSpellFx.targetInstanceId) {
        const targetEl = document.querySelector(
          `.battlefield-minion[data-instance-id="${CSS.escape(_pendingSpellFx.targetInstanceId)}"]`
        ) as HTMLElement | null;
        if (targetEl) {
          getFx().burstEl(targetEl, 'banX', { scale: _boardCardScale });
        } else {
          getFx().burstAt(pos?.x ?? 0, pos?.y ?? 0, 'banX', { scale: _boardCardScale });
        }
      } else if (pos) {
        getFx().burstAt(pos.x, pos.y, 'star', { scale: _boardCardScale });
      }
      _pendingSpellFx = null;
      return;
    }

    if (arraysEqual(currentIds, _lastBattlefieldMinionIds)
      && currentStatsHash === _lastBattlefieldMinionStatsHash
      && playerGold === _lastBattlefieldPlayerGold) {
      return;
    }
    // Slam FX attribution uses the persistent seen-set (not
    // _lastBattlefieldMinionIds, which the combat→prep reset blanks): a
    // minion slams only the first time it appears — i.e. when it is played
    // from the hand. Restored board cards after a round never re-slam.
    const newMinionIds = new Set(currentIds.filter((id) => !_fxSeenBattlefieldMinionIds.has(id)));
    for (const id of currentIds) _fxSeenBattlefieldMinionIds.add(id);
    // TRANSFORM detection: a swap IN PLACE — the board length is unchanged
    // (sells/plays change it, a combat→prep reset blanks the previous list)
    // and some position now holds a DIFFERENT instance of a DIFFERENT class.
    // The DISAPPEARED-OCCUPANT check is what separates a transform from a
    // plain REPOSITION (dragging a card past a card of another class): a
    // reposition keeps every id on the board (the previous occupant of the
    // slot is still present at a different index), while a transform
    // REMOVES the old minion — its id is gone from the board. Without this
    // check a move across different classes false-positives as a transform
    // (the white pulse + rays burst on the moved card). Upgrades keep the
    // id, so they never flag. Each state update carries exactly one action,
    // so this reads the transform unambiguously.
    const transformedIds = new Set<string>();
    if (currentIds.length === _lastBattlefieldMinionTypes.length) {
      const currentIdSet = new Set(currentIds);
      for (let i = 0; i < currentIds.length; i++) {
        const lastId = _lastBattlefieldMinionIds[i];
        if (
          currentIds[i] !== lastId &&
          !currentIdSet.has(lastId) &&
          _lastBattlefieldMinionTypes[i] !== undefined &&
          _lastBattlefieldMinionTypes[i] !== data.minions[i].minionType
        ) {
          transformedIds.add(currentIds[i]);
        }
      }
      // TRANSFORM SPELL (Transformed): the SAME instance keeps its id and
      // its stats but CHANGES CLASS (the server mutates the persistent
      // minion in place) — the id-based check above can never see it, so
      // detect the class swap on the same id directly (this is what made
      // the card render stale on a cast: the hash now includes the class,
      // so the rebuild runs, and the rays burst plays on the new class).
      for (let i = 0; i < currentIds.length; i++) {
        if (
          currentIds[i] === _lastBattlefieldMinionIds[i] &&
          _lastBattlefieldMinionTypes[i] !== undefined &&
          _lastBattlefieldMinionTypes[i] !== data.minions[i].minionType
        ) {
          transformedIds.add(currentIds[i]);
        }
      }
    }
    // HIGH STAKES destruction: the minion we just submitted a High Stakes
    // upgrade for is GONE from the board — the server rolled the destroy
    // (the only action that removes a minion without a sell). Capture the
    // PRE-REBUILD element's center so the black smoke burst can play over
    // its old slot after the rebuild. A minion gone without a pending High
    // Stakes request is a sell (no FX); a minion still present survived
    // (the level-up ring below covers it).
    let highStakesDestroyedCenter: { x: number; y: number } | null = null;
    if (_pendingHighStakesMinionId) {
      const pendingId = _pendingHighStakesMinionId;
      if (!currentIds.includes(pendingId)) {
        const deadEl = Array.from(battlefield.children).find(
          (c) => (c as HTMLElement).dataset?.instanceId === pendingId
        ) as HTMLElement | undefined;
        if (deadEl) {
          const r = deadEl.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) {
            highStakesDestroyedCenter = {
              x: r.left + r.width / 2,
              y: r.top + r.height / 2,
            };
          }
        }
      }
      _pendingHighStakesMinionId = null;
    }

    _lastBattlefieldMinionIds = [...currentIds];
    _lastBattlefieldMinionTypes = data.minions.map((m) => m.minionType);
    _lastBattlefieldMinionStatsHash = currentStatsHash;
    _lastBattlefieldPlayerGold = playerGold;

    battlefield.innerHTML = '';
    const minionIds = currentIds;

    for (let i = 0; i < data.minions.length; i++) {
      const minion = data.minions[i];
      const el = createBattlefieldMinionElement(minion, playerGold, maxUpgrades);
      // SELL PATHS (right-click-to-sell was REMOVED — it was too easy to
      // misclick): drag the card into the sell zone (top of the screen) or
      // press the card's Sell button (hidden by CSS, still in the DOM —
      // the programmatic/tutorial path). No contextmenu listener is
      // attached at all, so a mobile long-press can never sell (the OS
      // never fires the long-press dance without a listener).
      // Double-click the minion body to open the upgrade menu (only when the
      // minion can still be upgraded). onBattlefieldMinionSelected already
      // guards on the max-upgrade limit. Double-clicks on the buttons are left
      // to the buttons' own handlers.
      el.addEventListener('dblclick', (e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        e.preventDefault();
        onBattlefieldMinionSelected(minion.instanceId);
      });
      // Set up drag for repositioning
      if (dragAndDropService) {
        dragAndDropService.makeBattlefieldMinionDraggable(el, minion.instanceId, i, battlefield);
        dragAndDropService.setupBattlefieldMinionDropTarget(el, minion.instanceId, i, minionIds);
      }
      battlefield.appendChild(el);

      // FX: freshly played minions slam onto the board with a dust puff;
      // upgrades get a level-up ring (level tracked across rebuilds);
      // TRANSFORMED minions burst into white/yellow rays of light (the
      // swap-in-place detected above — checked FIRST: a transformed minion
      // is also a "new" id, but it must not slam, it must ray-burst).
      if (transformedIds.has(minion.instanceId)) {
        retriggerClass(el, 'fx-transform');
        getFx().burstEl(el, 'rays', { scale: _boardCardScale });
      } else if (newMinionIds.has(minion.instanceId)) {
        retriggerClass(el, 'fx-card-slam');
        // The card-slam dust puff scales with the board's zoom — at
        // desktop card sizes (~195px) an unscaled puff looks tiny.
        // LARGER (playtest, task 177 — "the smoke effect when we play a
        // card should be larger": count 32 → 48, the puff's own geometry
        // is bigger in FxCanvas._spawnPuff).
        getFx().burstEl(el, 'puff', { count: 48, scale: _boardCardScale });
      } else {
        const prevLevel = _lastBattlefieldLevels.get(minion.instanceId);
        if (prevLevel !== undefined && minion.level > prevLevel) {
          retriggerClass(el, 'fx-level-up');
          getFx().ringEl(el, '#f7c531', _boardCardScale);
        }
        // Heirloom transfer / Teacher redirect: permanent ATK/HP gain that
        // did NOT come from an upgrade (level unchanged). Highlight the
        // recipient with a green glow + green stat floats. THE PULSE PLAYS
        // ONCE PER GAIN (task 196 — the per-minion cooldown below dedupes
        // consecutive renders of the same gain).
        const prevStats = _lastBattlefieldStats.get(minion.instanceId);
        if (prevStats !== undefined && minion.level === prevStats.level) {
          const atkGain = minion.attack - prevStats.attack;
          const hpGain = minion.currentHp - prevStats.hp;
          if ((atkGain > 0 || hpGain > 0) && canPulseStatGain(minion.instanceId)) {
            retriggerClass(el, 'fx-stat-gain');
            getFx().ringEl(el, '#55efc4', _boardCardScale);
            if (atkGain > 0) floatText(el, `+${atkGain} ATK`, 'atk', atkGain);
            if (hpGain > 0) floatText(el, `+${hpGain} HP`, 'heal', hpGain);
          }
        }
      }
    }
    // SPELL CAST FX (targeted spells): the STAR explosion lands at the
    // RELEASE position — the drag drop point (where the user let go of the
    // card), recorded by the DnD service at cast time. Consumed AFTER the
    // rebuild (fresh elements) and BEFORE the stats-map update (the diff
    // needs the PRE-cast stats).
    if (_pendingSpellFx) {
      const pos = _pendingSpellCastPos;
      _pendingSpellCastPos = null;
      // BAN HAMMER (task 193): the large glowing red X pulses OVER THE
      // TARGET MINION and then explodes (not a star at the release point —
      // the mark is about the minion, not the hand).
      if (_pendingSpellFx.baseMinionId === BAN_HAMMER_SPELL_ID && _pendingSpellFx.targetInstanceId) {
        const targetEl = document.querySelector(
          `.battlefield-minion[data-instance-id="${CSS.escape(_pendingSpellFx.targetInstanceId)}"]`
        ) as HTMLElement | null;
        if (targetEl) {
          getFx().burstEl(targetEl, 'banX', { scale: _boardCardScale });
        } else {
          getFx().burstAt(pos?.x ?? 0, pos?.y ?? 0, 'banX', { scale: _boardCardScale });
        }
      } else if (pos) {
        getFx().burstAt(pos.x, pos.y, 'star', { scale: _boardCardScale });
      } else {
        const r = battlefield.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          getFx().burstAt(r.left + r.width / 2, r.top + r.height / 2, 'star', { scale: _boardCardScale });
        }
      }
      _pendingSpellFx = null;
    }

    _lastBattlefieldLevels = new Map(data.minions.map((m) => [m.instanceId, m.level]));
    _lastBattlefieldStats = new Map(
      data.minions.map((m) => [m.instanceId, { attack: m.attack, hp: m.currentHp, level: m.level }])
    );

    // HIGH STAKES destruction FX: black smoke over the destroyed minion's
    // old slot (the element is gone by now — the burst plays at the
    // captured pre-rebuild center; a no-op under reduced motion).
    if (highStakesDestroyedCenter) {
      getFx().burstAt(highStakesDestroyedCenter.x, highStakesDestroyedCenter.y, 'blackSmoke', { scale: _boardCardScale });
    }

    // Fit the (re)built board to the viewport width (desktop fill / mobile
    // fit — the board re-appears on the combat→prep transition, which is
    // a rebuild, so the fresh scale lands here).
    updateBoardCardScale();
  });
}

// ─── Wire CombatBoardView ─────────────────────────────────────────────────

// Track combat board minion elements for incremental updates
const _combatMinionElementsA = new Map<string, HTMLElement>();
const _combatMinionElementsB = new Map<string, HTMLElement>();

/**
 * IMPACT TIMING (task: impact timing): the impact FX + sound must play the
 * MOMENT the attacker CONTACTS the defender — the midpoint of the attack
 * lunge (furthest point from the start) — not when the stream's damage
 * event arrives (that is after the whole animation). The 'attack' case
 * registers a pending impact per (attacker→defender) pair; the animation's
 * onImpact callback fires the queued FX when the lunge reaches contact.
 * Stream events that arrive AFTER contact (or with no animation playing)
 * fire immediately — the queue is just a short-lived hand-off.
 */
const _pendingImpacts = new Map<string, { fx: (() => void) | null }>();
/** defender instance id → its current attack pair key (the death event
 *  carries no attacker id, so the death's Impact sound resolves the pair
 *  through this map). */
const _defenderPairKey = new Map<string, string>();

/**
 * Last-seen combat stats per side (playtest, task 177: "the effect that
 * is played when a minion receives permanent stats should happen THE
 * MOMENT it receives them" — permanent gains can land MID-COMBAT via
 * auras, and the old prep-board-only diff waited until the next
 * preparation rebuild). Diffed in updateCombatMinions BEFORE the DOM
 * update; the gain = attack up OR maxHp (startingHp) up — heals (only
 * currentHp up) never trigger it. Cleared in clearCombatMinionElements.
 */
const _lastCombatStatsA = new Map<string, { level: number; attack: number; hp: number; maxHp: number }>();
const _lastCombatStatsB = new Map<string, { level: number; attack: number; hp: number; maxHp: number }>();

/**
 * Which player owns which side CONTAINER during the current combat display
 * (container A = the top row, container B = the bottom row — see the
 * display swap in wireCombatBoardView: the current player is always
 * rendered on the bottom). Stream FX that key on a PLAYER instead of a
 * minion instance ID (the Toxic Cloud's green cloud over the affected
 * battlefield) resolve the container through this map. Kept in sync on
 * every combat board render; nulled when the combat area is cleared.
 */
let _combatSideOwner: { a: string | null; b: string | null } = { a: null, b: null };

/**
 * Starts the Mark (Blightcaster) continuous black-particle emitter on a
 * marked combat minion: a tiny DOM node (four dots on a staggered infinite
 * CSS loop — see .mark-particles in index.html) appended INSIDE the card
 * element, so it lives and dies with the card (combat-end cleanup removes
 * it for free). Skipped under reduced motion (the CSS media query hides
 * the node as a second layer). Idempotent per card (a re-mark never stacks
 * a second emitter).
 */
function startMarkEmitter(cardEl: HTMLElement): void {
  if (isReducedMotion()) return;
  if (cardEl.querySelector(':scope > .mark-particles')) return; // already marked
  const emitter = document.createElement('div');
  emitter.className = 'mark-particles';
  emitter.innerHTML =
    '<span class="mark-particle"></span>' +
    '<span class="mark-particle"></span>' +
    '<span class="mark-particle"></span>' +
    '<span class="mark-particle"></span>';
  cardEl.appendChild(emitter);
}

/** Removes the Mark emitter from a card (the marked minion died). */
function stopMarkEmitter(cardEl: HTMLElement): void {
  cardEl.querySelector(':scope > .mark-particles')?.remove();
}

/**
 * Incrementally update combat minion elements.
 * Creates new elements for new minions, updates existing elements for HP/alive changes,
 * and removes elements for dead/removed minions.
 */
/**
 * Permanent-buff pulse DEDUPE (task 196 — "the indicator that a minion got
 * a permanent buff should only pulse ONCE"): the stat-gain pulse is driven
 * by a RENDER DIFF, so the same gain can be seen by several consecutive
 * renders (the board re-renders on every stream event / state update while
 * the diff is fresh). A per-minion cooldown makes one logical gain play
 * exactly one pulse (the 1.2 s window covers the 0.8 s animation plus the
 * render gap; a genuinely LATER gain — a new permanent buff — always
 * passes, because the diff only exists for new deltas).
 */
const _statGainPulseAt = new Map<string, number>();
function canPulseStatGain(instanceId: string): boolean {
  const now = Date.now();
  const last = _statGainPulseAt.get(instanceId) ?? 0;
  if (now - last < 1200) return false;
  _statGainPulseAt.set(instanceId, now);
  return true;
}

/**
 * Incrementally update one side of the combat board (delegates to the shared,
 * unit-tested CombatMinionDom logic which also guards against stacking
 * duplicate minion elements when element tracking gets out of sync with the DOM).
 */
function updateCombatMinions(container: HTMLElement, minions: CombatMinionRenderData[], elementMap: Map<string, HTMLElement>, ownerId: string): void {
  // The OWNING player's (authoritative, server-sent) skins drive the art —
  // NOT the local player's selection, so the opponent's side renders with
  // the opponent's skins (and spectators see both sides' real skins).
  // COOP team side: per-minion — each minion renders with its OWNER's
  // selection (host's cards with the host's skins, the teammate's cards
  // with the teammate's — see combatMinionSkins).
  const usePerMinionSkins = !!shell?.gameState.match.combatStarted?.playerASkinsByPlayer
    && !!coopTeamOwnerMap();

  // ── PERMANENT stat-gain diff (task 177): detect gains BEFORE the DOM
  //    update (the stats maps must see the PRE-update values) and play
  //    the green pulse the MOMENT the gain lands — including mid-combat
  //    auras. Gain = attack up OR maxHp (startingHp) up, level unchanged
  //    (heals only raise currentHp — they never pulse).
  const statsMap = elementMap === _combatMinionElementsA ? _lastCombatStatsA : _lastCombatStatsB;
  const currentIds = new Set(minions.map((m) => m.instanceId));
  for (const id of Array.from(statsMap.keys())) {
    if (!currentIds.has(id)) statsMap.delete(id);
  }
  const gains: Array<{ id: string; atk: number; hp: number }> = [];
  for (const minion of minions) {
    const prev = statsMap.get(minion.instanceId);
    if (prev && minion.level === prev.level) {
      const atkGain = minion.attack - prev.attack;
      const hpGain = minion.startingHp - prev.maxHp;
      if (atkGain > 0 || hpGain > 0) gains.push({ id: minion.instanceId, atk: atkGain, hp: hpGain });
    }
    statsMap.set(minion.instanceId, {
      level: minion.level,
      attack: minion.attack,
      hp: minion.currentHp,
      maxHp: minion.startingHp,
    });
  }

  updateCombatMinionElements(container, minions, elementMap, (minion) =>
    createCombatMinionElement(minion, usePerMinionSkins
      ? combatMinionSkins(ownerId, minion.instanceId)
      : combatSideSkins(ownerId))
  );

  // Play the FX on the (freshly updated) elements — same signature look as
  // the preparation-board path: green pulse + green ring + stat floats,
  // scaled by the combat row's zoom. THE PULSE PLAYS ONCE PER GAIN (the
  // per-minion cooldown dedupes consecutive renders of the same gain).
  for (const gain of gains) {
    const el = elementMap.get(gain.id);
    if (!el || !canPulseStatGain(gain.id)) continue;
    retriggerClass(el, 'fx-stat-gain');
    getFx().ringEl(el, '#55efc4', _combatCardScale);
    if (gain.atk > 0) floatText(el, `+${gain.atk} ATK`, 'atk', gain.atk);
    if (gain.hp > 0) floatText(el, `+${gain.hp} HP`, 'heal', gain.hp);
  }
}

function wireCombatBoardView(): void {
  combatBoardView?.onRenderChange((data: CombatBoardViewRenderData) => {
    // The authoritative sides are A = first attacker / B = second attacker,
    // which looks arbitrary to the player. Always render the CURRENT PLAYER
    // on the LOWER side of the screen (container B) and the opponent on top
    // — swap the sides when the current player happens to be A. Spectators
    // (not a participant) see the raw A/B order. All combat-stream FX and
    // HP updates look elements up by instance ID, so this pure display swap
    // is transparent to them.
    // COOP team combat: the TEAM side (the combined row of BOTH humans'
    // minions) is ALWAYS the LOWER half for both humans (the bot sits on
    // top) — the plain "current player on the bottom" swap would put the
    // team on top for the teammate, because the team side carries the
    // team-seat player's id (the host, or the teammate if the host fell).
    const teamSide = (() => {
      const coop = _coopTeamInfo;
      if (!coop || coop.mode === 'normal' || !data.playerA || !data.playerB) return null;
      const isTeamSeat = (id: string) => id === coop.hostId || id === coop.teammateId;
      return isTeamSeat(data.playerA.playerId) ? data.playerA : isTeamSeat(data.playerB.playerId) ? data.playerB : null;
    })();
    let top: typeof data.playerA;
    let bottom: typeof data.playerA;
    if (teamSide) {
      bottom = teamSide;
      top = teamSide === data.playerA ? data.playerB : data.playerA;
    } else {
      top = data.playerA?.isCurrentPlayer ? data.playerB : data.playerA;
      bottom = data.playerA?.isCurrentPlayer ? data.playerA : data.playerB;
    }

    // Track which player owns which side container (top = A, bottom = B) —
    // the stream FX that key on a player (the Toxic Cloud) need it.
    _combatSideOwner = { a: top?.playerId ?? null, b: bottom?.playerId ?? null };

    // The TEAM side's label: "<viewer's name> + <teammate's name>" (both
    // names, not "You") — and it is styled as the viewer's own side.
    const teamSideLabel = (sidePlayerId: string): string | null => {
      const coop = _coopTeamInfo;
      if (!coop || coop.mode === 'normal') return null;
      if (sidePlayerId !== coop.hostId && sidePlayerId !== coop.teammateId) return null;
      const viewerId = shell?.playerId;
      const otherId = viewerId === coop.hostId ? coop.teammateId : coop.hostId;
      const other = shell?.gameState.match.players.find((p) => p.id === otherId);
      const myName = currentUsername ?? currentPlayerId ?? 'You';
      return `${myName} + ${displayUsername(other?.displayName ?? other?.id ?? 'Teammate')}`;
    };

    // The OPPONENT's label: their DISPLAY NAME (COOP mission bots carry
    // their campaign character name — "The Blight", "Midas", ...; the
    // authoritative PlayerData.displayName, the same source the player
    // pills use) with a fallback to the id (regular matches: the
    // opponent's username / guest id; spectators: the watched players).
    const opponentNameOf = (id: string): string =>
      displayUsername(shell?.gameState.match.players.find((p) => p.id === id)?.displayName ?? id);

    // Update side labels (name span + active rune icons to the right of
    // the name — cheap text/DOM updates, rebuilt only when the combat
    // sides change since this callback fires on every stream event).
    if (top) {
      const teamLabel = teamSideLabel(top.playerId);
      combatLabelAName.textContent = teamLabel ?? (top.isCurrentPlayer ? 'You' : opponentNameOf(top.playerId));
      const ownSide = teamLabel !== null || top.isCurrentPlayer;
      combatLabelA.className = `combat-side-label ${ownSide ? 'you' : top.isFirstAttacker ? 'spectator' : 'opponent'}`;
      renderCombatSideRunes(combatRunesA, combatSideRunes(top.playerId));
    }
    if (bottom) {
      const teamLabel = teamSideLabel(bottom.playerId);
      combatLabelBName.textContent = teamLabel ?? (bottom.isCurrentPlayer ? 'You' : opponentNameOf(bottom.playerId));
      const ownSide = teamLabel !== null || bottom.isCurrentPlayer;
      combatLabelB.className = `combat-side-label ${ownSide ? 'you' : bottom.isFirstAttacker ? 'spectator' : 'opponent'}`;
      renderCombatSideRunes(combatRunesB, combatSideRunes(bottom.playerId));
    }

    // Show/hide combat area based on combat state
    if (data.combatStarted) {
      combatArea.classList.add('active');
      preparationArea.classList.add('hidden');
    }

    // COOP mobile fit: rows that can carry 10 minions (the team's combined
    // row / the bot row in a coop team combat) get the .wide class — the
    // mobile CSS shrinks the cards (56×76, 4px gaps) so ALL 10 are visible
    // (at the 70px card size only ~8 fit in a landscape phone). Normal
    // 7-max rows never carry > 8 minions, so they are unaffected; the
    // class is inert on desktop (the CSS rule is mobile-fullscreen only).
    combatMinionsA.classList.toggle('wide', (top?.minions.length ?? 0) > 8);
    combatMinionsB.classList.toggle('wide', (bottom?.minions.length ?? 0) > 8);

    // Incremental update: only create/update/remove changed minion elements
    if (top) {
      updateCombatMinions(combatMinionsA, top.minions, _combatMinionElementsA, top.playerId);
    }
    if (bottom) {
      updateCombatMinions(combatMinionsB, bottom.minions, _combatMinionElementsB, bottom.playerId);
    }

    // COOP team combat: tint the TEAMMATE's combat copies yellow (the
    // team side is the combined row — the teammate's copies are the ones
    // whose instance ids are in the teammate's persistent board). Own
    // copies stay plain. Non-coop combats: no-op.
    updateCoopCombatOverlay();

    // Fit the combat rows to the viewport width (desktop fill / mobile
    // fit — re-measured on every stream event, so a row that grows mid-
    // combat via swarm/summon tokens re-fits immediately).
    updateCombatCardScale();
  });

  combatBoardView?.onCombatEnd((result) => {
    // IMPORTANT: do NOT clear element tracking here. In the batched CombatStream
    // flow the CombatResult arrives inside the stream message — BEFORE the
    // animation has finished playing. Clearing tracking here would make the next
    // render recreate every minion element on top of the existing DOM, stacking
    // duplicate sets of minions on the combat board.
    // DOM cleanup happens when the combat area is actually hidden (see
    // onStateChange) and in hideMatchComponents().
    void result;
  });
}

/**
 * Removes all combat minion elements from the board and clears element
 * tracking. Called when the combat area is hidden (combat fully over, after
 * the animation window) and when leaving the match screen.
 */
function clearCombatMinionElements(): void {
  clearCombatMinionElementSet(combatMinionsA, _combatMinionElementsA);
  clearCombatMinionElementSet(combatMinionsB, _combatMinionElementsB);
  // Reset the permanent-gain diff baselines (task 177) — the next combat
  // starts clean (no phantom gains vs the previous combat's stats).
  _lastCombatStatsA.clear();
  _lastCombatStatsB.clear();
  // Clear the impact-timing hand-off maps (no stale pairs across combats).
  _pendingImpacts.clear();
  _defenderPairKey.clear();
  // Drop any queued lunges — they reference the elements we just removed.
  _attackAnimationChain = Promise.resolve();
  // No combat on the board anymore — drop the player→container mapping so a
  // stale Toxic Cloud-style FX never resolves against a previous combat's
  // sides.
  _combatSideOwner = { a: null, b: null };
}

/**
 * COOP team combat: apply the yellow .coop-teammine overlay to the
 * TEAMMATE's copies on the (combined) team side of the combat board.
 * The teammate's combat copies carry the same instance ids as their
 * persistent board, so membership in that set is the owner test. No-op
 * outside a coop team match.
 */
function updateCoopCombatOverlay(): void {
  const coop = _coopTeamInfo;
  if (!coop || coop.mode === 'normal' || !shell) {
    for (const el of [..._combatMinionElementsA.values(), ..._combatMinionElementsB.values()]) {
      el.classList.remove('coop-teammine');
    }
    return;
  }
  // The overlay marks the OTHER human's minions ("the ones that are not
  // yours"): coop.hostId/teammateId are MISSION-level (teammate = the one
  // who joined the host), so resolve the other side RELATIVE TO THE VIEWER.
  const viewerId = shell.playerId;
  const otherId = viewerId === coop.hostId ? coop.teammateId : coop.hostId;
  const other = shell.gameState.match.players.find((p) => p.id === otherId);
  const otherIds = new Set(other?.battlefield.map((m) => m.instanceId) ?? []);
  for (const el of [..._combatMinionElementsA.values(), ..._combatMinionElementsB.values()]) {
    const id = el.getAttribute('data-instance-id');
    el.classList.toggle('coop-teammine', !!id && otherIds.has(id));
  }
}

// ─── Wire PreparationTimerUI ──────────────────────────────────────────────

function wirePreparationTimerUI(): void {
  // The last SECOND the timer displayed (ClockTic.wav plays on every
  // change 5 → 0, task: sounds).
  let lastSecond: number | null = null;
  preparationTimerUI?.onRenderChange((data: PreparationTimerRenderData) => {
    if (data.remainingSeconds !== null) {
      timerValue.textContent = String(data.remainingSeconds);
      timerValue.classList.toggle('locked', data.isLocked);
      // LAST 5 SECONDS (5, 4, 3, 2, 1, 0): the floating box is FULL size +
      // red (it is HALF size outside the window — task 197). The pulse is
      // SYNCED with the timer: exactly ONE pulse per second CHANGE (the
      // one-shot .timer-tick animation is re-triggered when the value
      // ticks — not a continuous loop).
      const critical = data.remainingSeconds >= 0 && data.remainingSeconds <= 5;
      timerBox.classList.toggle('timer-critical', critical);
      if (critical && lastSecond !== data.remainingSeconds) {
        timerBox.classList.remove('timer-tick');
        void timerBox.offsetWidth; // reflow — restart the one-shot animation
        timerBox.classList.add('timer-tick');
      }
      // SOUND: ClockTic on every value change from 5 down to 0.
      if (data.remainingSeconds <= 5 && lastSecond !== data.remainingSeconds) {
        soundService.play('clockTic');
      }
      lastSecond = data.remainingSeconds;
    } else {
      timerValue.textContent = '—';
      timerValue.classList.remove('locked');
      timerBox.classList.remove('timer-critical');
      timerBox.classList.remove('timer-tick');
      lastSecond = null;
    }
  });

  // (The old onLocked → "locked" phase label was removed with #hud-phase —
  // the phase text no longer exists.)
}

// ─── Wire CombatAnimationView ─────────────────────────────────────────────

/**
 * Heals arrive as damage events with NEGATIVE values (the server emits the
 * actual HP diff, so damage = prevHp - hpAfter — the exact pre-heal HP is
 * hpAfter + damage). Returns true only when a LIVING card is healed: the
 * card was alive before the heal AND is alive after it. A heal that
 * resurrects a dead card (pre-heal HP <= 0, e.g. the Killer full-heal or
 * the Rune of Rebirth) is NOT "healing an alive card" and skips the
 * plus-sign burst — those revivals have their own dedicated FX.
 */
function isAliveCardHealed(hpAfter: number, damage: number): boolean {
  return damage < 0 && hpAfter > 0 && hpAfter + damage > 0;
}

/** The combat minion's class by instance ID (attack-sound selection). */
function combatMinionTypeById(instanceId: string): string | null {
  const board = combatBoardView?.renderData;
  if (!board) return null;
  for (const side of [board.playerA, board.playerB]) {
    const m = side?.minions.find((mm) => mm.instanceId === instanceId);
    if (m) return m.minionType;
  }
  return null;
}

/**
 * Attack sounds (task: sounds) — the ATTACKER's class picks the voice:
 * Warrior/Tank/Runekeeper/Swarmer/Undead → Attack_3.wav (physical),
 * Healer/Mage/Buffer/Bard/Blightcaster (and anything else) → Attack_2.wav
 * (caster/support).
 */
const ATTACK3_MINION_TYPES = new Set(['warrior', 'tank', 'runekeeper', 'swarmer', 'undead']);
function playAttackSound(attackerInstanceId: string): void {
  const type = combatMinionTypeById(attackerInstanceId);
  soundService.play(type && !ATTACK3_MINION_TYPES.has(type) ? 'attack2' : 'attack3');
}

function wireCombatAnimationView(): void {
  combatAnimationView?.onNewEvent((event: AnimatedCombatEvent) => {
    // Feed event to simultaneous damage animator
    simultaneousDamageAnimator?.feedEvent(event);

    // Log the event and trigger animations
    switch (event.eventType) {
      case 'attack': {
        const msg = event.message as any;
        addCombatLogEntry(`${msg.attackerInstanceId} attacks ${msg.defenderInstanceId}`, 'attack');

        // SOUND: the attacker's class picks the voice (Attack_3 physical /
        // Attack_2 caster-support).
        playAttackSound(msg.attackerInstanceId);

        // Trigger attack animation: attacker moves toward defender.
        // THE IMPACT FX/SOUND (damage case) is scheduled on onImpact —
        // the lunge's contact point (see _pendingImpacts).
        // QUEUE the lunge on the animation chain — NEVER skip it: if the
        // previous lunge is still in flight (its event fired late because
        // the buff/aura FX stressed the main thread), this one starts as
        // soon as it finishes. Element lookup happens at RUN time, so a
        // mid-combat swap/rebuild (Necromancy, Infestation) is safe.
        {
          const pairKey = `${msg.attackerInstanceId}->${msg.defenderInstanceId}`;
          const lunge = () => new Promise<void>((resolve) => {
            const attackerEl = document.querySelector(`.combat-minion[data-instance-id="${msg.attackerInstanceId}"]`) as HTMLElement | null;
            const defenderEl = document.querySelector(`.combat-minion[data-instance-id="${msg.defenderInstanceId}"]`) as HTMLElement | null;
            _defenderPairKey.set(msg.defenderInstanceId, pairKey);
            if (attackerEl && defenderEl) {
              _pendingImpacts.set(pairKey, { fx: null });
              DragAndDropService.animateAttack(attackerEl, defenderEl, 320, () => {
                const entry = _pendingImpacts.get(pairKey);
                if (entry && entry.fx) entry.fx();
                _pendingImpacts.delete(pairKey);
              }).then(() => resolve());
            } else {
              resolve();
            }
          });
          _attackAnimationChain = _attackAnimationChain.then(lunge);
        }
        // FX: spark trail from attacker toward defender (every attack)
        {
          const fxAttackerEl = document.querySelector(`.combat-minion[data-instance-id="${msg.attackerInstanceId}"]`) as HTMLElement | null;
          const fxDefenderEl = document.querySelector(`.combat-minion[data-instance-id="${msg.defenderInstanceId}"]`) as HTMLElement | null;
          if (fxAttackerEl && fxDefenderEl) {
            getFx().trailEl(fxAttackerEl, fxDefenderEl, undefined, _combatCardScale);
          }
        }
        break;
      }
      case 'damage': {
        const msg = event.message as any;
        addCombatLogEntry(`${msg.attackerInstanceId} ↔ ${msg.defenderInstanceId} (${msg.damageToAttacker}/${msg.damageToDefender})`, 'damage');

        // Trigger simultaneous damage animation + the impact FX/sound.
        // IMPACT TIMING: when the defender is hit, the damage flash,
        // sparks, float and ShieldImpact all play at the lunge's CONTACT
        // (queued via _pendingImpacts + the animation's onImpact) — not
        // when this stream event arrives (that is after the animation).
        const attackerEl = document.querySelector(`.combat-minion[data-instance-id="${msg.attackerInstanceId}"]`) as HTMLElement;
        const defenderEl = document.querySelector(`.combat-minion[data-instance-id="${msg.defenderInstanceId}"]`) as HTMLElement;

        const impactFx = () => {
          DragAndDropService.animateSimultaneousDamage(
            attackerEl,
            defenderEl,
            msg.damageToAttacker,
            msg.damageToDefender,
          );
          // FX: impact sparks + floating damage numbers (font scales with
          // the amount so big hits read as big hits)
          if (defenderEl && msg.damageToDefender > 0) {
            floatText(defenderEl, `-${msg.damageToDefender}`, 'dmg', msg.damageToDefender);
            getFx().burstEl(defenderEl, 'sparks', { count: Math.min(4 + msg.damageToDefender, 16), scale: _combatCardScale });
          }
        };

        if (defenderEl && msg.damageToDefender > 0) {
          const pairKey = `${msg.attackerInstanceId}->${msg.defenderInstanceId}`;
          const entry = _pendingImpacts.get(pairKey);
          if (entry && entry.fx === null) {
            // The lunge is in flight — play at contact. (The ShieldImpact
            // sound joins the impact: the defender survived.)
            if (msg.defenderHpAfter > 0) {
              entry.fx = () => { impactFx(); soundService.play('shieldImpact'); };
            } else {
              entry.fx = impactFx; // lethal — the death's Impact sound plays on contact
            }
          } else {
            // No pending lunge (contact already passed, or no animation —
            // e.g. off-screen spectator) — play now.
            _pendingImpacts.delete(pairKey);
            impactFx();
            if (msg.defenderHpAfter > 0) soundService.play('shieldImpact');
          }
        } else {
          // No defender damage (heal-only exchange) — the flash feedback
          // stays immediate (there is no impact to align with).
          DragAndDropService.animateSimultaneousDamage(
            attackerEl,
            defenderEl,
            msg.damageToAttacker,
            msg.damageToDefender,
          );
        }
        if (attackerEl && msg.damageToAttacker > 0) {
          floatText(attackerEl, `-${msg.damageToAttacker}`, 'dmg', msg.damageToAttacker);
        }
        // Heals arrive as damage events with negative values (Lifesteal /
        // OnKill heals) — show a green heal indicator, scaled the same way.
        if (defenderEl && msg.damageToDefender < 0) {
          floatText(defenderEl, `+${-msg.damageToDefender}`, 'heal', -msg.damageToDefender);
          // FX: green plus-sign burst out of the healed card — only when an
          // ALIVE card is healed (resurrections keep their own effects).
          if (isAliveCardHealed(msg.defenderHpAfter, msg.damageToDefender)) {
            getFx().burstEl(defenderEl, 'plus', { count: Math.min(4 + -msg.damageToDefender, 16), scale: _combatCardScale });
          }
        }
        if (attackerEl && msg.damageToAttacker < 0) {
          floatText(attackerEl, `+${-msg.damageToAttacker}`, 'heal', -msg.damageToAttacker);
          if (isAliveCardHealed(msg.attackerHpAfter, msg.damageToAttacker)) {
            getFx().burstEl(attackerEl, 'plus', { count: Math.min(4 + -msg.damageToAttacker, 16), scale: _combatCardScale });
          }
        }

        // Update the combat board's HP state in playback order so the HP
        // numbers follow the animation (batched streams no longer set the
        // per-event lastCombatDamage state field).
        combatBoardView?.applyStreamDamage({
          attackerInstanceId: msg.attackerInstanceId,
          defenderInstanceId: msg.defenderInstanceId,
          attackerHpAfter: msg.attackerHpAfter,
          defenderHpAfter: msg.defenderHpAfter,
        });
        break;
      }
      case 'death': {
        const msg = event.message as any;
        addCombatLogEntry(`${msg.minionInstanceId} dies`, 'death');

        // Mark the minion dead on the combat board exactly when its death
        // is animated (batched streams no longer set lastCombatDeath).
        combatBoardView?.applyStreamDeath({
          minionInstanceId: msg.minionInstanceId,
          playerId: msg.playerId,
        });

        // THE DEATH EFFECT (explosion + Impact sound) plays at the lunge's
        // CONTACT — the same queued moment as the impact FX (the death
        // event carries no attacker id; the pair resolves through
        // _defenderPairKey). No pending lunge → play now.
        const deadEl = document.querySelector(`.combat-minion[data-instance-id="${msg.minionInstanceId}"]`) as HTMLElement | null;
        const deathFx = () => {
          if (deadEl) {
            getFx().burstEl(deadEl, 'explosion', { scale: _combatCardScale });
            soundService.play('impact');
          }
        };
        const killerPairKey = _defenderPairKey.get(msg.minionInstanceId);
        const killerEntry = killerPairKey ? _pendingImpacts.get(killerPairKey) : undefined;
        if (killerEntry && killerEntry.fx !== null) {
          // A queued impact already owns the contact moment — append the
          // death to it (capture the PREVIOUS fx in a const — the closure
          // must not read killerEntry.fx after we replace it).
          const prevFx = killerEntry.fx;
          killerEntry.fx = () => {
            prevFx();
            deathFx();
          };
        } else {
          deathFx();
        }

        if (deadEl) {
          // Mark (Blightcaster): a dead minion stops emitting its black
          // particles (the emitter is a child of the card element — combat
          // end removes it along with the card).
          stopMarkEmitter(deadEl);
        }
        break;
      }
      case 'toxicCloud': {
        const msg = event.message as any;
        addCombatLogEntry(`toxic cloud: ${msg.affectedPlayerId}'s minions take ${msg.damage}`, 'ability');
        // The green cloud rolls over the ENTIRE enemy battlefield — resolve
        // the affected player's side container through the display swap
        // (top = A / bottom = B) and play the FX there: a bank of green
        // mist (FxCanvas.cloudEl) + a green tint flash on the side row.
        const containerId =
          _combatSideOwner.a === msg.affectedPlayerId ? 'combat-minions-a'
          : _combatSideOwner.b === msg.affectedPlayerId ? 'combat-minions-b'
          : null;
        const sideEl = containerId ? document.getElementById(containerId) : null;
        if (sideEl) {
          getFx().cloudEl(sideEl, undefined, _combatCardScale);
          retriggerClass(sideEl, 'fx-toxic-cloud');
        }
        // The per-minion HP changes arrive as the simultaneous damage events
        // that follow in the stream (their damage numbers + death FX).
        break;
      }
      case 'mark': {
        const msg = event.message as any;
        addCombatLogEntry(`mark: ${msg.sourceInstanceId} marks ${msg.targetInstanceId}`, 'ability');
        // Start the continuous black-particle emitter at the marked minion
        // (the moment-of-mark burst at the caster already played from the
        // generic 'ability' event — ABILITY_FX.mark). The emitter runs until
        // the target dies (the 'death' case) or the combat ends (element
        // cleanup).
        const targetEl = document.querySelector(`.combat-minion[data-instance-id="${msg.targetInstanceId}"]`) as HTMLElement | null;
        if (targetEl) {
          startMarkEmitter(targetEl);
        }
        break;
      }
      case 'sharedPain': {
        const msg = event.message as any;
        addCombatLogEntry(`shared pain: ${msg.sourceInstanceId} → ${(msg.targetInstanceIds ?? []).join(', ')}`, 'ability');
        // The red expanding particle line(s): from the Shared Pain holder to
        // every marked victim that also received the damage (one line per
        // target; the per-target damage numbers arrive with the damage
        // events that follow).
        const srcEl = document.querySelector(`.combat-minion[data-instance-id="${msg.sourceInstanceId}"]`) as HTMLElement | null;
        for (const id of msg.targetInstanceIds ?? []) {
          const targetEl = document.querySelector(`.combat-minion[data-instance-id="${id}"]`) as HTMLElement | null;
          if (srcEl && targetEl) {
            getFx().painLineEl(srcEl, targetEl, undefined, _combatCardScale);
          }
        }
        break;
      }
      case 'freeze': {
        const msg = event.message as any;
        addCombatLogEntry(`freeze: ${msg.sourceInstanceId} freezes ${msg.targetInstanceId}`, 'ability');
        // FX: the large white ice/snow burst at the FROZEN minion (it plays
        // right after the hit that froze it lands — the damage number +
        // sparks already played from the damage event).
        const targetEl = document.querySelector(`.combat-minion[data-instance-id="${msg.targetInstanceId}"]`) as HTMLElement | null;
        if (targetEl) {
          getFx().burstEl(targetEl, 'ice', { scale: _combatCardScale });
        }
        // The blue/white ice overlay stays on the minion until it
        // unfreezes (freezeSkip) or dies — tracked in playback order so the
        // overlay lands exactly when the ice burst plays.
        combatBoardView?.applyStreamFreeze({
          instanceId: msg.targetInstanceId,
          playerId: msg.targetPlayerId,
        });
        break;
      }
      case 'freezeSkip': {
        const msg = event.message as any;
        addCombatLogEntry(`freeze skip: ${msg.instanceId} unfreezes and skips its attack`, 'ability');
        // FX: the ice-shatter burst at the unfrozen minion — the moment the
        // ice cracks and the skipped attack plays (no attack event follows
        // for it; the sequence moves on to the next minion).
        const skipEl = document.querySelector(`.combat-minion[data-instance-id="${msg.instanceId}"]`) as HTMLElement | null;
        if (skipEl) {
          getFx().burstEl(skipEl, 'ice', { count: 14, scale: _combatCardScale });
        }
        // The minion unfreezes — drop the ice overlay at the shatter moment.
        combatBoardView?.applyStreamFreezeSkip({
          instanceId: msg.instanceId,
          playerId: msg.playerId,
        });
        break;
      }
      case 'killGold': {
        const msg = event.message as any;
        addCombatLogEntry(`+ ${msg.goldAwarded} gold (${msg.killerInstanceId})`, 'gold');
        // FX: gold float ("+ x gold") + sparks on the element the gold
        // anchors to (the minion that caused the death — the client keeps
        // dead elements in the DOM with the .dead class, so the float
        // works for corpse-anchored events like sudden-death kills too).
        // No magnitude arg: the gold float keeps the fixed .fx-float-gold
        // size (bumped 16px → 20px — "slightly larger"), not the amount
        // scaling the damage floats use.
        //
        // The float is a REWARD indicator: it plays ONLY when the gold
        // lands with the VIEWER (killerPlayerId = the gold's recipient).
        // Enemy kills (and spectated combats — the viewer is in no pair)
        // show no gold float.
        const killerEl = document.querySelector(`.combat-minion[data-instance-id="${msg.killerInstanceId}"]`) as HTMLElement | null;
        if (killerEl && shouldShowKillGoldFloat(msg.killerPlayerId, currentPlayerId)) {
          floatText(killerEl, `+ ${msg.goldAwarded} gold`, 'gold');
          getFx().burstEl(killerEl, 'sparks', { count: 6, colors: FX_GOLD_COLORS, scale: _combatCardScale });
        }
        break;
      }
      case 'spellCast': {
        // REPEATER (Spellmaker, level 3 — task 184): the holder re-cast the
        // last spell that was cast on it on this neighbor at the start of
        // combat — play the SPELL-CAST effect here: the star burst (the
        // minion-targeting-spell cast FX) + the spell sound, at the
        // neighbor.
        const msg = event.message as any;
        addCombatLogEntry(`Repeater casts ${msg.spellId} on ${msg.targetInstanceId} (${msg.sourceInstanceId})`, 'ability');
        const spellTargetEl = document.querySelector(`.combat-minion[data-instance-id="${msg.targetInstanceId}"]`) as HTMLElement | null;
        if (spellTargetEl) {
          // BAN HAMMER re-cast (a Repeater echoing the ban) gets the red X
          // over the neighbor too — every other spell keeps the star.
          const kind = msg.spellId === BAN_HAMMER_SPELL_ID ? 'banX' : 'star';
          getFx().burstEl(spellTargetEl, kind, { scale: _combatCardScale });
          soundService.play('playCard');
        }
        // TASK 196: the re-cast's GRANTED KEYWORDS land on the neighbor's
        // combat card (the temporary keyword badge — e.g. an Infested
        // re-cast's SwarmLord). The stat gains ride on the aura/heal
        // events that precede this one (the client's numbers already tick
        // up by the time the star plays).
        if (Array.isArray(msg.grantedAbilities) && msg.grantedAbilities.length > 0) {
          combatBoardView?.applyStreamSpellCastAbilities({
            playerId: msg.targetPlayerId,
            instanceId: msg.targetInstanceId,
            abilities: msg.grantedAbilities,
          });
        }
        break;
      }
      case 'ability': {
        const msg = event.message as any;
        addCombatLogEntry(`${msg.abilityId} triggers (${msg.sourceInstanceId})`, 'ability');
        // FX: ability-specific magic at the source minion
        const srcEl = document.querySelector(`.combat-minion[data-instance-id="${msg.sourceInstanceId}"]`) as HTMLElement | null;
        if (srcEl) {
          if (msg.abilityId === 'greater_heal') {
            // Greater Heal signature look: a line of green plus-sign
            // particles shoots from the caster over EVERY living friendly
            // minion (the source's side of the combat board — the side
            // container is the source's parent). Dead minions are skipped
            // (the heal skips them too). The per-target +N floats and plus
            // bursts still play from the heal damage events that follow.
            const sideEl = srcEl.parentElement;
            if (sideEl) {
              const targets = Array.from(sideEl.querySelectorAll<HTMLElement>('.combat-minion:not(.dead)'))
                .filter((el) => el !== srcEl);
              for (const target of targets) {
                getFx().plusLineEl(srcEl, target, undefined, _combatCardScale);
              }
            }
          } else {
            const spec = fxForAbility(msg.abilityId);
            getFx().burstEl(srcEl, spec.kind, { colors: spec.colors, count: spec.count, scale: _combatCardScale });
            // Explosive / Exploding signature: on top of the red/yellow
            // 'boom' particle burst (ABILITY_FX), emit the large "BOOM!"
            // text at the exploding minion (grows rapidly, fades out while
            // wobbling — CSS in index.html).
            if (msg.abilityId === 'explosive' || msg.abilityId === 'exploding') {
              boomText(srcEl);
              // SOUND: the Boom effect plays Explode.wav.
              soundService.play('explode');
            }
          }
        }
        break;
      }
      case 'explodingShimmer': {
        // Exploding passive trigger: the BOOM just played at the dead
        // minion (its 'ability' event) — the red shimmering glow appears
        // around the EDGES of every living Exploding source whose passive
        // caused it (CSS in index.html, self-cleaning 0.9s animation).
        const msg = event.message as any;
        addCombatLogEntry(`exploding shimmer (${(msg.instanceIds ?? []).join(', ')})`, 'ability');
        for (const id of msg.instanceIds ?? []) {
          const el = document.querySelector(`.combat-minion[data-instance-id="${id}"]`) as HTMLElement | null;
          retriggerClass(el, 'fx-exploding-shimmer');
        }
        break;
      }
      case 'aura': {
        const msg = event.message as any;
        // Warband aura changed (stacked up, dropped, or removed) — update the
        // ATK numbers on the combat board exactly when the change is animated.
        // Snapshot the PRE-change attack per minion BEFORE the stream apply
        // (the board view's tracked state still holds the old totals) so the
        // float can show the DIFFERENCE ("- 2 atk" when a Warband holder
        // dies, "+ 2 atk" when a stack lands or Brute doubles on attack).
        const boardData = combatBoardView?.renderData;
        const prevAttack = new Map<string, number>();
        for (const side of [boardData?.playerA, boardData?.playerB]) {
          for (const m of side?.minions ?? []) prevAttack.set(m.instanceId, m.attack);
        }
        combatBoardView?.applyStreamAura({
          playerId: msg.playerId,
          changes: msg.changes,
        });
        if (Array.isArray(msg.changes)) {
          const summary = msg.changes.map((c: any) => `${c.instanceId}→${c.attack}`).join(', ');
          addCombatLogEntry(`aura update: ${summary}`, 'ability');
          // FX: gold spark flash + ATK-difference float on each changed
          // minion ("- x atk" / "+ x atk" — the signed delta from the
          // pre-change tracked value; falls back to the absolute value
          // when there is no tracked state, e.g. a freshly spawned copy).
          for (const c of msg.changes) {
            const changedEl = document.querySelector(`.combat-minion[data-instance-id="${c.instanceId}"]`) as HTMLElement | null;
            if (changedEl) {
              const prev = prevAttack.get(c.instanceId);
              if (prev !== undefined && c.attack !== prev) {
                const delta = c.attack - prev;
                floatText(changedEl, delta > 0 ? `+ ${delta} atk` : `-${-delta} atk`, 'atk', Math.abs(delta));
              } else {
                floatText(changedEl, `ATK ${c.attack}`, 'atk');
              }
              getFx().burstEl(changedEl, 'sparks', { count: 6, colors: FX_GOLD_COLORS, scale: _combatCardScale });
            }
          }
        }
        break;
      }
      case 'inspired': {
        const msg = event.message as any;
        addCombatLogEntry(`hero: ${msg.sourceInstanceId} inspired ${msg.targets?.map((t: any) => t.instanceId).join(', ') ?? ''}`, 'ability');
        // SOUND: the buff lands — PowerUp (the stat-gain sound).
        soundService.play('powerUp');
        // Tracked board state: the temporary keyword badge + the NEW
        // authoritative stats (the keyword's +N, doubled per re-grant).
        // The badge disappears with the post-combat state update (the
        // stats are temporary by construction).
        combatBoardView?.applyStreamInspired({
          playerId: msg.playerId,
          sourceInstanceId: msg.sourceInstanceId,
          targets: msg.targets ?? [],
        });
        // FX: a gold stat-gain burst + the +N ATK/HP float on each
        // inspired minion (the badge renders with the card rebuild).
        for (const t of msg.targets ?? []) {
          const el = document.querySelector(`.combat-minion[data-instance-id="${t.instanceId}"]`) as HTMLElement | null;
          if (el) {
            getFx().burstEl(el, 'sparks', { colors: FX_GOLD_COLORS, count: 12, scale: _combatCardScale });
            floatText(el, `+${t.statTotal} ATK / +${t.statTotal} HP`, 'atk');
          }
        }
        break;
      }
      case 'lifeBurst': {
        const msg = event.message as any;
        addCombatLogEntry(`lifeBurst: ${msg.sourceInstanceId} → ALL minions heal`, 'ability');
        // SOUND: the board-wide heal — PowerUp (the heal signature).
        soundService.play('powerUp');
        // FX (task 206): the BRIGHT GREEN flash covers the entire
        // battlefield…
        screenFlash('green');
        // …followed by bursting green plus-signs ALL OVER the battlefield
        // (every living minion on BOTH sides — the per-target +N floats
        // and their own plus bursts ride the heal damage events that
        // follow in the stream; this adds the board-wide green burst).
        for (const h of msg.healed ?? []) {
          const el = document.querySelector(`.combat-minion[data-instance-id="${h.instanceId}"]`) as HTMLElement | null;
          if (el) {
            getFx().burstEl(el, 'plus', { count: 14, scale: _combatCardScale });
          }
        }
        break;
      }
      case 'suddenDeath': {
        const msg = event.message as any;
        addCombatLogEntry(`SUDDEN DEATH (attack #${msg.attackNumber}): all minions take ${msg.damage} damage`, 'death');

        // Track the mode in the combat board state (render data) and update
        // the red background fade + top-right damage indicator exactly when
        // the tick is animated. The per-victim HP/death updates arrive as
        // the simultaneous damage/death events that follow in the stream.
        combatBoardView?.applyStreamSuddenDeath({
          attackNumber: msg.attackNumber,
          damage: msg.damage,
        });
        updateSuddenDeathVisuals(msg.damage);
        // FX: embers rising across the combat area
        getFx().burstEl(combatArea, 'embers', { scale: _combatCardScale });
        break;
      }
      case 'necromancy': {
        const msg = event.message as any;
        // SOUND: an Undead is spawned → BeastSound.
        soundService.play('beast');
        addCombatLogEntry(`necromancy: ${msg.victimInstanceId} → Undead (${msg.undeadInstanceId})`, 'ability');

        // Replace the dead minion with the temporary Undead on the combat
        // board exactly when the spawn is animated. The Undead disappears
        // when the post-combat state update re-initializes the board from
        // the restored (original) battlefield.
        combatBoardView?.applyStreamNecromancy({
          playerId: msg.playerId,
          position: msg.position,
          victimInstanceId: msg.victimInstanceId,
          undeadInstanceId: msg.undeadInstanceId,
          attack: msg.attack,
          currentHp: msg.currentHp,
          abilities: msg.abilities,
        });
        // FX: green mist where the Undead materializes
        const undeadEl = document.querySelector(`.combat-minion[data-instance-id="${msg.undeadInstanceId}"]`) as HTMLElement | null;
        if (undeadEl) {
          getFx().burstEl(undeadEl, 'mist', { count: 18, scale: _combatCardScale });
        }
        break;
      }
      case 'infestation': {
        const msg = event.message as any;
        // SOUND: the Swarmer hatches in → BeastSound (the token sound —
        // the Swarmer's own sound).
        soundService.play('beast');
        addCombatLogEntry(`infestation: ${msg.victimInstanceId} → Fragile Swarmer (${msg.infestationInstanceId})`, 'ability');

        // Replace the corpse with the temporary FRAGILE SWARMER on the
        // combat board exactly when the spawn is animated (the Necromancy
        // swap convention). The token disappears when the post-combat
        // state update re-initializes the board from the restored
        // battlefield. The identity (minionType/abilities) renders the
        // Swarmer card with the Fragile badge (task 196; task 204 — the
        // token IS the Swarmer, the accidental Spawner class is gone).
        combatBoardView?.applyStreamInfestation({
          playerId: msg.playerId,
          position: msg.position,
          victimInstanceId: msg.victimInstanceId,
          infestationInstanceId: msg.infestationInstanceId,
          attack: msg.attack,
          currentHp: msg.currentHp,
          minionType: msg.minionType,
          abilities: msg.abilities,
        });
        // FX: spore mist + hatch ring where the Swarmer materializes
        // (it is about to attack immediately — the attack events follow
        // in the stream and play the normal lunge/impact FX).
        const tokenEl = document.querySelector(`.combat-minion[data-instance-id="${msg.infestationInstanceId}"]`) as HTMLElement | null;
        if (tokenEl) {
          getFx().burstEl(tokenEl, 'mist', { count: 18, scale: _combatCardScale });
          getFx().ringEl(tokenEl, '#8e44ad', _combatCardScale);
        }
        break;
      }
      case 'treasure': {
        const msg = event.message as any;
        // SOUND: the treasure materializes → the buy/pickup sound (the
        // item's sound — a golden chest appearing).
        soundService.play('apply');
        addCombatLogEntry(`treasure hunter: ${msg.victimInstanceId} → treasure (${msg.treasureInstanceId})`, 'ability');
        // Replace the corpse with the temporary treasure on the combat
        // board exactly when the spawn is animated (the Necromancy/
        // Infestation corpse-swap convention — the generic token swap).
        // The token disappears when the post-combat state update re-
        // initializes the board from the restored battlefield. The
        // identity (minionType / abilities) renders the Treasure Chest
        // card with the Treasure badge (the enemy can kill it for its
        // 25-gold reward).
        combatBoardView?.applyStreamInfestation({
          playerId: msg.playerId,
          position: msg.position,
          victimInstanceId: msg.victimInstanceId,
          infestationInstanceId: msg.treasureInstanceId,
          attack: msg.attack,
          currentHp: msg.currentHp,
          minionType: msg.minionType,
          abilities: msg.abilities,
        });
        // FX: gold burst + gold ring where the treasure materializes.
        const treasureEl = document.querySelector(`.combat-minion[data-instance-id="${msg.treasureInstanceId}"]`) as HTMLElement | null;
        if (treasureEl) {
          getFx().burstEl(treasureEl, 'sparks', { count: 18, colors: FX_GOLD_COLORS, scale: _combatCardScale });
          getFx().ringEl(treasureEl, '#f1c40f', _combatCardScale);
        }
        break;
      }
      case 'shieldBroken': {
        const msg = event.message as any;
        addCombatLogEntry(`shield breaker: ${msg.sourceInstanceId} broke ${msg.targetInstanceId}'s Shield`, 'ability');
        // SOUND: the shield SHATTERS — the boom (the shield-break burst is
        // a particle explosion).
        soundService.play('explode');
        // Drop the Shield badge from the card (the server removed the
        // keyword from the combat copy — the tracked board mirrors it so
        // the card stops showing the taunt badge for the rest of combat;
        // the post-combat state update restores it).
        combatBoardView?.applyStreamShieldBroken({
          targetPlayerId: msg.targetPlayerId,
          targetInstanceId: msg.targetInstanceId,
        });
        // FX: the red shield-shatter particle burst on the targeted unit
        // (the shield wave in RED — it plays ONLY when a Shield is
        // actually removed, so it never fires on unshielded targets).
        const brokenEl = document.querySelector(`.combat-minion[data-instance-id="${msg.targetInstanceId}"]`) as HTMLElement | null;
        if (brokenEl) {
          getFx().burstEl(brokenEl, 'shield', { colors: FX_RED_COLORS, scale: _combatCardScale });
        }
        break;
      }
      case 'swarm': {
        const msg = event.message as any;
        // SOUND: swarmers are hatching → BeastSound (once per hatch event).
        soundService.play('beast');
        const swarmerCount = Array.isArray(msg.swarmers) ? msg.swarmers.length : 0;
        addCombatLogEntry(`swarmlord: ${msg.sourceInstanceId} hatches ${swarmerCount} Swarmer(s)`, 'ability');

        // Fill the SwarmLord holder's combat board with the temporary
        // Swarmer tokens at their authoritative positions exactly when the
        // spawn is animated. The swarmers' attack / damage / death events
        // follow in the stream; the tokens disappear when the post-combat
        // state update re-initializes the board (like Necromancy Undeads).
        combatBoardView?.applyStreamSwarm({
          playerId: msg.playerId,
          sourceInstanceId: msg.sourceInstanceId,
          swarmers: msg.swarmers ?? [],
        });
        // FX: every spawned swarmer card plays its own egg-hatching burst
        // (the shell crack + tumbling shell shards) — one 'hatch' burst per
        // spawned card, so a full board of 7 hatches at once.
        for (const swarmer of msg.swarmers ?? []) {
          const swarmerEl = document.querySelector(`.combat-minion[data-instance-id="${swarmer.instanceId}"]`) as HTMLElement | null;
          if (swarmerEl) {
            getFx().burstEl(swarmerEl, 'hatch', { scale: _combatCardScale });
          }
        }
        break;
      }
      case 'combatResult': {
        // The batched stream has finished playing (every attack/damage/death
        // animation has been triggered) — safe to reveal the result now without
        // spoiling the outcome. The result data was already populated on the
        // CombatResultUI as soon as the stream arrived.
        showCombatResult();
        break;
      }
    }
  });

  // Periodically build damage groups for animation
  combatAnimationView?.onRenderChange((data: CombatAnimationViewRenderData) => {
    if (data.pendingCount > 0) {
      // Build groups from pending events
      simultaneousDamageAnimator?.buildGroups();
    }
  });
}

// ─── Wire CombatGoldDisplay ───────────────────────────────────────────────

function wireCombatGoldDisplay(): void {
  combatGoldDisplay?.onRenderChange((data: CombatGoldDisplayRenderData) => {
    if (data.totalCount > 0) {
      combatGoldDisplayEl.classList.add('visible');
      combatGoldDisplayEl.innerHTML = `
        <div style="font-weight:700;margin-bottom:4px;color:#f39c12;">Combat Gold</div>
        <div class="gold-gain-entry">
          <span>Your kills:</span>
          <span class="gold-amount">+${data.currentPlayerTotalGold}</span>
        </div>
        <div class="gold-gain-entry">
          <span>Opponent:</span>
          <span class="gold-amount">+${data.opponentTotalGold}</span>
        </div>
      `;
    } else {
      combatGoldDisplayEl.classList.remove('visible');
      combatGoldDisplayEl.innerHTML = '';
    }
  });
}

/**
 * Updates the sudden-death visuals for a tick that just landed:
 *  - the combat area background fades further toward red (more red on each
 *    tick, capped so the board stays readable),
 *  - the top-right damage indicator becomes visible and shows the damage
 *    all minions take on the current attack,
 *  - every living combat minion gets a short red flash.
 * The per-victim HP updates are applied separately by the simultaneous
 * damage events that follow the suddenDeath event in the stream.
 */
function updateSuddenDeathVisuals(damage: number): void {
  _suddenDeathRedLevel += 1;
  const opacity = Math.min(0.05 * _suddenDeathRedLevel, 0.45);
  // The red fade is the TOP background layer of #combat-area (see the CSS
  // `--combat-area-red` variable), so it tints the Battlefield.png backdrop
  // instead of hiding it.
  combatArea.style.setProperty('--combat-area-red', `rgba(231, 76, 60, ${opacity})`);

  suddenDeathIndicator.classList.add('visible');
  suddenDeathDamageEl.textContent = String(damage);

  // Red-flash all living minions (retrigger the CSS animation on each tick).
  for (const el of combatArea.querySelectorAll('.combat-minion:not(.dead)')) {
    const minionEl = el as HTMLElement;
    minionEl.classList.remove('sudden-death-hit');
    void minionEl.offsetWidth; // force reflow so the animation restarts
    minionEl.classList.add('sudden-death-hit');
  }
}

/**
 * Clears the sudden-death visuals (background fade, indicator, flashes).
 * Called when the combat area is hidden / when leaving the match.
 */
function clearSuddenDeathVisuals(): void {
  _suddenDeathRedLevel = 0;
  combatArea.style.removeProperty('--combat-area-red');
  suddenDeathIndicator.classList.remove('visible');
  suddenDeathDamageEl.textContent = '0';
  for (const el of combatArea.querySelectorAll('.combat-minion.sudden-death-hit')) {
    (el as HTMLElement).classList.remove('sudden-death-hit');
  }
}

/**
 * COOP treasure turn: flash the "Treasure fight!" banner for 2 seconds
 * when the combat board loads (the battle phase starts). Reuses the
 * combat-result overlay (same element/look as the win/lose/tie message —
 * gold label, no details line). The banner auto-hides on its own (the
 * combat stream always runs longer than 2 s) and never sets
 * `_combatResultShown`, so the end-of-combat result overlay still shows.
 */
function showTreasureFightBanner(): void {
  _treasureBannerShownForCombat = true;
  combatResultEl.classList.add('visible');
  combatResultLabel.textContent = 'Treasure fight!';
  combatResultLabel.className = 'result-label treasure';
  combatResultDetails.textContent = '';
  setTimeout(() => {
    combatResultEl.classList.remove('visible');
    _treasureBannerShownForCombat = false;
  }, 2000);
}

/**
 * Reveal the combat result overlay (Victory/Defeat/Tie/Timeout) once the
 * combat animation stream has finished playing.
 *
 * The result DATA is available as soon as the batched CombatStream arrives,
 * but showing it that early would spoil the outcome while the minions are
 * still fighting. So this is only invoked when CombatAnimationView reaches
 * the stream's CombatResult event (after every attack/damage/death animation
 * has been triggered) — see wireCombatAnimationView's 'combatResult' case.
 */
function showCombatResult(): void {
  if (_combatResultShown) return;
  const data = combatResultUI?.renderData;
  if (!data || !data.resultType) return;

  // Spectators are watching a pair they do NOT take part in — the
  // Victory/Defeat/Tie overlay (and its win/loss screen flashes) is for
  // combat participants only. Any non-idle spectator status (waiting /
  // watching / finished) means we are spectating, so the overlay stays
  // hidden; the "Spectating" banner carries the outcome instead.
  const spectatorStatus = spectatorModeUI?.renderData.status;
  if (spectatorStatus && spectatorStatus !== 'idle') return;

  _combatResultShown = true;
  // HEAD-TO-HEAD HISTORY (task 181): the pill tooltip lists the viewer's
  // fights vs each player — recorded from the OPPONENT's side (viewer win
  // → the player 'loss'/red). Spectators already returned above; the
  // viewer is a participant here.
  recordHeadToHeadResult(data.resultType);
  combatResultEl.classList.add('visible');
  combatResultLabel.textContent = data.resultLabel;
  combatResultLabel.className = `result-label ${data.resultType}`;

  // COOP team match: list the gold EACH player gained (both humans + the
  // enemy, labeled by name) — the authoritative per-player goldGained map.
  const coop = _coopTeamInfo;
  const goldGained = data.rawResult?.goldGained;
  if (coop && coop.mode !== 'normal' && goldGained) {
    const state = shell?.gameState;
    const players = state ? state.match.players : [];
    const nameOf = (id: string) =>
      displayUsername(
        players.find((p) => p.id === id)?.displayName ??
        (id === state?.match.players.find((p) => p.id === shell?.playerId)?.id ? 'You' : id)
      );
    const rows: string[] = [];
    // Team members first (both humans — the kill gold each earned), then
    // the enemy bots (their 2x end-of-combat gold).
    for (const id of [coop.hostId, coop.teammateId, ...coop.enemyIds]) {
      if (!(id in goldGained)) continue;
      const earned = goldGained[id];
      if (earned === 0) continue;
      rows.push(`${nameOf(id)}: +${earned}🪙`);
    }
    combatResultDetails.textContent =
      `Life: ${data.lifeLost > 0 ? `-${data.lifeLost}` : '—'} | ` +
      (rows.length > 0 ? rows.join('   ') : `Gold: +${data.goldGained}`);
  } else {
    combatResultDetails.textContent = `Life: ${data.lifeLost > 0 ? `-${data.lifeLost}` : '—'} | Gold: +${data.goldGained}`;
  }

  // FX: celebrate combat wins, sting on losses (match-win confetti comes
  // later at game over — keep combat wins lighter)
  if (data.resultType === 'win') {
    screenFlash('gold');
    getFx().burstEl(combatResultLabel, 'sparks', { count: 14, colors: FX_GOLD_COLORS });
  } else if (data.resultType === 'loss') {
    screenFlash('red');
  }

  // Auto-hide after the result display window.
  setTimeout(() => {
    combatResultEl.classList.remove('visible');
    _combatResultShown = false;
  }, COMBAT_RESULT_DISPLAY_MS);
}

// ─── Wire SpectatorModeUI ─────────────────────────────────────────────────

function wireSpectatorModeUI(): void {
  spectatorModeUI?.onRenderChange((data: SpectatorModeRenderData) => {
    if (data.status !== 'idle') {
      spectatorBanner.classList.add('visible');
      spectatorMessageEl.textContent = data.spectatorMessage;
    } else {
      spectatorBanner.classList.remove('visible');
    }
  });
}

// ─── Wire EliminatedPlayerScreen ──────────────────────────────────────────

function wireEliminatedPlayerScreen(): void {
  eliminatedPlayerScreen?.onRenderChange((data: EliminatedPlayerRenderData) => {
    if (data.isEliminated) {
      eliminatedScreen.classList.add('visible');
      eliminatedMessage.textContent = data.eliminationMessage;
      // FX: red flash the first time the elimination screen shows
      if (!_eliminationFxPlayed) {
        _eliminationFxPlayed = true;
        screenFlash('red');
      }
    } else {
      eliminatedScreen.classList.remove('visible');
    }
  });
}

// ─── Wire WinnerScreen ────────────────────────────────────────────────────

function wireWinnerScreen(): void {
  winnerScreen?.onRenderChange((data: WinnerScreenRenderData) => {
    if (data.isGameOver) {
      updateGameOverScreenFromWinner(data);
    }
  });
}

// ─── Wire ReconnectUI ─────────────────────────────────────────────────────

function wireReconnectUI(): void {
  reconnectUI?.onRenderChange((data: ReconnectRenderData) => {
    if (data.isVisible) {
      reconnectOverlay.classList.add('visible');
      reconnectMessage.textContent = data.statusMessage;
      reconnectStatus.textContent = data.matchId
        ? `Match: ${data.matchId}${data.reconnectedPhase ? ` | Phase: ${data.reconnectedPhase}` : ''}`
        : '';
    } else {
      reconnectOverlay.classList.remove('visible');
    }
  });
}

// ─── Wire UpgradeChoiceUI ────────────────────────────────────────────────

function wireUpgradeChoiceUI(): void {
  // Track the minion submitted for a HIGH STAKES upgrade: the confirming
  // board update (wireBattlefieldUI) resolves it — minion gone → black
  // smoke destruction FX over its old slot; minion alive → the regular
  // level-up FX. Any other branch clears the tracker.
  upgradeChoiceUI?.onBranchSelected((minionInstanceId, branchId) => {
    _pendingHighStakesMinionId =
      branchId === HIGH_STAKES_BRANCH_ID ? minionInstanceId : null;
  });
  upgradeChoiceUI?.onRenderChange((data: UpgradeChoiceUIRenderData) => {
    if (_pendingUpgradeMinionId && data.hasSelectedMinion && data.selectedMinion?.instanceId === _pendingUpgradeMinionId) {
      // Only show the panel if the server has responded with options.
      // The fallback shows "Gain an ability…" — wait for the server
      // response which shows the specific ability name (e.g., "Gain Lifesteal").
      const abilityBranch = data.availableBranches.find(b => b.branchId === 'ability');
      const hasServerOptions = !abilityBranch || abilityBranch.name !== 'Gain an ability…';

      if (hasServerOptions && data.availableBranches.length > 0 && data.canAffordUpgrade) {
        showUpgradePanel(data);
        _pendingUpgradeMinionId = null;
      }
      // If still waiting for server options, keep _pendingUpgradeMinionId set.
      // The next render callback (when server responds) will pick it up.
    }
    // If the minion was deselected (e.g., sold or another action happened),
    // clear the pending flag.
    if (_pendingUpgradeMinionId && !data.hasSelectedMinion) {
      _pendingUpgradeMinionId = null;
    }
  });
}

// ─── Wire FullscreenService ───────────────────────────────────────────────

function wireFullscreenService(): void {
  if (!fullscreenService || !btnFullscreen) return;

  // Hide button if fullscreen is not supported
  btnFullscreen.style.display = fullscreenService.getState().isSupported ? '' : 'none';

  // Wire toggle button
  btnFullscreen.addEventListener('click', async () => {
    const service = fullscreenService;
    if (!service) return;
    const success = await service.toggle();
    if (success) {
      updateFullscreenButtonState(service.isFullscreen);
    }
  });

  // Subscribe to fullscreen state changes (e.g., user presses Escape)
  fullscreenService.onChange((isFullscreen) => {
    updateFullscreenButtonState(isFullscreen);
  });

  // Keep the rotate-device hint in sync with orientation changes
  // (device rotation, lock applied/released, fullscreen exit)
  fullscreenService.onOrientationChange(() => {
    updateRotateHintVisibility();
  });

  // Set initial button state
  updateFullscreenButtonState(fullscreenService.isFullscreen);
}

// ─── Mobile Game Orientation Enforcement ──────────────────────────────────
// Mobile devices play best in landscape (board + hand + HUD do not fit in
// portrait). While a MATCH is active (screens 'match' AND 'gameOver' — the
// winner/elimination views are part of the game until the player leaves
// it), main.ts:
//   1. asks the FullscreenService to enter fullscreen + lock the screen to
//      landscape (best effort — browsers require a user gesture, and a
//      server-driven match start usually has none), and
//   2. whenever the device is in portrait AND the lock is not in effect
//      (Screen Orientation API missing, the gesture-less fullscreen request
//      was rejected, or the user bailed out of fullscreen), shows the
//      full-screen "rotate your device" overlay. Tapping the overlay
//      retries the fullscreen + lock — the tap itself is a user gesture.
// Login / identity-choices / queue views are NEVER affected: the moment the
// app leaves the match screens the lock is released and the overlay hidden.

let _gameOrientationEnforced = false;

/** Show/hide the rotate hint based on the current enforcement state. */
function updateRotateHintVisibility(): void {
  // CrazyGames subpage: the hint is never shown (the platform handles
  // orientation — regular users keep the hint).
  if (IS_CRAZYGAMES_MODE) {
    rotateHintOverlay.classList.remove('visible');
    return;
  }
  const show =
    _gameOrientationEnforced &&
    !!fullscreenService &&
    fullscreenService.isMobile &&
    !fullscreenService.isLandscape;
  rotateHintOverlay.classList.toggle('visible', show);
}

/**
 * Begin enforcement for the active match (screen 'match'). On mobile this
 * attempts fullscreen + landscape lock; the rotate hint appears whenever
 * the device is in portrait without an active lock. Idempotent.
 */
async function beginGameOrientationEnforcement(): Promise<void> {
  // CrazyGames subpage: the platform owns fullscreen + orientation (the
  // game runs inside their frame) — no lock attempt, no rotate hint.
  if (IS_CRAZYGAMES_MODE) {
    return;
  }
  _gameOrientationEnforced = true;
  if (!fullscreenService) {
    return;
  }
  if (!fullscreenService.isMobile) {
    return; // Desktop — nothing to lock, the overlay never shows
  }
  // The orientation-change subscription (wireFullscreenService) keeps the
  // overlay in sync while the lock is applied or lost later (e.g. the user
  // exits fullscreen via the browser UI).
  await fullscreenService.beginGameOrientation();
  updateRotateHintVisibility();
}

/** Release enforcement when leaving the match screens. Idempotent. */
async function releaseGameOrientationEnforcement(): Promise<void> {
  _gameOrientationEnforced = false;
  rotateHintOverlay.classList.remove('visible');
  await fullscreenService?.releaseGameOrientation();
}

function updateFullscreenButtonState(isFullscreen: boolean): void {
  if (!btnFullscreen) return;
  btnFullscreen.classList.toggle('active', isFullscreen);
  btnFullscreen.title = isFullscreen
    ? 'Exit fullscreen'
    : 'Enter fullscreen' + (fullscreenService?.isMobile ? ' (landscape)' : '');
  btnFullscreen.textContent = isFullscreen ? '⛶' : '⛶';
}

// ─── Game Over Screen ─────────────────────────────────────────────────────

function updateGameOverScreen(): void {
  if (!shell) return;
  const matchState = shell.gameState.match;
  const winnerId = matchState.winnerId;
  const currentPlayerId = shell.playerId;

  // COOP: the team wins together — the canonical winner is the host, so a
  // teammate viewing a team victory must get the VICTORY screen too (the
  // "teammate sees defeat while the team won" bug).
  const coop = matchState.coop;
  const teamWon = !!coop && coop.mode !== 'normal' &&
    (winnerId === coop.hostId || winnerId === coop.teammateId);

  if (winnerId === currentPlayerId || teamWon) {
    gameoverTitle.textContent = 'You Win!';
    gameoverMessage.textContent = 'Congratulations! You are the last player standing.';
    // FX: victory celebration (once per match)
    if (!_victoryFxPlayed) {
      _victoryFxPlayed = true;
      screenFlash('gold');
      getFx().confetti();
    }
  } else {
    gameoverTitle.textContent = 'Game Over';
    gameoverMessage.textContent = `Winner: ${winnerId ?? 'unknown'}. Better luck next time!`;
    // FX: defeat flash (once per match)
    if (!_defeatFxPlayed) {
      _defeatFxPlayed = true;
      screenFlash('red');
    }
  }

  // Render standings — TASK 199: when the server assigned the FINAL
  // PLACEMENTS (present on every player after match end — purely
  // survival-based, random tie-break among same-turn deaths), sort by
  // them and show the position ("3.") on each row.
  const hasPlacements = matchState.players.length > 0 &&
    matchState.players.every((p) => typeof p.finalPlacement === 'number');
  const ordered = hasPlacements
    ? [...matchState.players].sort((a, b) => (a.finalPlacement ?? 0) - (b.finalPlacement ?? 0))
    : matchState.players;
  gameoverStandings.innerHTML = '';
  for (const player of ordered) {
    const standing = document.createElement('div');
    standing.className = 'standing';
    if (player.id === winnerId) standing.classList.add('winner');
    if (player.id === currentPlayerId) standing.classList.add('current-player');
    standing.innerHTML = `
      ${hasPlacements ? `<span class="placement">${player.finalPlacement}.</span>` : ''}
      <span class="player-name">${displayUsername(player.displayName ?? player.id)}</span>
      <span class="player-stats">❤️ ${player.life} | 💰 ${player.gold}</span>
    `;
    gameoverStandings.appendChild(standing);
  }

  // Highlight OUR position in the headline message (task 199).
  const myStanding = matchState.players.find((p) => p.id === currentPlayerId);
  if (hasPlacements && myStanding && typeof myStanding.finalPlacement === 'number') {
    const ordinal = `${myStanding.finalPlacement}${(function (n: number) { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return s[(v - 20) % 10] || s[v] || s[0]; })(myStanding.finalPlacement)}`;
    const extra = ` You finished ${ordinal} out of ${matchState.players.length}.`;
    if (!gameoverMessage.textContent.includes(extra.trim())) {
      gameoverMessage.textContent += extra;
    }
  }
}

function updateGameOverScreenFromWinner(data: WinnerScreenRenderData): void {
  const currentPlayerId = playerIdFromShell();

  if (data.isWinner) {
    gameoverTitle.textContent = 'You Win!';
    gameoverMessage.textContent = data.victoryMessage;
    // FX: victory celebration (once per match)
    if (!_victoryFxPlayed) {
      _victoryFxPlayed = true;
      screenFlash('gold');
      getFx().confetti();
    }
  } else {
    gameoverTitle.textContent = 'Game Over';
    gameoverMessage.textContent = data.victoryMessage;
    // FX: defeat flash (once per match)
    if (!_defeatFxPlayed) {
      _defeatFxPlayed = true;
      screenFlash('red');
    }
  }

  // Render standings
  gameoverStandings.innerHTML = '';
  for (const s of data.standings) {
    const standing = document.createElement('div');
    standing.className = 'standing';
    if (s.isWinner) standing.classList.add('winner');
    if (s.isCurrentPlayer) standing.classList.add('current-player');
    standing.innerHTML = `
      <span class="placement">${s.placement}.</span>
      <span class="player-name">${displayUsername(s.displayName ?? s.playerId)}</span>
      <span class="player-stats">❤️ ${s.finalLife} | 💰 ${s.finalGold}</span>
    `;
    gameoverStandings.appendChild(standing);
  }
}

// ─── Utility ──────────────────────────────────────────────────────────────

function playerIdFromShell(): string {
  return shell?.playerId ?? 'unknown';
}

/**
 * Compare two string arrays for equality (same length, same elements, same order).
 */
function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Start the auto-disconnect timer for eliminated players.
 * After 20 seconds, automatically disconnects and returns to queue.
 */
function startEliminationAutoDisconnect(): void {
  // Clear any existing timer
  if (_eliminationAutoDisconnectTimer) {
    clearTimeout(_eliminationAutoDisconnectTimer);
    _eliminationAutoDisconnectTimer = null;
  }

  _eliminationAutoDisconnectTimer = setTimeout(() => {
    if (shell) {
      shell.shutdown();
      showScreen('landing');
    }
  }, AUTO_DISCONNECT_DELAY_MS);
}

/**
 * Cancel the auto-disconnect timer (e.g., if player manually clicks "Back to Queue").
 */
function cancelEliminationAutoDisconnect(): void {
  if (_eliminationAutoDisconnectTimer) {
    clearTimeout(_eliminationAutoDisconnectTimer);
    _eliminationAutoDisconnectTimer = null;
  }
}

/**
 * Handle the "Back to Queue" button click for eliminated/dead players.
 * Keeps the current identity (username for logged-in users, guest ID for
 * guests) — never regenerates a random ID here, or a logged-in user would
 * lose their username.
 */
function onLeaveMatch(): void {
  cancelEliminationAutoDisconnect();
  restartShellKeepingIdentity();
}

// ─── Entry Point ───────────────────────────────────────────────────────────

main();
